import { sanitizeHtml } from '../utils/sanitizeHtml';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import BBALoader from '../components/BBALoader';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { ArrowLeft, User, Mail, Phone, MapPin, Building, Users, Calendar, Edit2, Save, X, Building2, Flag, Activity, MessageSquare, Settings, Trash2, ChevronDown, DollarSign, Target, Clock, CheckCircle, XCircle, RefreshCw, Download, FileText, FileCheck, Shield, PenTool, Copy, Play, Lock, Power, Search, Plus, Star, LogIn, Eye, SlidersHorizontal, ShoppingCart, Sparkles } from 'lucide-react';
import { generateLeadSummary } from '../services/openaiService';
import { getAccountById, updateAccount, deleteAccount, getCrmCompanyLogoDisplayUrl } from '../services/firestoreService';
import { createPortalInvite } from '../services/portalInviteService.js';
import ContactEmailAliases from '../components/crm/ContactEmailAliases';
import { accountAliasEmails, PRIMARY_CONTACT_KEY } from '../utils/contactEmailAliases';
import { PDFService } from '../services/pdfService';
import { getUserNdaTemplate } from '../services/firebase/ndaTemplateService';
import ESignatureService from '../services/eSignatureService';
import { triggerFlaggedLeadNotification } from '../services/notificationTriggerService';
import { getInternalUsers } from '../services/userService';
import { fetchGoogleCalendarEvents } from '../services/googleCalendarService';
import { fetchGmailMessagesForEmailWithStatus } from '../services/gmailService';
import { markUserIntegrationNeedsReauth } from '../services/userIntegrationService';
import GmailConnectionNotice from '../components/GmailConnectionNotice';
import EmailThreadList from '../components/crm/EmailThreadList';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import PortalBackground from '../components/PortalBackground';
import { db, storage } from '../firebase';
import { findMeetingSummaryForEvent } from '../utils/meetingSummaryLookup';
import { doc, onSnapshot, updateDoc, collection, query, where, getDocs, Timestamp, setDoc, orderBy } from 'firebase/firestore';
import { getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import clientRoadmapService from '../services/clientRoadmapService';
import AccountAlaCarteBidsTab from '../components/crm/AccountAlaCarteBidsTab';
import AccountDocumentsTab, { normalizeCollectionForConversion } from '../components/crm/AccountDocumentsTab';
import ProgramDetail, { CUSTOM_PROGRAM, CUSTOM_TIER } from './ProgramDetail';
import { isRetiredProgram } from '../utils/retiredPrograms';
import StartProgramActivityRow from '../components/crm/StartProgramActivityRow';
import { normalizeLeadSigningChance, SIGNING_CHANCE_META, SIGNING_CHANCE_VALUES } from '../utils/leadSigningChance';
import { CONTACT_FLAG_THRESHOLD_DAYS } from '../utils/contactFlags';
import { syncLeadCalendlyCalls } from '../services/calendlySyncService';
import { MINIMUM_TERM_OPTIONS } from '../utils/accountProgramDeliverables';
import { START_FREE_PAGES as START_PAGES, getStartFreePageLabel } from '../data/startFreePages';
import { fetchStartActivity } from '../services/startActivityLog';
import { fetchTemplateStructure } from '../services/hrStructureService';
import { leadConversionFields, leadLostFields, programMapWith } from '../utils/accountLifecycle';
import MeetingSummaryModal from '../components/MeetingSummaryModal';
import LeadLongTermFollowUpCard from '../components/crm/LeadLongTermFollowUpCard';
import { logAccountView } from '../services/staffActivityLog';

// Webhook URL for when a lead is marked as Cold (Zapier)
const COLD_LEAD_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/17294713/ult2m6m/';

// Send cold lead data to Zapier webhook (fire-and-forget; does not block UI)
// Zapier catch hooks parse form-encoded body into separate fields; JSON often appears as one field.
const sendColdLeadWebhook = async (lead) => {
  if (!lead) return;
  const fullName = [lead.contact?.firstName, lead.contact?.lastName].filter(Boolean).join(' ').trim() || '';
  const companyName = lead.company || '';
  const email = lead.contact?.email || '';
  const phone = lead.contact?.phone || '';
  const formData = new URLSearchParams();
  formData.append('fullName', fullName);
  formData.append('companyName', companyName);
  formData.append('email', email);
  formData.append('phone', phone);
  try {
    const response = await fetch(COLD_LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData.toString()
    });
    if (!response.ok) {
      console.warn('Cold lead webhook response not ok:', response.status);
    }
  } catch (err) {
    console.error('Cold lead webhook failed:', err);
  }
};

// Helper function to recursively remove undefined values from objects (Firestore doesn't allow undefined)
const removeUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }
  if (typeof obj === 'object' && obj.constructor === Object) {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
  return obj;
};

// startFlowProgress.completedSteps id for "Schedule Analysis Call" (see StartDashboard.js)
const SCHEDULE_CALL_STEP_ID = 4;

// Coerce the many date shapes we get back (Timestamp, Date, ISO string, {seconds}) into a Date
const toDateOrNull = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw.toDate === 'function') {
    const d = raw.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (raw.seconds != null) return new Date(raw.seconds * 1000);
  if (typeof raw === 'string') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// The summary tabs on the Lead overview: an AI summary of the other three, then the three in the order they happen.
// `tabLabel` is the short name on the tab row; `label` names the content in placeholders.
const SUMMARY_TABS = [
  { id: 'summary', tabLabel: 'Summary', label: 'Summary', field: 'leadSummary' },
  { id: 'intake', tabLabel: 'Intake', label: 'Intake Message', field: 'intakeNote' },
  { id: 'discovery', tabLabel: 'Discovery', label: 'Discovery Call', field: 'discoveryCallSummary' },
  { id: 'analysis', tabLabel: 'Analysis', label: 'Analysis Call', field: 'analysisCallSummary' }
];

// Fingerprint of the three notes the AI summary was built from, stored as leadSummarySourceKey
// so the Summary tab knows when intake/discovery/analysis changed and it needs regenerating.
const leadSummarySourceKey = (intake, discovery, analysis) => {
  const text = [intake, discovery, analysis].map((part) => (part || '').trim()).join('\u0001');
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  return String(hash >>> 0);
};

// Plain-text copy of the AI summary for leadSummary (read by the MCP tools and Client Lead History).
const flattenLeadSummary = ({ overview = '', highlights = [], nextSteps = [] }) =>
  [
    overview,
    highlights.length ? `Highlights:\n${highlights.map((h) => `• ${h}`).join('\n')}` : '',
    nextSteps.length ? `Next steps:\n${nextSteps.map((s) => `• ${s}`).join('\n')}` : ''
  ]
    .filter(Boolean)
    .join('\n\n');

// The two calendar/Calendly bookings that drive the lead's call dates. Same names the
// Calendly sync (functions/leadCalendlyUtils.js) matches on — keep them in step.
const DISCOVERY_EVENT_TITLE = 'bba discovery call';
const ANALYSIS_EVENT_TITLE = 'assessment review & next steps';

// Google Calendar titles arrive prefixed with the start time ("2:00 PM - BBA Discovery Call").
const normalizeEventTitle = (title) =>
  (title || '')
    .replace(/^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*/i, '')
    .trim()
    .toLowerCase();

const toLocalDateString = (d) =>
  d && !isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : null;

// Stored call dates are 'YYYY-MM-DD', an ISO string or 'TBD' — flatten for comparison.
const storedCallDateString = (raw) => {
  if (!raw || raw === 'TBD') return null;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return toLocalDateString(toDateOrNull(raw));
};

// Next upcoming booking of that event, else the most recent past one — mirrors the Calendly rule.
const pickCallDateFromEvents = (events, targetTitle, now = new Date()) => {
  const matching = (events || [])
    .filter((ev) => ev?.status !== 'cancelled' && ev?.status !== 'canceled')
    .map((ev) => ({ at: toDateOrNull(ev.start || ev.startTime), title: normalizeEventTitle(ev.title || ev.summary) }))
    .filter((x) => x.at && x.title.includes(targetTitle));
  if (matching.length === 0) return null;
  const upcoming = matching.filter((x) => x.at >= now).sort((a, b) => a.at - b.at);
  if (upcoming.length > 0) return toLocalDateString(upcoming[0].at);
  return toLocalDateString(matching.sort((a, b) => b.at - a.at)[0].at);
};

// Timeline entries the lead did themselves in the portal — rendered in blue
const CLIENT_PORTAL_ACTION_KINDS = new Set([
  'portal_signup',
  'portal_signup_alt',
  'portal_login',
  'nda_signed',
  'assessment_complete',
  'analysis_call_scheduled',
  'program_chosen',
  'free_resource',
  // Start-side browsing, logged by src/services/startActivityLog.js
  'start_page_view',
  'start_program_viewed',
  'start_program_customized',
  'start_program_chosen'
]);

/**
 * Page numbers to render for the notes timeline: always first and last, plus the
 * current page's neighbours, with 'gap' markers between. Start activity can push
 * a busy lead past 25 pages, and one button per page overruns the row.
 */
const buildPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const ordered = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  ordered.forEach((page, i) => {
    if (i > 0 && page - ordered[i - 1] > 1) out.push('gap');
    out.push(page);
  });
  return out;
};

// Map a startActivity doc onto the timeline kind that decides its icon.
const startActivityKind = (entry) => {
  switch (entry?.kind) {
    case 'program_viewed':
      return 'start_program_viewed';
    case 'program_customized':
      return 'start_program_customized';
    case 'program_chosen':
      return 'start_program_chosen';
    default:
      return 'start_page_view';
  }
};

// Customized / chosen program rows render as an expandable deliverables card (StartProgramActivityRow)
const START_PROGRAM_ROW_KINDS = new Set(['start_program_customized', 'start_program_chosen']);

// Proposal page tabs - ids match StartProposals.js activeTab values for filtering
const PROPOSAL_TABS = [
  { id: 'proposal', label: 'Design Proposal', desc: 'Cover page, scope, budget, timeline, next steps, about us, measure meeting', icon: FileText },
  { id: 'contracts', label: 'Design Contract', desc: 'Agreement pages and signatures', icon: FileCheck },
  { id: 'buildProposal', label: 'Build Proposal', desc: 'Build proposal template and download', icon: FileText },
  { id: 'buildContract', label: 'Build Contract', desc: 'Construction contract template and download', icon: FileCheck }
];

const LeadOverview = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const { isMobile, isTablet } = useResponsive();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [notes, setNotes] = useState([]);
  const [currentNotesPage, setCurrentNotesPage] = useState(1);
  const [notesPerPage] = useState(20); // Merged timeline (system + notes) per page
  const [interactionNoteModal, setInteractionNoteModal] = useState(null);
  const [interactionModalDate, setInteractionModalDate] = useState('');
  const [interactionModalInitiatedBy, setInteractionModalInitiatedBy] = useState('client');
  const [interactionModalChannel, setInteractionModalChannel] = useState('phone');
  const [quickNoteDraft, setQuickNoteDraft] = useState('');
  const [quickNoteComposerOpen, setQuickNoteComposerOpen] = useState(false);
  const quickNoteInputRef = useRef(null);
  const [textNoteModal, setTextNoteModal] = useState(null);
  const [textNoteModalBody, setTextNoteModalBody] = useState('');
  const [textNoteModalDate, setTextNoteModalDate] = useState('');
  // Timeline row opened from the Summary tab's Recent Activity card — scrolled to and ringed in Notes
  const [highlightedTimelineId, setHighlightedTimelineId] = useState(null);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignedTo: '',
    priority: 'Medium',
    dueDate: '',
    status: 'Pending'
  });
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [newDocument, setNewDocument] = useState({ title: '', file: null });
  const [documents, setDocuments] = useState([
    {
      id: 1,
      title: 'Non-Disclosure Agreement (NDA)',
      description: 'Required for project confidentiality',
      status: 'Pending',
      icon: 'ndatemp.svg'
    }
  ]);
  const [showAddSummaryModal, setShowAddSummaryModal] = useState(false);
  const [newSummary, setNewSummary] = useState('');
  const [currentCallType, setCurrentCallType] = useState('intakeNote'); // 'intakeNote', 'intake', 'discovery', 'onboarding', or 'proposal'
  // Summary card: one tab per summary, only one editable at a time
  const [summaryTab, setSummaryTab] = useState('summary');
  const [editingSummaryTab, setEditingSummaryTab] = useState(null);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [summaryGenError, setSummaryGenError] = useState('');
  const summaryAutoGenRef = useRef(null);
  const leadIdRef = useRef(leadId);
  leadIdRef.current = leadId;
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState(false);
  const [companyLogoDragOver, setCompanyLogoDragOver] = useState(false);
  const [intakeNote, setIntakeNote] = useState('');
  const [editingIntakeNote, setEditingIntakeNote] = useState(false);
  const [wasFlagged, setWasFlagged] = useState(false);
  const [intakeFormData, setIntakeFormData] = useState({
    whyReachOut: '',
    biggestPainPoint: '',
    whatDoWell: '',
    wantToImprove: '',
    notes: ''
  });
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [currentAssessmentType, setCurrentAssessmentType] = useState('');
  const [assessmentData, setAssessmentData] = useState(null);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showUpdateSubstatusModal, setShowUpdateSubstatusModal] = useState(false);
  const [showMakeClientModal, setShowMakeClientModal] = useState(false);
  const [clientFormData, setClientFormData] = useState({
    program: '',
    startDate: '',
    minimumTerm: ''
  });
  const [statusHistory, setStatusHistory] = useState([]);
  const [isInvitingToPortal, setIsInvitingToPortal] = useState(false);
  const [isDeletingPortalAccount, setIsDeletingPortalAccount] = useState(false);
  const [generatedInviteLink, setGeneratedInviteLink] = useState(null);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [signedNDA, setSignedNDA] = useState(null);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [showContractAuditTrailModal, setShowContractAuditTrailModal] = useState(false);
  
  // BBA signing state
  const [showBBASigningModal, setShowBBASigningModal] = useState(false);
  const [ndaTemplate, setNdaTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [showSuccessManagerPopup, setShowSuccessManagerPopup] = useState(false);
  const [leadEvents, setLeadEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsFilter, setEventsFilter] = useState('all');
  const [eventsCurrentPage, setEventsCurrentPage] = useState(1);
  const eventsPerPage = 6;
  const [showSigningChancePopup, setShowSigningChancePopup] = useState(false);
  const [showMeetingSummaryModal, setShowMeetingSummaryModal] = useState(false);
  const [selectedEventForSummary, setSelectedEventForSummary] = useState(null);
  const [meetingSummary, setMeetingSummary] = useState(null);
  const [meetingSummaryLoading, setMeetingSummaryLoading] = useState(false);
  const [leadEmails, setLeadEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  // Internal users whose Gmail is disconnected / needs reconnecting, so the Emails
  // tab can say so instead of silently showing "No emails found"
  const [gmailDisconnectedUsers, setGmailDisconnectedUsers] = useState([]);
  const [emailsFilter, setEmailsFilter] = useState('all');
  const [portalUserActivity, setPortalUserActivity] = useState(null);
  const [portalUserActivityLoading, setPortalUserActivityLoading] = useState(false);
  // Rolled-up Start-side page views and program actions (users/{uid}/startActivity)
  const [startActivity, setStartActivity] = useState([]);
  const [bbaFormData, setBbaFormData] = useState({});
  const [roadmapTemplates, setRoadmapTemplates] = useState([]);
  const [roadmapLoading, setRoadmapLoading] = useState(false);
  const [roadmapCurrentPage, setRoadmapCurrentPage] = useState(1);
  const roadmapPerPage = 12;
  const [isBBASigned, setIsBBASigned] = useState(false);
  const [finalizedNDA, setFinalizedNDA] = useState(null);
  const [qualified, setQualified] = useState(null); // null = not set, true = qualified, false = not qualified
  const [showOnKanban, setShowOnKanban] = useState(true); // default to true (yes)
  const [result, setResult] = useState(null); // null = not set, 'Won' or 'Lost'
  const [resultReason, setResultReason] = useState(null); // Reason for Won or Lost
  const [showACHModal, setShowACHModal] = useState(false);
  const [achInfo, setAchInfo] = useState(null);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [showContractBBASigningModal, setShowContractBBASigningModal] = useState(false);
  const [contractBBASigned, setContractBBASigned] = useState(false);
  const [contractBBAData, setContractBBAData] = useState({});
  const [contractSigningLoading, setContractSigningLoading] = useState(false);
  const [contractSigningError, setContractSigningError] = useState(null);
  const [showReceivedDatePicker, setShowReceivedDatePicker] = useState(false);
  const [showDiscoveryCallDatePicker, setShowDiscoveryCallDatePicker] = useState(false);
  const [showOnboardingCallDatePicker, setShowOnboardingCallDatePicker] = useState(false);
  const [currentUserCalendlyConnected, setCurrentUserCalendlyConnected] = useState(false);
  const [leadCalendlySyncing, setLeadCalendlySyncing] = useState(false);
  const [expandedStages, setExpandedStages] = useState({});
  const [editingAssessmentCard, setEditingAssessmentCard] = useState(null); // 'general', 'sales', 'operations', 'finance', 'technology', 'marketing'
  const [assessmentEditData, setAssessmentEditData] = useState({});
  const [isEditingAssessmentModal, setIsEditingAssessmentModal] = useState(false);
  const [showSubstatusDateModal, setShowSubstatusDateModal] = useState(false);
  const [selectedSubstatus, setSelectedSubstatus] = useState(null); // { name, stageName, currentDate }
  const [substatusDate, setSubstatusDate] = useState('');
  const [showACHUploadModal, setShowACHUploadModal] = useState(false);
  const [achFormData, setAchFormData] = useState({
    accountType: '',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    nameOnAccount: ''
  });
  const [achFormErrors, setAchFormErrors] = useState({});
  const [conferences, setConferences] = useState([]);
  const [loadingConferences, setLoadingConferences] = useState(false);
  
  // Free page selection state
  const [selectedFreePage, setSelectedFreePage] = useState(null);
  const [freePageSearch, setFreePageSearch] = useState('');
  const [showFreePageDropdown, setShowFreePageDropdown] = useState(false);
  const [availableSOPs, setAvailableSOPs] = useState([]);
  const [selectedSOPs, setSelectedSOPs] = useState([]);
  const [loadingSOPs, setLoadingSOPs] = useState(false);
  const [availableKPIs, setAvailableKPIs] = useState([]);
  const [selectedKPIs, setSelectedKPIs] = useState([]);
  const [loadingKPIs, setLoadingKPIs] = useState(false);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [availableEstimatingCategories, setAvailableEstimatingCategories] = useState([]);
  const [selectedEstimatingCategories, setSelectedEstimatingCategories] = useState([]);
  const [loadingEstimatingCategories, setLoadingEstimatingCategories] = useState(false);
  const [availableGPTs, setAvailableGPTs] = useState([]);
  const [selectedGPTs, setSelectedGPTs] = useState([]);
  const [loadingGPTs, setLoadingGPTs] = useState(false);
  const [availableTechStack, setAvailableTechStack] = useState([]);
  const [selectedTechStack, setSelectedTechStack] = useState([]);
  const [loadingTechStack, setLoadingTechStack] = useState(false);
  const [techStackUnlocked, setTechStackUnlocked] = useState(false);
  const [selectedProposalTabs, setSelectedProposalTabs] = useState([]);
  // Sales materials come from the CRM-managed catalog (templatesStructures/sales);
  // items are identified by catalogKey (the structure item's title), not doc ids.
  const [availableSalesMaterials, setAvailableSalesMaterials] = useState([]);
  const [selectedSalesMaterials, setSelectedSalesMaterials] = useState([]);
  const [loadingSalesMaterials, setLoadingSalesMaterials] = useState(false);

  // Proposal tab state
  const [proposalStandardProgram, setProposalStandardProgram] = useState('Lite');
  const [proposalPrograms, setProposalPrograms] = useState({});
  // Proposed Program is read-only until "Edit Program" is clicked; the key remounts ProgramDetail
  // so cancelling drops any unsaved deliverable changes.
  const [editingProposedProgram, setEditingProposedProgram] = useState(false);
  // Same builder, but for a lead whose program was never confirmed in Assessment Analysis.
  const [creatingProposedProgram, setCreatingProposedProgram] = useState(false);
  const [proposedProgramEditKey, setProposedProgramEditKey] = useState(0);

  // Refs to prevent infinite loops in useEffect hooks
  const assessmentCompletionProcessedRef = useRef(false);
  const coldLeadStatusProcessedRef = useRef(false);
  const lastLeadSubstatusRef = useRef(null);
  const lastAssessmentDataRef = useRef(null);
  const lastAssessmentCompletionDataRef = useRef(null);
  const lastLeadIdRef = useRef(null); // Track lead ID to reset refs when lead changes
  const lastProcessTimeRef = useRef(0); // Track last processing time to prevent rapid re-runs
  const calendlySyncedForLeadRef = useRef(null);
  const summaryMigrationRef = useRef(null);
  const successManagerDefaultRef = useRef(null);
  const calendarCallDatesRef = useRef(null);

  useEffect(() => {
    if (!leadId) return;

    const fetchLead = async () => {
      try {
        const data = await getAccountById(leadId);
        if (data.type !== 'Leads') {
          throw new Error('This is not a lead');
        }
        
        console.log('=== LEAD DATA LOADED ===');
        
        // Log staff account view
        if (currentUser) {
          logAccountView(
            { uid: currentUser.uid, email: currentUser.email, name: currentUser.displayName },
            leadId,
            'lead',
            data.company
          );
        }
        
        const leadDocRef = doc(db, 'accounts', leadId);
          
          // Don't set lead state here - we'll set it after all data updates including ACH info
          setStatusHistory(data.statusHistory || []);
          setNotes(data.notes || []);
          setQualified(data.qualified || null);
          setShowOnKanban(data.showOnKanban !== undefined ? data.showOnKanban : true); // default to true if not set
          setResult(data.result || null); // result can be 'Won', 'Lost', or null
          setResultReason(data.resultReason || null); // reason for result
          setIntakeNote(data.intakeNote || ''); // intake note field
          setSelectedFreePage(data.freePage || null); // free page selection
          setSelectedSOPs(data.unlockedSOPs || []); // unlocked SOPs for this lead
          setSelectedKPIs(data.unlockedKPIs || []); // unlocked KPIs for this lead
          setSelectedJobs(data.unlockedJobs || []); // unlocked Jobs for this lead
          setSelectedPrograms(data.unlockedPrograms || []); // unlocked Partner Programs for this lead
          setSelectedEstimatingCategories(data.unlockedEstimatingTemplates || []); // unlocked Estimating Templates for this lead
          setSelectedGPTs(data.unlockedGPTs || []); // unlocked GPTs for this lead
          setSelectedTechStack(data.unlockedTechStack || []); // unlocked Tech Stack for this lead
          setTechStackUnlocked(data.unlockedTechStack === true || data.unlockedTechStack === undefined); // Tech Stack unlock status (legacy)
          setSelectedProposalTabs(data.unlockedProposalTabs || []); // unlocked Proposals tabs for this lead
          setSelectedSalesMaterials(data.unlockedSalesMaterials || []); // unlocked Sales Materials (catalogKeys) for this lead
          // Proposal tab
          const proposals = data.proposals || {};
          setProposalStandardProgram(proposals.standard || 'Lite');
          
          // Check if lead is already flagged (has any flag note)
          const hasFlagNote = (data.notes || []).some(note => {
            if (!note || !note.content) return false;
            return note.content.includes('has been flagged for needing contact');
          });
          setWasFlagged(hasFlagNote);
          
          // Check for NDA data in lead account first
          let ndaData = data.signedNDA || null;
          let ndaStatus = data.ndaStatus || 'Pending';
          
          // If no NDA data in lead account, check user's document
          const leadEmail = data.email || data.contact?.email;
          if (!ndaData && leadEmail) {
            ndaData = await checkUserDocumentForNDA(leadEmail);
            if (ndaData) {
              ndaStatus = 'Completed';
              
              // Only update if NDA data doesn't already exist or is different
              const existingNDA = data.signedNDA;
              const cleanNDAData = Object.fromEntries(
                Object.entries(ndaData).filter(([key, value]) => value !== undefined)
              );
              
              const ndaMatches = existingNDA && 
                JSON.stringify(removeUndefinedValues(existingNDA)) === JSON.stringify(cleanNDAData);
              
              if (!ndaMatches || data.ndaStatus !== 'Completed') {
                // Update the lead account with the NDA data from user document
                await updateDoc(leadDocRef, {
                  ndaStatus: 'Completed',
                  ndaLastUpdated: new Date().toISOString(),
                  signedNDA: cleanNDAData,
                  lastUpdated: new Date().toISOString()
                });
              }
            }
          }
          
          // Check for discovery data in lead account first
          let discoveryData = data.discovery || null;
          let discoveryStatus = data.discoveryCompleted ? 'Completed' : 'Pending';
          
          // Fallback: Check if discovery data might be stored directly on lead account (old structure)
          if (!discoveryData && (data.firstName || data.lastName || data.phoneNumber || data.companyName)) {
            discoveryData = {
              firstName: data.firstName,
              lastName: data.lastName,
              companyName: data.companyName,
              phoneNumber: data.phoneNumber,
              timezone: data.timezone,
              streetAddress: data.streetAddress,
              city: data.city,
              state: data.state,
              zipCode: data.zipCode,
              country: data.country,
              website: data.website,
              businessStructure: data.businessStructure,
              businessStructureOther: data.businessStructureOther,
              dba: data.dba,
              yearsInBusiness: data.yearsInBusiness,
              background: data.background,
              whyStarted: data.whyStarted,
              marketInfo: data.marketInfo
            };
          }
          
          // If no discovery data in lead account, check user's document
          if (!discoveryData && leadEmail) {
            discoveryData = await checkUserDocumentForDiscovery(leadEmail);
            
            // If still no discovery data, try with the current user's email as fallback
            if (!discoveryData && currentUser && currentUser.email !== leadEmail) {
              discoveryData = await checkUserDocumentForDiscovery(currentUser.email);
            }
            if (discoveryData) {
              discoveryStatus = 'Completed';
              
              // Only update if discovery data doesn't already exist or is different
              const existingDiscovery = data.discovery;
              const cleanDiscoveryData = removeUndefinedValues(discoveryData);
              
              // Check if discovery data is already in lead and matches
              const discoveryMatches = existingDiscovery && 
                JSON.stringify(removeUndefinedValues(existingDiscovery)) === JSON.stringify(cleanDiscoveryData);
              
              if (!discoveryMatches || !data.discoveryCompleted) {
                // Update the lead account with the discovery data from user document
                const updateData = removeUndefinedValues({
                  discoveryCompleted: true,
                  discoveryCompletedAt: new Date().toISOString(),
                  discovery: cleanDiscoveryData,
                  lastUpdated: new Date().toISOString()
                });
                
                await updateDoc(leadDocRef, updateData);
              }
            }
          }

          // Check for program selection data in lead account first
          let programSelectionData = data.programSelectionData || null;
          let selectedProgram = data.selectedProgram || null;
          let selectedProgramKey = data.selectedProgramKey || null;
          let billingCycle = data.billingCycle || null;
          
          // If no program selection data in lead account, check user's document
          if (!programSelectionData && leadEmail) {
            const userProgramData = await checkUserDocumentForProgramSelection(leadEmail);
            if (userProgramData) {
              // Clean the program selection data to remove undefined values
              const cleanProgramData = Object.fromEntries(
                Object.entries(userProgramData).filter(([key, value]) => value !== undefined && value !== '')
              );
              
              // Only update if data is different
              const existingProgramData = data.programSelectionData;
              const programMatches = existingProgramData && 
                JSON.stringify(removeUndefinedValues(existingProgramData)) === JSON.stringify(cleanProgramData);
              
              if (!programMatches) {
                // Update the lead account with the program selection data from user document
                await updateDoc(leadDocRef, {
                  selectedProgram: cleanProgramData.selectedProgram,
                  selectedProgramKey: cleanProgramData.selectedProgramKey,
                  billingCycle: cleanProgramData.billingCycle,
                  programSelectionData: cleanProgramData.programSelectionData,
                  lastUpdated: new Date().toISOString()
                });
              }
              
              // Update local state
              selectedProgram = cleanProgramData.selectedProgram;
              selectedProgramKey = cleanProgramData.selectedProgramKey;
              billingCycle = cleanProgramData.billingCycle;
              programSelectionData = cleanProgramData.programSelectionData;
            }
          }

          // Check for ACH info - Priority order:
          // 1. Signed contract documentContent (source of truth - what was actually signed)
          // 2. Lead's associated user's billing collection
          // 3. Lead account's ACH info
          // IMPORTANT: Use leadEmail (lead's email), NOT currentUser.email
          let achInfo = null;
          let achStatus = 'Pending';
          let achLastUpdated = null;
          
          // First priority: Extract ACH info from signed contract documentContent
          if (data.signedContract?.documentContent) {
            const contractContent = data.signedContract.documentContent;
            
            // Extract banking info from contract document
            const bankNameMatch = contractContent.match(/Bank Name:\s*([^\n]+)/i);
            const bankAddressMatch = contractContent.match(/Bank Address:\s*([^\n]+)/i);
            const accountNumberMatch = contractContent.match(/Account Number:\s*([^\n]+)/i);
            const routingNumberMatch = contractContent.match(/Routing Number:\s*([^\n]+)/i);
            
            if (bankNameMatch || accountNumberMatch || routingNumberMatch) {
              const extractedACHInfo = {
                bankName: bankNameMatch ? bankNameMatch[1].trim() : null,
                bankAddress: bankAddressMatch ? bankAddressMatch[1].trim() : null,
                accountNumber: accountNumberMatch ? accountNumberMatch[1].trim() : null,
                routingNumber: routingNumberMatch ? routingNumberMatch[1].trim() : null
              };
              
              // Only use if we have at least bank name and account number
              if (extractedACHInfo.bankName && extractedACHInfo.accountNumber) {
                // Only update if ACH data is different
                const existingACH = data.achInfo;
                const achMatches = existingACH && 
                  JSON.stringify(removeUndefinedValues(existingACH)) === JSON.stringify(extractedACHInfo);
                
                if (!achMatches || data.achStatus !== 'Completed') {
                  // Update the lead account with the ACH data from contract
                  const updatedSignedContract = {
                    ...data.signedContract,
                    bankingInfo: extractedACHInfo
                  };
                  
                  await updateDoc(leadDocRef, {
                    achInfo: extractedACHInfo,
                    achStatus: 'Completed',
                    achLastUpdated: new Date().toISOString(),
                    signedContract: updatedSignedContract,
                    lastUpdated: new Date().toISOString()
                  });
                }
                
                achInfo = extractedACHInfo;
                achStatus = 'Completed';
                achLastUpdated = new Date().toISOString();
              }
            }
          }
          
          // Second priority: Check lead's associated user's billing collection
          if (!achInfo && leadEmail) {
            const userACHData = await checkUserBillingForACH(leadEmail);
            if (userACHData) {
              // Clean the ACH data to remove undefined values
              const cleanACHData = Object.fromEntries(
                Object.entries(userACHData).filter(([key, value]) => value !== undefined && value !== '')
              );
              
              // Only update if ACH data is different
              const existingACH = data.achInfo;
              const achMatches = existingACH && 
                JSON.stringify(removeUndefinedValues(existingACH)) === JSON.stringify(cleanACHData);
              
              if (!achMatches || data.achStatus !== 'Completed') {
                // Update the lead account with the ACH data from user billing collection
                await updateDoc(leadDocRef, {
                  achInfo: cleanACHData,
                  achStatus: 'Completed',
                  achLastUpdated: new Date().toISOString(),
                  lastUpdated: new Date().toISOString()
                });
              }
              
              achInfo = cleanACHData;
              achStatus = 'Completed';
              achLastUpdated = new Date().toISOString();
            }
          }
          
          // Third priority: Fall back to lead account's ACH info
          if (!achInfo && data.achInfo) {
            achInfo = data.achInfo;
            achStatus = data.achStatus || 'Pending';
            achLastUpdated = data.achLastUpdated || null;
          }
          
          setSignedNDA(ndaData);
          setError(null);
          
          // Update lead data with discovery information
          if (discoveryData) {
            data.discovery = discoveryData;
            data.discoveryCompleted = discoveryStatus === 'Completed';
          }

          // Update lead data with program selection information
          if (programSelectionData) {
            data.selectedProgram = selectedProgram;
            data.selectedProgramKey = selectedProgramKey;
            data.billingCycle = billingCycle;
            data.programSelectionData = programSelectionData;
          }

          // Update lead data with ACH information
          if (achInfo) {
            data.achInfo = achInfo;
            data.achStatus = achStatus;
            data.achLastUpdated = achLastUpdated;
          }
          
          // NOW set lead state after all data updates including ACH info
          setLead(data);

          // Load conferences if lead source is conference or appears to be a conference name
          const standardSources = ['Website', 'Google Ads', 'Meta Ads', 'LinkedIn', 'Facebook', 'Instagram', 'YouTube', 'Tiktok', 'Newsletter', 'Podcast', 'Referral', 'Reddit', 'Jobtread'];
          if (data.leadSource === 'Conference' || (data.leadSource && !standardSources.includes(data.leadSource))) {
            loadConferences();
          }
        } catch (error) {
          console.error('Error fetching lead:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };

      fetchLead();
    }, [leadId]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setCurrentUserCalendlyConnected(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (!cancelled) {
          setCurrentUserCalendlyConnected(snap.exists() && snap.data()?.integrations?.calendly?.connected === true);
        }
      } catch (e) {
        console.error('LeadOverview: Calendly connection check failed', e);
        if (!cancelled) setCurrentUserCalendlyConnected(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  useEffect(() => {
    calendlySyncedForLeadRef.current = null;
  }, [leadId]);

  useEffect(() => {
    if (!leadId || !lead || !currentUserCalendlyConnected) return;
    const email = lead.contact?.email || lead.primaryContact?.email || lead.email;
    if (!email) return;

    const syncKey = `${leadId}:${email}`;
    if (calendlySyncedForLeadRef.current === syncKey) return;

    let cancelled = false;
    (async () => {
      setLeadCalendlySyncing(true);
      try {
        const result = await syncLeadCalendlyCalls(leadId);
        if (cancelled || !result?.success) return;
        calendlySyncedForLeadRef.current = syncKey;
        setLead((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          if ('discoveryCallDate' in result) {
            if (result.discoveryCallDate == null) {
              delete next.discoveryCallDate;
            } else {
              next.discoveryCallDate = result.discoveryCallDate;
            }
          }
          if ('onboardingCallDate' in result) {
            if (result.onboardingCallDate == null) {
              delete next.onboardingCallDate;
            } else {
              next.onboardingCallDate = result.onboardingCallDate;
            }
          }
          return next;
        });
      } catch (e) {
        console.error('LeadOverview: Calendly lead sync failed', e);
      } finally {
        if (!cancelled) setLeadCalendlySyncing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId, lead?.contact?.email, currentUserCalendlyConnected]);

  // Real-time sync for proposedProgram (updated from Assessment Analysis when user confirms program)
  useEffect(() => {
    if (!leadId) return;
    console.log('[LeadOverview] Setting up proposedProgram listener', { leadId });
    const leadDocRef = doc(db, 'accounts', leadId);
    const unsub = onSnapshot(leadDocRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.proposedProgram) {
        console.log('[LeadOverview] onSnapshot received proposedProgram update', { proposedProgram: data.proposedProgram });
      }
      setLead((prev) => {
        if (!prev) return prev;
        const nextProposed = data.proposedProgram;
        if (prev.proposedProgram === nextProposed) return prev;
        if (prev.proposedProgram && nextProposed && JSON.stringify(prev.proposedProgram) === JSON.stringify(nextProposed)) return prev;
        return { ...prev, proposedProgram: nextProposed };
      });
    });
    return () => unsub();
  }, [leadId]);

  // Fetch portal user activity (lastLogin, loginHistory) for this lead when lead loads
  useEffect(() => {
    if (!leadId || !lead) {
      setPortalUserActivity(null);
      return;
    }
    let cancelled = false;
    setPortalUserActivityLoading(true);
    (async () => {
      try {
        const usersRef = collection(db, 'users');
        let snapshot = null;
        
        // Try querying by accountId first
        const qByAccountId = query(usersRef, where('accountId', '==', leadId));
        snapshot = await getDocs(qByAccountId);
        
        // If not found, try querying by email as fallback
        if (snapshot.empty && lead.email) {
          const normalizedEmail = lead.email.toLowerCase().trim();
          const qByEmail = query(usersRef, where('email', '==', normalizedEmail));
          snapshot = await getDocs(qByEmail);
        }
        
        // Also try contact.email if available
        if (snapshot.empty && lead.contact?.email) {
          const normalizedEmail = lead.contact.email.toLowerCase().trim();
          const qByEmail = query(usersRef, where('email', '==', normalizedEmail));
          snapshot = await getDocs(qByEmail);
        }
        
        if (cancelled) return;
        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          const data = userDoc.data();
          const lastLogin = data.lastLogin?.toDate?.() ?? (data.lastLogin ? new Date(data.lastLogin) : null);
          const loginHistory = Array.isArray(data.loginHistory) ? data.loginHistory : [];
          // Check for free resource claim
          const startFlowProgress = data.startFlowProgress || {};
          
          // Log the full startFlowProgress to debug
          console.log('DEBUG: Full startFlowProgress object:', JSON.stringify(startFlowProgress, null, 2));
          console.log('DEBUG: startFlowProgress.freeResourceClaimed:', startFlowProgress.freeResourceClaimed);
          console.log('DEBUG: startFlowProgress.currentStep:', startFlowProgress.currentStep);
          console.log('DEBUG: typeof startFlowProgress.freeResourceClaimed:', typeof startFlowProgress.freeResourceClaimed);
          
          // Check for freeResourceClaimed - be explicit about true check.
          // currentStep > 1 only implies a claim on the legacy 5-step flow, where step 1 was
          // "Claim Your Free Resource". On the current flow (stepScheme set) claiming is explicit.
          const isLegacyStepScheme = startFlowProgress.stepScheme !== 'v2-no-free-resource';
          const freeResourceClaimed = startFlowProgress.freeResourceClaimed === true
            || startFlowProgress.freeResourceClaimed === 'true'
            || (isLegacyStepScheme && startFlowProgress.currentStep > 1);
          
          // Get the date - check multiple possible locations
          let freeResourceClaimedDate = null;
          if (freeResourceClaimed) {
            // Try startFlowProgress.lastUpdated first (this is updated when free resource is claimed)
            if (startFlowProgress.lastUpdated) {
              freeResourceClaimedDate = typeof startFlowProgress.lastUpdated === 'string' 
                ? new Date(startFlowProgress.lastUpdated)
                : (startFlowProgress.lastUpdated.toDate ? startFlowProgress.lastUpdated.toDate() : startFlowProgress.lastUpdated);
            }
            // Fallback to root lastUpdated
            if (!freeResourceClaimedDate && data.lastUpdated) {
              freeResourceClaimedDate = typeof data.lastUpdated === 'string'
                ? new Date(data.lastUpdated)
                : (data.lastUpdated.toDate ? data.lastUpdated.toDate() : data.lastUpdated);
            }
            // If we're inferring from currentStep > 1, use createdAt or a reasonable default
            if (!freeResourceClaimedDate && isLegacyStepScheme && startFlowProgress.currentStep > 1 && data.createdAt) {
              freeResourceClaimedDate = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            }
          }
          
          console.log('Portal user activity fetched:', {
            freeResourceClaimed,
            freeResourceClaimedDate,
            startFlowProgress: startFlowProgress,
            startFlowProgressKeys: Object.keys(startFlowProgress)
          });
          
          // Start-flow milestones, read straight off the portal user doc so the CRM
          // shows the same progress the lead sees on their Start dashboard.
          const completedStepIds = Array.isArray(startFlowProgress.completedSteps)
            ? startFlowProgress.completedSteps.map(Number)
            : [];
          const contractSigned = !!startFlowProgress.contractSigned;
          const assessmentProgress = Number(data.businessAssessmentProgress?.progress) || 0;
          const ndaSigned = !!(
            data.signedNDA ||
            (Array.isArray(data.collection) && data.collection.some((item) => item?.isSignedNDA))
          );
          const programData =
            startFlowProgress.programData || data.programSelectionData || data.selectedProgram || null;
          // The Calendly "event_scheduled" handler on StartDashboard marks step 4 and
          // stamps analysisCallScheduledAt; older records only have startFlowProgress.lastUpdated.
          const analysisCallScheduled = contractSigned || completedStepIds.includes(SCHEDULE_CALL_STEP_ID);
          const analysisCallScheduledAt = analysisCallScheduled
            ? toDateOrNull(startFlowProgress.analysisCallScheduledAt) ||
              toDateOrNull(startFlowProgress.lastUpdated)
            : null;

          setPortalUserActivity({
            userId: userDoc.id,
            lastLogin,
            loginHistory,
            freeResourceClaimed,
            freeResourceClaimedDate,
            startFlowProgress: startFlowProgress,
            assessmentProgress,
            assessmentCompletedAt: toDateOrNull(data.businessAssessmentProgress?.completedAt),
            ndaSigned,
            ndaSignedAt: toDateOrNull(data.pendingNda?.clientSignedAt),
            analysisCallScheduled,
            analysisCallScheduledAt,
            programData,
            programChosenAt: programData
              ? toDateOrNull(startFlowProgress.programChosenAt) || toDateOrNull(startFlowProgress.lastUpdated)
              : null,
            contractSigned
          });
        } else {
          console.log('No portal user found for lead:', leadId);
          setPortalUserActivity(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching portal user activity:', err);
          setPortalUserActivity(null);
        }
      } finally {
        if (!cancelled) setPortalUserActivityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId, lead]);

  // Everything the lead did on the Start side: which pages they opened, which programs
  // they looked at, what they customized, what they chose.
  useEffect(() => {
    const portalUserId = portalUserActivity?.userId;
    if (!portalUserId) {
      setStartActivity([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await fetchStartActivity(portalUserId);
      if (!cancelled) setStartActivity(entries);
    })();
    return () => { cancelled = true; };
  }, [portalUserActivity?.userId]);

  // Load internal users for Success Manager dropdown
  useEffect(() => {
    const loadInternalUsers = async () => {
      try {
        const users = await getInternalUsers();
        const formattedUsers = users.map(user => ({
          id: user.id,
          name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
          initials: user.initials || `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U',
          profileImage: user.profileImage || user.photoURL || null,
          email: user.email
        }));
        setInternalUsers(formattedUsers);
      } catch (error) {
        console.error('Error loading internal users:', error);
      }
    };
    loadInternalUsers();
  }, []);

  // Fetch Google Calendar events for the lead
  const fetchLeadEvents = async () => {
    const leadEmail = lead?.contact?.email || lead?.email || lead?.discovery?.email;
    if (!leadEmail || !internalUsers.length) {
      setLeadEvents([]);
      return;
    }

    try {
      setEventsLoading(true);
      const email = leadEmail.toLowerCase();
      
      // Get date range (past 6 months to future 6 months)
      const now = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 6);

      // Fetch events from all internal users who have Google Calendar connected
      const eventMap = new Map(); // Use Map to deduplicate by event ID
      
      for (const user of internalUsers) {
        try {
          const events = await fetchGoogleCalendarEvents(user.id, startDate, endDate);
          
          // Filter events where lead email is in attendees
          const leadAddresses = new Set([email, ...accountAliasEmails(lead)]);
          const leadEvents = events.filter(event => {
            const attendees = event.attendees || [];
            return attendees.some(attendee =>
              leadAddresses.has(attendee.email?.toLowerCase())
            );
          });

          // Process each event
          for (const event of leadEvents) {
            const eventId = event.id || event.googleEventId;
            
            // Find host from organizer email or creator email
            const organizerEmail = (event.organizer?.email || event.creator?.email || event.googleCalendarId)?.toLowerCase();
            let host = internalUsers.find(u => u.email?.toLowerCase() === organizerEmail);
            
            // If no host found by email, use the user who fetched the events (likely the organizer)
            if (!host) {
              host = user;
            }
            
            const hostInfo = host ? {
              id: host.id,
              name: host.name,
              profileImage: host.profileImage,
              initials: host.initials,
              email: host.email
            } : {
              id: null,
              name: event.organizer?.displayName || event.creator?.displayName || event.organizer?.email || event.creator?.email || 'Unknown',
              profileImage: null,
              initials: (event.organizer?.displayName || event.creator?.displayName || event.organizer?.email || event.creator?.email || 'U').substring(0, 2).toUpperCase(),
              email: event.organizer?.email || event.creator?.email
            };

            // Check if we already have this event
            if (eventMap.has(eventId)) {
              const existingEvent = eventMap.get(eventId);
              
              // Add current user as additional internal attendee if not already added
              const isAlreadyAdded = existingEvent.internalAttendees.some(a => a.id === user.id);
              if (!isAlreadyAdded && user.id !== existingEvent.host.id) {
                existingEvent.internalAttendees.push({
                  id: user.id,
                  name: user.name,
                  profileImage: user.profileImage,
                  initials: user.initials,
                  email: user.email
                });
              }
            } else {
              // Add new event with host and internal attendees
              const internalAttendees = [];
              
              // Find other internal users who are attendees (excluding host)
              const attendees = event.attendees || [];
              for (const attendee of attendees) {
                const internalUser = internalUsers.find(u => 
                  u.email?.toLowerCase() === attendee.email?.toLowerCase() && 
                  u.id !== hostInfo.id
                );
                if (internalUser && !internalAttendees.some(a => a.id === internalUser.id)) {
                  internalAttendees.push({
                    id: internalUser.id,
                    name: internalUser.name,
                    profileImage: internalUser.profileImage,
                    initials: internalUser.initials,
                    email: internalUser.email
                  });
                }
              }
              
              eventMap.set(eventId, {
                ...event,
                host: hostInfo,
                internalAttendees
              });
            }
          }
        } catch (error) {
          console.error(`Error fetching events for user ${user.id}:`, error);
          // Continue with other users even if one fails
        }
      }

      // Convert map to array and sort events by start date (newest first)
      const allEvents = Array.from(eventMap.values());
      allEvents.sort((a, b) => {
        const aDate = new Date(a.start || a.startTime);
        const bDate = new Date(b.start || b.startTime);
        return bDate - aDate; // Reverse order: newest first
      });

      setLeadEvents(allEvents);
    } catch (error) {
      console.error('Error fetching lead events:', error);
      setLeadEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchMeetingSummary = async (event) => {
    try {
      setMeetingSummaryLoading(true);
      const summary = await findMeetingSummaryForEvent(db, event, {
        accountId: lead?.id,
        internalEmails: internalUsers.map((u) => u.email),
      });
      setMeetingSummary(summary);
    } catch (error) {
      console.error('Error fetching meeting summary:', error);
      setMeetingSummary(null);
    } finally {
      setMeetingSummaryLoading(false);
    }
  };

  const handleViewMeetingSummary = async (event) => {
    setSelectedEventForSummary(event);
    setShowMeetingSummaryModal(true);
    await fetchMeetingSummary(event);
  };

  // Fetch events when lead, internal users are loaded and Events tab is active
  useEffect(() => {
    // Summary needs them too — the two call dates are derived from these events
    if (lead && internalUsers.length > 0 && (activeTab === 'events' || activeTab === 'notes' || activeTab === 'summary')) {
      fetchLeadEvents();
    }
  }, [lead, internalUsers, activeTab]);

  // Keep Discovery Call / Analysis Call in step with the calendar. The Calendly sync above only
  // runs when the viewing user has Calendly connected, so fall back to the lead's Google Calendar
  // events, matched on the same two booking names. Only ever writes a date we found — a manual
  // or Calendly-set date is never cleared from here.
  useEffect(() => {
    if (!leadId || !lead || !leadEvents || leadEvents.length === 0) return;

    const updates = {};
    // A stored 'TBD' is an explicit manual hold — never let the calendar sync replace it.
    const discovery = pickCallDateFromEvents(leadEvents, DISCOVERY_EVENT_TITLE);
    if (discovery && lead.discoveryCallDate !== 'TBD' && discovery !== storedCallDateString(lead.discoveryCallDate)) {
      updates.discoveryCallDate = discovery;
    }
    const analysis = pickCallDateFromEvents(leadEvents, ANALYSIS_EVENT_TITLE);
    if (analysis && lead.onboardingCallDate !== 'TBD' && analysis !== storedCallDateString(lead.onboardingCallDate)) {
      updates.onboardingCallDate = analysis;
    }
    if (Object.keys(updates).length === 0) return;

    const key = `${leadId}:${updates.discoveryCallDate || ''}:${updates.onboardingCallDate || ''}`;
    if (calendarCallDatesRef.current === key) return;
    calendarCallDatesRef.current = key;

    (async () => {
      try {
        await updateAccount(leadId, updates);
        setLead((prev) => (prev ? { ...prev, ...updates } : prev));
      } catch (error) {
        console.error('Error syncing lead call dates from calendar:', error);
        calendarCallDatesRef.current = null;
      }
    })();
  }, [lead, leadId, leadEvents]);

  // Fetch Gmail emails for the lead
  const fetchLeadEmails = async () => {
    const leadEmail = lead?.contact?.email || lead?.email || lead?.discovery?.email;
    if (!leadEmail || !internalUsers.length) {
      setLeadEmails([]);
      return;
    }

    try {
      setEmailsLoading(true);
      const email = leadEmail.toLowerCase();
      
      // Fetch emails from all internal users who have Gmail connected
      const allEmails = [];
      const disconnected = [];

      for (const user of internalUsers) {
        try {
          const { messages: emails, status, profileConnected } = await fetchGmailMessagesForEmailWithStatus(user.id, [email, ...accountAliasEmails(lead)], 50);

          if (status !== 'ok') {
            // Google rejected the saved grant for the signed-in user — flip the profile
            // flag so CRM ▸ My Profile (the source of truth) stops showing "connected".
            let stillConnected = profileConnected;
            if (status === 'needs_reauth' && profileConnected && user.id === currentUser?.uid) {
              const marked = await markUserIntegrationNeedsReauth(user.id, 'gmail');
              if (marked) stillConnected = false;
            }
            disconnected.push({ id: user.id, name: user.name, status, profileConnected: stillConnected });
          }

          // Add user info to each email
          const emailsWithUser = emails.map(email => ({
            ...email,
            fetchedBy: {
              id: user.id,
              name: user.name,
              profileImage: user.profileImage,
              initials: user.initials
            }
          }));
          
          allEmails.push(...emailsWithUser);
        } catch (error) {
          console.error(`Error fetching emails for user ${user.id}:`, error);
          // Continue with other users even if one fails
        }
      }

      // Remove duplicates based on email ID and sort by date (newest first)
      const uniqueEmails = allEmails.filter((email, index, self) => 
        index === self.findIndex(e => e.id === email.id)
      );
      
      uniqueEmails.sort((a, b) => new Date(b.date) - new Date(a.date));

      setLeadEmails(uniqueEmails);
      setGmailDisconnectedUsers(disconnected);

      // Check for emails sent TO the lead and update lastEmailContactDate
      if (uniqueEmails.length > 0 && leadEmail) {
        const emailsToLead = uniqueEmails.filter(email => {
          // Check if email is sent TO the lead (not from the lead)
          const recipients = email.recipients || [];
          const toRecipients = email.to ? email.to.split(',').map(e => e.trim().toLowerCase()) : [];
          const ccRecipients = email.cc ? email.cc.split(',').map(e => e.trim().toLowerCase()) : [];
          const allRecipients = [...toRecipients, ...ccRecipients];
          
          const isFromLead = email.from?.toLowerCase().includes(leadEmail.toLowerCase());
          const isToLead = allRecipients.some(r => r.includes(leadEmail.toLowerCase()));
          
          return !isFromLead && isToLead;
        });

        if (emailsToLead.length > 0) {
          // Get the most recent email sent to the lead
          const mostRecentEmailToLead = emailsToLead[0]; // Already sorted by date (newest first)
          const emailDate = new Date(mostRecentEmailToLead.date);
          
          // Check if we need to update lastEmailContactDate
          const currentLastEmailDate = lead?.lastEmailContactDate ? new Date(lead.lastEmailContactDate) : null;
          
          // Update if this email is more recent than the current lastEmailContactDate
          if (!currentLastEmailDate || emailDate > currentLastEmailDate) {
            try {
              const updatedData = { ...lead, lastEmailContactDate: emailDate.toISOString() };
              await updateAccount(leadId, updatedData);
              setLead(updatedData);
              console.log('Updated lastEmailContactDate for lead:', emailDate.toISOString());
            } catch (error) {
              console.error('Error updating lastEmailContactDate:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching lead emails:', error);
      setLeadEmails([]);
    } finally {
      setEmailsLoading(false);
    }
  };

  // Fetch emails when lead, internal users are loaded and Emails tab is active
  useEffect(() => {
    if (lead && internalUsers.length > 0 && (activeTab === 'emails' || activeTab === 'notes')) {
      fetchLeadEmails();
    }
  }, [lead, internalUsers, activeTab]);

  // Fetch programs and refresh lead (including proposedProgram) when Proposal tab is active
  useEffect(() => {
    if (activeTab !== 'proposal' || !leadId) return;
    console.log('[LeadOverview] Proposal tab active, refetching lead', { leadId });
    let cancelled = false;
    const run = async () => {
      try {
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!cancelled && !programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach((docSnap) => {
            const programData = docSnap.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              if (!programData.name) programData.name = docSnap.id;
              programsData[docSnap.id] = programData;
            }
          });
          setProposalPrograms(programsData);
        }
        const fresh = await getAccountById(leadId);
        console.log('[LeadOverview] Proposal tab refetch result', { hasFresh: !!fresh, proposedProgram: fresh?.proposedProgram, cancelled });
        if (!cancelled && fresh) {
          setLead((prev) => (prev ? { ...prev, proposedProgram: fresh.proposedProgram } : prev));
        }
      } catch (error) {
        if (!cancelled) console.error('Error fetching for proposal tab:', error);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, leadId]);

  // Leaving the Proposal tab drops an in-progress edit rather than leaving it half-open.
  useEffect(() => {
    if (activeTab !== 'proposal') {
      if (editingProposedProgram) setEditingProposedProgram(false);
      if (creatingProposedProgram) setCreatingProposedProgram(false);
    }
  }, [activeTab, editingProposedProgram, creatingProposedProgram]);

  // ProgramDetail re-seeds its deliverables whenever these props change identity, so they have to be
  // memoized — a fresh object each render would wipe edits in progress.
  const proposedProgram = lead?.proposedProgram || null;
  const proposedProgramForDetail = useMemo(
    () =>
      proposedProgram
        ? {
            name: proposedProgram.programName?.replace(/\s*Program\s*$/, '') || 'Program',
            features: proposedProgram.features || {},
            pricePerMonth: proposedProgram.totalPricePerMonth ?? 0,
            minimumTerm: proposedProgram.programMinimumTerm ?? 12
          }
        : null,
    [proposedProgram]
  );
  // isProgramConfirmed drives ProgramDetail's locked state — false while editing unlocks the
  // builder and swaps its footer back to "Confirm Program", without clearing the saved proposal.
  const proposedProgramData = useMemo(
    () => (proposedProgram ? { ...proposedProgram, isProgramConfirmed: !editingProposedProgram } : null),
    [proposedProgram, editingProposedProgram]
  );
  const proposedProgramDeliverables = useMemo(
    () => (proposedProgram?.selectedDeliverables ? new Set(proposedProgram.selectedDeliverables) : null),
    [proposedProgram]
  );
  const proposedProgramQuantities = useMemo(
    () => proposedProgram?.deliverableQuantities || null,
    [proposedProgram]
  );

  // Starting point when no program was ever confirmed: the first offered program in the same order
  // the builder's own dropdown uses, or Custom now that Lite, Plus and Pro are retired.
  const defaultProposalProgram = useMemo(() => {
    const tailOrder = ['Lite', 'Plus', 'Pro', 'Premier', 'BBA Bundle'];
    const orderIndex = (key) => {
      const i = tailOrder.indexOf(key);
      return i >= 0 ? i : 999;
    };
    const key = Object.keys(proposalPrograms || {})
      .filter((k) => !isRetiredProgram(k, proposalPrograms[k]))
      .sort((a, b) => orderIndex(a) - orderIndex(b))[0];
    if (!key) return { tier: CUSTOM_TIER, program: { ...CUSTOM_PROGRAM } };
    const program = proposalPrograms[key] || {};
    return {
      tier: key,
      program: {
        name: (program.name || key).replace(/\s*Program\s*$/, ''),
        features: program.features || {},
        pricePerMonth: program.pricePerMonth ?? 0,
        minimumTerm: program.minimumTerm ?? 12
      }
    };
  }, [proposalPrograms]);

  // The builder is open either to edit a confirmed proposal or to confirm the first one.
  const buildingProposedProgram = editingProposedProgram || creatingProposedProgram;
  const showProposedProgramBuilder = !!proposedProgram || (creatingProposedProgram && !!defaultProposalProgram);

  const saveProposedProgram = async (data) => {
    if (!leadId) return;
    const toSave = JSON.parse(JSON.stringify(data));
    toSave.isProgramConfirmed = true;
    toSave.confirmedAt = new Date().toISOString();
    await updateAccount(leadId, {
      proposedProgram: toSave,
      lastUpdated: new Date().toISOString()
    });
    setLead((prev) => (prev ? { ...prev, proposedProgram: toSave } : prev));
    setEditingProposedProgram(false);
    setCreatingProposedProgram(false);
  };

  // Function to check user document for discovery data
  const checkUserDocumentForDiscovery = async (email) => {
    try {
      console.log('=== CHECKING USER DOCUMENT FOR DISCOVERY ===');
      console.log('Searching for user with email:', email);
      
      // Find user by email
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      console.log('Found users:', usersSnapshot.docs.length);
      
      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        
        console.log('User document ID:', userDoc.id);
        console.log('User data keys:', Object.keys(userData));
        console.log('User has discovery:', !!userData.discovery);
        console.log('User discovery data:', userData.discovery);
        console.log('User has startFlowProgress:', !!userData.startFlowProgress);
        console.log('User startFlowProgress:', userData.startFlowProgress);
        
        // Check if user has discovery data
        if (userData.discovery) {
          console.log('Found discovery data in user document:', userData.discovery);
          return userData.discovery;
        } else {
          console.log('No discovery data found in user document');
        }
      } else {
        console.log('No user found with email:', email);
      }
      
      console.log('=== END USER DOCUMENT CHECK FOR DISCOVERY ===');
      return null;
    } catch (error) {
      console.error('Error checking user document for discovery:', error);
      return null;
    }
  };

  // Function to check user document for NDA data
  const checkUserDocumentForNDA = async (email) => {
    try {
      console.log('=== CHECKING USER DOCUMENT FOR NDA ===');
      console.log('Searching for user with email:', email);
      
      // Find user by email
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      console.log('Found users:', usersSnapshot.docs.length);
      
      // Debug: Log all users found to see what emails exist
      if (usersSnapshot.docs.length > 0) {
        console.log('Available users:');
        usersSnapshot.docs.forEach((doc, index) => {
          const userData = doc.data();
          console.log(`User ${index + 1}:`, {
            id: doc.id,
            email: userData.email,
            hasSignedNDA: !!userData.signedNDA
          });
        });
      }
      
      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        
        console.log('User document ID:', userDoc.id);
        console.log('User data keys:', Object.keys(userData));
        console.log('User has signedNDA:', !!userData.signedNDA);
        console.log('User has collection:', !!userData.collection);
        console.log('Collection length:', userData.collection?.length || 0);
        
        // Check if user has signed NDA data
        if (userData.signedNDA) {
          console.log('Found NDA data in user document:', userData.signedNDA);
          return userData.signedNDA;
        }
        
        // Also check in collection
        if (userData.collection) {
          console.log('Checking collection for NDA items...');
          const ndaItem = userData.collection.find(item => item.isSignedNDA);
          if (ndaItem) {
            console.log('Found NDA data in user collection:', ndaItem);
            
            // Extract NDA data, providing fallbacks for missing fields
            const extractedNDA = {
              documentId: ndaItem.documentId || ndaItem.id || 'unknown',
              signerName: ndaItem.signerName || 'Unknown',
              signerTitle: ndaItem.signerTitle || '',
              companyName: ndaItem.companyName || '',
              companyAddress: ndaItem.companyAddress || '',
              city: ndaItem.city || '',
              state: ndaItem.state || '',
              zipCode: ndaItem.zipCode || '',
              businessStructure: ndaItem.businessStructure || '',
              signature: ndaItem.signature || '',
              signedAt: ndaItem.signedAt || new Date().toISOString(),
              eSignCompliance: ndaItem.eSignCompliance || {},
              documentContent: ndaItem.documentContent || ndaItem.eSignCompliance?.recordIntegrity?.documentSnapshot?.documentContent || 'Document content not available'
            };
            
            console.log('Extracted NDA data:', extractedNDA);
            return extractedNDA;
          } else {
            console.log('No NDA item found in collection');
            console.log('Collection items:', userData.collection.map(item => ({ 
              id: item.id, 
              isSignedNDA: item.isSignedNDA, 
              title: item.title 
            })));
          }
        }
      } else {
        console.log('No user found with email:', email);
        
        // Debug: Try to find users with similar emails (case insensitive)
        console.log('Trying case-insensitive search...');
        const allUsersQuery = query(collection(db, 'users'));
        const allUsersSnapshot = await getDocs(allUsersQuery);
        console.log('Total users in collection:', allUsersSnapshot.docs.length);
        
        const matchingUsers = allUsersSnapshot.docs.filter(doc => {
          const userData = doc.data();
          return userData.email && userData.email.toLowerCase() === email.toLowerCase();
        });
        
        if (matchingUsers.length > 0) {
          console.log('Found case-insensitive match:', matchingUsers[0].data().email);
          const userDoc = matchingUsers[0];
          const userData = userDoc.data();
          
          if (userData.signedNDA) {
            console.log('Found NDA data in case-insensitive match:', userData.signedNDA);
            return userData.signedNDA;
          }
        } else {
          console.log('No case-insensitive matches found either');
        }
      }
      
      console.log('=== END USER DOCUMENT CHECK ===');
      return null;
    } catch (error) {
      console.error('Error checking user document for NDA:', error);
      return null;
    }
  };

  // Portal completion — the same four Start steps the lead sees on their own dashboard,
  // 25% each, read off the portal user resolved above (by accountId, then email). The old
  // version ran its own exact-match email query, so any casing difference between the lead
  // and the portal user left this stuck at 0%.
  const portalProgress = useMemo(() => {
    const steps = [
      { id: 'analysis', emoji: '📊', label: 'Business Analysis', complete: false, percent: 0 },
      { id: 'nda', emoji: '🔏', label: 'NDA Signed', complete: false, percent: 0 },
      { id: 'call', emoji: '📅', label: 'Analysis Call Scheduled', complete: false, percent: 0 },
      { id: 'program', emoji: '🎯', label: 'Program Chosen', complete: false, percent: 0 }
    ];

    if (!portalUserActivity) {
      return { hasPortalUser: false, total: 0, steps };
    }

    const contractSigned = !!portalUserActivity.contractSigned;
    const assessmentProgress = contractSigned ? 100 : (portalUserActivity.assessmentProgress || 0);

    // The assessment gives partial credit as the lead works through it; the rest are all-or-nothing.
    steps[0].percent = Math.min((assessmentProgress / 100) * 25, 25);
    steps[0].complete = assessmentProgress >= 99;
    steps[1].complete = contractSigned || !!portalUserActivity.ndaSigned;
    steps[1].percent = steps[1].complete ? 25 : 0;
    steps[2].complete = !!portalUserActivity.analysisCallScheduled;
    steps[2].percent = steps[2].complete ? 25 : 0;
    steps[3].complete = contractSigned || !!portalUserActivity.programData;
    steps[3].percent = steps[3].complete ? 25 : 0;

    return {
      hasPortalUser: true,
      total: steps.reduce((sum, step) => sum + step.percent, 0),
      steps
    };
  }, [portalUserActivity]);

  const portalCompletion = portalProgress.total;

  // Function to check user document for program selection data
  const checkUserDocumentForProgramSelection = async (email) => {
    try {
      console.log('=== CHECKING USER DOCUMENT FOR PROGRAM SELECTION ===');
      console.log('Searching for user with email:', email);
      
      // Find user by email
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      console.log('Found users:', usersSnapshot.docs.length);
      
      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        
        console.log('User document ID:', userDoc.id);
        console.log('User data keys:', Object.keys(userData));
        console.log('User has selectedProgram:', !!userData.selectedProgram);
        console.log('User has programSelectionData:', !!userData.programSelectionData);
        
        // Check if user has program selection data
        if (userData.selectedProgram || userData.programSelectionData) {
          console.log('Found program selection data in user document:', {
            selectedProgram: userData.selectedProgram,
            selectedProgramKey: userData.selectedProgramKey,
            billingCycle: userData.billingCycle,
            programSelectionData: userData.programSelectionData
          });
          return {
            selectedProgram: userData.selectedProgram,
            selectedProgramKey: userData.selectedProgramKey,
            billingCycle: userData.billingCycle,
            programSelectionData: userData.programSelectionData
          };
        }
      } else {
        console.log('No user found with email:', email);
      }
      
      console.log('=== END USER DOCUMENT CHECK FOR PROGRAM SELECTION ===');
      return null;
    } catch (error) {
      console.error('Error checking user document for program selection:', error);
      return null;
    }
  };

  // Function to check user billing collection for ACH info (using lead's email, NOT current user)
  const checkUserBillingForACH = async (email) => {
    try {
      console.log('=== CHECKING USER BILLING FOR ACH ===');
      console.log('Searching for user with email:', email);
      console.log('IMPORTANT: Using lead email, NOT current user email');
      
      // Find user by email (lead's email, not current user's)
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      console.log('Found users:', usersSnapshot.docs.length);
      
      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        
        console.log('User document ID:', userDoc.id);
        console.log('User UID from data:', userData.uid);
        console.log('User email from data:', userData.email);
        
        // Use userDoc.id as the uid (user document ID is typically the uid)
        // Also try userData.uid as fallback
        const userId = userDoc.id || userData.uid;
        
        if (userId) {
          console.log('Checking billing collection for user ID:', userId);
          const achDocRef = doc(db, 'users', userId, 'billing', 'ach');
          const achDoc = await getDoc(achDocRef);
          
          if (achDoc.exists()) {
            const achData = achDoc.data();
            console.log('Found ACH data in user billing collection for lead user:', achData);
            return achData;
          } else {
            console.log('No ACH data found in user billing collection for user ID:', userId);
          }
        } else {
          console.log('No user ID found (neither doc.id nor userData.uid)');
        }
      } else {
        console.log('No user found with email:', email);
      }
      
      console.log('=== END USER BILLING CHECK FOR ACH ===');
      return null;
    } catch (error) {
      console.error('Error checking user billing for ACH:', error);
      return null;
    }
  };

  // Load assessment data for completion indicators - from lead data only
  useEffect(() => {
    // Reset refs when leadId changes
    if (lastLeadIdRef.current !== leadId) {
      lastAssessmentDataRef.current = null;
    }

    if (!lead) {
      setAssessmentData(null);
      return;
    }

    // Create a stable key from relevant assessment data to detect actual changes
    // Normalize the data first to ensure consistent comparison
    const normalizeForComparison = (obj) => {
      if (!obj) return null;
      try {
        return JSON.stringify(removeUndefinedValues(obj));
      } catch (e) {
        return String(obj);
      }
    };

    const assessmentKey = JSON.stringify({
      discovery: normalizeForComparison(lead.discovery),
      businessAssessment: normalizeForComparison(lead.businessAssessment),
      businessAssessmentProgress: normalizeForComparison(lead.businessAssessmentProgress),
      salesData: normalizeForComparison(lead.salesData),
      marketingData: normalizeForComparison(lead.marketingData),
      operationsData: normalizeForComparison(lead.operationsData),
      financeData: normalizeForComparison(lead.financeData),
      techData: normalizeForComparison(lead.techData),
      hrData: normalizeForComparison(lead.hrData)
    });

    // Only reload if assessment data actually changed
    if (lastAssessmentDataRef.current === assessmentKey) {
      return;
    }
    lastAssessmentDataRef.current = assessmentKey;

    const loadAssessmentData = async () => {
      console.log('=== LOADING ASSESSMENT DATA (LEAD ONLY) ===');
      console.log('Current lead:', lead);
      
      try {
        // Create assessment data object using only lead data
        const assessmentData = {
          // Discovery data from lead
          discovery: lead.discovery || null,
          
          // Business assessment data from lead only
          businessAssessment: lead.businessAssessment || null,
          businessAssessmentProgress: lead.businessAssessmentProgress || null,
          
          // Assessment data from lead only
          salesData: lead.salesData || null,
          marketingData: lead.marketingData || null,
          operationsData: lead.operationsData || null,
          financeData: lead.financeData || null,
          techData: lead.techData || null,
          hrData: lead.hrData || null
        };
        
        console.log('Lead assessment data loaded:', assessmentData);
        console.log('Discovery data:', assessmentData.discovery ? 'EXISTS' : 'MISSING');
        console.log('Business assessment:', assessmentData.businessAssessment ? 'EXISTS' : 'MISSING');
        console.log('Sales data:', assessmentData.salesData ? 'EXISTS' : 'MISSING');
        console.log('Marketing data:', assessmentData.marketingData ? 'EXISTS' : 'MISSING');
        console.log('Operations data:', assessmentData.operationsData ? 'EXISTS' : 'MISSING');
        console.log('Finance data:', assessmentData.financeData ? 'EXISTS' : 'MISSING');
        console.log('Tech data:', assessmentData.techData ? 'EXISTS' : 'MISSING');
        console.log('HR data:', assessmentData.hrData ? 'EXISTS' : 'MISSING');
        
        setAssessmentData(assessmentData);
      } catch (error) {
        console.error('Error loading assessment data:', error);
      }
    };

    loadAssessmentData();
  }, [lead]); // Only depend on lead

  // Check if 8 days have passed since last substatus update and set to Cold Lead
  useEffect(() => {
    // Reset refs when leadId changes
    if (lastLeadIdRef.current !== leadId) {
      coldLeadStatusProcessedRef.current = false;
      lastLeadSubstatusRef.current = null;
    }

    const checkColdLeadStatus = async () => {
      if (!lead) {
        return;
      }

      // Only check for leads in Assessment stage
      if (lead.stage !== 'Assessment') {
        coldLeadStatusProcessedRef.current = false;
        return;
      }

      // Don't update if already marked as Cold Lead
      if (lead.substatus === 'Cold Lead') {
        coldLeadStatusProcessedRef.current = true;
        return;
      }

      // Don't update if substatus is Assessment Done (final state for assessment)
      if (lead.substatus === 'Assessment Done') {
        coldLeadStatusProcessedRef.current = true;
        return;
      }

      // Don't update if substatus is one of the final states
      const finalSubstatuses = ['Solutions Chosen'];
      if (finalSubstatuses.includes(lead.substatus)) {
        coldLeadStatusProcessedRef.current = true;
        return;
      }

      // Check if we've already processed this substatus
      const currentSubstatusKey = `${lead.substatus}-${lead.substatusDates?.[lead.substatus] || ''}`;
      if (coldLeadStatusProcessedRef.current && lastLeadSubstatusRef.current === currentSubstatusKey) {
        return;
      }
      lastLeadSubstatusRef.current = currentSubstatusKey;

      try {
        // Get the last substatus update date
        const substatusDates = lead.substatusDates || {};
        let lastSubstatusDate = null;

        // Find the most recent substatus date
        const currentSubstatus = lead.substatus;
        if (currentSubstatus && substatusDates[currentSubstatus]) {
          lastSubstatusDate = new Date(substatusDates[currentSubstatus]);
        } else {
          // If no substatus date, check status history for last Assessment substatus change
          const statusHistory = lead.statusHistory || [];
          const assessmentHistory = statusHistory
            .filter(entry => entry.status === 'Assessment' && entry.substatus)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          
          if (assessmentHistory.length > 0) {
            lastSubstatusDate = new Date(assessmentHistory[0].date);
          } else {
            // Fall back to stage date or createdAt
            if (lead.assessmentDate) {
              lastSubstatusDate = new Date(lead.assessmentDate);
            } else if (lead.createdAt) {
              const createdAt = lead.createdAt?.toDate ? lead.createdAt.toDate() : new Date(lead.createdAt);
              lastSubstatusDate = createdAt;
            }
          }
        }

        if (!lastSubstatusDate || isNaN(lastSubstatusDate.getTime())) {
          return;
        }

        // Calculate days since last substatus update
        const now = new Date();
        const diffTime = now - lastSubstatusDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = diffTime / (1000 * 60 * 60);

        // Skip auto-update if substatus was updated within the last hour
        // This prevents resetting a manually updated substatus immediately after update
        if (diffHours < 1) {
          coldLeadStatusProcessedRef.current = true;
          return;
        }

        // If 8 or more days have passed, update to Cold Lead
        if (diffDays >= 8) {
          console.log(`Updating substatus to Cold Lead - ${diffDays} days since last substatus update`);
          
          const currentDate = new Date().toISOString();
          const updatedData = { ...lead };
          updatedData.substatus = 'Cold Lead';
          
          // Track substatus dates
          if (!updatedData.substatusDates) {
            updatedData.substatusDates = {};
          }
          if (!updatedData.substatusDates['Cold Lead']) {
            updatedData.substatusDates['Cold Lead'] = currentDate;
          }
          
          // Add to status history
          if (!updatedData.statusHistory) {
            updatedData.statusHistory = [];
          }
          const statusEntry = {
            status: 'Assessment',
            substatus: 'Cold Lead',
            previousStatus: lead.stage || 'Unknown',
            previousSubstatus: lead.substatus || null,
            date: currentDate,
            updatedBy: 'system',
            action: `Automatically marked as Cold Lead - ${diffDays} days since last substatus update`
          };
          updatedData.statusHistory = [...updatedData.statusHistory, statusEntry];
          
          await updateAccount(leadId, updatedData);
          console.log('Successfully updated substatus to Cold Lead');
          sendColdLeadWebhook(updatedData);
          coldLeadStatusProcessedRef.current = true;
          lastLeadSubstatusRef.current = `Cold Lead-${currentDate}`;
        } else {
          // Mark as processed even if we don't update (not enough days passed)
          coldLeadStatusProcessedRef.current = true;
        }
      } catch (error) {
        console.error('Error checking cold lead status:', error);
        coldLeadStatusProcessedRef.current = false; // Reset on error to allow retry
      }
    };

    // Check every hour
    checkColdLeadStatus();
    const interval = setInterval(() => {
      coldLeadStatusProcessedRef.current = false; // Reset before checking
      checkColdLeadStatus();
    }, 60 * 60 * 1000); // 1 hour

    return () => clearInterval(interval);
  }, [lead, leadId]);

  // Check if all assessments are completed and update substatus
  useEffect(() => {
    // Reset refs when leadId changes
    if (lastLeadIdRef.current !== leadId) {
      lastLeadIdRef.current = leadId;
      assessmentCompletionProcessedRef.current = false;
      lastAssessmentCompletionDataRef.current = null;
      lastProcessTimeRef.current = 0;
    }

    const checkAssessmentCompletion = async () => {
      if (!lead) {
        console.log('Assessment completion check: No lead data');
        assessmentCompletionProcessedRef.current = false;
        return;
      }

      if (lead.stage !== 'Assessment') {
        console.log('Assessment completion check: Lead not in Assessment stage, current stage:', lead.stage);
        assessmentCompletionProcessedRef.current = false;
        return;
      }

      // Don't update if already marked as completed
      if (lead.substatus === 'Assessment Done') {
        assessmentCompletionProcessedRef.current = true;
        return;
      }

      // Create a stable key from assessment data to detect actual changes
      // Normalize the data first to ensure consistent comparison
      const normalizeForComparison = (obj) => {
        if (!obj) return null;
        try {
          return JSON.stringify(removeUndefinedValues(obj));
        } catch (e) {
          return String(obj);
        }
      };

      const createAssessmentKey = (lead) => {
        try {
          return JSON.stringify({
            businessAssessment: normalizeForComparison(lead.businessAssessment),
            salesData: normalizeForComparison(lead.salesData),
            operationsData: normalizeForComparison(lead.operationsData),
            financeData: normalizeForComparison(lead.financeData),
            techData: normalizeForComparison(lead.techData),
            marketingData: normalizeForComparison(lead.marketingData),
            substatus: lead.substatus
          });
        } catch (e) {
          return null;
        }
      };

      const assessmentDataKey = createAssessmentKey(lead);

      // Only check if assessment data actually changed or we haven't processed yet
      if (assessmentDataKey && assessmentCompletionProcessedRef.current && lastAssessmentCompletionDataRef.current === assessmentDataKey) {
        return; // Skip if already processed with same data
      }

      // Prevent rapid re-runs (debounce - wait at least 2 seconds between checks)
      const now = Date.now();
      if (lastProcessTimeRef.current && (now - lastProcessTimeRef.current) < 2000) {
        return; // Skip if processed recently
      }
      lastProcessTimeRef.current = now;

      console.log('=== CHECKING ASSESSMENT COMPLETION ===');
      console.log('Current substatus:', lead.substatus);

      try {
        const businessAssessment = lead?.businessAssessment || {};
        const salesData = lead?.salesData || {};
        const operationsData = lead?.operationsData || {};
        const financeData = lead?.financeData || {};
        const techData = lead?.techData || {};
        const marketingData = lead?.marketingData || {};

        console.log('Business Assessment exists:', !!businessAssessment);
        console.log('Sales Data exists:', !!salesData);
        console.log('Operations Data exists:', !!operationsData);
        console.log('Finance Data exists:', !!financeData);
        console.log('Tech Data exists:', !!techData);
        console.log('Marketing Data exists:', !!marketingData);

        // Check General Assessment (Business Assessment)
        const generalComplete = businessAssessment?.contractorType &&
          businessAssessment?.projectType &&
          (businessAssessment?.contractorType === 'General Contractor' 
            ? businessAssessment?.projectFocusType 
            : true);
        console.log('General Assessment complete:', generalComplete);

        // Check Sales Assessment (use businessAssessment and revenueData fallbacks)
        const salesRating = salesData?.salesRating ?? businessAssessment?.salesRating ?? 0;
        const salesCloseRatio = salesData?.closeRatio ?? businessAssessment?.closeRatio ?? 0;
        const salesReferralPct = salesData?.referralBusinessPercentage ?? businessAssessment?.referralBusinessPercentage ?? 0;
        const salesOneYearAgo = salesData?.oneYearAgo ?? salesData?.revenueData?.oneYearAgo ?? businessAssessment?.revenueData?.oneYearAgo;
        const salesCurrentProj = salesData?.currentYearProjection ?? salesData?.revenueData?.currentYearProjection ?? businessAssessment?.revenueData?.currentYearProjection;
        const salesNextGoal = salesData?.nextYearGoal ?? salesData?.revenueData?.nextYearGoal ?? salesData?.goalsData?.annualRevenueTarget ?? businessAssessment?.revenueData?.nextYearGoal;
        const salesComplete = salesRating > 0 &&
          salesCloseRatio > 0 &&
          salesReferralPct > 0 &&
          salesOneYearAgo &&
          salesCurrentProj &&
          salesNextGoal;
        console.log('Sales Assessment complete:', salesComplete, {
          salesRating,
          closeRatio: salesCloseRatio,
          referralBusinessPercentage: salesReferralPct,
          oneYearAgo: salesOneYearAgo,
          currentYearProjection: salesCurrentProj,
          nextYearGoal: salesNextGoal
        });

        // Check Operations Assessment
        const operationsComplete = operationsData?.operationsRating > 0 &&
          (operationsData?.hasSOPs !== undefined && operationsData?.hasSOPs !== null) &&
          (operationsData?.hasJobDescriptions !== undefined && operationsData?.hasJobDescriptions !== null) &&
          (operationsData?.hasKPIs !== undefined && operationsData?.hasKPIs !== null) &&
          operationsData?.teamSize &&
          operationsData?.teamGrade;
        console.log('Operations Assessment complete:', operationsComplete);

        // Check Finance Assessment
        const financeComplete = financeData?.financeRating > 0 &&
          financeData?.unencumberedCash &&
          financeData?.grossProfitMargin &&
          financeData?.netProfitMargin;
        console.log('Finance Assessment complete:', financeComplete);

        // Check Technology Assessment
        const techRating = (techData.techRating !== undefined && techData.techRating !== null) 
          ? techData.techRating 
          : ((businessAssessment.techRating !== undefined && businessAssessment.techRating !== null) ? businessAssessment.techRating : null);
        const whatCRM = (techData.whatCRM !== undefined && techData.whatCRM !== null && techData.whatCRM !== '')
          ? techData.whatCRM
          : ((businessAssessment.whatCRM !== undefined && businessAssessment.whatCRM !== null && businessAssessment.whatCRM !== '') ? businessAssessment.whatCRM : null);
        const crmOptimization = (techData.crmOptimization !== undefined && techData.crmOptimization !== null) 
          ? techData.crmOptimization 
          : ((businessAssessment.crmOptimization !== undefined && businessAssessment.crmOptimization !== null) ? businessAssessment.crmOptimization : null);
        const hasAutomations = (techData.hasAutomations !== undefined && techData.hasAutomations !== null)
          ? techData.hasAutomations
          : ((businessAssessment.hasAutomations !== undefined && businessAssessment.hasAutomations !== null) ? businessAssessment.hasAutomations : null);
        const hasAITools = (techData.hasAITools !== undefined && techData.hasAITools !== null)
          ? techData.hasAITools
          : ((businessAssessment.hasAITools !== undefined && businessAssessment.hasAITools !== null) ? businessAssessment.hasAITools : null);
        const hasWebsitePlugins = (techData.hasWebsitePlugins !== undefined && techData.hasWebsitePlugins !== null)
          ? techData.hasWebsitePlugins
          : ((techData.hasCustomWebsitePlugins !== undefined && techData.hasCustomWebsitePlugins !== null)
            ? techData.hasCustomWebsitePlugins
            : ((businessAssessment.hasWebsitePlugins !== undefined && businessAssessment.hasWebsitePlugins !== null)
              ? businessAssessment.hasWebsitePlugins
              : ((businessAssessment.hasCustomWebsitePlugins !== undefined && businessAssessment.hasCustomWebsitePlugins !== null)
                ? businessAssessment.hasCustomWebsitePlugins
                : null)));
        const techComplete = techRating !== null && techRating > 0 &&
          whatCRM !== null &&
          crmOptimization !== null &&
          hasAutomations !== null &&
          hasAITools !== null &&
          hasWebsitePlugins !== null;
        console.log('Technology Assessment complete:', techComplete, {
          techRating,
          whatCRM,
          crmOptimization,
          hasAutomations,
          hasAITools,
          hasWebsitePlugins
        });

        // Check Marketing Assessment
        const marketingRating = (marketingData.marketingRating !== undefined && marketingData.marketingRating !== null) 
          ? marketingData.marketingRating 
          : ((businessAssessment.marketingRating !== undefined && businessAssessment.marketingRating !== null) ? businessAssessment.marketingRating : null);
        const workingWithAgency = (marketingData.workingWithMarketingAgency !== undefined && marketingData.workingWithMarketingAgency !== null)
          ? marketingData.workingWithMarketingAgency
          : ((marketingData.workingWithAgency !== undefined && marketingData.workingWithAgency !== null)
            ? marketingData.workingWithAgency
            : ((businessAssessment.workingWithMarketingAgency !== undefined && businessAssessment.workingWithMarketingAgency !== null)
              ? businessAssessment.workingWithMarketingAgency
              : null));
        const websiteManager = marketingData.marketingAgencyType || businessAssessment.marketingAgencyType || marketingData.websiteManager || businessAssessment.websiteManager;
        const marketingComplete = marketingRating !== null && marketingRating > 0 &&
          workingWithAgency !== null &&
          websiteManager;
        console.log('Marketing Assessment complete:', marketingComplete, {
          marketingRating,
          workingWithAgency,
          websiteManager
        });

        const allComplete = generalComplete && salesComplete && operationsComplete && financeComplete && techComplete && marketingComplete;
        console.log('All assessments complete:', allComplete);

        // If all assessments are complete, update substatus
        if (allComplete) {
          console.log('Updating substatus to Assessment Done');
          
          const updatedData = { ...lead };
          updatedData.substatus = 'Assessment Done';
          
          // Track substatus dates
          if (!updatedData.substatusDates) {
            updatedData.substatusDates = {};
          }
          const assessmentDoneDate = new Date().toISOString();
          if (!updatedData.substatusDates['Assessment Done']) {
            updatedData.substatusDates['Assessment Done'] = assessmentDoneDate;
          }
          
          // Add to statusHistory
          if (!updatedData.statusHistory) {
            updatedData.statusHistory = [];
          }
          const statusEntry = {
            substatus: 'Assessment Done',
            date: assessmentDoneDate,
            updatedBy: currentUser?.displayName || currentUser?.email || 'System'
          };
          updatedData.statusHistory = [...updatedData.statusHistory, statusEntry];
          
          await updateAccount(leadId, updatedData);
          console.log('Successfully updated substatus to Assessment Done');
          
          // Assessment Complete notification is sent when the lead completes the assessment
          // (e.g. in ExistingConditions via leadAssessmentService), not when a user opens LeadOverview.
          
          // Mark as processed to prevent re-running - the data comparison will prevent loops
          assessmentCompletionProcessedRef.current = true;
          lastAssessmentCompletionDataRef.current = assessmentDataKey;
          
          // Don't call setLead here as the real-time listener will update it
        } else {
          console.log('Not all assessments are complete yet');
          // Mark as processed even if not complete (to prevent re-checking same state)
          assessmentCompletionProcessedRef.current = true;
          lastAssessmentCompletionDataRef.current = assessmentDataKey;
        }
      } catch (error) {
        console.error('Error checking assessment completion:', error);
        assessmentCompletionProcessedRef.current = false; // Reset on error to allow retry
      }
    };

    // Add a delay to ensure data is fully loaded and prevent rapid re-runs
    const timeoutId = setTimeout(() => {
      checkAssessmentCompletion();
    }, 1000); // Increased delay to prevent rapid re-runs

    return () => clearTimeout(timeoutId);
  }, [lead, leadId, currentUser]);

  // Auto-save notes when they change
  useEffect(() => {
    if (lead && notes !== (lead.notes || [])) {
      const saveNotes = async () => {
        try {
          const updatedData = { ...lead, notes };
          await updateAccount(leadId, updatedData);
          // Don't update lead state here to avoid infinite loop
          // The real-time listener will update it automatically
        } catch (error) {
          console.error('Error saving notes:', error);
        }
      };
      saveNotes();
    }
  }, [notes, leadId]); // Removed 'lead' from dependencies to prevent infinite loop


  const tabs = [
    'Summary',
    'Notes',
    'Emails',
    'Events',
    'Assessment',
    'Proposal',
    'Bids',
    'Collection',
    'Status'
  ];

  // Lead substatuses by stage
  const leadSubstatuses = {
    'Prospects': [],
    'Lead Received': [
      { 
        name: 'Initial Message Sent (Unopened)', 
        icon: 'leademail.svg', 
        description: 'First message sent but not opened yet',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Initial Message Sent (Opened)', 
        icon: 'newleadopened.svg', 
        description: 'First message sent and opened by client',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'First Drip Message Sent', 
        icon: 'leademailtwo.svg', 
        description: 'Second follow-up message sent',
        color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      },
      { 
        name: 'First Drip Message Sent (Client Opened)', 
        icon: 'leademailtwoopened.svg', 
        description: 'Second follow-up message opened by client',
        color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      },
      { 
        name: 'Second Drip Message Sent', 
        icon: 'leademailthree.svg', 
        description: 'Second drip follow-up message sent',
        color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      },
      { 
        name: 'Second Drip Message Sent (Opened)', 
        icon: 'leademailthreeopened.svg', 
        description: 'Second drip follow-up message opened by client',
        color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      },
      { 
        name: 'Final Message Sent', 
        icon: 'leademaillast.svg', 
        description: 'Final follow-up message sent',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      { 
        name: 'Final Message Sent (Opened)', 
        icon: 'leademaillastopened.svg', 
        description: 'Final follow-up message opened by client',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any messages',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Discovery Call': [
      { 
        name: 'Scheduled (Confirmed)', 
        icon: 'scheduled.svg', 
        description: 'Discovery call scheduled and confirmed',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'Scheduled (Not Confirmed)', 
        icon: 'schedulednoconfirm.svg', 
        description: 'Discovery call scheduled but not yet confirmed',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Ghosted', 
        icon: 'ghost.svg', 
        description: 'Lead has stopped responding to communications',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      { 
        name: 'Rescheduled (Not Confirmed)', 
        icon: 'rescheduled.svg', 
        description: 'Discovery call rescheduled but not yet confirmed',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      { 
        name: 'Rescheduled (Confirmed)', 
        icon: 'rescheduledconfirm.svg', 
        description: 'Discovery call rescheduled and confirmed',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any communications',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Assessment': [
      { 
        name: 'Portal Invite Sent', 
        icon: 'invitesent.svg', 
        description: 'Assessment portal invite link has been created',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Signed Up For Portal', 
        icon: 'portalsignup.png', 
        description: 'Lead has signed up for the portal',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'NDA Signed', 
        icon: 'ndadone.png', 
        description: 'Lead has signed the NDA',
        color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      },
      { 
        name: 'Assessment Done', 
        icon: 'assesdone.png', 
        description: 'Lead has completed the assessment',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Solutions Chosen', 
        icon: 'solutionsdone.png', 
        description: 'Lead has chosen their solutions',
        color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any communications',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Proposal': [
      { 
        name: 'Not Started', 
        icon: 'nostart.svg', 
        description: 'Proposal has not been started yet',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      },
      { 
        name: 'Draft', 
        icon: 'draft.svg', 
        description: 'Proposal is in draft stage',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Pending Payment', 
        icon: 'hourglass.svg', 
        description: 'Proposal sent and waiting for payment',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      }
    ],
    'Onboarding Call': [
      { 
        name: 'Scheduled (Confirmed)', 
        icon: 'scheduled.svg', 
        description: 'Onboarding call scheduled and confirmed',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'Scheduled (Not Confirmed)', 
        icon: 'schedulednoconfirm.svg', 
        description: 'Onboarding call scheduled but not yet confirmed',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Ghosted', 
        icon: 'ghost.svg', 
        description: 'Lead has stopped responding to communications',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      { 
        name: 'Rescheduled (Not Confirmed)', 
        icon: 'rescheduled.svg', 
        description: 'Onboarding call rescheduled but not yet confirmed',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      { 
        name: 'Rescheduled (Confirmed)', 
        icon: 'rescheduledconfirm.svg', 
        description: 'Onboarding call rescheduled and confirmed',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any communications',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
  };

  const handleEdit = (field, value) => {
    setEditingField(field);
    setEditValue(value);
  };

  const handleSave = async (field) => {
    try {
      const updatedData = { ...lead };
      
      // Handle nested contact fields
      if (field.startsWith('contact.')) {
        const contactField = field.split('.')[1];
        updatedData.contact = {
          ...updatedData.contact,
          [contactField]: editValue
        };
      } else {
        updatedData[field] = editValue;
      }

      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setEditingField(null);
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const handleSaveAssessmentCard = async (cardType) => {
    try {
      const updatedData = { ...lead };
      const editData = assessmentEditData[cardType] || {};

      if (cardType === 'general') {
        if (!updatedData.businessAssessment) {
          updatedData.businessAssessment = {};
        }
        Object.assign(updatedData.businessAssessment, editData);
      } else if (cardType === 'sales') {
        if (!updatedData.salesData) {
          updatedData.salesData = {};
        }
        if (!updatedData.businessAssessment) {
          updatedData.businessAssessment = {};
        }
        Object.assign(updatedData.salesData, editData);
        // Also save to businessAssessment for consistency
        Object.assign(updatedData.businessAssessment, {
          salesRating: editData.salesRating,
          closeRatio: editData.closeRatio,
          referralBusinessPercentage: editData.referralBusinessPercentage,
          revenueData: editData.revenueData
        });
      } else if (cardType === 'operations') {
        if (!updatedData.operationsData) {
          updatedData.operationsData = {};
        }
        if (!updatedData.businessAssessment) {
          updatedData.businessAssessment = {};
        }
        Object.assign(updatedData.operationsData, editData);
        // Also save to businessAssessment for consistency
        Object.assign(updatedData.businessAssessment, {
          operationsRating: editData.operationsRating,
          hasSOPs: editData.hasSOPs,
          hasJobDescriptions: editData.hasJobDescriptions,
          hasKPIs: editData.hasKPIs,
          teamSize: editData.teamSize,
          teamGrade: editData.teamGrade,
          cultureGrade: editData.cultureGrade
        });
      } else if (cardType === 'finance') {
        if (!updatedData.financeData) {
          updatedData.financeData = {};
        }
        if (!updatedData.businessAssessment) {
          updatedData.businessAssessment = {};
        }
        Object.assign(updatedData.financeData, editData);
        // Also save to businessAssessment for consistency
        Object.assign(updatedData.businessAssessment, {
          financeRating: editData.financeRating,
          unencumberedCash: editData.unencumberedCash,
          grossProfitMargin: editData.grossProfitMargin,
          netProfitMargin: editData.netProfitMargin,
          balanceSheetUrl: editData.balanceSheetUrl,
          balanceSheetFileName: editData.balanceSheetFileName,
          incomeStatementUrl: editData.incomeStatementUrl,
          incomeStatementFileName: editData.incomeStatementFileName,
          cashFlowStatementUrl: editData.cashFlowStatementUrl,
          cashFlowStatementFileName: editData.cashFlowStatementFileName,
          knowsOverhead: editData.knowsOverhead,
          knowsLaborBurden: editData.knowsLaborBurden,
          knowsProjectRunway: editData.knowsProjectRunway
        });
      } else if (cardType === 'technology') {
        if (!updatedData.techData) {
          updatedData.techData = {};
        }
        Object.assign(updatedData.techData, editData);
      } else if (cardType === 'marketing') {
        if (!updatedData.marketingData) {
          updatedData.marketingData = {};
        }
        if (!updatedData.businessAssessment) {
          updatedData.businessAssessment = {};
        }
        Object.assign(updatedData.marketingData, editData);
        // Also save to businessAssessment for consistency
        Object.assign(updatedData.businessAssessment, {
          marketingRating: editData.marketingRating,
          workingWithMarketingAgency: editData.workingWithMarketingAgency,
          websiteManager: editData.websiteManager,
          marketingAgencyType: editData.marketingAgencyType,
          engagesSocialMedia: editData.engagesSocialMedia,
          engagesPaidAds: editData.engagesPaidAds,
          engagesNewsletter: editData.engagesNewsletter
        });
      }

      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setEditingAssessmentCard(null);
      setAssessmentEditData({});
    } catch (error) {
      console.error('Error saving assessment card:', error);
    }
  };

  // Helper functions for editing solutions data
  const toggleDepartment = (department) => {
    if (!isEditMode) return;
    
    const updatedData = { ...lead };
    if (!updatedData.solutionsData) {
      updatedData.solutionsData = {};
    }
    if (!updatedData.solutionsData.selectedDepartments) {
      updatedData.solutionsData.selectedDepartments = [];
    }
    
    const departments = [...updatedData.solutionsData.selectedDepartments];
    const index = departments.indexOf(department);
    
    if (index > -1) {
      departments.splice(index, 1);
    } else {
      departments.push(department);
    }
    
    updatedData.solutionsData.selectedDepartments = departments;
    setLead(updatedData);
  };

  const toggleMeetingType = (meetingType) => {
    if (!isEditMode) return;
    
    const updatedData = { ...lead };
    if (!updatedData.solutionsData) {
      updatedData.solutionsData = {};
    }
    if (!updatedData.solutionsData.selectedMeetingTypes) {
      updatedData.solutionsData.selectedMeetingTypes = [];
    }
    
    const meetingTypes = [...updatedData.solutionsData.selectedMeetingTypes];
    const index = meetingTypes.indexOf(meetingType);
    
    if (index > -1) {
      meetingTypes.splice(index, 1);
    } else {
      meetingTypes.push(meetingType);
    }
    
    updatedData.solutionsData.selectedMeetingTypes = meetingTypes;
    setLead(updatedData);
  };

  const updateTimeInvestment = (field, value) => {
    if (!isEditMode) return;
    
    const updatedData = { ...lead };
    if (!updatedData.solutionsData) {
      updatedData.solutionsData = {};
    }
    
    updatedData.solutionsData[field] = value === '' ? null : parseInt(value) || null;
    setLead(updatedData);
  };

  const handleSaveSolutionsData = async () => {
    try {
      const updatedData = { ...lead };
      const cleanedData = removeUndefinedValues(updatedData);
      await updateAccount(leadId, cleanedData);
      setLead(updatedData);
    } catch (error) {
      console.error('Error saving solutions data:', error);
      setError('Failed to save solutions data');
    }
  };

  const handleDeleteLead = async () => {
    try {
      await deleteAccount(leadId);
      navigate('/crm/leads');
    } catch (error) {
      console.error('Error deleting lead:', error);
      setError('Failed to delete lead');
    }
  };

  const handleSubstatusUpdate = async (substatusName) => {
    try {
      const updatedData = { ...lead };
      updatedData.substatus = substatusName;
      
      // Track substatus dates
      if (!updatedData.substatusDates) {
        updatedData.substatusDates = {};
      }
      // Only set the date when first achieving this substatus - don't overwrite existing dates
      // User can edit dates via the Notes tab timeline pickers; status changes should not auto-update them
      const currentDate = new Date().toISOString();
      if (!updatedData.substatusDates[substatusName]) {
        updatedData.substatusDates[substatusName] = currentDate;
      }
      
      // Also add to statusHistory for tracking
      if (!updatedData.statusHistory) {
        updatedData.statusHistory = [];
      }
      const statusEntry = {
        substatus: substatusName,
        date: updatedData.substatusDates[substatusName] || currentDate,
        updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User'
      };
      updatedData.statusHistory = [...updatedData.statusHistory, statusEntry];
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setStatusHistory(updatedData.statusHistory);
      
      // Reset cold lead check refs to prevent immediate re-checking after manual update
      // This prevents the auto-check from resetting the substatus back to Cold Lead
      coldLeadStatusProcessedRef.current = false;
      lastLeadSubstatusRef.current = null;
      
      if (substatusName === 'Cold Lead') {
        sendColdLeadWebhook(updatedData);
      }
    } catch (error) {
      console.error('Error updating substatus:', error);
      setError('Failed to update substatus');
    }
  };

  const handleSubstatusDateUpdate = async (substatusName, stageName, date) => {
    try {
      const updatedData = { ...lead };
      
      // Track substatus dates
      if (!updatedData.substatusDates) {
        updatedData.substatusDates = {};
      }
      
      // Update the date for this substatus
      if (date) {
        // Store as date-only string (YYYY-MM-DD) to avoid timezone issues
        // If it's already in YYYY-MM-DD format, use it directly
        // Otherwise, convert to local date string
        let dateString = date;
        if (date.includes('T')) {
          // If it's an ISO string, extract just the date part
          dateString = date.split('T')[0];
        } else {
          // Ensure it's in YYYY-MM-DD format
          const dateObj = new Date(date + 'T00:00:00');
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          dateString = `${year}-${month}-${day}`;
        }
        updatedData.substatusDates[substatusName] = dateString;
      } else {
        // If no date provided, remove the date (mark as incomplete)
        delete updatedData.substatusDates[substatusName];
      }
      
      // Also add to statusHistory for tracking if date is being set
      if (date) {
        if (!updatedData.statusHistory) {
          updatedData.statusHistory = [];
        }
        // For statusHistory, use ISO string with local time to preserve the exact date
        const dateObj = new Date(date + 'T00:00:00');
        const statusEntry = {
          substatus: substatusName,
          date: dateObj.toISOString(),
          updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User',
          action: `Substatus date updated via Notes tab`
        };
        updatedData.statusHistory = [...updatedData.statusHistory, statusEntry];
      }
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      if (updatedData.statusHistory) {
        setStatusHistory(updatedData.statusHistory);
      }
      
      // Close modal
      setShowSubstatusDateModal(false);
      setSelectedSubstatus(null);
      setSubstatusDate('');
    } catch (error) {
      console.error('Error updating substatus date:', error);
      setError('Failed to update substatus date');
    }
  };

  const handleQualifiedUpdate = async (isQualified) => {
    try {
      const updatedData = { ...lead };
      updatedData.qualified = isQualified;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setQualified(isQualified);
    } catch (error) {
      console.error('Error updating qualified status:', error);
      setError('Failed to update qualified status');
    }
  };

  const handleShowOnKanbanUpdate = async (shouldShow) => {
    try {
      const updatedData = { ...lead };
      updatedData.showOnKanban = shouldShow;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setShowOnKanban(shouldShow);
    } catch (error) {
      console.error('Error updating show on kanban status:', error);
      setError('Failed to update show on kanban status');
    }
  };

  const handleResultUpdate = async (newResult) => {
    try {
      const updatedData = { ...lead };
      updatedData.result = newResult;
      // Clear reason if result is cleared
      if (!newResult) {
        updatedData.resultReason = null;
        setResultReason(null);
      }
      if (newResult === 'Lost') Object.assign(updatedData, leadLostFields(lead));

      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setResult(newResult);
    } catch (error) {
      console.error('Error updating result:', error);
      setError('Failed to update result');
    }
  };

  const handleResultReasonUpdate = async (reason) => {
    try {
      const updatedData = { ...lead };
      updatedData.resultReason = reason;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setResultReason(reason);
    } catch (error) {
      console.error('Error updating result reason:', error);
      setError('Failed to update result reason');
    }
  };

  const handleFreePageUpdate = async (page) => {
    try {
      const updatedData = { ...lead };
      updatedData.freePage = page;
      // Clear unlocked SOPs if page changes away from operations/sops
      if (page !== 'operations/sops') {
        updatedData.unlockedSOPs = [];
        setSelectedSOPs([]);
      }
      
      // Clear unlocked KPIs if page changes away from operations/kpis
      if (page !== 'operations/kpis') {
        updatedData.unlockedKPIs = [];
        setSelectedKPIs([]);
      }
      
      // Clear unlocked Jobs if page changes away from operations/job-descriptions
      if (page !== 'operations/job-descriptions') {
        updatedData.unlockedJobs = [];
        setSelectedJobs([]);
      }
      
      // Clear unlocked Programs if page changes away from sales-referral-programs
      if (page !== 'sales-referral-programs') {
        updatedData.unlockedPrograms = [];
        setSelectedPrograms([]);
      }
      // Clear unlocked Proposal tabs if page changes away from sales-proposals
      if (page !== 'sales-proposals') {
        updatedData.unlockedProposalTabs = [];
        setSelectedProposalTabs([]);
      }

      // Clear unlocked Sales Materials if page changes away from sales/resources
      if (page !== 'sales/resources') {
        updatedData.unlockedSalesMaterials = [];
        setSelectedSalesMaterials([]);
      }
      
      // Clear unlocked Estimating Templates if page changes away from technology/estimating-template
      if (page !== 'technology/estimating-template') {
        updatedData.unlockedEstimatingTemplates = [];
        setSelectedEstimatingCategories([]);
      }
      
      // Clear unlocked GPTs if page changes away from technology/chatgpt
      if (page !== 'technology/chatgpt') {
        updatedData.unlockedGPTs = [];
        setSelectedGPTs([]);
      }
      
      // Tech Stack: full unlock when selected as free page, no content selection
      if (page === 'technology/crmtechstack') {
        updatedData.unlockedTechStack = true;
      } else {
        updatedData.unlockedTechStack = false;
        setSelectedTechStack([]);
      }
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setSelectedFreePage(page);
      setShowFreePageDropdown(false);
      setFreePageSearch('');
      
      // Fetch SOPs if operations/sops page is selected
      if (page === 'operations/sops') {
        await fetchSOPsForLead();
      }
      
      // Fetch KPIs if operations/kpis page is selected
      if (page === 'operations/kpis') {
        await fetchKPIsForLead();
      }
      
      // Fetch Jobs if operations/job-descriptions page is selected
      if (page === 'operations/job-descriptions') {
        await fetchJobsForLead();
      }
      
      // Fetch Programs if sales-referral-programs page is selected
      if (page === 'sales-referral-programs') {
        await fetchProgramsForLead();
      }

      // Fetch Sales Materials if sales/resources page is selected
      if (page === 'sales/resources') {
        await fetchSalesMaterialsForLead();
      }
      
      // Fetch Estimating Templates if technology/estimating-template page is selected
      if (page === 'technology/estimating-template') {
        await fetchEstimatingTemplatesForLead();
      }
      
      // Fetch GPTs if technology/chatgpt page is selected
      if (page === 'technology/chatgpt') {
        await fetchGPTsForLead();
      }
      
      // Tech Stack: no fetch needed - full unlock when selected
    } catch (error) {
      console.error('Error updating free page:', error);
      setError('Failed to update free page');
    }
  };

  const handleSOPToggle = (sopId, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate sopId
    if (!sopId) {
      console.error('Invalid SOP ID:', sopId);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedSOPs(prevSelectedSOPs => {
      // Ensure prevSelectedSOPs is an array
      const currentSOPs = Array.isArray(prevSelectedSOPs) ? prevSelectedSOPs : [];
      
      // Toggle the specific SOP ID
      const updatedSOPs = currentSOPs.includes(sopId)
        ? currentSOPs.filter(id => id !== sopId)
        : [...currentSOPs, sopId];
      
      return updatedSOPs;
    });
  };

  const handleSaveSOPs = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedSOPs = selectedSOPs;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedSOPs.length} SOP${selectedSOPs.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked SOPs:', error);
      setError('Failed to save unlocked SOPs');
      alert('Failed to save SOP selections. Please try again.');
    }
  };

  const handleProposalTabToggle = (tabId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!tabId) return;
    setSelectedProposalTabs(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(tabId)
        ? current.filter(id => id !== tabId)
        : [...current, tabId];
    });
  };

  const handleSaveProposalTabs = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedProposalTabs = selectedProposalTabs;
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      alert(`Successfully saved ${selectedProposalTabs.length} proposal tab${selectedProposalTabs.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked proposal tabs:', error);
      setError('Failed to save proposal tab selections');
      alert('Failed to save proposal tab selections. Please try again.');
    }
  };

  const handleSalesMaterialToggle = (catalogKey, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!catalogKey) return;
    setSelectedSalesMaterials(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(catalogKey)
        ? current.filter(key => key !== catalogKey)
        : [...current, catalogKey];
    });
  };

  const handleSaveSalesMaterials = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedSalesMaterials = selectedSalesMaterials;
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      alert(`Successfully saved ${selectedSalesMaterials.length} sales material${selectedSalesMaterials.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked sales materials:', error);
      setError('Failed to save sales material selections');
      alert('Failed to save sales material selections. Please try again.');
    }
  };

  // Sales materials come from the CRM-managed catalog; the stable identifier is
  // the structure item's title (catalogKey), which is what the Start portal
  // checks against accounts.unlockedSalesMaterials.
  const fetchSalesMaterialsForLead = async () => {
    try {
      setLoadingSalesMaterials(true);
      const sections = await fetchTemplateStructure('sales', []);
      const materials = sections.flatMap(section =>
        (section.items || []).map(item => ({
          id: item.title,
          name: item.name || item.title,
          emoji: item.emoji || '',
          section: section.label
        }))
      );
      setAvailableSalesMaterials(materials);
    } catch (error) {
      console.error('Error fetching sales materials:', error);
      setError('Failed to fetch sales materials');
    } finally {
      setLoadingSalesMaterials(false);
    }
  };

  const fetchSOPsForLead = async () => {
    try {
      setLoadingSOPs(true);
      const templateQuerySnapshot = await getDocs(collection(db, 'sopTemplates'));
      const templateSOPs = templateQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Also fetch account-specific SOPs if leadId exists
      let accountSOPs = [];
      if (leadId) {
        try {
          const accountSOPsQuerySnapshot = await getDocs(collection(db, 'accounts', leadId, 'sops'));
          accountSOPs = accountSOPsQuerySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific SOPs:', error);
        }
      }
      
      const allSOPs = [...templateSOPs, ...accountSOPs];
      setAvailableSOPs(allSOPs);
    } catch (error) {
      console.error('Error fetching SOPs:', error);
      setError('Failed to fetch SOPs');
    } finally {
      setLoadingSOPs(false);
    }
  };

  const handleKPIToggle = (kpiId, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate kpiId
    if (!kpiId) {
      console.error('Invalid KPI ID:', kpiId);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedKPIs(prevSelectedKPIs => {
      // Ensure prevSelectedKPIs is an array
      const currentKPIs = Array.isArray(prevSelectedKPIs) ? prevSelectedKPIs : [];
      
      // Toggle the specific KPI ID
      const updatedKPIs = currentKPIs.includes(kpiId)
        ? currentKPIs.filter(id => id !== kpiId)
        : [...currentKPIs, kpiId];
      
      return updatedKPIs;
    });
  };

  const handleSaveKPIs = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedKPIs = selectedKPIs;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedKPIs.length} KPI${selectedKPIs.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked KPIs:', error);
      setError('Failed to save unlocked KPIs');
      alert('Failed to save KPI selections. Please try again.');
    }
  };

  const fetchKPIsForLead = async () => {
    try {
      setLoadingKPIs(true);
      const templateQuerySnapshot = await getDocs(collection(db, 'kpiTemplates'));
      const templateKPIs = templateQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Also fetch account-specific KPIs if leadId exists
      let accountKPIs = [];
      if (leadId) {
        try {
          const accountKPIsQuerySnapshot = await getDocs(collection(db, 'accounts', leadId, 'kpiJobs'));
          accountKPIs = accountKPIsQuerySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific KPIs:', error);
        }
      }
      
      const allKPIs = [...templateKPIs, ...accountKPIs];
      setAvailableKPIs(allKPIs);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      setError('Failed to fetch KPIs');
    } finally {
      setLoadingKPIs(false);
    }
  };

  // Fetch SOPs when operations/sops page is selected
  useEffect(() => {
    if (selectedFreePage === 'operations/sops' && availableSOPs.length === 0 && !loadingSOPs) {
      fetchSOPsForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);

  // Fetch Sales Materials when sales/resources page is selected
  useEffect(() => {
    if (selectedFreePage === 'sales/resources' && availableSalesMaterials.length === 0 && !loadingSalesMaterials) {
      fetchSalesMaterialsForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);

  const handleJobToggle = (jobId, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate jobId
    if (!jobId) {
      console.error('Invalid Job ID:', jobId);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedJobs(prevSelectedJobs => {
      // Ensure prevSelectedJobs is an array
      const currentJobs = Array.isArray(prevSelectedJobs) ? prevSelectedJobs : [];
      
      // Toggle the specific Job ID
      const updatedJobs = currentJobs.includes(jobId)
        ? currentJobs.filter(id => id !== jobId)
        : [...currentJobs, jobId];
      
      return updatedJobs;
    });
  };

  const handleSaveJobs = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedJobs = selectedJobs;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedJobs.length} Job${selectedJobs.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked Jobs:', error);
      setError('Failed to save unlocked Jobs');
      alert('Failed to save Job selections. Please try again.');
    }
  };

  const fetchJobsForLead = async () => {
    try {
      setLoadingJobs(true);
      // Fetch published job descriptions
      const templateQuerySnapshot = await getDocs(collection(db, 'jobDescriptions'));
      const templateJobs = templateQuerySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(job => job.status === 'published'); // Only show published jobs
      
      setAvailableJobs(templateJobs);
    } catch (error) {
      console.error('Error fetching Jobs:', error);
      setError('Failed to fetch Jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  // Fetch KPIs when operations/kpis page is selected
  useEffect(() => {
    if (selectedFreePage === 'operations/kpis' && availableKPIs.length === 0 && !loadingKPIs) {
      fetchKPIsForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);

  // Fetch Jobs when operations/job-descriptions page is selected
  useEffect(() => {
    if (selectedFreePage === 'operations/job-descriptions' && availableJobs.length === 0 && !loadingJobs) {
      fetchJobsForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);

  const handleProgramToggle = (programId, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate programId
    if (!programId) {
      console.error('Invalid Program ID:', programId);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedPrograms(prevSelectedPrograms => {
      // Ensure prevSelectedPrograms is an array
      const currentPrograms = Array.isArray(prevSelectedPrograms) ? prevSelectedPrograms : [];
      
      // Toggle the specific Program ID
      const updatedPrograms = currentPrograms.includes(programId)
        ? currentPrograms.filter(id => id !== programId)
        : [...currentPrograms, programId];
      
      return updatedPrograms;
    });
  };

  const handleSavePrograms = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedPrograms = selectedPrograms;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedPrograms.length} Partner Program${selectedPrograms.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked Programs:', error);
      setError('Failed to save unlocked Programs');
      alert('Failed to save Partner Program selections. Please try again.');
    }
  };

  const fetchProgramsForLead = async () => {
    try {
      setLoadingPrograms(true);
      // Partner programs are based on types: realtors, designers, architects
      const programTypes = [
        { id: 'realtors', label: 'Realtors', description: 'Build relationships with real estate professionals' },
        { id: 'designers', label: 'Designers', description: 'Partner with interior designers and design firms' },
        { id: 'architects', label: 'Architects', description: 'Collaborate with architects and architectural firms' }
      ];
      
      setAvailablePrograms(programTypes);
    } catch (error) {
      console.error('Error fetching Partner Programs:', error);
      setError('Failed to fetch Partner Programs');
    } finally {
      setLoadingPrograms(false);
    }
  };

  // Fetch Programs when sales-referral-programs page is selected
  useEffect(() => {
    if (selectedFreePage === 'sales-referral-programs' && availablePrograms.length === 0 && !loadingPrograms) {
      fetchProgramsForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);

  const handleEstimatingTemplateToggle = (categoryCode, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate categoryCode
    if (!categoryCode) {
      console.error('Invalid Category Code:', categoryCode);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedEstimatingCategories(prevSelectedCategories => {
      // Ensure prevSelectedCategories is an array
      const currentCategories = Array.isArray(prevSelectedCategories) ? prevSelectedCategories : [];
      
      // Toggle the specific Category Code
      const updatedCategories = currentCategories.includes(categoryCode)
        ? currentCategories.filter(code => code !== categoryCode)
        : [...currentCategories, categoryCode];
      
      return updatedCategories;
    });
  };

  const handleSaveEstimatingTemplates = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedEstimatingTemplates = selectedEstimatingCategories;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedEstimatingCategories.length} Estimating Template Categor${selectedEstimatingCategories.length !== 1 ? 'ies' : 'y'} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked Estimating Templates:', error);
      setError('Failed to save unlocked Estimating Templates');
      alert('Failed to save Estimating Template selections. Please try again.');
    }
  };

  const fetchEstimatingTemplatesForLead = async () => {
    try {
      setLoadingEstimatingCategories(true);
      // Mapping of category codes to their actual names
      const categoryNameMap = {
        '01': '01 - Permits',
        '02': '02 - Architecture & Engineering',
        '03': '03 - Demo',
        '04': '04 - Survey',
        '06': '06 - Setup/Cleanup',
        '07': '07 - Grading & Drainage',
        '08': '08 - Landscaping & Finish Grade',
        '09': '09 - Landscape Hardscapes',
        '11': '11 - Footings',
        '12': '12 - Concrete Slabs',
        '13': '13 - Driveway',
        '14': '14 - Foundation & Waterproofing',
        '15': '15 - Pest Control',
        '16': '16 - Stone Veneer & Stucco',
        '17': '17 - Siding',
        '18': '18 - Exterior Trim',
        '19': '19 - Framing',
        '20': '20 - Exterior Doors',
        '21': '21 - Door Hardware',
        '22': '22 - Windows / Skylights',
        '23': '23 - Garage Doors',
        '24': '24 - Plumbing',
        '25': '25 - Fireplace',
        '27': '27 - Electrical',
        '28': '28 - HVAC',
        '29': '29 - Insulation',
        '30': '30 - Roofing',
        '31': '31 - Gutter',
        '32': '32 - Screen Porch',
        '33': '33 - Decking',
        '34': '34 - Interior Trim',
        '35': '35 - Shelving & Closets',
        '36': '36 - Shower Doors',
        '37': '37 - Bath Hardware',
        '38': '38 - Drywall',
        '39': '39 - Tile',
        '40': '40 - Flooring',
        '41': '41 - Interior Paint',
        '42': '42 - Exterior Paint',
        '43': '43 - Cabinets & Hardware',
        '44': '44 - Countertops',
        '45': '45 - Appliances',
        '46': '46 - Management',
        '47': '47 - Punchwork'
      };
      
      const categoryCodes = [
        '01', '02', '03', '04', '06', '07', '08', '09', '11', '12', '13', '14', '15', '16', '17', '18', '19',
        '20', '21', '22', '23', '24', '25', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37',
        '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'
      ];
      
      const categories = categoryCodes.map(code => ({
        id: code,
        name: categoryNameMap[code] || `Category ${code}`,
        code: code
      }));
      
      setAvailableEstimatingCategories(categories);
    } catch (error) {
      console.error('Error fetching Estimating Templates:', error);
      setError('Failed to fetch Estimating Templates');
    } finally {
      setLoadingEstimatingCategories(false);
    }
  };

  const handleGPTToggle = (gptId, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate gptId
    if (!gptId) {
      console.error('Invalid GPT ID:', gptId);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedGPTs(prevSelectedGPTs => {
      // Ensure prevSelectedGPTs is an array
      const currentGPTs = Array.isArray(prevSelectedGPTs) ? prevSelectedGPTs : [];
      
      // Toggle the specific GPT ID
      const updatedGPTs = currentGPTs.includes(gptId)
        ? currentGPTs.filter(id => id !== gptId)
        : [...currentGPTs, gptId];
      
      return updatedGPTs;
    });
  };

  const handleSaveGPTs = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedGPTs = selectedGPTs;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedGPTs.length} GPT${selectedGPTs.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked GPTs:', error);
      setError('Failed to save unlocked GPTs');
      alert('Failed to save GPT selections. Please try again.');
    }
  };

  const fetchGPTsForLead = async () => {
    try {
      setLoadingGPTs(true);
      // Fetch GPTs from Firestore
      const gptsQuerySnapshot = await getDocs(query(collection(db, 'gpts'), orderBy('createdAt', 'desc')));
      const gptsList = gptsQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAvailableGPTs(gptsList);
    } catch (error) {
      console.error('Error fetching GPTs:', error);
      setError('Failed to fetch GPTs');
    } finally {
      setLoadingGPTs(false);
    }
  };

  const handleTechStackToggle = (toolId, event) => {
    // Prevent event propagation to avoid any interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate toolId
    if (!toolId) {
      console.error('Invalid Tech Stack Tool ID:', toolId);
      return;
    }
    
    // Only update local state, don't save yet
    setSelectedTechStack(prevSelectedTechStack => {
      // Ensure prevSelectedTechStack is an array
      const currentTechStack = Array.isArray(prevSelectedTechStack) ? prevSelectedTechStack : [];
      
      // Toggle the specific Tool ID
      const updatedTechStack = currentTechStack.includes(toolId)
        ? currentTechStack.filter(id => id !== toolId)
        : [...currentTechStack, toolId];
      
      return updatedTechStack;
    });
  };

  const handleSaveTechStack = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.unlockedTechStack = selectedTechStack;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setError(null);
      // Show success message or feedback
      alert(`Successfully saved ${selectedTechStack.length} Tech Stack Tool${selectedTechStack.length !== 1 ? 's' : ''} for this lead.`);
    } catch (error) {
      console.error('Error saving unlocked Tech Stack:', error);
      setError('Failed to save unlocked Tech Stack');
      alert('Failed to save Tech Stack selections. Please try again.');
    }
  };

  const fetchTechStackForLead = async () => {
    try {
      setLoadingTechStack(true);
      // Fetch tech stack tools from Firestore
      const collectionNames = ['Core', 'Primary', 'Secondary'];
      const toolsList = [];
      
      for (const collectionName of collectionNames) {
        const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
        
        typesSnapshot.docs.forEach(typeDoc => {
          const typeData = typeDoc.data();
          if (typeData && typeData.name) {
            toolsList.push({
              id: typeDoc.id,
              name: typeData.name,
              category: collectionName.toLowerCase(),
              description: typeData.description || ''
            });
          }
        });
      }
      
      setAvailableTechStack(toolsList);
    } catch (error) {
      console.error('Error fetching Tech Stack:', error);
      setError('Failed to fetch Tech Stack');
    } finally {
      setLoadingTechStack(false);
    }
  };

  // Fetch Estimating Templates when technology/estimating-template page is selected
  useEffect(() => {
    if (selectedFreePage === 'technology/estimating-template' && availableEstimatingCategories.length === 0 && !loadingEstimatingCategories) {
      fetchEstimatingTemplatesForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);

  // Fetch GPTs when technology/chatgpt page is selected
  useEffect(() => {
    if (selectedFreePage === 'technology/chatgpt' && availableGPTs.length === 0 && !loadingGPTs) {
      fetchGPTsForLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFreePage]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFreePageDropdown && !event.target.closest('.free-page-dropdown-container')) {
        setShowFreePageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFreePageDropdown]);

  const activeSummaryTab = SUMMARY_TABS.find((tab) => tab.id === summaryTab) || SUMMARY_TABS[0];
  const selectSummaryTab = (tabId) => {
    setSummaryTab(tabId);
    setEditingSummaryTab(null);
    setSummaryDraft('');
  };

  // Every lead opens on the Summary tab.
  useEffect(() => {
    setSummaryTab('summary');
    setEditingSummaryTab(null);
    setSummaryDraft('');
    setSummaryGenerating(false);
    setSummaryGenError('');
  }, [leadId]);

  // Leads default to Ian Riddle as success manager until someone assigns a different one.
  useEffect(() => {
    if (!lead || !leadId || internalUsers.length === 0) return;
    if (lead.successManagerId || lead.successManager) return;
    if (successManagerDefaultRef.current === leadId) return;
    const ian =
      internalUsers.find((u) => (u.name || '').trim().toLowerCase() === 'ian riddle') ||
      internalUsers.find((u) => (u.email || '').toLowerCase() === 'ianr@buildersbreakthrough.com');
    if (!ian) return;
    successManagerDefaultRef.current = leadId;
    (async () => {
      try {
        await updateAccount(leadId, { successManagerId: ian.id, successManager: ian.name });
        setLead((prev) => (prev ? { ...prev, successManagerId: ian.id, successManager: ian.name } : prev));
      } catch (error) {
        console.error('Error defaulting success manager:', error);
        successManagerDefaultRef.current = null;
      }
    })();
  }, [lead, leadId, internalUsers]);

  const getSummaryValue = (tabId) => {
    if (tabId === 'summary') return lead?.leadSummary || '';
    if (tabId === 'intake') return intakeNote || '';
    if (tabId === 'discovery') return lead?.discoveryCallSummary || '';
    return lead?.analysisCallSummary || '';
  };

  // Only the edited field is written — spreading the whole (possibly stale) lead back
  // over the doc used to clobber fields other tabs had changed in the meantime.
  const handleSummarySave = async (tabId) => {
    const tab = SUMMARY_TABS.find((t) => t.id === tabId);
    if (!tab || !lead) return;
    const value = summaryDraft.trim();
    setEditingSummaryTab(null);
    setSummaryDraft('');
    if (value === getSummaryValue(tabId).trim()) return;
    try {
      const patch = {
        [tab.field]: value,
        [`${tab.field}UpdatedAt`]: new Date().toISOString()
      };
      await updateAccount(leadId, patch);
      setLead((prev) => (prev ? { ...prev, ...patch } : prev));
      if (tab.id === 'intake') setIntakeNote(value);
    } catch (error) {
      console.error('Error updating summary:', error);
      setError('Failed to update summary');
    }
  };

  // AI Summary tab — built from the other three tabs, regenerated whenever they change.
  const summarySources = {
    intake: (lead?.intakeNote || '').trim(),
    discovery: (lead?.discoveryCallSummary || '').trim(),
    analysis: (lead?.analysisCallSummary || '').trim()
  };
  const hasSummarySources = !!(summarySources.intake || summarySources.discovery || summarySources.analysis);
  const currentSummarySourceKey = leadSummarySourceKey(summarySources.intake, summarySources.discovery, summarySources.analysis);
  const leadSummaryIsAi = !!lead?.leadSummaryGeneratedAt;
  const leadSummaryIsStale = leadSummaryIsAi && lead?.leadSummarySourceKey !== currentSummarySourceKey;

  const handleGenerateLeadSummary = async () => {
    if (!lead || !leadId || lead.id !== leadId || summaryGenerating || !hasSummarySources) return;
    const targetLeadId = leadId;
    const sourceKey = currentSummarySourceKey;
    setSummaryGenerating(true);
    setSummaryGenError('');
    try {
      const ai = await generateLeadSummary({ companyName: lead.company || '', ...summarySources });
      const now = new Date().toISOString();
      const patch = {
        leadSummary: flattenLeadSummary(ai),
        leadSummaryAi: ai,
        leadSummaryGeneratedAt: now,
        leadSummaryUpdatedAt: now,
        leadSummarySourceKey: sourceKey
      };
      await updateAccount(targetLeadId, patch);
      if (leadIdRef.current === targetLeadId) setLead((prev) => (prev ? { ...prev, ...patch } : prev));
    } catch (error) {
      console.error('Error generating lead summary:', error);
      if (leadIdRef.current === targetLeadId) setSummaryGenError(error.message || 'Failed to generate the summary.');
    } finally {
      if (leadIdRef.current === targetLeadId) setSummaryGenerating(false);
    }
  };

  // Generate once when there's something to summarize and no summary yet, and again whenever
  // an AI summary's source notes change. A hand-written legacy summary is never overwritten
  // automatically — the Generate button replaces it.
  useEffect(() => {
    if (!lead || !leadId || lead.id !== leadId || !hasSummarySources || summaryGenerating) return;
    const needsSummary = !(lead.leadSummary || '').trim() || leadSummaryIsStale;
    if (!needsSummary) return;
    const attemptKey = `${leadId}:${currentSummarySourceKey}`;
    if (summaryAutoGenRef.current === attemptKey) return;
    summaryAutoGenRef.current = attemptKey;
    handleGenerateLeadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, leadId, hasSummarySources, currentSummarySourceKey, leadSummaryIsStale, summaryGenerating]);

  // One-time move of the old single-blob summary into the Intake Message tab.
  // The previous card concatenated intake + discovery + proposal into one textarea and
  // re-split it on blank lines when saving, so discoveryCallSummary/proposalCallSummary
  // hold arbitrary paragraphs of that blob rather than real call notes. Fold everything
  // into intake (keeping a copy in legacySummaryBackup) so the new tabs start clean.
  useEffect(() => {
    if (!lead || !leadId) return;
    if (lead.summaryTabsMigratedAt) return;
    const legacyDiscovery = (lead.discoveryCallSummary || '').trim();
    const legacyProposal = (lead.proposalCallSummary || '').trim();
    if (!legacyDiscovery && !legacyProposal) return;
    if (summaryMigrationRef.current === leadId) return;
    summaryMigrationRef.current = leadId;

    (async () => {
      const merged = [(lead.intakeNote || '').trim(), legacyDiscovery, legacyProposal]
        .filter(Boolean)
        .join('\n\n');
      try {
        const updatedData = {
          ...lead,
          intakeNote: merged,
          discoveryCallSummary: '',
          proposalCallSummary: '',
          legacySummaryBackup: {
            intakeNote: lead.intakeNote || '',
            discoveryCallSummary: lead.discoveryCallSummary || '',
            proposalCallSummary: lead.proposalCallSummary || ''
          },
          summaryTabsMigratedAt: new Date().toISOString()
        };
        await updateAccount(leadId, updatedData);
        setLead(updatedData);
        setIntakeNote(merged);
      } catch (error) {
        console.error('Error migrating lead summaries:', error);
        summaryMigrationRef.current = null;
      }
    })();
  }, [lead, leadId]);

  const handleIntakeNoteSave = async () => {
    try {
      const updatedData = { ...lead };
      updatedData.intakeNote = intakeNote;
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setEditingIntakeNote(false);
    } catch (error) {
      console.error('Error updating intake note:', error);
      setError('Failed to update intake note');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return null;
    }
  };

  // Format date with timestamp for display (similar to last login)
  const formatDateWithTime = (dateString) => {
    if (!dateString) return null;
    try {
      let date;
      // Check if this is a date-only string (YYYY-MM-DD) or ISO string with midnight UTC (likely from date-only)
      if (typeof dateString === 'string') {
        // Check for date-only format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          // Date-only string - create date in local timezone at noon
          const [year, month, day] = dateString.split('-').map(Number);
          date = new Date(year, month - 1, day, 12, 0, 0);
        } else if (/^\d{4}-\d{2}-\d{2}T00:00:00/.test(dateString)) {
          // ISO string with midnight UTC (likely converted from date-only) - use noon local time
          const dateOnly = dateString.split('T')[0];
          const [year, month, day] = dateOnly.split('-').map(Number);
          date = new Date(year, month - 1, day, 12, 0, 0);
        } else {
          // Full ISO string with time - preserve the time
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return null;
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (error) {
      return null;
    }
  };

  // Get received date (receivedDate or createdAt as fallback)
  const getReceivedDate = () => {
    if (!lead) return null;
    
    // Try receivedDate first
    if (lead.receivedDate) {
      if (typeof lead.receivedDate === 'string') {
        return lead.receivedDate;
      }
      if (lead.receivedDate.toDate && typeof lead.receivedDate.toDate === 'function') {
        return lead.receivedDate.toDate().toISOString();
      }
      if (lead.receivedDate.seconds) {
        return new Date(lead.receivedDate.seconds * 1000).toISOString();
      }
    }
    
    // Fallback to createdAt
    if (lead.createdAt) {
      if (typeof lead.createdAt === 'string') {
        return lead.createdAt;
      }
      if (lead.createdAt.toDate && typeof lead.createdAt.toDate === 'function') {
        return lead.createdAt.toDate().toISOString();
      }
      if (lead.createdAt.seconds) {
        return new Date(lead.createdAt.seconds * 1000).toISOString();
      }
    }
    
    return null;
  };

  // Helper to convert date field to ISO string
  const getDateField = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue === 'string') {
      // If it's already a date-only string (YYYY-MM-DD), convert to ISO with local time
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const dateObj = new Date(dateValue + 'T00:00:00');
        return dateObj.toISOString();
      }
      return dateValue;
    }
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toISOString();
    }
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000).toISOString();
    }
    return null;
  };

  // Calculate days in stage
  const getDaysInStage = (stageDate, nextStageDate = null) => {
    if (!stageDate) return null;
    
    const startDate = new Date(getDateField(stageDate) || stageDate);
    if (isNaN(startDate.getTime())) return null;
    
    const endDate = nextStageDate 
      ? new Date(getDateField(nextStageDate) || nextStageDate)
      : new Date();
    
    if (isNaN(endDate.getTime())) return null;
    
    // Set both dates to midnight for accurate day calculation
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    const diffTime = endDate - startDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 0 ? diffDays : null;
  };

  // Get row styling based on stage status
  const getRowStyling = (stageName) => {
    if (!lead) return '';
    
    const currentStage = (lead.stage || '').toLowerCase();
    const stageOrder = ['Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal'];
    
    // Find current stage index - check for exact match or partial match
    let currentStageIndex = -1;
    stageOrder.forEach((stage, index) => {
      const stageLower = stage.toLowerCase();
      if (currentStage === stageLower || 
          currentStage.includes(stageLower) || 
          stageLower.includes(currentStage)) {
        currentStageIndex = index;
      }
    });
    
    const stageIndex = stageOrder.indexOf(stageName);
    
    // Check if this stage has completed substatuses (any substatus with a date)
    const hasCompletedSubstatuses = (() => {
      const substatusDates = getSubstatusDates(stageName);
      return Object.keys(substatusDates).length > 0;
    })();
    
    // Find the latest stage with completed substatuses
    let latestCompletedStageIndex = -1;
    for (let i = stageOrder.length - 1; i >= 0; i--) {
      const stageDates = getSubstatusDates(stageOrder[i]);
      if (Object.keys(stageDates).length > 0) {
        latestCompletedStageIndex = i;
        break;
      }
    }
    
    // If this is the latest completed stage, make it red
    if (latestCompletedStageIndex >= 0 && stageIndex === latestCompletedStageIndex) {
      return 'bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800';
    }
    
    // If this stage has completed substatuses and is before the latest completed stage, make it green
    if (hasCompletedSubstatuses && latestCompletedStageIndex >= 0 && stageIndex < latestCompletedStageIndex) {
      return 'bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800';
    }
    
    // If this is the current stage (but no completed substatuses yet), make it red
    if (currentStageIndex >= 0 && stageIndex === currentStageIndex) {
      return 'bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800';
    }
    
    // Default styling
    return 'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50';
  };

  // Get substatus dates for any stage
  const getSubstatusDates = (stageName) => {
    if (!lead) return {};
    
    const substatusDates = {};
    const stageSubstatuses = leadSubstatuses[stageName] || [];
    const substatusNames = stageSubstatuses.map(s => s.name);
    
    // Check if there's a substatusDates field
    if (lead.substatusDates && typeof lead.substatusDates === 'object') {
      substatusNames.forEach(substatus => {
        if (lead.substatusDates[substatus]) {
          substatusDates[substatus] = lead.substatusDates[substatus];
        }
      });
    }
    
    // Also check statusHistory for substatus changes (fallback only - don't overwrite substatusDates)
    if (lead.statusHistory && Array.isArray(lead.statusHistory)) {
      lead.statusHistory.forEach(entry => {
        if (entry.substatus && substatusNames.includes(entry.substatus) && entry.date) {
          // Only use statusHistory when substatusDates doesn't have it - preserve manual/edited dates
          if (!substatusDates[entry.substatus]) {
            substatusDates[entry.substatus] = entry.date;
          }
        }
      });
    }
    
    return substatusDates;
  };

  const systemTimelineEvents = useMemo(() => {
    if (!lead) return [];

    const parseAt = (raw) => {
      if (!raw) return null;
      if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
      if (typeof raw.toDate === 'function') return raw.toDate();
      if (raw.seconds != null) return new Date(raw.seconds * 1000);
      if (typeof raw === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split('-').map(Number);
          return new Date(y, m - 1, d, 12, 0, 0);
        }
        const d = new Date(raw.includes('T') ? raw : `${raw}T12:00:00`);
        return isNaN(d.getTime()) ? null : d;
      }
      return null;
    };

    const events = [];
    const push = (id, kind, label, at, extra = null) => {
      if (!at || isNaN(at.getTime())) return;
      events.push({ id, kind, label, at, ...(extra || {}) });
    };

    const received = getReceivedDate();
    if (received) push('sys-received', 'lead_received', 'Lead received', parseAt(received));

    const disc = lead.discoveryCallDate;
    if (disc && disc !== 'TBD') {
      const iso = getDateField(disc) || disc;
      push('sys-discovery', 'discovery_call', 'Discovery call', parseAt(iso));
    }

    if (lead.onboardingCallDate && lead.onboardingCallDate !== 'TBD') {
      const iso = getDateField(lead.onboardingCallDate) || lead.onboardingCallDate;
      push('sys-onboarding', 'onboarding_call', 'Onboarding call', parseAt(iso));
    }

    if (lead.portalSignupDate) {
      push('sys-portal-signup', 'portal_signup', 'Portal signup', parseAt(lead.portalSignupDate));
    }

    const history = Array.isArray(portalUserActivity?.loginHistory)
      ? portalUserActivity.loginHistory.filter((e) => e?.timestamp)
      : [];
    if (history.length > 0) {
      const sorted = [...history].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      sorted.slice(0, 10).forEach((entry, i) => {
        push(`sys-pl-${i}-${entry.timestamp}`, 'portal_login', 'Portal login', new Date(entry.timestamp));
      });
    } else if (portalUserActivity?.lastLogin) {
      const ll = portalUserActivity.lastLogin;
      push('sys-portal-last', 'portal_login', 'Portal login', ll instanceof Date ? ll : parseAt(ll));
    }

    const a = getSubstatusDates('Assessment');
    const ndaAt = parseAt(a['NDA Signed']) || portalUserActivity?.ndaSignedAt || null;
    if (ndaAt) push('sys-nda', 'nda_signed', 'NDA signed', ndaAt);
    const assessDone = a['Assessment Done'] || a['Solutions Chosen'];
    const assessAt = parseAt(assessDone) || portalUserActivity?.assessmentCompletedAt || null;
    if (assessAt) push('sys-assessment', 'assessment_complete', 'Assessment complete', assessAt);
    if (portalUserActivity?.analysisCallScheduledAt) {
      push(
        'sys-analysis-call',
        'analysis_call_scheduled',
        'Analysis call scheduled',
        portalUserActivity.analysisCallScheduledAt
      );
    }
    if (portalUserActivity?.programChosenAt) {
      const programName =
        portalUserActivity.programData?.name || portalUserActivity.programData?.selectedProgram || '';
      push(
        'sys-program-chosen',
        'program_chosen',
        programName ? `Program chosen: ${programName}` : 'Program chosen',
        portalUserActivity.programChosenAt
      );
    }
    if (a['Portal Invite Sent']) push('sys-invite', 'portal_invite', 'Portal invite sent', parseAt(a['Portal Invite Sent']));
    if (a['Signed Up For Portal'] && !lead.portalSignupDate) {
      push('sys-signed-up', 'portal_signup_alt', 'Signed up for portal', parseAt(a['Signed Up For Portal']));
    }
    const frRaw = portalUserActivity?.freeResourceClaimedDate || a['Claimed Free Resource'];
    if (frRaw) {
      const frAt = portalUserActivity?.freeResourceClaimedDate instanceof Date
        ? portalUserActivity.freeResourceClaimedDate
        : parseAt(frRaw);
      if (frAt) push('sys-fr', 'free_resource', 'Claimed free resource', frAt);
    }

    // Start-side browsing — one row per visit/action, so repeat visits show as
    // their own timestamped entries rather than collapsing into a single row.
    (startActivity || []).forEach((entry) => {
      if (!entry?.label) return;
      const at = toDateOrNull(entry.at);
      if (!at) return;
      push(`sys-start-${entry.id}`, startActivityKind(entry), entry.label, at, {
        detail: entry.detail || null
      });
    });

    // Auto-log emails exchanged with the lead (from the Emails tab data)
    const leadEmailAddr = (lead.contact?.email || lead.email || lead.discovery?.email || '').toLowerCase();
    (leadEmails || []).forEach((em) => {
      if (!em?.id) return;
      const at = em.date ? new Date(em.date) : null;
      if (!at || isNaN(at.getTime())) return;
      const isFromLead = leadEmailAddr && em.from?.toLowerCase().includes(leadEmailAddr);
      const subject = (em.subject || '').trim();
      const prefix = isFromLead ? 'Client email' : 'BBA email';
      const label = subject ? `${prefix}: ${subject}` : prefix;
      push(`sys-email-${em.id}`, 'email_log', label, at);
    });

    // Auto-log calendar events with the lead (from the Events tab data)
    (leadEvents || []).forEach((ev) => {
      const evId = ev?.id || ev?.googleEventId;
      if (!evId) return;
      const at = parseAt(ev.start || ev.startTime);
      if (!at || isNaN(at.getTime())) return;
      const rawTitle = ev.title || ev.summary || 'Untitled event';
      const cleanTitle = rawTitle.replace(/^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*/, '').trim() || 'Untitled event';
      push(`sys-event-${evId}`, 'calendar_event', cleanTitle, at);
    });

    return events;
  }, [lead, portalUserActivity, leadEmails, leadEvents, startActivity]);

  const mergedTimelineRows = useMemo(() => {
    const rows = [];
    systemTimelineEvents.forEach((e) => {
      rows.push({ ...e, type: 'system', id: e.id, kind: e.kind, label: e.label, at: e.at });
    });
    (notes || []).forEach((note) => {
      let at = note.timestamp ? new Date(note.timestamp) : new Date();
      if (isNaN(at.getTime())) at = new Date();
      rows.push({ type: 'note', id: `note-${note.id}`, note, at });
    });
    rows.sort((a, b) => b.at.getTime() - a.at.getTime());
    return rows;
  }, [systemTimelineEvents, notes]);

  const getSystemTimelineIcon = (kind) => {
    switch (kind) {
      case 'lead_received':
        return Calendar;
      case 'discovery_call':
        return Calendar;
      case 'onboarding_call':
        return Calendar;
      case 'portal_signup':
      case 'portal_signup_alt':
        return User;
      case 'portal_login':
        return LogIn;
      case 'nda_signed':
        return Shield;
      case 'assessment_complete':
        return CheckCircle;
      case 'analysis_call_scheduled':
        return Calendar;
      case 'program_chosen':
        return Star;
      case 'portal_invite':
        return Mail;
      case 'email_log':
        return Mail;
      case 'calendar_event':
        return Calendar;
      case 'free_resource':
        return MessageSquare;
      case 'start_page_view':
        return Eye;
      case 'start_program_viewed':
        return Search;
      case 'start_program_customized':
        return SlidersHorizontal;
      case 'start_program_chosen':
        return ShoppingCart;
      default:
        return Activity;
    }
  };

  const getInteractionChannelEmoji = (channel) => {
    switch (channel) {
      case 'phone':
        return '📞';
      case 'text':
        return '📲';
      case 'email':
        return '📧';
      default:
        return '📲';
    }
  };

  // Flatten a Notes timeline row into the icon + one-liner the Recent Activity card shows.
  const describeTimelineRow = (row) => {
    if (row.type === 'system') {
      return { Icon: getSystemTimelineIcon(row.kind), emoji: null, label: row.label };
    }
    const note = row.note;
    if (note.type === 'system' || (note.author?.name === 'System' && !note.interaction)) {
      return {
        Icon: note.content?.includes('flagged') ? Flag : Activity,
        emoji: null,
        label: note.content?.includes('flagged') ? 'Contact flag' : 'System update'
      };
    }
    if (note.interaction) {
      const channelLabel =
        note.interaction.channel === 'phone' ? 'Phone' : note.interaction.channel === 'text' ? 'Text' : 'Email';
      const who = note.interaction.initiatedBy === 'client' ? 'Client' : note.author?.name || 'User';
      return {
        Icon: null,
        emoji: getInteractionChannelEmoji(note.interaction.channel),
        label: `${channelLabel} · ${who}`
      };
    }
    return { Icon: null, emoji: '📝', label: note.content || 'Note' };
  };

  // The five newest entries from the Notes timeline, surfaced on the Summary tab.
  const recentActivityRows = useMemo(() => mergedTimelineRows.slice(0, 5), [mergedTimelineRows]);

  // Jump from a Recent Activity row to that same entry on the Notes tab.
  const openTimelineRowInNotes = (row) => {
    const index = mergedTimelineRows.findIndex((r) => r.id === row.id);
    if (index >= 0) setCurrentNotesPage(Math.floor(index / notesPerPage) + 1);
    setHighlightedTimelineId(row.id);
    setActiveTab('notes');
  };

  // Scroll the jumped-to row into view once the Notes tab has rendered it, then let the ring fade.
  useEffect(() => {
    if (!highlightedTimelineId || activeTab !== 'notes') return;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`timeline-row-${highlightedTimelineId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    const clearTimer = setTimeout(() => setHighlightedTimelineId(null), 4000);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightedTimelineId, activeTab]);

  // Program proposed for this lead (confirmed in the Proposal tab or Assessment Analysis),
  // reduced to the name + what it costs at the chosen billing cycle.
  const projectedProgramSummary = useMemo(() => {
    const proposal = lead?.proposedProgram;
    if (!proposal) return null;
    const name = (proposal.programName || 'Program').replace(/\s*Program\s*$/, '').trim() || 'Program';
    const perMonth = Number(proposal.totalPricePerMonth) || 0;
    const cycle = proposal.billingCycle || 'monthly';
    const amount = cycle === 'quarterly' ? perMonth * 3 * 0.9 : cycle === 'annual' ? perMonth * 12 * 0.8 : perMonth;
    const cycleLabel = cycle === 'quarterly' ? '/quarter' : cycle === 'annual' ? '/year' : '/month';
    return {
      name,
      investment: amount ? `$${Math.round(amount).toLocaleString()}${cycleLabel}` : null
    };
  }, [lead?.proposedProgram]);

  // Get the latest substatus date for a stage (for calculating stage days)
  const getLatestSubstatusDate = (stageName) => {
    const substatusDates = getSubstatusDates(stageName);
    const dates = Object.values(substatusDates).filter(d => d);
    if (dates.length === 0) return null;
    
    // Sort dates and return the latest one
    const sortedDates = dates.sort((a, b) => {
      const dateA = new Date(a.includes('T') ? a : a + 'T00:00:00');
      const dateB = new Date(b.includes('T') ? b : b + 'T00:00:00');
      return dateB - dateA;
    });
    
    return sortedDates[0];
  };

  // Get styling for substatus items (similar to getRowStyling)
  const getSubstatusStyling = (substatusName, stageName) => {
    if (!lead) return '';
    
    const currentSubstatus = lead.substatus || '';
    const currentStage = lead.stage || '';
    const stageSubstatuses = leadSubstatuses[stageName] || [];
    const substatusNames = stageSubstatuses.map(s => s.name);
    
    // Only apply styling if we're in the correct stage
    if (currentStage !== stageName) {
      return '';
    }
    
    // Find current substatus index
    const currentSubstatusIndex = substatusNames.indexOf(currentSubstatus);
    const substatusIndex = substatusNames.indexOf(substatusName);
    
    // If this is the current substatus, make it red
    if (currentSubstatusIndex >= 0 && substatusIndex === currentSubstatusIndex) {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    }
    
    // Check if this substatus has a date (completed)
    const substatusDates = getSubstatusDates(stageName);
    const hasDate = !!substatusDates[substatusName];
    
    // If this substatus is completed (has date and is before current substatus), make it green
    // Also show as green if it has a date and there's no current substatus in the list
    if (hasDate) {
      if (currentSubstatusIndex >= 0 && substatusIndex < currentSubstatusIndex) {
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      } else if (currentSubstatusIndex < 0 && substatusIndex >= 0) {
        // No current substatus in list, but this one has a date - show as completed
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      }
    }
    
    // Default styling
    return 'bg-white dark:bg-gray-700/50 border-gray-200 dark:border-gray-600';
  };

  // Toggle stage expansion
  const toggleStageExpansion = (stageName) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageName]: !prev[stageName]
    }));
  };

  // Calculate days since last contact
  const getDaysSinceLastContact = () => {
    // Helper to convert timestamp to Date
    const toDate = (timestamp) => {
      if (!timestamp) return null;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      if (typeof timestamp === 'string') {
        return new Date(timestamp);
      }
      if (timestamp instanceof Date) {
        return timestamp;
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      return null;
    };

    let lastContactDate = null;

    // First, check for lastEmailContactDate (from emails)
    if (lead?.lastEmailContactDate) {
      lastContactDate = toDate(lead.lastEmailContactDate);
    }

    // Then check contact notes (if no email contact date or if note is more recent)
    if (lead?.notes && Array.isArray(lead.notes) && lead.notes.length > 0) {
      const contactNotes = lead.notes.filter(note => note && (note.contact === true || note.contact === 'true'));
      
      if (contactNotes.length > 0) {
        // Get the most recent contact note
        const lastContactNote = contactNotes.reduce((latest, note) => {
          const noteDate = toDate(note.timestamp);
          const latestDate = toDate(latest?.timestamp);
          
          if (!noteDate) return latest || note;
          if (!latestDate) return note;
          
          return noteDate > latestDate ? note : latest;
        });

        if (lastContactNote?.timestamp) {
          const noteDate = toDate(lastContactNote.timestamp);
          // Use the most recent date between email contact and note contact
          if (!lastContactDate || (noteDate && noteDate > lastContactDate)) {
            lastContactDate = noteDate;
          }
        }
      }
    }

    // If no contact date found, return null
    if (!lastContactDate || isNaN(lastContactDate.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastContactDate.setHours(0, 0, 0, 0);
    
    const diffTime = today - lastContactDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Calculate contact progress
  const getContactProgress = () => {
    // If no lead data, return empty state
    if (!lead) {
      return {
        daysSinceContact: 0,
        daysUntilFlagged: 0,
        progress: 0,
        needsContact: false,
        noContactNotes: true,
        daysSinceCreation: null,
        disabled: false
      };
    }

    // If Needs Contacted is disabled for this lead, return disabled state
    if (lead.needsContactDisabled === true) {
      return {
        daysSinceContact: 0,
        daysUntilFlagged: 0,
        progress: 0,
        needsContact: false,
        noContactNotes: false,
        daysSinceCreation: null,
        disabled: true
      };
    }

    const daysSinceContact = getDaysSinceLastContact();
    const daysUntilFlagged = CONTACT_FLAG_THRESHOLD_DAYS;
    
    // If no contact notes yet, calculate days since lead creation
    if (daysSinceContact === null) {
      // Calculate days since lead creation
      let daysSinceCreation = null;
      if (lead?.createdAt) {
        const toDate = (timestamp) => {
          if (!timestamp) return null;
          if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
          }
          if (typeof timestamp === 'string') {
            return new Date(timestamp);
          }
          if (timestamp instanceof Date) {
            return timestamp;
          }
          if (typeof timestamp === 'number') {
            return new Date(timestamp);
          }
          return null;
        };
        
        const createdDate = toDate(lead.createdAt);
        if (createdDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          createdDate.setHours(0, 0, 0, 0);
          const diffTime = today - createdDate;
          daysSinceCreation = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        }
      }
      
      const progress = daysSinceCreation !== null && daysUntilFlagged > 0 
        ? Math.min((daysSinceCreation / daysUntilFlagged) * 100, 100) 
        : 0;
      const needsContact = daysSinceCreation !== null && daysSinceCreation >= daysUntilFlagged;
      
      return {
        daysSinceContact: daysSinceCreation || 0,
        daysUntilFlagged,
        progress,
        needsContact,
        noContactNotes: true,
        daysSinceCreation,
        disabled: false
      };
    }

    const progress = Math.min((daysSinceContact / daysUntilFlagged) * 100, 100);
    const needsContact = daysSinceContact >= daysUntilFlagged;
    
    return {
      daysSinceContact,
      daysUntilFlagged,
      progress,
      needsContact,
      noContactNotes: false,
      disabled: false
    };
  };

  // Check if flag note already exists (any time, not just today)
  const hasFlagNote = () => {
    if (!notes || !Array.isArray(notes)) return false;
    
    return notes.some(note => {
      if (!note || !note.content) return false;
      return note.content.includes('has been flagged for needing contact');
    });
  };

  // Create system note when lead becomes flagged
  useEffect(() => {
    if (!lead || !leadId) return;
    
    // Don't flag lost leads
    if (lead.stage === 'Lost' || lead.result === 'Lost') {
      setWasFlagged(false);
      return;
    }
    
    const contactProgress = getContactProgress();
    if (!contactProgress) {
      setWasFlagged(false);
      return;
    }

    const needsContact = contactProgress.needsContact;
    const alreadyHasFlagNote = hasFlagNote();
    
    // Only create a flag note when lead first becomes flagged (not if already flagged)
    if (needsContact && !wasFlagged && !alreadyHasFlagNote) {
      // Create system note
      const currentDate = new Date();
      const formattedDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()} ${currentDate.getHours() > 12 ? currentDate.getHours() - 12 : currentDate.getHours()}:${currentDate.getMinutes().toString().padStart(2, '0')}${currentDate.getHours() >= 12 ? 'PM' : 'AM'}`;

      const flagNote = {
        id: Date.now(),
        content: `Lead has been flagged for needing contact. ${contactProgress.daysSinceContact} days since last contact (threshold: ${contactProgress.daysUntilFlagged} days).`,
        contact: false,
        author: {
          name: 'System',
          photoURL: null,
          uid: null
        },
        timestamp: new Date().toISOString(),
        date: formattedDate,
        type: 'system'
      };

      const updatedNotes = [flagNote, ...notes];
      
      // Update lead with new note
      const leadDocRef = doc(db, 'accounts', leadId);
      updateDoc(leadDocRef, {
        notes: updatedNotes,
        lastActivityAt: new Date().toISOString()
      }).then(async () => {
        setNotes(updatedNotes);
        setWasFlagged(true);
        
        // Trigger flagged lead notification
        try {
          const flagInfo = {
            daysSinceContact: contactProgress.daysSinceContact,
            daysSinceCreation: contactProgress.daysSinceCreation,
            daysUntilFlagged: contactProgress.daysUntilFlagged,
            reason: `Lead has been flagged for needing contact. ${contactProgress.daysSinceContact !== null ? contactProgress.daysSinceContact : contactProgress.daysSinceCreation} days since ${contactProgress.daysSinceContact !== null ? 'last contact' : 'creation'} (threshold: ${contactProgress.daysUntilFlagged} days).`
          };
          await triggerFlaggedLeadNotification({ ...lead, id: leadId }, flagInfo);
        } catch (error) {
          console.error('Error triggering flagged lead notification:', error);
        }
      }).catch((error) => {
        console.error('Error creating flag note:', error);
      });
    } else {
      // Update wasFlagged state based on current status
      setWasFlagged(needsContact);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, notes, leadId]);

  const buildInteractionNoteContent = (initiatedBy, channel) => {
    const channelLabels = { phone: 'Phone call', text: 'Text message', email: 'Email' };
    const who = initiatedBy === 'client' ? 'Client' : 'BBA';
    return `${who} — ${channelLabels[channel]} logged`;
  };

  const formatInteractionNoteLegacyDate = (dateObj) => {
    const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours() > 12 ? d.getHours() - 12 : d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}${d.getHours() >= 12 ? 'PM' : 'AM'}`;
  };

  const addInteractionNote = async (initiatedBy, channel) => {
    const content = buildInteractionNoteContent(initiatedBy, channel);

    const currentDate = new Date();
    const formattedDate = formatInteractionNoteLegacyDate(currentDate);

    const newNoteItem = {
      id: Date.now(),
      content,
      contact: true,
      interaction: { initiatedBy, channel },
      author: {
        name: currentUser?.displayName || currentUser?.email || 'Current User',
        photoURL: currentUser?.photoURL || null,
        uid: currentUser?.uid || null
      },
      timestamp: new Date().toISOString(),
      date: formattedDate
    };

    const updatedNotes = [newNoteItem, ...notes];
    setNotes(updatedNotes);
    setCurrentNotesPage(1);
    try {
      await updateAccount(leadId, { ...lead, notes: updatedNotes });
      setLead((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
    } catch (error) {
      console.error('Error saving interaction log:', error);
      setError('Failed to save contact log');
    }
  };

  const submitQuickNote = async () => {
    const text = quickNoteDraft.trim();
    if (!text) return;

    const currentDate = new Date();
    const formattedDate = formatInteractionNoteLegacyDate(currentDate);
    const newNoteItem = {
      id: Date.now(),
      content: text,
      contact: false,
      author: {
        name: currentUser?.displayName || currentUser?.email || 'Current User',
        photoURL: currentUser?.photoURL || null,
        uid: currentUser?.uid || null
      },
      timestamp: currentDate.toISOString(),
      date: formattedDate
    };

    const updatedNotes = [newNoteItem, ...notes];
    const previousNotes = notes;
    setNotes(updatedNotes);
    setCurrentNotesPage(1);
    try {
      await updateAccount(leadId, { ...lead, notes: updatedNotes });
      setLead((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
      setQuickNoteDraft('');
      setQuickNoteComposerOpen(false);
    } catch (error) {
      console.error('Error saving note:', error);
      setError('Failed to save note');
      setNotes(previousNotes);
    }
  };

  const closeQuickNoteComposer = () => {
    setQuickNoteComposerOpen(false);
    setQuickNoteDraft('');
  };

  useEffect(() => {
    if (quickNoteComposerOpen) {
      const id = window.setTimeout(() => quickNoteInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [quickNoteComposerOpen]);

  const openInteractionNoteModal = (note) => {
    if (!note?.interaction) return;
    if (note.author?.uid !== currentUser?.uid) return;
    const base = note.timestamp ? new Date(note.timestamp) : new Date();
    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, '0');
    const day = String(base.getDate()).padStart(2, '0');
    setInteractionModalDate(`${y}-${m}-${day}`);
    setInteractionModalInitiatedBy(note.interaction.initiatedBy || 'client');
    setInteractionModalChannel(note.interaction.channel || 'phone');
    setInteractionNoteModal({ note });
  };

  const closeInteractionNoteModal = () => {
    setInteractionNoteModal(null);
  };

  const saveInteractionNoteModal = async () => {
    if (!interactionNoteModal?.note?.id) return;
    const noteId = interactionNoteModal.note.id;
    const parts = interactionModalDate.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return;
    const [y, mo, da] = parts;
    const at = new Date(y, mo - 1, da, 12, 0, 0, 0);
    const ts = at.toISOString();
    const formattedDate = formatInteractionNoteLegacyDate(at);
    const content = buildInteractionNoteContent(interactionModalInitiatedBy, interactionModalChannel);

    try {
      const updatedNotes = notes.map((note) =>
        note.id === noteId
          ? {
              ...note,
              timestamp: ts,
              date: formattedDate,
              content,
              contact: true,
              interaction: { initiatedBy: interactionModalInitiatedBy, channel: interactionModalChannel }
            }
          : note
      );
      setNotes(updatedNotes);
      closeInteractionNoteModal();
      const updatedData = { ...lead, notes: updatedNotes };
      await updateAccount(leadId, updatedData);
      setLead((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
    } catch (error) {
      console.error('Error saving interaction log:', error);
      setError('Failed to save log');
    }
  };

  const deleteInteractionNoteFromModal = async () => {
    if (!interactionNoteModal?.note?.id) return;
    const noteId = interactionNoteModal.note.id;
    try {
      const updatedNotes = notes.filter((n) => n.id !== noteId);
      setNotes(updatedNotes);
      closeInteractionNoteModal();
      const mergedLen = systemTimelineEvents.length + updatedNotes.length;
      const totalPages = Math.ceil(mergedLen / notesPerPage);
      if (currentNotesPage > totalPages && totalPages > 0) {
        setCurrentNotesPage(totalPages);
      }
      await updateAccount(leadId, { ...lead, notes: updatedNotes });
      setLead((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
    } catch (error) {
      console.error('Error deleting interaction log:', error);
      setError('Failed to delete log');
    }
  };

  const openTextNoteModal = (note) => {
    if (!note || note.type === 'system' || note.interaction) return;
    if (note.author?.uid !== currentUser?.uid) return;
    setTextNoteModalBody(note.content || '');
    const base = note.timestamp ? new Date(note.timestamp) : new Date();
    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, '0');
    const day = String(base.getDate()).padStart(2, '0');
    setTextNoteModalDate(`${y}-${m}-${day}`);
    setTextNoteModal({ note });
  };

  const closeTextNoteModal = () => {
    setTextNoteModal(null);
    setTextNoteModalBody('');
    setTextNoteModalDate('');
  };

  const saveTextNoteModal = async () => {
    if (!textNoteModal?.note?.id) return;
    const noteId = textNoteModal.note.id;
    const contentTrim = textNoteModalBody.trim();
    if (!contentTrim) return;

    const parts = textNoteModalDate.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return;
    const [y, mo, da] = parts;
    const at = new Date(y, mo - 1, da, 12, 0, 0, 0);
    const ts = at.toISOString();
    const formattedDate = formatInteractionNoteLegacyDate(at);

    try {
      const updatedNotes = notes.map((n) =>
        n.id === noteId
          ? {
              ...n,
              content: contentTrim,
              timestamp: ts,
              date: formattedDate
            }
          : n
      );
      setNotes(updatedNotes);
      closeTextNoteModal();
      const updatedData = { ...lead, notes: updatedNotes };
      await updateAccount(leadId, updatedData);
      setLead((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
    } catch (error) {
      console.error('Error saving note:', error);
      setError('Failed to save note');
    }
  };

  const deleteTextNoteFromModal = async () => {
    if (!textNoteModal?.note?.id) return;
    const noteId = textNoteModal.note.id;
    try {
      const updatedNotes = notes.filter((n) => n.id !== noteId);
      setNotes(updatedNotes);
      closeTextNoteModal();
      const mergedLen = systemTimelineEvents.length + updatedNotes.length;
      const totalPages = Math.ceil(mergedLen / notesPerPage);
      if (currentNotesPage > totalPages && totalPages > 0) {
        setCurrentNotesPage(totalPages);
      }
      await updateAccount(leadId, { ...lead, notes: updatedNotes });
      setLead((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
    } catch (error) {
      console.error('Error deleting note:', error);
      setError('Failed to delete note');
    }
  };

  const handleImageUpload = async (fileOrEvent) => {
    // Handle both file input events and direct file objects
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      setUploadingImage(true);
      
      // Create a unique filename for the lead
      const timestamp = Date.now();
      const fileName = `lead-${leadId}-${timestamp}`;
      const storageRef = ref(storage, `lead-photos/${fileName}`);
      
      // Delete old image if exists
      if (lead.contact?.photoURL) {
        try {
          // Extract the file path from the URL to delete the old image
          const oldImageRef = ref(storage, lead.contact.photoURL);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.log('No previous image to delete or error deleting:', error);
        }
      }

      // Upload new image
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update the lead with the new photo URL
      const updatedLead = {
        ...lead,
        contact: {
          ...lead.contact,
          photoURL: downloadURL
        }
      };
      
      // Save to database
      await updateAccount(leadId, updatedLead);
      setLead(updatedLead);
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEditMode && !uploadingImage) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    
    if (!isEditMode || uploadingImage) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  /** CRM-only company logo (`crmCompanyLogo`) — never touches the portal-synced `companyLogo`. */
  const handleCompanyLogoUpload = async (fileOrEvent) => {
    let file;
    if (fileOrEvent?.target?.files) {
      file = fileOrEvent.target.files[0];
      fileOrEvent.target.value = '';
    } else if (fileOrEvent?.dataTransfer?.files) {
      file = fileOrEvent.dataTransfer.files[0];
    } else if (fileOrEvent instanceof File) {
      file = fileOrEvent;
    }
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      setUploadingCompanyLogo(true);

      const timestamp = Date.now();
      const storageRef = ref(storage, `lead-company-logos/${leadId}-${timestamp}`);

      if (lead.crmCompanyLogo) {
        try {
          const oldRef = ref(storage, lead.crmCompanyLogo);
          await deleteObject(oldRef);
        } catch (err) {
          console.log('No previous CRM company logo to delete or error deleting:', err);
        }
      }

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const updatedLead = { ...lead, crmCompanyLogo: downloadURL };
      await updateAccount(leadId, updatedLead);
      setLead(updatedLead);
    } catch (error) {
      console.error('Error uploading company logo:', error);
      alert('Failed to upload company logo. Please try again.');
    } finally {
      setUploadingCompanyLogo(false);
    }
  };

  const handleCompanyLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEditMode && !uploadingCompanyLogo) {
      setCompanyLogoDragOver(true);
    }
  };

  const handleCompanyLogoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCompanyLogoDragOver(false);
  };

  const handleCompanyLogoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCompanyLogoDragOver(false);
    if (!isEditMode || uploadingCompanyLogo) return;
    handleCompanyLogoUpload(e);
  };

  const handleAddDocument = () => {
    if (!newDocument.title.trim()) {
      return; // Don't add empty documents
    }

    const newDocumentItem = {
      id: Date.now(),
      title: newDocument.title,
      fileName: newDocument.file ? newDocument.file.name : null,
      status: 'Pending',
      icon: 'ndatemp.svg' // Default icon
    };

    setDocuments([...documents, newDocumentItem]);
    setNewDocument({ title: '', file: null });
    setShowAddDocumentModal(false);
  };

  const handleDeleteDocument = (documentId) => {
    setDocuments(documents.filter(doc => doc.id !== documentId));
  };

  const handleDocumentUpload = async (documentType, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.includes('pdf') && !file.type.includes('image/')) {
      alert('Please select a PDF or image file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    try {
      setUploadingDocument(true);
      
      // Create a unique filename for the document
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `lead-${leadId}-${documentType.toLowerCase()}-${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, `lead-documents/${fileName}`);
      
      // Upload document
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Create document object
      const newDocument = {
        id: `doc-${timestamp}`,
        fileName: file.name,
        fileUrl: downloadURL,
        uploadedAt: new Date().toISOString(),
        fileSize: file.size,
        fileType: file.type
      };
      
      // Update the lead with the document in array
      const updatedData = { ...lead };
      const documentsField = `${documentType.toLowerCase()}Documents`;
      const statusField = `${documentType.toLowerCase()}Status`;
      const lastUpdatedField = `${documentType.toLowerCase()}LastUpdated`;
      
      // Initialize documents array if it doesn't exist
      if (!updatedData[documentsField]) {
        updatedData[documentsField] = [];
      }
      
      // Add new document to array
      updatedData[documentsField] = [...updatedData[documentsField], newDocument];
      updatedData[statusField] = 'Completed';
      updatedData[lastUpdatedField] = new Date().toISOString();
      
      // Keep backward compatibility with single URL fields
      if (!updatedData[`${documentType.toLowerCase()}Url`]) {
        updatedData[`${documentType.toLowerCase()}Url`] = downloadURL;
      }
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      
      alert(`${documentType} document uploaded successfully!`);
      
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteLeadDocument = async (documentType, documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const updatedData = { ...lead };
      const documentsField = `${documentType.toLowerCase()}Documents`;
      
      if (!updatedData[documentsField]) {
        return;
      }

      // Find the document to delete
      const documentToDelete = updatedData[documentsField].find(doc => doc.id === documentId);
      if (!documentToDelete) {
        return;
      }

      // Delete from storage
      try {
        const storageRef = ref(storage, documentToDelete.fileUrl);
        await deleteObject(storageRef);
      } catch (error) {
        console.error('Error deleting file from storage:', error);
        // Continue with deletion from database even if storage deletion fails
      }

      // Remove from array
      updatedData[documentsField] = updatedData[documentsField].filter(doc => doc.id !== documentId);
      
      // Update status if no documents remain
      if (updatedData[documentsField].length === 0) {
        updatedData[`${documentType.toLowerCase()}Status`] = 'Pending';
        updatedData[`${documentType.toLowerCase()}LastUpdated`] = null;
      } else {
        updatedData[`${documentType.toLowerCase()}LastUpdated`] = new Date().toISOString();
      }
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      
      alert('Document deleted successfully!');
      
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const formatRoutingNumber = (value) => {
    return value.replace(/\D/g, '').slice(0, 9);
  };

  const formatAccountNumber = (value) => {
    return value.replace(/\D/g, '').slice(0, 17);
  };

  const validateACHForm = () => {
    const errors = {};
    
    if (!achFormData.accountType) {
      errors.accountType = 'Account type is required';
    }
    if (!achFormData.bankName.trim()) {
      errors.bankName = 'Bank name is required';
    }
    if (!achFormData.routingNumber.trim()) {
      errors.routingNumber = 'Routing number is required';
    } else if (achFormData.routingNumber.length !== 9) {
      errors.routingNumber = 'Routing number must be 9 digits';
    }
    if (!achFormData.accountNumber.trim()) {
      errors.accountNumber = 'Account number is required';
    }
    if (!achFormData.nameOnAccount.trim()) {
      errors.nameOnAccount = 'Name on account is required';
    }
    
    setAchFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const loadConferences = async () => {
    try {
      setLoadingConferences(true);
      // Try conferences-reports first, then fall back to conferences
      let conferencesRef = collection(db, 'conferences-reports');
      let snapshot = await getDocs(conferencesRef);
      
      // If conferences-reports is empty, try conferences collection
      if (snapshot.empty) {
        conferencesRef = collection(db, 'conferences');
        snapshot = await getDocs(conferencesRef);
      }
      
      const conferencesData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.data().title || doc.id,
        ...doc.data()
      }));
      
      // Sort by name
      conferencesData.sort((a, b) => a.name.localeCompare(b.name));
      setConferences(conferencesData);
    } catch (error) {
      console.error('Error loading conferences:', error);
      // Try fallback to conferences collection
      try {
        const conferencesRef = collection(db, 'conferences');
        const snapshot = await getDocs(conferencesRef);
        const conferencesData = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().title || doc.id,
          ...doc.data()
        }));
        conferencesData.sort((a, b) => a.name.localeCompare(b.name));
        setConferences(conferencesData);
      } catch (fallbackError) {
        console.error('Error loading conferences from fallback:', fallbackError);
      }
    } finally {
      setLoadingConferences(false);
    }
  };

  const handleACHFormSubmit = async () => {
    if (!validateACHForm()) {
      return;
    }

    try {
      const updatedData = { ...lead };
      updatedData.achInfo = achFormData;
      updatedData.achStatus = 'Completed';
      updatedData.achLastUpdated = new Date().toISOString();
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      
      // Also sync to linked portal user's billing collection if lead has email
      const leadEmail = lead.email || lead.contact?.email;
      if (leadEmail) {
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', leadEmail)
        );
        const usersSnapshot = await getDocs(usersQuery);
        
        if (!usersSnapshot.empty) {
          const userDoc = usersSnapshot.docs[0];
          const userData = userDoc.data();
          
          // Save to user's billing collection
          await setDoc(doc(db, 'users', userData.uid, 'billing', 'ach'), {
            ...achFormData,
            lastUpdated: new Date().toISOString()
          });
        }
      }
      
      setShowACHUploadModal(false);
      setAchFormData({
        accountType: '',
        bankName: '',
        routingNumber: '',
        accountNumber: '',
        nameOnAccount: ''
      });
      setAchFormErrors({});
      
      alert('ACH information saved successfully!');
    } catch (error) {
      console.error('Error saving ACH information:', error);
      alert('Failed to save ACH information. Please try again.');
    }
  };

  const handleAddSummary = async () => {
    if (currentCallType === 'intake') {
      // For intake calls, check if at least one field has content
      const hasContent = Object.values(intakeFormData).some(value => value.trim());
      if (!hasContent) {
        return; // Don't add empty intake form
      }

      try {
        const updatedData = { ...lead };
        updatedData.intakeCallSummary = intakeFormData;
        // Only update the date if this is a new summary (not an edit)
        if (!lead.intakeCallSummary) {
          updatedData.intakeCallDate = new Date().toISOString();
        }
        
        await updateAccount(leadId, updatedData);
        setLead(updatedData);
        setIntakeFormData({
          whyReachOut: '',
          biggestPainPoint: '',
          whatDoWell: '',
          wantToImprove: '',
          notes: ''
        });
        setShowAddSummaryModal(false);
      } catch (error) {
        console.error('Error updating intake call summary:', error);
      }
    } else if (currentCallType === 'discovery') {
      // For discovery calls, use the existing logic
      if (!newSummary.trim()) {
        return; // Don't add empty summary
      }

      try {
        const updatedData = { ...lead };
        updatedData.discoveryCallSummary = newSummary;
        // Only update the date if this is a new summary (not an edit)
        if (!lead.discoveryCallSummary) {
          updatedData.discoveryCallDate = new Date().toISOString();
        }
        
        await updateAccount(leadId, updatedData);
        setLead(updatedData);
        setNewSummary('');
        setShowAddSummaryModal(false);
      } catch (error) {
        console.error('Error updating discovery call summary:', error);
      }
    } else if (currentCallType === 'onboarding') {
      // For onboarding calls, use the same logic as discovery calls
      if (!newSummary.trim()) {
        return; // Don't add empty summary
      }

      try {
        const updatedData = { ...lead };
        updatedData.onboardingCallSummary = newSummary;
        // Only update the date if this is a new summary (not an edit)
        if (!lead.onboardingCallSummary) {
          updatedData.onboardingCallDate = new Date().toISOString();
        }
        
        await updateAccount(leadId, updatedData);
        setLead(updatedData);
        setNewSummary('');
        setShowAddSummaryModal(false);
      } catch (error) {
        console.error('Error updating onboarding call summary:', error);
      }
    } else if (currentCallType === 'proposal') {
      // For proposal calls, use the same logic as discovery calls
      if (!newSummary.trim()) {
        return; // Don't add empty summary
      }

      try {
        const updatedData = { ...lead };
        updatedData.proposalCallSummary = newSummary;
        // Only update the date if this is a new summary (not an edit)
        if (!lead.proposalCallSummary) {
          updatedData.proposalCallDate = new Date().toISOString();
        }
        
        await updateAccount(leadId, updatedData);
        setLead(updatedData);
        setNewSummary('');
        setShowAddSummaryModal(false);
      } catch (error) {
        console.error('Error updating proposal call summary:', error);
      }
    }
  };

  // Helper function to get icon filename from substatus name
  const getSubstatusIcon = (substatusName) => {
    // Search through all stages to find the substatus
    for (const stage in leadSubstatuses) {
      const substatus = leadSubstatuses[stage].find(s => s.name === substatusName);
      if (substatus) {
        return substatus.icon;
      }
    }
    return 'newlead.svg'; // Default fallback
  };

  // Helper function to get available substatuses for current stage
  const getCurrentSubstatuses = () => {
    return leadSubstatuses[lead?.stage] || leadSubstatuses['Lead Received'] || [];
  };

  const renderEditableField = (label, value, field, icon) => {
    const isEditing = editingField === field;
    return (
      <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">{label}</label>
          {isEditing ? (
            <div className="flex items-center space-x-2 mt-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                onKeyPress={(e) => e.key === 'Enter' && handleSave(field)}
              />
              <button
                onClick={() => handleSave(field)}
                className="p-1 text-green-600 hover:text-green-800"
              >
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={() => setEditingField(null)}
                className="p-1 text-gray-600 hover:text-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-900 dark:text-white">{value || 'Not set'}</span>
              {isEditMode && (
                <button
                  onClick={() => handleEdit(field, value)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const initializeAssessmentEditData = (type) => {
    if (type === 'general') {
      setAssessmentEditData({
        general: { ...lead?.businessAssessment }
      });
    } else if (type === 'sales') {
      const salesData = lead?.salesData || {};
      const businessAssessment = lead?.businessAssessment || {};
      setAssessmentEditData({
        sales: {
          ...salesData,
          closeRatio: salesData.closeRatio ?? businessAssessment.closeRatio ?? 0,
          referralBusinessPercentage: salesData.referralBusinessPercentage ?? businessAssessment.referralBusinessPercentage ?? 0
        }
      });
    } else if (type === 'operations') {
      const operationsData = lead?.operationsData || {};
      const businessAssessment = lead?.businessAssessment || {};
      setAssessmentEditData({
        operations: {
          ...operationsData,
          cultureGrade: operationsData.cultureGrade ?? businessAssessment.cultureGrade ?? ''
        }
      });
    } else if (type === 'finance') {
      const financeData = lead?.financeData || {};
      const businessAssessment = lead?.businessAssessment || {};
      setAssessmentEditData({
        finance: {
          financeRating: financeData.financeRating ?? financeData.rating ?? businessAssessment.financeRating ?? null,
          unencumberedCash: financeData.unencumberedCash ?? businessAssessment.unencumberedCash ?? '',
          grossProfitMargin: financeData.grossProfitMargin ?? businessAssessment.grossProfitMargin ?? '',
          netProfitMargin: financeData.netProfitMargin ?? businessAssessment.netProfitMargin ?? '',
          balanceSheetUrl: financeData.balanceSheetUrl ?? businessAssessment.balanceSheetUrl ?? '',
          balanceSheetFileName: financeData.balanceSheetFileName ?? businessAssessment.balanceSheetFileName ?? '',
          incomeStatementUrl: financeData.incomeStatementUrl ?? businessAssessment.incomeStatementUrl ?? '',
          incomeStatementFileName: financeData.incomeStatementFileName ?? businessAssessment.incomeStatementFileName ?? '',
          cashFlowStatementUrl: financeData.cashFlowStatementUrl ?? businessAssessment.cashFlowStatementUrl ?? '',
          cashFlowStatementFileName: financeData.cashFlowStatementFileName ?? businessAssessment.cashFlowStatementFileName ?? '',
          knowsOverhead: financeData.knowsOverhead ?? businessAssessment.knowsOverhead ?? '',
          knowsLaborBurden: financeData.knowsLaborBurden ?? businessAssessment.knowsLaborBurden ?? '',
          knowsProjectRunway: financeData.knowsProjectRunway ?? businessAssessment.knowsProjectRunway ?? ''
        }
      });
    } else if (type === 'technology') {
      const techData = lead?.techData || {};
      const businessAssessment = lead?.businessAssessment || {};
      setAssessmentEditData({
        technology: {
          techRating: techData.techRating ?? businessAssessment.techRating ?? null,
          whatCRM: techData.whatCRM ?? businessAssessment.whatCRM ?? '',
          crmOptimization: techData.crmOptimization ?? businessAssessment.crmOptimization ?? null,
          hasAutomations: techData.hasAutomations ?? businessAssessment.hasAutomations ?? null,
          hasAITools: techData.hasAITools ?? businessAssessment.hasAITools ?? null,
          hasWebsitePlugins: techData.hasWebsitePlugins ?? techData.hasCustomWebsitePlugins ?? businessAssessment.hasWebsitePlugins ?? businessAssessment.hasCustomWebsitePlugins ?? null
        }
      });
    } else if (type === 'marketing') {
      const marketingData = lead?.marketingData || {};
      const businessAssessment = lead?.businessAssessment || {};
      setAssessmentEditData({
        marketing: {
          marketingRating: marketingData.marketingRating ?? businessAssessment.marketingRating ?? null,
          monthlyMarketingBudget: marketingData.monthlyMarketingBudget ?? businessAssessment.monthlyMarketingBudget ?? '',
          workingWithMarketingAgency: marketingData.workingWithMarketingAgency ?? marketingData.workingWithAgency ?? businessAssessment.workingWithMarketingAgency ?? null,
          websiteManager: marketingData.websiteManager ?? businessAssessment.websiteManager ?? '',
          marketingAgencyType: marketingData.marketingAgencyType ?? businessAssessment.marketingAgencyType ?? '',
          runningDigitalAds: marketingData.runningDigitalAds ?? businessAssessment.runningDigitalAds ?? null,
          digitalAdPlatforms: marketingData.digitalAdPlatforms ?? businessAssessment.digitalAdPlatforms ?? [],
          engagesSocialMedia: marketingData.engagesSocialMedia ?? businessAssessment.engagesSocialMedia ?? '',
          engagesPaidAds: marketingData.engagesPaidAds ?? businessAssessment.engagesPaidAds ?? '',
          engagesNewsletter: marketingData.engagesNewsletter ?? businessAssessment.engagesNewsletter ?? ''
        }
      });
    }
  };

  const handleOpenAssessment = async (type) => {
    console.log('=== OPENING ASSESSMENT (LEAD DATA ONLY) ===');
    console.log('Assessment type:', type);
    console.log('Current lead:', lead);
    console.log('Current assessmentData:', assessmentData);
    
    // Note: Removed auto-update to "Completing Assessment" substatus as it no longer exists
    // Substatus should be manually updated or set by other actions (NDA signing, assessment completion, etc.)
    
    setCurrentAssessmentType(type);
    setShowAssessmentModal(true);
    setIsEditingAssessmentModal(false);
    
    // Initialize edit data when opening modal in edit mode
    if (isEditMode) {
      initializeAssessmentEditData(type);
    }
    
    // Assessment data should already be loaded from lead data in the useEffect
    // No need to fetch anything - just use the existing assessmentData
    console.log('Using existing lead assessment data for modal');
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const updatedData = { ...lead };
      const previousStatus = updatedData.stage;
      
      // Only set date if moving TO a different stage (not staying in the same stage)
      const isMovingToNewStage = previousStatus !== newStatus;
      
      updatedData.stage = newStatus;
      updatedData.substatus = ''; // Reset substatus when changing main status
      
      // Set the date for the new stage when entering it (only if moving to a new stage)
      if (isMovingToNewStage) {
        const newStatusLower = newStatus.toLowerCase();
        const now = new Date();
        // Use ISO string for consistency with other date fields
        const currentDate = now.toISOString();
        
        // Always set the date when entering a stage (captures when they entered that stage)
        if (newStatusLower.includes('lead received') || newStatusLower.includes('received')) {
          // Only set if not already set (Lead Received might already have a date from creation)
          if (!updatedData.receivedDate) {
            updatedData.receivedDate = Timestamp.fromDate(now);
            console.log('Set receivedDate to:', updatedData.receivedDate);
          }
        } else if (newStatusLower.includes('discovery')) {
          // Always set/update when entering Discovery Call stage
          updatedData.discoveryCallDate = currentDate;
          console.log('Set discoveryCallDate to:', currentDate);
        } else if (newStatusLower.includes('assessment')) {
          // Always set/update when entering Assessment stage
          updatedData.discoveryCompletedAt = currentDate;
          console.log('Set discoveryCompletedAt to:', currentDate);
        } else if (newStatusLower.includes('onboarding')) {
          // Always set/update when entering Onboarding Call stage
          updatedData.onboardingCallDate = currentDate;
          console.log('Set onboardingCallDate to:', currentDate);
        } else if (newStatusLower.includes('proposal')) {
          // Always set/update when entering Proposal stage
          updatedData.proposalCallDate = currentDate;
          console.log('Set proposalCallDate to:', currentDate);
          
          // Set substatus to "Not Started" when entering Proposal stage
          updatedData.substatus = 'Not Started';
          
          // Track substatus dates
          if (!updatedData.substatusDates) {
            updatedData.substatusDates = {};
          }
          const notStartedDate = new Date().toISOString().split('T')[0];
          updatedData.substatusDates['Not Started'] = notStartedDate;
          
          // Add to statusHistory for tracking
          if (!updatedData.statusHistory) {
            updatedData.statusHistory = [];
          }
          const substatusEntry = {
            substatus: 'Not Started',
            date: new Date().toISOString(),
            updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User',
            action: 'Moved to Proposal stage - substatus set to Not Started'
          };
          updatedData.statusHistory = [...updatedData.statusHistory, substatusEntry];
          
          // When lead moves to Proposal, mark all assessment steps as completed in their dashboard
          try {
            const leadEmail = lead?.contact?.email || lead?.email;
            if (leadEmail) {
              const normalizedEmail = leadEmail.toLowerCase().trim();
              
              // Find user by email
              const usersQuery = query(
                collection(db, 'users'),
                where('email', '==', normalizedEmail)
              );
              const usersSnapshot = await getDocs(usersQuery);
              
              if (!usersSnapshot.empty) {
                const userDoc = usersSnapshot.docs[0];
                const userId = userDoc.id;
                
                // Update user's startFlowProgress to mark all steps as completed
                await updateDoc(doc(db, 'users', userId), {
                  startFlowProgress: {
                    completedSteps: [1, 2, 3, 4],
                    lastUpdated: new Date().toISOString(),
                    completedByStatusChange: true // Flag to indicate this was auto-completed
                  },
                  lastUpdated: new Date().toISOString()
                });
                
                console.log('Marked all assessment steps as completed for user:', userId);
              } else {
                console.log('No user found with email:', normalizedEmail);
              }
            } else {
              console.log('No email found for lead, cannot update user steps');
            }
          } catch (error) {
            console.error('Error updating user steps when moving to Proposal:', error);
            // Don't fail the status update if this fails
          }
        }
      }
      
      // Add to status history
      const statusEntry = {
        status: newStatus,
        previousStatus: previousStatus,
        date: new Date().toISOString(),
        updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User'
      };
      
      updatedData.statusHistory = [...(updatedData.statusHistory || []), statusEntry];

      // Marking Lost: keep the pipeline stage they were in and when it happened, so the Lost
      // Leads report and lead analytics never have to dig it out of statusHistory.
      if (newStatus === 'Lost') {
        Object.assign(updatedData, leadLostFields({ ...lead, stage: previousStatus }));
      }

      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setStatusHistory(updatedData.statusHistory);
      setShowUpdateStatusModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleClientFormChange = (e) => {
    const { name, value } = e.target;
    setClientFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMakeClient = async () => {
    try {
      // Validate form data
      if (!clientFormData.program || !clientFormData.startDate || !clientFormData.minimumTerm) {
        alert('Please fill in all required fields: Program, Start Date, and Minimum Term');
        return;
      }

      const updatedData = { ...lead };
      // Freeze the lead's pipeline position BEFORE stage is overwritten with the program tier.
      Object.assign(
        updatedData,
        leadConversionFields(lead, {
          by: currentUser?.displayName || currentUser?.email || 'Unknown User',
          source: 'crm',
          program: clientFormData.program,
        })
      );
      updatedData.stage = clientFormData.program; // Set stage to the selected program (Lite, Plus, or Pro)
      updatedData.substatus = null; // Clear substatus for clients
      updatedData.type = 'Clients'; // Change type to Clients
      updatedData.result = 'Won'; // Set result to Won when converting to client

      // Add client-specific data — program must be the object shape ClientOverview reads
      // (program.status / program.startDate / program.minimumTerm)
      const startDate = new Date(clientFormData.startDate + 'T00:00:00');
      updatedData.program = programMapWith(lead.program, {
        status: clientFormData.program,
        startDate: startDate.toISOString(),
        minimumTerm: clientFormData.minimumTerm
      });
      updatedData.contractStart = startDate;

      // The document collection rides along on the same account doc — only backfill the
      // department/visibility that pre-shared-collection lead uploads are missing.
      const carriedDocuments = normalizeCollectionForConversion(lead.customDocuments);
      if (carriedDocuments) updatedData.customDocuments = carriedDocuments;

      // Add to status history
      const statusEntry = {
        status: clientFormData.program,
        previousStatus: lead.stage,
        date: new Date().toISOString(),
        updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User',
        action: `Converted to Client - Program: ${clientFormData.program}`
      };
      
      updatedData.statusHistory = [...(updatedData.statusHistory || []), statusEntry];
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setStatusHistory(updatedData.statusHistory);
      setShowMakeClientModal(false);
      
      // Reset form data
      setClientFormData({
        program: '',
        startDate: '',
        minimumTerm: ''
      });
      
      // Navigate to clients page
      navigate('/crm/clients');
    } catch (error) {
      console.error('Error making client:', error);
    }
  };

  const handleInviteToPortal = async () => {
    if (!lead) return;
    
    setIsInvitingToPortal(true);
    try {
      const inviteData = {
        leadId: leadId,
        email: lead.contact?.email || '',
        firstName: lead.firstName || lead.contact?.firstName || '',
        lastName: lead.lastName || lead.contact?.lastName || '',
        company: lead.companyName || lead.company || 'Unknown Company'
      };

      const result = await createPortalInvite(inviteData);
      
      // Use the signup URL from the service
      setGeneratedInviteLink(result.signupUrl);
      setShowInviteLinkModal(true);
      
      // Update lead status to "Portal Invite Sent" when link is created
      const currentDate = new Date().toISOString();
      const updatedData = { ...lead };
      
      // Set stage to Assessment if not already there
      if (updatedData.stage !== 'Assessment') {
        updatedData.stage = 'Assessment';
      }
      updatedData.substatus = 'Portal Invite Sent';
      
      // Track substatus dates
      if (!updatedData.substatusDates) {
        updatedData.substatusDates = {};
      }
      if (!updatedData.substatusDates['Portal Invite Sent']) {
        updatedData.substatusDates['Portal Invite Sent'] = currentDate;
      }
      
      // Add to status history
      const statusEntry = {
        status: 'Assessment',
        substatus: 'Portal Invite Sent',
        previousStatus: lead.stage || 'Unknown',
        previousSubstatus: lead.substatus || null,
        date: currentDate,
        updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User',
        action: 'Portal invitation link created for lead'
      };
      
      updatedData.statusHistory = [...(updatedData.statusHistory || []), statusEntry];
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setStatusHistory(updatedData.statusHistory);
      
    } catch (error) {
      console.error('Error generating portal invite:', error);
      alert(`Failed to generate portal invitation: ${error.message}`);
    } finally {
      setIsInvitingToPortal(false);
    }
  };

  const handleDeletePortalAccount = async () => {
    if (!lead) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to delete this portal account? This will remove the user\'s portal access and signup date. This action cannot be undone.'
    );
    
    if (!confirmed) return;
    
    setIsDeletingPortalAccount(true);
    try {
      // Update lead data to remove portal signup information
      const updatedData = { ...lead };
      delete updatedData.portalSignupDate;
      
      // Add to status history
      const statusEntry = {
        status: 'Portal Account Deleted',
        previousStatus: lead.substatus || lead.stage,
        date: new Date().toISOString(),
        updatedBy: currentUser?.displayName || currentUser?.email || 'Unknown User',
        action: 'Portal account access removed for lead'
      };
      
      updatedData.statusHistory = [...(updatedData.statusHistory || []), statusEntry];
      
      await updateAccount(leadId, updatedData);
      setLead(updatedData);
      setStatusHistory(updatedData.statusHistory);
      
      alert('Portal account has been deleted successfully.');
      
    } catch (error) {
      console.error('Error deleting portal account:', error);
      alert(`Failed to delete portal account: ${error.message}`);
    } finally {
      setIsDeletingPortalAccount(false);
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedInviteLink);
      alert('Invite link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link. Please copy it manually.');
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const data = await getAccountById(leadId);
      if (data.type !== 'Leads') {
        throw new Error('This is not a lead');
      }
      setLead(data);
      setStatusHistory(data.statusHistory || []);
      setNotes(data.notes || []);
      setSignedNDA(data.signedNDA || null);
      setQualified(data.qualified || null);
      setError(null);
    } catch (error) {
      console.error('Error refreshing lead:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewNDA = () => {
    if (lead?.signedNDA) {
      setSignedNDA(lead.signedNDA);
      setShowNDAModal(true);
      // Load template to ensure renderedNDAContent is available for variable replacement
      loadNDATemplate();
    }
  };

  // Load NDA template for BBA signing
  const loadNDATemplate = async () => {
    try {
      setTemplateLoading(true);
      setTemplateError(null);
      const template = await getUserNdaTemplate(currentUser.uid);
      console.log('Loaded NDA template for BBA signing:', template);
      console.log('Template ndaElements:', template.ndaElements);
      
      // Debug: Check for BBA-assigned elements
      if (template.ndaElements && template.ndaElements.length > 0) {
        const bbaElements = template.ndaElements.filter(el => el.assignedTo === 'BBA');
        console.log('BBA-assigned elements found:', bbaElements);
        
        const allElements = template.ndaElements.map(el => ({
          id: el.id,
          type: el.type,
          assignedTo: el.assignedTo,
          content: el.content,
          variableTitle: el.variableTitle
        }));
        console.log('All template elements:', allElements);
      }
      
      setNdaTemplate(template);
      
      // Initialize BBA form data with BBA-assigned variables
      if (template.ndaElements && template.ndaElements.length > 0) {
        initializeBBAFormData(template.ndaElements);
      }
    } catch (err) {
      console.error('Error loading NDA template:', err);
      setTemplateError('Failed to load NDA template. Please try again.');
    } finally {
      setTemplateLoading(false);
    }
  };

  // Initialize BBA form data from template variables
  const initializeBBAFormData = (ndaElements) => {
    const newFormData = {};
    
    console.log('Initializing BBA form data from elements:', ndaElements);
    
    ndaElements.forEach(element => {
      // Direct input field types assigned to BBA
      if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type) && 
          element.assignedTo === 'BBA') {
        console.log('Adding direct BBA field:', element.id);
        newFormData[element.id] = '';
      }
      
      // BBA variables in text content assigned to BBA
      if (element.type === 'text' && element.content && element.assignedTo === 'BBA') {
        // Extract special variables like [SIGNATURE], [INITIAL], etc.
        const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/g;
        let match;
        while ((match = specialPattern.exec(element.content)) !== null) {
          const variableId = `${element.id}-special-${match.index}`;
          console.log('Adding BBA special variable:', variableId);
          newFormData[variableId] = '';
        }
        
        // Extract bracket variables like [BBA_Company_Name]
        const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
        while ((match = bracketPattern.exec(element.content)) !== null) {
          const variableName = match[1];
          if (variableName.toLowerCase().includes('bba')) {
            const variableId = `${element.id}-bracket-${variableName}-${match.index}`;
            console.log('Adding BBA bracket variable:', variableId);
            newFormData[variableId] = '';
          }
        }
      }
      
      // BBA variables in any text content (regardless of assignedTo)
      if (element.type === 'text' && element.content) {
        const bbaPattern = /\[(BBA_[A-Za-z_][A-Za-z0-9_]*)\]/g;
        let match;
        while ((match = bbaPattern.exec(element.content)) !== null) {
          const bracketName = match[1];
          const variableId = `${element.id}-bba-${bracketName}-${match.index}`;
          console.log('Adding BBA variable from unassigned text:', variableId);
          newFormData[variableId] = '';
        }
      }
    });
    
    // Initialize default BBA variables if none found
    if (Object.keys(newFormData).length === 0) {
      console.log('No BBA variables found, initializing BBA signature section variables');
      newFormData['default-bba-name'] = '';
      newFormData['default-bba-title'] = '';
      newFormData['default-bba-signature'] = '';
      newFormData['default-bba-date'] = '';
    }
    
    console.log('Initialized BBA form data:', newFormData);
    setBbaFormData(newFormData);
  };

  // Get BBA-assigned variables from template
  const getBBAVariables = () => {
    if (!ndaTemplate) return [];
    
    const bbaVariables = [];
    
    console.log('Getting BBA variables from template:', ndaTemplate);
    
    if (ndaTemplate.ndaElements && ndaTemplate.ndaElements.length > 0) {
      ndaTemplate.ndaElements.forEach(element => {
        console.log('Processing element for BBA variables:', {
          id: element.id,
          type: element.type,
          assignedTo: element.assignedTo,
          content: element.content,
          variableTitle: element.variableTitle
        });
        
        // Direct input field types assigned to BBA
        if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type) && 
            element.assignedTo === 'BBA') {
          console.log('Found direct BBA variable:', element);
          bbaVariables.push({
            id: element.id,
            type: element.type,
            variableTitle: element.variableTitle || element.type,
            content: element.content,
            assignedTo: element.assignedTo,
            isDirect: true
          });
        }
        
        // Look for BBA signature section with specific variables
        if (element.type === 'text' && element.content && 
            (element.content.includes('Builders Breakthrough Advisors') || 
             element.content.includes('By:') ||
             element.assignedTo === 'BBA')) {
          console.log('Found BBA signature section:', element);
          
          // Extract the specific BBA variables: [Name], [Title], [Signature], [Date]
          const bbaVariablePattern = /\[(Name|Title|Signature|Date)\]/g;
          let match;
          while ((match = bbaVariablePattern.exec(element.content)) !== null) {
            const variableName = match[1];
            const variableType = getVariableTypeFromBracket(variableName);
            
            console.log('Found BBA variable:', variableName, 'type:', variableType);
            
            bbaVariables.push({
              id: `${element.id}-bba-${variableName}-${match.index}`,
              type: variableType,
              variableTitle: variableName,
              content: `[${variableName}]`,
              assignedTo: 'BBA',
              isDirect: false,
              parentElementId: element.id,
              isBracketVariable: true,
              bracketName: variableName
            });
          }
        }
        
        // Variables embedded in text content assigned to BBA
        if (element.type === 'text' && element.content && element.assignedTo === 'BBA') {
          console.log('Processing BBA text element:', element);
          
          // Extract special variables like [SIGNATURE], [INITIAL], etc.
          const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/g;
          let match;
          while ((match = specialPattern.exec(element.content)) !== null) {
            let variableType = match[1].toLowerCase();
            if (variableType === 'initials') {
              variableType = 'initial';
            }
            
            console.log('Found special BBA variable:', match[1], 'type:', variableType);
            
            bbaVariables.push({
              id: `${element.id}-special-${match.index}`,
              type: variableType,
              variableTitle: variableType === 'text_input' ? 'Text Input' : 
                            variableType === 'checkbox' ? 'Checkbox' :
                            variableType === 'initial' ? 'Initial' :
                            variableType.charAt(0).toUpperCase() + variableType.slice(1),
              content: `[${match[1]}]`,
              assignedTo: 'BBA',
              isDirect: false,
              parentElementId: element.id
            });
          }
          
          // Extract bracket variables like [BBA_Company_Name]
          const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
          while ((match = bracketPattern.exec(element.content)) !== null) {
            const bracketName = match[1];
            if (bracketName.toLowerCase().includes('bba')) {
              console.log('Found BBA bracket variable:', bracketName);
              const variableType = getVariableTypeFromBracket(bracketName);
              const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              
              bbaVariables.push({
                id: `${element.id}-bracket-${bracketName}-${match.index}`,
                type: variableType,
                variableTitle: displayName,
                content: `[${bracketName}]`,
                assignedTo: 'BBA',
                isDirect: false,
                parentElementId: element.id,
                isBracketVariable: true,
                bracketName: bracketName
              });
            }
          }
        }
        
        // Also check for any elements that might have BBA variables in their content regardless of assignedTo
        if (element.type === 'text' && element.content && !element.assignedTo) {
          // Look for BBA-specific variables in any text content
          const bbaPattern = /\[(BBA_[A-Za-z_][A-Za-z0-9_]*)\]/g;
          let match;
          while ((match = bbaPattern.exec(element.content)) !== null) {
            const bracketName = match[1];
            console.log('Found BBA variable in unassigned text:', bracketName);
            const variableType = getVariableTypeFromBracket(bracketName);
            const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            bbaVariables.push({
              id: `${element.id}-bba-${bracketName}-${match.index}`,
              type: variableType,
              variableTitle: displayName,
              content: `[${bracketName}]`,
              assignedTo: 'BBA',
              isDirect: false,
              parentElementId: element.id,
              isBracketVariable: true,
              bracketName: bracketName
            });
          }
        }
      });
    }
    
    console.log('Final BBA variables found:', bbaVariables);
    
    // If no BBA variables found, create the specific ones for the BBA signature section
    if (bbaVariables.length === 0) {
      console.log('No BBA variables found, creating BBA signature section variables');
      bbaVariables.push(
        {
          id: 'default-bba-name',
          type: 'text_input',
          variableTitle: 'Name',
          content: '[Name]',
          assignedTo: 'BBA',
          isDirect: true
        },
        {
          id: 'default-bba-title',
          type: 'text_input',
          variableTitle: 'Title',
          content: '[Title]',
          assignedTo: 'BBA',
          isDirect: true
        },
        {
          id: 'default-bba-signature',
          type: 'signature',
          variableTitle: 'Signature',
          content: '[Signature]',
          assignedTo: 'BBA',
          isDirect: true
        },
        {
          id: 'default-bba-date',
          type: 'date',
          variableTitle: 'Date',
          content: '[Date]',
          assignedTo: 'BBA',
          isDirect: true
        }
      );
    }
    
    return bbaVariables;
  };

  // Get variable type from bracket name
  const getVariableTypeFromBracket = (bracketName) => {
    const lowerName = bracketName.toLowerCase();
    if (lowerName.includes('signature') || lowerName.includes('sign')) return 'signature';
    if (lowerName.includes('initial')) return 'initial';
    if (lowerName.includes('date')) return 'date';
    if (lowerName.includes('check') || lowerName.includes('agree')) return 'checkbox';
    return 'text_input'; // Default to text input
  };

  // Render content with variables replaced (similar to NDA page)
  const renderContentWithVariables = (content, elementId = null) => {
    if (!content) return '';
    
    let updatedContent = content;
    
    // First, identify BBA variables to avoid replacing them with client data
    const bbaVariables = getBBAVariables();
    const bbaVariableNames = new Set();
    bbaVariables.forEach(variable => {
      if (variable.bracketName) {
        bbaVariableNames.add(variable.bracketName);
      }
    });
    
    // First, replace client variables with their actual values from lead data and signedNDA
    // Construct address from multiple fields if companyAddress is empty
    // Only use street address for [Address] - city, state, zip are separate fields in the template
    let companyAddress = lead?.streetAddress || lead?.discovery?.streetAddress || lead?.location || signedNDA?.companyAddress || '';
    // If still empty, try to get from address object
    if (!companyAddress && lead?.address?.street) {
      companyAddress = lead.address.street;
    }
    // Don't add city/state/zip to address - they're separate fields in the template
    
    const clientData = {
      // Use lead data first, then fall back to signedNDA
      companyName: lead?.companyName || lead?.discovery?.companyName || signedNDA?.companyName || '',
      state: lead?.state || lead?.discovery?.state || signedNDA?.state || '',
      companyAddress: companyAddress,
      city: lead?.city || lead?.discovery?.city || signedNDA?.city || '',
      zipCode: lead?.zipCode || lead?.discovery?.zipCode || signedNDA?.zipCode || '',
      businessStructure: lead?.discovery?.businessStructure || lead?.businessStructure || signedNDA?.businessStructure || '',
      signerName: lead?.contact?.name || lead?.discovery?.signerName || (lead?.discovery?.firstName && lead?.discovery?.lastName ? `${lead.discovery.firstName} ${lead.discovery.lastName}` : '') || signedNDA?.signerName || '',
      signerTitle: lead?.discovery?.signerTitle || signedNDA?.signerTitle || lead?.contact?.title || '',
      signature: signedNDA?.signature || '',
      initial: signedNDA?.initial || lead?.discovery?.initial || ''
    };

    if (clientData.companyName || clientData.signerName) {
      // Replace common client variables, but skip BBA variables
      const clientVariables = {
        // Company Name variations
        'Company Name': clientData.companyName,
        'Company_Name': clientData.companyName,
        'COMPANY_NAME': clientData.companyName,
        // Business Structure variations
        'Business Structure': clientData.businessStructure,
        'Business_Structure': clientData.businessStructure,
        'BUSINESS_STRUCTURE': clientData.businessStructure,
        // State variations
        'State': clientData.state,
        'STATE': clientData.state,
        // Address variations
        'Company Address': clientData.companyAddress,
        'Company_Address': clientData.companyAddress,
        'COMPANY_ADDRESS': clientData.companyAddress,
        'Address': clientData.companyAddress,
        'ADDRESS': clientData.companyAddress,
        'Street Address': clientData.companyAddress,
        'Street_Address': clientData.companyAddress,
        'STREET_ADDRESS': clientData.companyAddress,
        // City variations
        'City': clientData.city,
        'CITY': clientData.city,
        // Zip Code variations
        'Zip Code': clientData.zipCode,
        'Zip_Code': clientData.zipCode,
        'ZIP_CODE': clientData.zipCode,
        // Name variations (signer name, not company name)
        'Signer Name': clientData.signerName,
        'Signer_Name': clientData.signerName,
        'SIGNER_NAME': clientData.signerName,
        'Name': clientData.signerName,
        'NAME': clientData.signerName,
        // Title variations
        'Signer Title': clientData.signerTitle,
        'Signer_Title': clientData.signerTitle,
        'SIGNER_TITLE': clientData.signerTitle,
        'Title': clientData.signerTitle,
        'TITLE': clientData.signerTitle,
        // Signature variations
        'Signature': clientData.signature,
        'SIGNATURE': clientData.signature,
        // Initials variations
        'INITIAL': clientData.initial,
        'INITIALS': clientData.initial,
        'Initial': clientData.initial,
        'Initials': clientData.initial,
        'Initials_Two': clientData.initial,
        'Initials Two': clientData.initial,
        'INITIALS_TWO': clientData.initial,
        'Initials_Three': clientData.initial,
        'Initials Three': clientData.initial,
        'INITIALS_THREE': clientData.initial,
        // Date variations
        'Current Date': new Date().toLocaleDateString(),
        'CURRENT_DATE': new Date().toLocaleDateString()
      };
      
      // Only add Date variables if they're not BBA variables
      if (!bbaVariableNames.has('Date')) {
        clientVariables['Date'] = new Date().toLocaleDateString();
        clientVariables['DATE'] = new Date().toLocaleDateString();
      }
      
      // Replace client variables in bracket notation
      // Always replace mapped variables (even if empty) to ensure all NDA fields are displayed
      Object.entries(clientVariables).forEach(([key, value]) => {
        // Always replace if the variable is in our mapping to avoid showing placeholders
        const pattern = new RegExp(`\\[${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
        updatedContent = updatedContent.replace(pattern, (match) => {
          // Apply cursive styling for signature text
          if (key.toLowerCase().includes('signature') || key.toLowerCase().includes('sign')) {
            return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value || ''}</span>`;
          }
          return value || '';
        });
      });
      
      // Debug logging
      console.log('Client data for document rendering:', {
        lead: lead ? {
          companyName: lead.companyName,
          state: lead.state,
          streetAddress: lead.streetAddress,
          city: lead.city,
          zipCode: lead.zipCode,
          contact: lead.contact,
          discovery: lead.discovery,
          businessStructure: lead.businessStructure
        } : 'No lead data',
        signedNDA: signedNDA ? {
          companyName: signedNDA.companyName,
          signerName: signedNDA.signerName,
          signature: signedNDA.signature,
          initial: signedNDA.initial
        } : 'No signedNDA data',
        clientData: clientData
      });
    }
    
    // Replace BBA signature section variables: [Name], [Title], [Signature], [Date]
    // Only process these if they're in a BBA signature section
    const bbaSignaturePattern = /\[(Name|Title|Signature|Date)\]/g;
    updatedContent = updatedContent.replace(bbaSignaturePattern, (match, variableName) => {
      const bbaVariables = getBBAVariables();
      const bbaVariable = bbaVariables.find(v => 
        v.bracketName === variableName && 
        (v.parentElementId === elementId || !v.parentElementId)
      );
      
      // If it's a BBA variable, only use BBA form data
      if (bbaVariable) {
        let value = bbaFormData[bbaVariable.id] ? bbaFormData[bbaVariable.id] : '';
        
        if (value) {
          // Apply cursive styling for signature text
          if (variableName.toLowerCase().includes('signature') || variableName.toLowerCase().includes('sign')) {
            return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
          }
          return value;
        }
        
        // If no BBA form data, keep the placeholder
        return match;
      }
      
      // If it's not a BBA variable, treat as client variable
      if (clientData) {
        let value = '';
        switch (variableName) {
          case 'Name':
            value = clientData.signerName;
            break;
          case 'Title':
            value = clientData.signerTitle;
            break;
          case 'Signature':
            value = clientData.signature;
            break;
          case 'Date':
            value = new Date().toLocaleDateString();
            break;
          default:
            value = '';
        }
        
        // Replace Title and Name even if empty to avoid showing placeholders
        if (value || variableName === 'Title' || variableName === 'Name') {
          // Apply cursive styling for signature text
          if (variableName.toLowerCase().includes('signature') || variableName.toLowerCase().includes('sign')) {
            return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value || ''}</span>`;
          }
          return value || '';
        }
      }
      
      // If no value found, keep the original placeholder
      return match;
    });
    
    // Replace special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
    const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/g;
    updatedContent = updatedContent.replace(specialPattern, (match, variableType) => {
      let variableTypeLower = variableType.toLowerCase();
      if (variableTypeLower === 'initials') {
        variableTypeLower = 'initial';
      }
      
      // Find BBA variables first
      const bbaVariables = getBBAVariables();
      const bbaVariable = bbaVariables.find(v => 
        v.type === variableTypeLower && 
        v.parentElementId === elementId
      );
      
      // Only get value from BBA form data - don't fall back to client data
      let value = bbaVariable && bbaFormData[bbaVariable.id] ? bbaFormData[bbaVariable.id] : '';
      
      if (value) {
        // Apply cursive styling for signature text
        if (variableTypeLower === 'signature') {
          return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
        }
        // Render checkbox as actual checkbox instead of text
        if (variableTypeLower === 'checkbox') {
          const isChecked = value === 'true' || value === true;
          return `<input type="checkbox" ${isChecked ? 'checked' : ''} disabled style="margin-right: 4px;" />`;
        }
        return value;
      }
      
      // If no BBA form data found, keep the original placeholder
      return match;
    });
    
    // Replace bracket variables like [Company_Name]
    const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
    updatedContent = updatedContent.replace(bracketPattern, (match, variableName) => {
      // Find BBA variables first
      const bbaVariables = getBBAVariables();
      const bbaVariable = bbaVariables.find(v => 
        v.isBracketVariable && 
        v.bracketName === variableName && 
        v.parentElementId === elementId
      );
      
      // If it's a BBA variable, only use BBA form data
      if (bbaVariable) {
        let value = bbaFormData[bbaVariable.id] ? bbaFormData[bbaVariable.id] : '';
        
        if (value) {
          // Apply cursive styling for signature text
          if (variableName.toLowerCase().includes('signature') || variableName.toLowerCase().includes('sign')) {
            return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
          }
          return value;
        }
        
        // If no BBA form data, keep the placeholder
        return match;
      }
      
      // If it's not a BBA variable, try to map bracket variables to client data
      if (clientData) {
        const bracketToClientMap = {
          // Company Name variations
          'Company_Name': clientData.companyName,
          'Company Name': clientData.companyName,
          'COMPANY_NAME': clientData.companyName,
          // Business Structure variations
          'Business_Structure': clientData.businessStructure,
          'Business Structure': clientData.businessStructure,
          'BUSINESS_STRUCTURE': clientData.businessStructure,
          // State variations
          'State': clientData.state,
          'STATE': clientData.state,
          // Address variations
          'Company_Address': clientData.companyAddress,
          'Company Address': clientData.companyAddress,
          'COMPANY_ADDRESS': clientData.companyAddress,
          'Address': clientData.companyAddress,
          'ADDRESS': clientData.companyAddress,
          'Street_Address': clientData.companyAddress,
          'Street Address': clientData.companyAddress,
          'STREET_ADDRESS': clientData.companyAddress,
          // City variations
          'City': clientData.city,
          'CITY': clientData.city,
          // Zip Code variations
          'Zip_Code': clientData.zipCode,
          'Zip Code': clientData.zipCode,
          'ZIP_CODE': clientData.zipCode,
          // Name variations (signer name, not company name)
          'Signer_Name': clientData.signerName,
          'Signer Name': clientData.signerName,
          'SIGNER_NAME': clientData.signerName,
          'Name': clientData.signerName,
          'NAME': clientData.signerName,
          // Title variations
          'Signer_Title': clientData.signerTitle,
          'Signer Title': clientData.signerTitle,
          'SIGNER_TITLE': clientData.signerTitle,
          'Title': clientData.signerTitle,
          'TITLE': clientData.signerTitle,
          // Signature variations
          'Signature': clientData.signature,
          'SIGNATURE': clientData.signature,
          // Initials variations
          'INITIAL': clientData.initial,
          'INITIALS': clientData.initial,
          'Initial': clientData.initial,
          'Initials': clientData.initial,
          'Initials_Two': clientData.initial,
          'Initials Two': clientData.initial,
          'INITIALS_TWO': clientData.initial,
          'Initials_Three': clientData.initial,
          'Initials Three': clientData.initial,
          'INITIALS_THREE': clientData.initial
        };
        
        // Only add Date variables if they're not BBA variables
        if (!bbaVariableNames.has('Date')) {
          bracketToClientMap['Date'] = new Date().toLocaleDateString();
          bracketToClientMap['DATE'] = new Date().toLocaleDateString();
        }
        
        const value = bracketToClientMap[variableName] || '';
        
        // Always replace if the variable is in our mapping (even if empty) to avoid showing placeholders
        // This ensures all NDA fields are properly displayed
        if (bracketToClientMap.hasOwnProperty(variableName)) {
          // Apply cursive styling for signature text
          if (variableName.toLowerCase().includes('signature') || variableName.toLowerCase().includes('sign')) {
            return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value || ''}</span>`;
          }
          return value || '';
        }
      }
      
      // If no value found, keep the original bracket notation
      return match;
    });
    
    return updatedContent;
  };

  // Handle BBA form input changes
  const handleBBAFormChange = (variableId, value) => {
    setBbaFormData(prev => ({
      ...prev,
      [variableId]: value
    }));
  };

  // Memoized rendered NDA content that updates when form data changes
  const renderedNDAContent = useMemo(() => {
    if (!ndaTemplate || !ndaTemplate.ndaElements) return null;
    
    return ndaTemplate.ndaElements.map((element) => {
      if (element.type === 'header') {
        return (
          <h2 key={element.id} className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
          </h2>
        );
      } else if (element.type === 'text') {
        return (
          <div key={element.id} className="mb-3">
            <p dangerouslySetInnerHTML={{ 
              __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) 
            }} />
            {/* Render sub-items if they exist */}
            {(element.subItems || []).length > 0 && (
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-4 mt-2">
                {(element.subItems || []).map((subItem) => (
                  <li key={subItem.id} className="text-sm">
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(subItem.text || 'Empty sub-item', element.id)) }} />
                    {(subItem.subItems || []).length > 0 && (
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                        {(subItem.subItems || []).map((nestedSubItem) => (
                          <li key={nestedSubItem.id} className="text-sm text-gray-600 dark:text-gray-300">
                            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(nestedSubItem.text || 'Empty nested sub-item', element.id)) }} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      } else if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
        // Show all elements, not just BBA assigned ones
        if (element.assignedTo === 'BBA') {
          // BBA assigned elements - show as form fields
          return (
            <div key={element.id} className="mb-3">
              <span className="text-sm text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ 
                __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) 
              }} />
            </div>
          );
        } else {
          // Client assigned elements - show as completed fields
          return (
            <div key={element.id} className="mb-3">
              <span className="text-sm text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ 
                __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) 
              }} />
            </div>
          );
        }
      }
      return null;
    });
  }, [ndaTemplate, bbaFormData, lead, signedNDA]);

  // Handle BBA signing
  const handleBBASigning = async () => {
    try {
      if (!ndaTemplate || !signedNDA) {
        throw new Error('Missing template or signed NDA data');
      }

      // Get client info for audit trail
      const clientInfo = await ESignatureService.getClientInfo();
      
      // Save BBA signature using ESignatureService
      const bbaSignatureData = await ESignatureService.saveBBASignature(
        currentUser.uid,
        signedNDA.id,
        bbaFormData,
        clientInfo
      );

      // Generate the complete document content with BBA variables injected
      let finalizedDocumentContent = '';
      if (ndaTemplate && ndaTemplate.ndaElements) {
        ndaTemplate.ndaElements.forEach(element => {
          if (element.type === 'header') {
            finalizedDocumentContent += `<h2>${renderContentWithVariables(element.content, element.id)}</h2>\n\n`;
          } else if (element.type === 'text') {
            finalizedDocumentContent += `<p>${renderContentWithVariables(element.content, element.id)}</p>`;
            
            // Add sub-items if they exist
            if (element.subItems && element.subItems.length > 0) {
              finalizedDocumentContent += `\n<ul style="list-style-type: disc; margin-left: 1rem; margin-top: 0.5rem;">`;
              element.subItems.forEach(subItem => {
                finalizedDocumentContent += `<li style="margin-bottom: 0.25rem; font-size: 0.875rem;">${renderContentWithVariables(subItem.text || 'Empty sub-item', element.id)}`;
                
                // Add nested sub-items if they exist
                if (subItem.subItems && subItem.subItems.length > 0) {
                  finalizedDocumentContent += `\n<ul style="list-style-type: disc; margin-left: 1rem; margin-top: 0.25rem;">`;
                  subItem.subItems.forEach(nestedSubItem => {
                    finalizedDocumentContent += `<li style="margin-bottom: 0.25rem; font-size: 0.875rem;">${renderContentWithVariables(nestedSubItem.text || 'Empty nested sub-item', element.id)}</li>`;
                  });
                  finalizedDocumentContent += `</ul>`;
                }
                
                finalizedDocumentContent += `</li>`;
              });
              finalizedDocumentContent += `</ul>`;
            }
            
            finalizedDocumentContent += `\n\n`;
          } else if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
            finalizedDocumentContent += `<div>${renderContentWithVariables(element.content, element.id)}</div>\n\n`;
          }
        });
      }

      // Create finalized NDA with both client and BBA data
      const finalizedDocument = {
        ...signedNDA,
        ...bbaSignatureData,
        isFullyExecuted: true,
        finalizedAt: new Date().toISOString(),
        finalizedDocumentContent: finalizedDocumentContent
      };

      // Update the lead account with finalized NDA and substatus
      const currentDate = new Date().toISOString();
      const updatedData = {
        signedNDA: finalizedDocument,
        ndaStatus: 'Fully Executed',
        ndaLastUpdated: currentDate,
        lastUpdated: currentDate
      };
      
      // Update substatus to "NDA Signed" if in Assessment stage and not already set
      if (lead?.stage === 'Assessment' && lead?.substatus !== 'NDA Signed') {
        updatedData.substatus = 'NDA Signed';
        
        // Track substatus dates
        const substatusDates = lead?.substatusDates || {};
        if (!substatusDates['NDA Signed']) {
          substatusDates['NDA Signed'] = currentDate;
        }
        updatedData.substatusDates = substatusDates;
        
        // Add to status history
        const statusHistory = lead?.statusHistory || [];
        const statusEntry = {
          status: 'Assessment',
          substatus: 'NDA Signed',
          previousStatus: lead.stage || 'Unknown',
          previousSubstatus: lead.substatus || null,
          date: currentDate,
          updatedBy: currentUser?.displayName || currentUser?.email || 'System',
          action: 'NDA fully executed - substatus updated automatically'
        };
        updatedData.statusHistory = [...statusHistory, statusEntry];
      }
      
      await updateAccount(leadId, updatedData);

      setFinalizedNDA(finalizedDocument);
      setIsBBASigned(true);
      setShowBBASigningModal(false);
      setShowNDAModal(true);
      
      // Lead data will be automatically updated via real-time listener
      
    } catch (error) {
      console.error('Error signing NDA as BBA:', error);
      setTemplateError('Failed to sign NDA. Please try again.');
    }
  };

  // Open BBA signing modal
  const handleOpenBBASigning = () => {
    setShowBBASigningModal(true);
    loadNDATemplate();
  };

  const handleDownloadNDA = async () => {
    if (lead?.signedNDA) {
      try {
        // Create enhanced NDA document with lead data and BBA form data
        // IMPORTANT: finalizedDocumentContent (if available) already has all variables replaced
        // and should be used for PDF generation to match what's shown in the popup
        const enhancedNDADocument = {
          ...lead.signedNDA,
          // finalizedDocumentContent is already included from lead.signedNDA spread above
          // This contains the fully processed content with all variables replaced
          // Add lead data for client variables (fallback if finalizedDocumentContent not available)
          leadData: {
            companyName: lead?.companyName || lead?.discovery?.companyName || lead.signedNDA?.companyName || '',
            state: lead?.state || lead?.discovery?.state || lead.signedNDA?.state || '',
            companyAddress: lead?.streetAddress || lead?.discovery?.streetAddress || lead?.location || lead.signedNDA?.companyAddress || '',
            city: lead?.city || lead?.discovery?.city || lead.signedNDA?.city || '',
            zipCode: lead?.zipCode || lead?.discovery?.zipCode || lead.signedNDA?.zipCode || '',
            businessStructure: lead?.discovery?.businessStructure || lead.signedNDA?.businessStructure || '',
            signerName: lead?.contact?.name || lead?.discovery?.signerName || lead.signedNDA?.signerName || '',
            signerTitle: lead?.discovery?.signerTitle || lead.signedNDA?.signerTitle || ''
          },
          // Add BBA form data if available (from bbaSignature spread)
          bbaFormData: lead.signedNDA?.bbaSignature || bbaFormData,
          // Add template data for proper rendering (fallback if finalizedDocumentContent not available)
          templateData: ndaTemplate,
          // Also add template elements directly for PDF generation (fallback)
          templateElements: lead.signedNDA?.templateElements || ndaTemplate?.ndaElements || []
        };
        
        // Debug: Log the enhanced NDA document structure
        console.log('Enhanced NDA Document for PDF generation:', {
          hasFinalizedDocumentContent: !!enhancedNDADocument.finalizedDocumentContent,
          finalizedDocumentContentLength: enhancedNDADocument.finalizedDocumentContent?.length || 0,
          hasTemplateElements: !!enhancedNDADocument.templateElements,
          templateElementsCount: enhancedNDADocument.templateElements?.length || 0,
          hasTemplateData: !!enhancedNDADocument.templateData,
          hasNdaTemplate: !!ndaTemplate,
          ndaTemplateElementsCount: ndaTemplate?.ndaElements?.length || 0,
          hasSignedNDA: !!lead.signedNDA,
          isFullyExecuted: lead.signedNDA?.isFullyExecuted
        });
        
        // Debug: Log page break information from template
        if (ndaTemplate?.ndaElements && ndaTemplate.ndaElements.length > 0) {
          const elementsWithPageBreaks = ndaTemplate.ndaElements.filter(el => el.hasPageBreak);
          console.log('Template elements with page breaks:', elementsWithPageBreaks.length);
          console.log('Page break elements in template:', elementsWithPageBreaks.map(el => ({
            id: el.id,
            type: el.type,
            content: el.content?.substring(0, 50),
            hasPageBreak: el.hasPageBreak
          })));
        }
        
        // Use the PDF service to generate the NDA PDF
        const result = await PDFService.downloadNDAPDF(enhancedNDADocument);
        if (result.success) {
          console.log(`NDA downloaded successfully: ${result.filename}`);
        } else {
          console.error('Error downloading NDA:', result.error);
          // Fallback to text download if PDF generation fails
          const ndaData = lead.signedNDA;
          const documentContent = ndaData.documentContent || 'NDA Document Content';
          
          const formattedContent = `
NON-DISCLOSURE AGREEMENT

Company: ${ndaData.companyName}
Signer: ${ndaData.signerName}
Title: ${ndaData.signerTitle}
Address: ${ndaData.companyAddress}
City: ${ndaData.city}
State: ${ndaData.state}
ZIP Code: ${ndaData.zipCode}
Business Structure: ${ndaData.businessStructure}

Signed: ${new Date(ndaData.signedAt).toLocaleString()}

Document Content:
${documentContent}

Electronic Signature: ${ndaData.signature}

E-SIGN Act Compliance:
- Electronic signature consent: ${ndaData.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
- Electronic records consent: ${ndaData.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
- Signed at: ${new Date(ndaData.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
- IP Address: ${ndaData.eSignCompliance?.auditTrail?.ipAddress}
- Browser: ${ndaData.eSignCompliance?.auditTrail?.browser}
- Device: ${ndaData.eSignCompliance?.auditTrail?.device}
          `.trim();
          
          const blob = new Blob([formattedContent], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `NDA_${ndaData.companyName}_${new Date(ndaData.signedAt).toISOString().split('T')[0]}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error generating PDF:', error);
        // Fallback to text download if PDF generation fails
        const ndaData = lead.signedNDA;
        const documentContent = ndaData.documentContent || 'NDA Document Content';
        
        const formattedContent = `
NON-DISCLOSURE AGREEMENT

Company: ${ndaData.companyName}
Signer: ${ndaData.signerName}
Title: ${ndaData.signerTitle}
Address: ${ndaData.companyAddress}
City: ${ndaData.city}
State: ${ndaData.state}
ZIP Code: ${ndaData.zipCode}
Business Structure: ${ndaData.businessStructure}

Signed: ${new Date(ndaData.signedAt).toLocaleString()}

Document Content:
${documentContent}

Electronic Signature: ${ndaData.signature}

E-SIGN Act Compliance:
- Electronic signature consent: ${ndaData.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
- Electronic records consent: ${ndaData.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
- Signed at: ${new Date(ndaData.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
- IP Address: ${ndaData.eSignCompliance?.auditTrail?.ipAddress}
- Browser: ${ndaData.eSignCompliance?.auditTrail?.browser}
- Device: ${ndaData.eSignCompliance?.auditTrail?.device}
        `.trim();
        
        const blob = new Blob([formattedContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `NDA_${ndaData.companyName}_${new Date(ndaData.signedAt).toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    }
  };

  const handleViewACH = () => {
    // Check for ACH info from contract first, then fall back to direct ACH info
    const achData = lead.signedContract?.bankingInfo || lead.achInfo;
    if (achData) {
      setAchInfo(achData);
      setShowACHModal(true);
      setShowSensitiveData(false); // Reset sensitive data visibility
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if you have one
      console.log('Copied to clipboard:', text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const maskSensitiveData = (text, visibleChars = 4) => {
    if (!text) return 'Not provided';
    if (showSensitiveData) return text;
    return text.slice(0, visibleChars) + '•'.repeat(Math.max(0, text.length - visibleChars));
  };

  // Handle contract viewing (e-signed contract or file uploaded under Document Collection)
  const handleViewContract = () => {
    if (lead?.signedContract) {
      setContractData(lead.signedContract);
      setShowContractModal(true);
      return;
    }
    const docs = lead?.contractDocuments;
    const lastDoc = Array.isArray(docs) && docs.length > 0 ? docs[docs.length - 1] : null;
    const uploadUrl = lead?.contractUrl || lastDoc?.fileUrl;
    if (uploadUrl) {
      setContractData({
        isUploadedContractPreview: true,
        previewUrl: uploadUrl,
        uploadFileType: lastDoc?.fileType || null
      });
      setShowContractModal(true);
      return;
    }
    console.error('No contract data available');
    alert('No contract document is available to view.');
  };

  // Handle contract downloading
  const handleDownloadContract = async () => {
    if (!lead?.signedContract) {
      console.error('No contract data available for download');
      return;
    }

    try {
      // Create enhanced contract document with lead data and BBA form data
      const enhancedContractDocument = {
        ...lead.signedContract,
        // Add lead data for client variables
        leadData: {
          companyName: lead?.companyName || lead?.discovery?.companyName || '',
          state: lead?.state || lead?.discovery?.state || '',
          companyAddress: lead?.streetAddress || lead?.discovery?.streetAddress || lead?.location || '',
          city: lead?.city || lead?.discovery?.city || '',
          zipCode: lead?.zipCode || lead?.discovery?.zipCode || '',
          businessStructure: lead?.discovery?.businessStructure || '',
          signerName: lead?.contact?.name || lead?.discovery?.signerName || '',
          signerTitle: lead?.discovery?.signerTitle || ''
        },
        // Ensure contract document has the fields expected by PDF service
        companyName: lead?.companyName || lead?.discovery?.companyName || lead.signedContract?.companyName || '',
        state: lead?.state || lead?.discovery?.state || lead.signedContract?.state || '',
        companyAddress: lead?.streetAddress || lead?.discovery?.streetAddress || lead?.location || lead.signedContract?.companyAddress || '',
        city: lead?.city || lead?.discovery?.city || lead.signedContract?.city || '',
        zipCode: lead?.zipCode || lead?.discovery?.zipCode || lead.signedContract?.zipCode || '',
        businessStructure: lead?.discovery?.businessStructure || lead.signedContract?.businessStructure || '',
        signerName: lead?.contact?.name || lead?.discovery?.signerName || lead.signedContract?.signerName || '',
        signerTitle: lead?.discovery?.signerTitle || lead.signedContract?.signerTitle || '',
        signature: lead.signedContract?.signature || '',
        initial: lead.signedContract?.initial || lead?.discovery?.initial || '',
        // Add additional fields that might be needed
        signedAt: lead.signedContract?.signedAt || new Date().toISOString(),
        // Add BBA form data if available - ensure proper field mapping
        bbaFormData: {
          ...contractBBAData,
          // Map contract BBA data to expected field names
          'default-bba-name': contractBBAData.bbaName || contractBBAData.name || '',
          'default-bba-title': contractBBAData.bbaTitle || contractBBAData.title || '',
          'default-bba-signature': contractBBAData.bbaSignature || contractBBAData.signature || '',
          'default-bba-date': contractBBAData.bbaSignatureDate || contractBBAData.date || new Date().toLocaleDateString()
        },
        // Add template data for proper rendering
        templateData: lead.signedContract.templateData,
        // Also add template elements directly for PDF generation
        templateElements: lead.signedContract.templateElements || lead.signedContract.templateData?.templateElements || [],
        // Ensure document content is available for processing
        documentContent: lead.signedContract.finalizedDocumentContent || lead.signedContract.documentContent || ''
      };
      
      // Process the document content with variables before PDF generation
      if (enhancedContractDocument.documentContent) {
        console.log('Document content BEFORE variable replacement:', enhancedContractDocument.documentContent.substring(0, 200) + '...');
        
        // Use the same variable replacement logic as the contract modal
        enhancedContractDocument.documentContent = renderContractContentWithVariables(
          enhancedContractDocument.documentContent
        );
        
        console.log('Document content AFTER variable replacement:', enhancedContractDocument.documentContent.substring(0, 200) + '...');
      }
      
      // Also process template elements with variables if they exist
      if (enhancedContractDocument.templateElements && enhancedContractDocument.templateElements.length > 0) {
        console.log('Processing template elements with variables...');
        enhancedContractDocument.templateElements = enhancedContractDocument.templateElements.map(element => {
          if (element.content) {
            const processedContent = renderContractContentWithVariables(element.content);
            console.log(`Element ${element.id}: "${element.content.substring(0, 50)}..." -> "${processedContent.substring(0, 50)}..."`);
            return {
              ...element,
              content: processedContent
            };
          }
          return element;
        });
      }
      
      // If no template elements, create a simple template structure for PDF generation
      if (!enhancedContractDocument.templateElements || enhancedContractDocument.templateElements.length === 0) {
        // Create a simple template structure similar to NDA
        enhancedContractDocument.templateElements = [
          {
            id: 'contract-header',
            type: 'header',
            content: 'Service Agreement',
            style: { fontSize: '24px', marginBottom: '16px' }
          },
          {
            id: 'contract-content',
            type: 'text',
            content: enhancedContractDocument.documentContent || 'Contract content not available',
            style: { fontSize: '14px', marginBottom: '12px' }
          }
        ];
      }
      
      // Debug: Log the enhanced contract document structure
      console.log('Enhanced Contract Document for PDF generation:', {
        hasTemplateElements: !!enhancedContractDocument.templateElements,
        templateElementsCount: enhancedContractDocument.templateElements?.length || 0,
        hasTemplateData: !!enhancedContractDocument.templateData,
        hasSignedContract: !!lead.signedContract,
        hasDocumentContent: !!enhancedContractDocument.documentContent,
        hasLeadData: !!enhancedContractDocument.leadData,
        hasBbaFormData: !!enhancedContractDocument.bbaFormData,
        bbaFormDataKeys: Object.keys(enhancedContractDocument.bbaFormData || {}),
        leadDataKeys: Object.keys(enhancedContractDocument.leadData || {}),
        // Log the actual field values that PDF service will use
        companyName: enhancedContractDocument.companyName,
        signerName: enhancedContractDocument.signerName,
        signerTitle: enhancedContractDocument.signerTitle,
        signature: enhancedContractDocument.signature,
        bbaName: enhancedContractDocument.bbaFormData?.['default-bba-name'],
        bbaTitle: enhancedContractDocument.bbaFormData?.['default-bba-title'],
        bbaSignature: enhancedContractDocument.bbaFormData?.['default-bba-signature']
      });
      
      // Debug: Log page break information from template
      if (lead.signedContract?.templateElements && lead.signedContract.templateElements.length > 0) {
        const elementsWithPageBreaks = lead.signedContract.templateElements.filter(el => el.hasPageBreak);
        console.log('Template elements with page breaks:', elementsWithPageBreaks.length);
        console.log('Page break elements in template:', elementsWithPageBreaks.map(el => ({
          id: el.id,
          type: el.type,
          content: el.content?.substring(0, 50),
          hasPageBreak: el.hasPageBreak
        })));
      }
      
      // Use the PDF service to generate the contract PDF (using NDA method as base)
      const result = await PDFService.downloadNDAPDF(enhancedContractDocument);
      if (result.success) {
        console.log(`Contract downloaded successfully: ${result.filename}`);
      } else {
        console.error('Error downloading contract:', result.error);
        // Fallback to text download if PDF generation fails
        const contractData = lead.signedContract;
        const documentContent = contractData.finalizedDocumentContent || contractData.documentContent || 'Contract Document Content';
        
        const formattedContent = `
SERVICE AGREEMENT

Company: ${contractData.companyName}
Signer: ${contractData.signerName}
Title: ${contractData.signerTitle}
Address: ${contractData.companyAddress}
City: ${contractData.city}
State: ${contractData.state}
ZIP Code: ${contractData.zipCode}
Business Structure: ${contractData.businessStructure}

Signed: ${new Date(contractData.signedAt).toLocaleString()}

Document Content:
${documentContent}

Client Electronic Signature: ${contractData.signature}

E-SIGN Act Compliance:
- Electronic signature consent: ${contractData.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
- Electronic records consent: ${contractData.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
- Signed at: ${new Date(contractData.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
- IP Address: ${contractData.eSignCompliance?.auditTrail?.ipAddress}
- Browser: ${contractData.eSignCompliance?.auditTrail?.browser}
- Device: ${contractData.eSignCompliance?.auditTrail?.device}
        `.trim();
        
        const blob = new Blob([formattedContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Contract_${contractData.companyName}_${new Date(contractData.signedAt).toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to text download if PDF generation fails
      const contractData = lead.signedContract;
      const documentContent = contractData.finalizedDocumentContent || contractData.documentContent || 'Contract Document Content';
      
      const formattedContent = `
SERVICE AGREEMENT

Company: ${contractData.companyName}
Signer: ${contractData.signerName}
Title: ${contractData.signerTitle}
Address: ${contractData.companyAddress}
City: ${contractData.city}
State: ${contractData.state}
ZIP Code: ${contractData.zipCode}
Business Structure: ${contractData.businessStructure}

Signed: ${new Date(contractData.signedAt).toLocaleString()}

Document Content:
${documentContent}

Client Electronic Signature: ${contractData.signature}

E-SIGN Act Compliance:
- Electronic signature consent: ${contractData.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
- Electronic records consent: ${contractData.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
- Signed at: ${new Date(contractData.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
- IP Address: ${contractData.eSignCompliance?.auditTrail?.ipAddress}
- Browser: ${contractData.eSignCompliance?.auditTrail?.browser}
- Device: ${contractData.eSignCompliance?.auditTrail?.device}
      `.trim();
      
      const blob = new Blob([formattedContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Contract_${contractData.companyName}_${new Date(contractData.signedAt).toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  // Handle contract BBA signing
  const handleContractBBASigning = async () => {
    setContractSigningLoading(true);
    setContractSigningError(null);
    
    try {
      if (!lead?.signedContract) {
        throw new Error('No contract data available');
      }

      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }

      // Get client info for audit trail
      const clientInfo = await ESignatureService.getClientInfo();
      
      // Save BBA signature using ESignatureService
      const bbaSignatureData = await ESignatureService.saveBBASignature(
        currentUser.uid,
        lead.signedContract.documentId,
        contractBBAData,
        clientInfo
      );

      if (!bbaSignatureData) {
        throw new Error('Failed to save BBA signature');
      }

      // Generate the complete document content with BBA variables injected
      let finalizedDocumentContent = '';
      if (lead.signedContract.templateElements && lead.signedContract.templateElements.length > 0) {
        // Process contract template elements (similar to NDA)
        lead.signedContract.templateElements.forEach(element => {
          if (element.type === 'header') {
            finalizedDocumentContent += `<h2>${renderContractContentWithVariables(element.content, element.id)}</h2>\n\n`;
          } else if (element.type === 'text') {
            finalizedDocumentContent += `<p>${renderContractContentWithVariables(element.content, element.id)}</p>\n\n`;
          } else if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
            finalizedDocumentContent += `<div>${renderContractContentWithVariables(element.content, element.id)}</div>\n\n`;
          }
        });
      } else {
        // Fallback: process the existing document content
        finalizedDocumentContent = renderContractContentWithVariables(lead.signedContract.documentContent || '');
      }

      // Create finalized contract with both client and BBA data
      const finalizedContract = {
        ...lead.signedContract,
        ...bbaSignatureData,
        isFullyExecuted: true,
        finalizedAt: new Date().toISOString(),
        finalizedDocumentContent: finalizedDocumentContent
      };

      // Update the lead account with finalized contract
      await updateAccount(leadId, {
        signedContract: finalizedContract,
        contractStatus: 'Fully Executed',
        contractLastUpdated: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });

      setContractBBASigned(true);
      setShowContractBBASigningModal(false);
      setShowContractModal(true);
      
      // Lead data will be automatically updated via real-time listener
      
    } catch (error) {
      console.error('Error signing contract as BBA:', error);
      setContractSigningError(error.message || 'Failed to sign and finalize contract. Please try again.');
    } finally {
      setContractSigningLoading(false);
    }
  };

  // Helper function to process document content with BBA signature variables
  const processDocumentContentWithBBASignatures = (content, bbaSignatureData) => {
    if (!content) return '';
    
    let displayContent = content;
    
    // If we have BBA signature data, replace the variables
    if (bbaSignatureData) {
      console.log('Processing BBA signature data:', bbaSignatureData);
      
      // Replace BBA signature section variables: [Name], [Title], [Signature], [Date]
      const bbaSignaturePattern = /\[(Name|Title|Signature|Date)\]/g;
      displayContent = displayContent.replace(bbaSignaturePattern, (match, variableName) => {
        let value = '';
        
        // Try multiple possible field names for each variable type
        switch (variableName) {
          case 'Name':
            value = bbaSignatureData.bbaName || 
                   bbaSignatureData.name || 
                   bbaSignatureData.bbaName || 
                   bbaSignatureData.signerName ||
                   bbaSignatureData.bbaSignerName || '';
            break;
          case 'Title':
            value = bbaSignatureData.bbaTitle || 
                   bbaSignatureData.title || 
                   bbaSignatureData.bbaTitle || 
                   bbaSignatureData.signerTitle ||
                   bbaSignatureData.bbaSignerTitle || '';
            break;
          case 'Signature':
            // Look for any field that contains 'signature' in the key name
            const signatureKeys = Object.keys(bbaSignatureData).filter(key => 
              key.toLowerCase().includes('signature') && bbaSignatureData[key] && bbaSignatureData[key].trim().length > 0
            );
            if (signatureKeys.length > 0) {
              value = bbaSignatureData[signatureKeys[0]];
            } else {
              value = bbaSignatureData.bbaSignature || 
                     bbaSignatureData.signature || 
                     bbaSignatureData.bbaSignature || '';
            }
            break;
          case 'Date':
            value = bbaSignatureData.bbaSignatureDate || 
                   bbaSignatureData.bbaDate ||
                   bbaSignatureData.date || 
                   bbaSignatureData.signatureDate ||
                   bbaSignatureData.bbaSignatureDate || 
                   new Date().toLocaleDateString();
            break;
          default:
            value = '';
        }
        
        console.log(`BBA Variable ${variableName}:`, value);
        
        if (value) {
          // Return signature as plain text without cursive styling
          return value;
        }
        
        // If no value found, keep the original placeholder
        return match;
      });
    }
    
    return displayContent;
  };

  // Render contract elements with subitems (similar to NDA rendering)
  const renderContractElements = (elements) => {
    if (!elements || !Array.isArray(elements)) return '';
    
    let renderedContent = elements.map(element => {
      if (element.type === 'header') {
        return `<h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 1rem; color: #1f2937;">${element.content}</h2>`;
      } else if (element.type === 'text') {
        let content = renderContractContentWithVariables(element.content, element.id);
        
        // Add subitems if they exist
        if (element.subItems && element.subItems.length > 0) {
          content += '<ul style="list-style-type: disc; margin-left: 1rem; margin-top: 0.5rem;">';
          element.subItems.forEach(subItem => {
            const subItemContent = renderContractContentWithVariables(subItem.text || 'Empty sub-item', element.id);
            content += `<li style="margin-bottom: 0.25rem;">${subItemContent}`;
            
            // Add nested subitems if they exist
            if (subItem.subItems && subItem.subItems.length > 0) {
              content += '<ul style="list-style-type: disc; margin-left: 1rem; margin-top: 0.25rem;">';
              subItem.subItems.forEach(nestedSubItem => {
                const nestedContent = renderContractContentWithVariables(nestedSubItem.text || 'Empty nested sub-item', element.id);
                content += `<li style="margin-bottom: 0.25rem;">${nestedContent}</li>`;
              });
              content += '</ul>';
            }
            
            content += '</li>';
          });
          content += '</ul>';
        }
        
        return `<div style="margin-bottom: 0.75rem;">${content}</div>`;
      } else if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
        // Handle form elements
        const value = element.value || '';
        if (element.type === 'signature' && value) {
          return `<div style="margin-bottom: 0.75rem;"><strong>${element.label || 'Signature'}:</strong> <span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span></div>`;
        } else if (element.type === 'initial' && value) {
          return `<div style="margin-bottom: 0.75rem;"><strong>${element.label || 'Initial'}:</strong> <span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span></div>`;
        } else if (value) {
          return `<div style="margin-bottom: 0.75rem;"><strong>${element.label || element.type}:</strong> ${value}</div>`;
        }
        return '';
      }
      return '';
    }).join('');
    
    // After rendering all elements, replace signature sections with formatted versions
    if (renderedContent.includes('Builders Breakthrough Advisors By:')) {
      // Get client signature data using the same logic as the modal
      const clientSignatureDisplay = contractData?.signature || '';
      
      // Get client name using the same priority logic as the modal
      let clientNameDisplay = 'Unknown';
      if (lead?.discovery) {
        if (lead.discovery.firstName && lead.discovery.lastName) {
          clientNameDisplay = `${lead.discovery.firstName} ${lead.discovery.lastName}`;
        } else {
          const discoveryName = lead.discovery.signerName || 
                              lead.discovery.firstName || 
                              lead.discovery.lastName ||
                              lead.discovery.name ||
                              lead.discovery.clientName ||
                              lead.discovery.contactName;
          if (discoveryName) {
            clientNameDisplay = discoveryName;
          }
        }
      } else if (lead?.firstName && lead?.lastName) {
        clientNameDisplay = `${lead.firstName} ${lead.lastName}`;
      } else if (lead?.contact?.name) {
        clientNameDisplay = lead.contact.name;
      } else if (lead?.contact?.firstName && lead?.contact?.lastName) {
        clientNameDisplay = `${lead.contact.firstName} ${lead.contact.lastName}`;
      } else if (contractData?.signerName && !contractData.signerName.includes('Chase') && !contractData.signerName.includes('Bank')) {
        clientNameDisplay = contractData.signerName;
      } else if (contractData?.companyName) {
        clientNameDisplay = contractData.companyName;
      }
      
      const clientTitleDisplay = contractData?.signerTitle || lead?.discovery?.signerTitle || '';
      const clientDateDisplay = contractData?.signedAt ? new Date(contractData.signedAt).toLocaleString() : '';
      
      const bbaData = contractData?.bbaSignature || contractBBAData || {};
      const bbaSignatureDisplay = bbaData.bbaSignature || bbaData.signature || '';
      const bbaNameDisplay = bbaData.bbaName || bbaData.name || '';
      const bbaTitleDisplay = bbaData.bbaTitle || bbaData.title || '';
      const bbaDateDisplay = bbaData.bbaSignatureDate || bbaData.bbaDate || bbaData.date || '';
      
      // Build formatted signature sections
      let formattedSignatures = '';
      
      if (clientSignatureDisplay) {
        formattedSignatures += '<div style="margin-bottom: 1.5rem; padding: 1rem; background-color: #f9fafb; border-radius: 0.5rem;">';
        formattedSignatures += '<h4 style="font-size: 1rem; font-weight: 600; margin-bottom: 0.75rem; color: #1f2937;">Client Electronic Signature</h4>';
        formattedSignatures += `<div style="font-size: 1.125rem; font-weight: 600; font-family: 'Dancing Script', 'Brush Script MT', cursive; margin-bottom: 0.5rem; color: #1f2937;">${clientSignatureDisplay}</div>`;
        if (clientNameDisplay && clientDateDisplay) {
          formattedSignatures += `<div style="font-size: 0.875rem; color: #6b7280; margin-top: 0.5rem;">Signed by: ${clientNameDisplay}${clientTitleDisplay ? ` (${clientTitleDisplay})` : ''} on ${clientDateDisplay}</div>`;
        }
        formattedSignatures += '</div>';
      }
      
      if (contractData?.isFullyExecuted && bbaSignatureDisplay) {
        formattedSignatures += '<div style="margin-bottom: 1.5rem; padding: 1rem; background-color: #eff6ff; border-radius: 0.5rem;">';
        formattedSignatures += '<h4 style="font-size: 1rem; font-weight: 600; margin-bottom: 0.75rem; color: #1e40af;">BBA Electronic Signature</h4>';
        formattedSignatures += `<div style="font-size: 1.125rem; font-weight: 600; font-family: 'Dancing Script', 'Brush Script MT', cursive; margin-bottom: 0.5rem; color: #1e40af;">${bbaSignatureDisplay}</div>`;
        if (bbaNameDisplay && bbaDateDisplay) {
          const formattedBBADate = bbaDateDisplay.includes('T') ? new Date(bbaDateDisplay).toLocaleString() : bbaDateDisplay;
          formattedSignatures += `<div style="font-size: 0.875rem; color: #3b82f6; margin-top: 0.5rem;">Signed by: ${bbaNameDisplay}${bbaTitleDisplay ? ` (${bbaTitleDisplay})` : ''} on ${formattedBBADate}</div>`;
        }
        formattedSignatures += '</div>';
      }
      
      if (formattedSignatures) {
        // Find the start of the signature section
        const signaturesIndex = renderedContent.search(/(?:<h[1-6][^>]*>)?\s*Signatures?\s*(?:<\/h[1-6]>)?|Builders Breakthrough Advisors By:/i);
        
        if (signaturesIndex !== -1) {
          const beforeSignatures = renderedContent.substring(0, signaturesIndex);
          const afterSignatures = renderedContent.substring(signaturesIndex);
          
          // Find where the signature section ends - look for "The remainder" or next major section
          const endPattern = /(?:The remainder|Contract Payment|Attachment|Payments:|Client account to be debited|Bank Name:|Account Number:|Routing Number:)/i;
          const endMatch = afterSignatures.search(endPattern);
          
          if (endMatch !== -1) {
            // Replace everything from signatures start to the end pattern
            const afterSignatureSection = afterSignatures.substring(endMatch);
            renderedContent = beforeSignatures + formattedSignatures + afterSignatureSection;
          } else {
            // If no clear end pattern, remove everything that looks like signature content
            // Remove all lines containing Name:, Title:, Signature:, Date:, or By: until we find non-signature content
            let cleanedAfter = afterSignatures;
            const signatureLinePattern = /(?:<[^>]*>)?\s*(?:Signatures?|Builders Breakthrough Advisors By:|[^\n<]+By:|Name:\s*[^\n<]+|Title:\s*[^\n<]+|Signature:\s*[^\n<]+|Date:\s*[^\n<]+)(?:<\/[^>]*>)?/gi;
            
            // Keep removing signature lines until we hit non-signature content
            let previousLength = cleanedAfter.length;
            let iterations = 0;
            while (iterations < 20) { // Safety limit
              cleanedAfter = cleanedAfter.replace(signatureLinePattern, '');
              if (cleanedAfter.length === previousLength) break; // No more matches
              previousLength = cleanedAfter.length;
              iterations++;
            }
            
            // Find the next real content
            const nextContentMatch = cleanedAfter.search(/(?:The remainder|Contract Payment|Attachment|Payments:|Client account|Bank|Account|Routing|☑)/i);
            if (nextContentMatch !== -1) {
              renderedContent = beforeSignatures + formattedSignatures + cleanedAfter.substring(nextContentMatch);
            } else {
              // Just append formatted signatures
              renderedContent = beforeSignatures + formattedSignatures;
            }
          }
        }
      }
    }
    
    return renderedContent;
  };

  // Render contract content with BBA variables injected (similar to NDA)
  const renderContractContentWithVariables = (content, elementId = null) => {
    if (!content) return '';
    
    let updatedContent = content;
    
    // First, replace client variables with their actual values from lead data and contract data
    const clientData = {
      // Use lead data first, then fall back to contract data
      companyName: lead?.companyName || lead?.discovery?.companyName || contractData?.companyName || '',
      state: lead?.state || lead?.discovery?.state || contractData?.state || '',
      companyAddress: lead?.streetAddress || lead?.discovery?.streetAddress || lead?.location || contractData?.companyAddress || '',
      city: lead?.city || lead?.discovery?.city || contractData?.city || '',
      zipCode: lead?.zipCode || lead?.discovery?.zipCode || contractData?.zipCode || '',
      businessStructure: lead?.discovery?.businessStructure || lead?.businessStructure || contractData?.businessStructure || '',
      signerName: lead?.contact?.name || lead?.discovery?.signerName || (lead?.discovery?.firstName && lead?.discovery?.lastName ? `${lead.discovery.firstName} ${lead.discovery.lastName}` : '') || contractData?.signerName || '',
      signerTitle: lead?.discovery?.signerTitle || contractData?.signerTitle || '',
      signature: contractData?.signature || '',
      initial: contractData?.initial || lead?.discovery?.initial || ''
    };

    if (clientData.companyName || clientData.signerName) {
      // Get contract-specific data - use system contract number if available
      let contractNumber = contractData?.contractNumber;
      
      // If no contract number, try to get it from the contract data structure
      if (!contractNumber) {
        // Check if contract number is stored in a different field
        contractNumber = contractData?.contractNumber || contractData?.contractId || contractData?.id;
      }
      
      // If still no contract number, try to extract from document ID as last resort
      if (!contractNumber && contractData?.documentId) {
        // Only use document ID if it looks like a contract number (not a UUID or timestamp)
        const docId = contractData.documentId;
        if (docId && !docId.includes('-') && !isNaN(parseInt(docId))) {
          contractNumber = docId;
        } else if (docId && docId.includes('-')) {
          // Extract the last part if it's a valid number
          const lastPart = docId.split('-').pop();
          if (!isNaN(parseInt(lastPart))) {
            contractNumber = lastPart;
          }
        }
      }
      
      if (!contractNumber) {
        contractNumber = 'N/A';
      }
      // Get program items - check programSelectionData first as it has the actual items
      let programItems = 'Not specified';
      if (lead?.programSelectionData?.items && Array.isArray(lead.programSelectionData.items) && lead.programSelectionData.items.length > 0) {
        // Format items like Contract.js does
        programItems = lead.programSelectionData.items
          .map(item => {
            const name = item.name || item.itemName || item.title || 'Item';
            const quantity = item.quantity || item.qty || 1;
            if (quantity === '0' || quantity === 0) {
              return name;
            } else {
              return `${name} (${quantity})`;
            }
          })
          .join(', ');
      } else if (lead?.programSelectionData?.contractItems && Array.isArray(lead.programSelectionData.contractItems) && lead.programSelectionData.contractItems.length > 0) {
        // Alternative field name
        programItems = lead.programSelectionData.contractItems
          .map(item => {
            const name = item.name || item.itemName || item.title || 'Item';
            const quantity = item.quantity || item.qty || 1;
            if (quantity === '0' || quantity === 0) {
              return name;
            } else {
              return `${name} (${quantity})`;
            }
          })
          .join(', ');
      } else {
        // Fallback to program name
        programItems = contractData?.program || 
                      lead?.selectedProgram?.name || 
                      lead?.programSelectionData?.programName ||
                      lead?.program || 
                      'Not specified';
      }
      
      const bankingInfo = contractData?.bankingInfo || lead?.signedContract?.bankingInfo || {};
      
      // Get monthly price from multiple sources - prioritize programSelectionData
      let monthlyPrice = null;
      
      // First, try programSelectionData which should have the price
      if (lead?.programSelectionData) {
        const programData = lead.programSelectionData;
        const basePrice = programData.pricePerMonth || 
                         programData.monthlyPrice || 
                         programData.price ||
                         programData.monthlyPrice ||
                         (programData.selectedProgram && (programData.selectedProgram.pricePerMonth || programData.selectedProgram.monthlyPrice || programData.selectedProgram.price));
        
        if (basePrice) {
          const billingCycle = contractData?.billingCycle || lead?.billingCycle || programData.billingCycle || 'monthly';
          
          if (billingCycle === 'quarterly') {
            monthlyPrice = Math.round(basePrice * 3 * 0.9); // 10% discount
          } else if (billingCycle === 'annual') {
            monthlyPrice = Math.round(basePrice * 12 * 0.8); // 20% discount
          } else {
            monthlyPrice = basePrice; // Monthly
          }
        }
      }
      
      // If still no price, check other sources
      if (!monthlyPrice) {
        monthlyPrice = contractData?.monthlyPrice || 
                      contractData?.price || 
                      contractData?.monthlyAmount ||
                      contractData?.amount ||
                      lead?.signedContract?.monthlyPrice ||
                      lead?.signedContract?.price ||
                      lead?.signedContract?.monthlyAmount ||
                      lead?.signedContract?.amount ||
                      lead?.program?.monthlyAmount ||
                      lead?.program?.price ||
                      lead?.discovery?.monthlyPrice ||
                      lead?.discovery?.price ||
                      lead?.discovery?.monthlyAmount ||
                      lead?.discovery?.amount ||
                      null;
      }
      
      // If still no price, try selectedProgram
      if (!monthlyPrice && lead?.selectedProgram) {
        monthlyPrice = lead.selectedProgram.pricePerMonth || 
                      lead.selectedProgram.monthlyPrice || 
                      lead.selectedProgram.price ||
                      lead.selectedProgram.monthlyAmount ||
                      lead.selectedProgram.amount;
      }
      
      // Last resort: try to get from contract program data
      if (!monthlyPrice && contractData?.programId) {
        const programId = contractData.programId;
        const programName = contractData.program;
        
        if (lead?.programs && Array.isArray(lead.programs)) {
          const program = lead.programs.find(p => p.id === programId || p.name === programName);
          if (program) {
            monthlyPrice = program.pricePerMonth || program.monthlyPrice || program.price;
          }
        }
      }
      
      // Format monthly price if it's a number
      let formattedMonthlyPrice = '$0.00';
      if (monthlyPrice) {
        if (typeof monthlyPrice === 'number') {
          formattedMonthlyPrice = `$${monthlyPrice.toFixed(2)}`;
        } else if (typeof monthlyPrice === 'string') {
          // If it's already a string, check if it starts with $, if not add it
          formattedMonthlyPrice = monthlyPrice.startsWith('$') ? monthlyPrice : `$${monthlyPrice}`;
        } else {
          formattedMonthlyPrice = monthlyPrice.toString();
        }
      }
      
      // Debug logging
      console.log('Program and price lookup:', {
        programSelectionData: lead?.programSelectionData,
        programItems: programItems,
        contractDataMonthlyPrice: contractData?.monthlyPrice,
        contractDataPrice: contractData?.price,
        signedContractMonthlyPrice: lead?.signedContract?.monthlyPrice,
        selectedProgram: lead?.selectedProgram,
        billingCycle: contractData?.billingCycle || lead?.billingCycle,
        finalMonthlyPrice: monthlyPrice,
        formattedMonthlyPrice: formattedMonthlyPrice
      });
      
      // Get client initials - try multiple sources
      const clientInitials = contractData?.initial || 
                            contractData?.initials ||
                            lead?.discovery?.initial || 
                            lead?.discovery?.initials ||
                            (clientData.signerName ? clientData.signerName.split(' ').map(n => n[0]).join('').toUpperCase() : '');
      
      // Replace common client variables
      const clientVariables = {
        'Company Name': clientData.companyName,
        'Company_Name': clientData.companyName,
        'COMPANY_NAME': clientData.companyName,
        'State': clientData.state,
        'STATE': clientData.state,
        'Company Address': clientData.companyAddress,
        'Company_Address': clientData.companyAddress,
        'COMPANY_ADDRESS': clientData.companyAddress,
        'Address': clientData.companyAddress,
        'ADDRESS': clientData.companyAddress,
        'Street Address': clientData.companyAddress,
        'Street_Address': clientData.companyAddress,
        'STREET_ADDRESS': clientData.companyAddress,
        'City': clientData.city,
        'CITY': clientData.city,
        'Zip Code': clientData.zipCode,
        'Zip_Code': clientData.zipCode,
        'ZIP_CODE': clientData.zipCode,
        'Business Structure': clientData.businessStructure,
        'Business_Structure': clientData.businessStructure,
        'BUSINESS_STRUCTURE': clientData.businessStructure,
        'Signer Name': clientData.signerName,
        'Signer_Name': clientData.signerName,
        'SIGNER_NAME': clientData.signerName,
        'Signer Title': clientData.signerTitle,
        'Signer_Title': clientData.signerTitle,
        'SIGNER_TITLE': clientData.signerTitle,
        'SIGNATURE': clientData.signature,
        'INITIAL': clientInitials,
        'INITIALS': clientInitials,
        'Initial': clientInitials,
        'Initials': clientInitials,
        'Your_Initials': clientInitials,
        'YOUR_INITIALS': clientInitials,
        'Initials_Two': clientInitials,
        'INITIALS_TWO': clientInitials,
        'Initials_Three': clientInitials,
        'INITIALS_THREE': clientInitials,
        'Current Date': new Date().toLocaleDateString(),
        'CURRENT_DATE': new Date().toLocaleDateString(),
        'Date': new Date().toLocaleDateString(),
        'DATE': new Date().toLocaleDateString(),
        // Contract-specific variables
        'Contract_Number': contractNumber,
        'CONTRACT_NUMBER': contractNumber,
        'Program_Items': programItems,
        'PROGRAM_ITEMS': programItems,
        'Program Name': programItems,
        'PROGRAM_NAME': programItems,
        'Bank_Name': bankingInfo.bankName || lead?.achInfo?.bankName || '',
        'BANK_NAME': bankingInfo.bankName || lead?.achInfo?.bankName || '',
        'Bank_Address': bankingInfo.bankAddress || lead?.achInfo?.bankAddress || '',
        'BANK_ADDRESS': bankingInfo.bankAddress || lead?.achInfo?.bankAddress || '',
        'Account_Number': bankingInfo.accountNumber || lead?.achInfo?.accountNumber || '',
        'ACCOUNT_NUMBER': bankingInfo.accountNumber || lead?.achInfo?.accountNumber || '',
        'Routing_Number': bankingInfo.routingNumber || lead?.achInfo?.routingNumber || '',
        'ROUTING_NUMBER': bankingInfo.routingNumber || lead?.achInfo?.routingNumber || '',
        'Checkbox': '☑', // Checkbox symbol
        'CHECKBOX': '☑',
        // Additional contract variables
        'Start_Date': contractData?.startDate ? new Date(contractData.startDate).toLocaleDateString() : new Date().toLocaleDateString(),
        'START_DATE': contractData?.startDate ? new Date(contractData.startDate).toLocaleDateString() : new Date().toLocaleDateString(),
        'Billing_Cycle': contractData?.billingCycle || 'Monthly',
        'BILLING_CYCLE': contractData?.billingCycle || 'Monthly',
        'Monthly_Price': formattedMonthlyPrice,
        'MONTHLY_PRICE': formattedMonthlyPrice,
        'Monthly Price': formattedMonthlyPrice,
        'MONTHLY PRICE': formattedMonthlyPrice,
        'Price': formattedMonthlyPrice,
        'PRICE': formattedMonthlyPrice,
        'Amount': formattedMonthlyPrice,
        'AMOUNT': formattedMonthlyPrice,
        'Monthly_Amount': formattedMonthlyPrice,
        'MONTHLY_AMOUNT': formattedMonthlyPrice,
        'Monthly Amount': formattedMonthlyPrice,
        'MONTHLY AMOUNT': formattedMonthlyPrice,
        'Payment_Amount': formattedMonthlyPrice,
        'PAYMENT_AMOUNT': formattedMonthlyPrice,
        'Payment Amount': formattedMonthlyPrice,
        'PAYMENT AMOUNT': formattedMonthlyPrice,
        'Billing_Days': contractData?.billingDays || '30',
        'BILLING_DAYS': contractData?.billingDays || '30'
      };
      
      // Replace client variables in bracket notation
      Object.entries(clientVariables).forEach(([key, value]) => {
        // Always replace price/amount variables even if $0.00, and always replace other variables if they have a value
        const isPriceVariable = key.toLowerCase().includes('price') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('payment');
        // For price variables, always replace (even with $0.00). For others, only replace if value exists
        if (isPriceVariable || value) {
          const pattern = new RegExp(`\\[${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
          updatedContent = updatedContent.replace(pattern, (match) => {
            // For price variables, always use formattedMonthlyPrice to ensure consistency
            const replacementValue = isPriceVariable ? formattedMonthlyPrice : value;
            // Apply cursive styling for signature text
            if (key.toLowerCase().includes('signature') || key.toLowerCase().includes('sign')) {
              return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${replacementValue}</span>`;
            }
            return replacementValue;
          });
        }
      });
    }
    
    // Replace BBA-specific variables: [BBA_Name], [BBA_Title], [BBA_Signature], [BBA_Date]
    const bbaData = contractData?.bbaSignature || contractBBAData || {};
    const bbaVariables = {
      'BBA_Name': bbaData.bbaName || bbaData.name || '',
      'BBA_TITLE': bbaData.bbaName || bbaData.name || '',
      'BBA_Title': bbaData.bbaTitle || bbaData.title || '',
      'BBA_TITLE': bbaData.bbaTitle || bbaData.title || '',
      'BBA_Signature': bbaData.bbaSignature || bbaData.signature || '',
      'BBA_SIGNATURE': bbaData.bbaSignature || bbaData.signature || '',
      'BBA_Date': bbaData.bbaSignatureDate || bbaData.bbaDate || bbaData.date || new Date().toLocaleDateString(),
      'BBA_DATE': bbaData.bbaSignatureDate || bbaData.bbaDate || bbaData.date || new Date().toLocaleDateString()
    };
    
    // Replace BBA variables first
    Object.entries(bbaVariables).forEach(([key, value]) => {
      if (value) {
        const pattern = new RegExp(`\\[${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
        updatedContent = updatedContent.replace(pattern, (match) => {
          // Apply cursive styling for signature text
          if (key.toLowerCase().includes('signature')) {
            return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
          }
          return value;
        });
      }
    });
    
    // Replace signature variables: [Name], [Title], [Signature], [Date]
    // Use a simple approach that works on individual elements
    const clientSignatureData = {
      name: clientData.signerName || contractData?.signerName || '',
      title: clientData.signerTitle || contractData?.signerTitle || '',
      signature: clientData.signature || contractData?.signature || '',
      date: contractData?.signedAt ? new Date(contractData.signedAt).toLocaleDateString() : new Date().toLocaleDateString()
    };
    
    // Replace signature variables with improved section detection
    // Check if this is in a client signature section by looking for company name pattern
    const companyNamePattern = clientData.companyName ? new RegExp(clientData.companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i') : null;
    const hasCompanyName = companyNamePattern?.test(updatedContent);
    const hasCompanyNameBy = updatedContent.includes('[Company_Name] By:') || updatedContent.includes('By:');
    const hasBBASection = updatedContent.includes('Builders Breakthrough Advisors By:') || updatedContent.includes('Builders Breakthrough Advisors');
    
    const signaturePattern = /\[(Name|Title|Signature|Date)\]/g;
    updatedContent = updatedContent.replace(signaturePattern, (match, variableName) => {
      let value = '';
      
      // Determine if this is a client or BBA section
      // Look for context around the variable - if it's after "Builders Breakthrough Advisors By:" it's BBA
      // If it's after company name or "[Company_Name] By:" it's client
      const matchIndex = updatedContent.indexOf(match);
      const beforeMatch = updatedContent.substring(Math.max(0, matchIndex - 200), matchIndex);
      const afterMatch = updatedContent.substring(matchIndex, Math.min(updatedContent.length, matchIndex + 200));
      const context = beforeMatch + afterMatch;
      
      // Check if this is clearly a BBA section
      const isBBASection = context.includes('Builders Breakthrough Advisors By:') || 
                          (beforeMatch.includes('Builders Breakthrough Advisors') && !hasCompanyName);
      
      // Check if this is clearly a client section
      const isClientSignatureSection = (hasCompanyName && hasCompanyNameBy && !isBBASection) ||
                                       (context.includes('[Company_Name] By:') && !isBBASection) ||
                                       (hasCompanyNameBy && !hasBBASection && hasCompanyName);
      
      if (isClientSignatureSection) {
        // Use client data for client signature sections
        switch (variableName) {
          case 'Name':
            value = clientSignatureData.name || '';
            break;
          case 'Title':
            value = clientSignatureData.title || '';
            break;
          case 'Signature':
            value = clientSignatureData.signature || '';
            break;
          case 'Date':
            value = clientSignatureData.date || new Date().toLocaleDateString();
            break;
        }
      } else {
        // Use BBA data for BBA signature sections
        switch (variableName) {
          case 'Name':
            value = bbaData.bbaName || bbaData.name || '';
            break;
          case 'Title':
            value = bbaData.bbaTitle || bbaData.title || '';
            break;
          case 'Signature':
            value = bbaData.bbaSignature || bbaData.signature || '';
            break;
          case 'Date':
            value = bbaData.bbaSignatureDate || bbaData.bbaDate || bbaData.date || new Date().toLocaleDateString();
            break;
        }
      }
      
      if (value) {
        // Apply cursive styling for signature text
        if (variableName.toLowerCase().includes('signature')) {
          return `<span style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
        }
        return value;
      }
      
      return match;
    });
    
    // Replace signature sections with formatted signature display
    // Get client signature data for display
    const clientSignatureDisplay = contractData?.signature || clientSignatureData.signature || '';
    const clientNameDisplay = clientSignatureData.name || 'Unknown';
    const clientTitleDisplay = clientSignatureData.title || '';
    const clientDateDisplay = contractData?.signedAt ? new Date(contractData.signedAt).toLocaleString() : '';
    
    // Get BBA signature data for display
    const bbaSignatureDisplay = bbaData.bbaSignature || bbaData.signature || '';
    const bbaNameDisplay = bbaData.bbaName || bbaData.name || '';
    const bbaTitleDisplay = bbaData.bbaTitle || bbaData.title || '';
    const bbaDateDisplay = bbaData.bbaSignatureDate || bbaData.bbaDate || bbaData.date || '';
    
    // Build formatted signature sections
    let formattedSignatures = '';
    
    // Client Electronic Signature section
    if (clientSignatureDisplay) {
      formattedSignatures += '<div style="margin-bottom: 1.5rem; padding: 1rem; background-color: #f9fafb; border-radius: 0.5rem;">';
      formattedSignatures += '<h4 style="font-size: 1rem; font-weight: 600; margin-bottom: 0.75rem; color: #1f2937;">Client Electronic Signature</h4>';
      formattedSignatures += `<div style="font-size: 1.125rem; font-weight: 600; font-family: 'Dancing Script', 'Brush Script MT', cursive; margin-bottom: 0.5rem; color: #1f2937;">${clientSignatureDisplay}</div>`;
      if (clientNameDisplay && clientDateDisplay) {
        formattedSignatures += `<div style="font-size: 0.875rem; color: #6b7280; margin-top: 0.5rem;">Signed by: ${clientNameDisplay}${clientTitleDisplay ? ` (${clientTitleDisplay})` : ''} on ${clientDateDisplay}</div>`;
      }
      formattedSignatures += '</div>';
    }
    
    // BBA Electronic Signature section
    if (contractData?.isFullyExecuted && bbaSignatureDisplay) {
      formattedSignatures += '<div style="margin-bottom: 1.5rem; padding: 1rem; background-color: #eff6ff; border-radius: 0.5rem;">';
      formattedSignatures += '<h4 style="font-size: 1rem; font-weight: 600; margin-bottom: 0.75rem; color: #1e40af;">BBA Electronic Signature</h4>';
      formattedSignatures += `<div style="font-size: 1.125rem; font-weight: 600; font-family: 'Dancing Script', 'Brush Script MT', cursive; margin-bottom: 0.5rem; color: #1e40af;">${bbaSignatureDisplay}</div>`;
      if (bbaNameDisplay && bbaDateDisplay) {
        const formattedBBADate = bbaDateDisplay.includes('T') ? new Date(bbaDateDisplay).toLocaleString() : bbaDateDisplay;
        formattedSignatures += `<div style="font-size: 0.875rem; color: #3b82f6; margin-top: 0.5rem;">Signed by: ${bbaNameDisplay}${bbaTitleDisplay ? ` (${bbaTitleDisplay})` : ''} on ${formattedBBADate}</div>`;
      }
      formattedSignatures += '</div>';
    }
    
    // Detect and replace signature sections - try multiple patterns
    // Look for signature sections that contain "Builders Breakthrough Advisors By:" followed by signature fields
    // and then client signature section
    if (formattedSignatures && updatedContent.includes('Builders Breakthrough Advisors By:')) {
      // Find the start of the signature section (look for "Signatures" heading or start of BBA section)
      const signaturesIndex = updatedContent.search(/(?:<h[1-6][^>]*>)?\s*Signatures?\s*(?:<\/h[1-6]>)?|Builders Breakthrough Advisors By:/i);
      
      if (signaturesIndex !== -1) {
        // Find where the signature section ends (look for next major section or end of document)
        const afterSignatures = updatedContent.substring(signaturesIndex);
        const endPattern = /(?:<h[1-6]|The remainder|Contract Payment|Attachment|Payments:|Client account to be debited)/i;
        const endMatch = afterSignatures.search(endPattern);
        
        let signatureSection = '';
        if (endMatch !== -1) {
          // Extract the signature section
          signatureSection = afterSignatures.substring(0, endMatch);
        } else {
          // If no end pattern found, take everything after signatures
          signatureSection = afterSignatures;
        }
        
        // Check if this section contains both BBA and client signature blocks
        if (signatureSection.includes('Builders Breakthrough Advisors By:') && 
            (signatureSection.includes('By:') || signatureSection.includes('Name:') || signatureSection.includes('Title:'))) {
          // Replace the signature section with formatted version
          const beforeSignatures = updatedContent.substring(0, signaturesIndex);
          const afterSignatureSection = endMatch !== -1 ? afterSignatures.substring(endMatch) : '';
          updatedContent = beforeSignatures + formattedSignatures + afterSignatureSection;
        }
      }
    }
    
    return updatedContent;
  };

  // Open contract BBA signing modal
  const handleOpenContractBBASigning = () => {
    setContractSigningError(null);
    setShowContractBBASigningModal(true);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-xl">Lead not found</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isMobile ? 'p-2' : isTablet ? 'p-3' : 'p-4'} relative`}>
      {/* Same ambient BBA background as the Dashboard and Leads board */}
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className={`flex ${isMobile ? 'flex-col gap-3' : 'justify-between'} items-center ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <BackButton />
        
        {/* Company Name - Centered */}
        <div className="flex flex-col items-center space-y-2">
          <h1 className={`${isMobile ? 'text-2xl' : isTablet ? 'text-3xl' : 'text-4xl'} font-bold text-gray-900 dark:text-white`}>{lead.companyName || lead.company}</h1>
          <div className="relative">
            <span className="px-4 py-1 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-sm font-medium">
              {lead.stage || 'No Stage Set'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {!isEditMode ? (
            <button
              onClick={() => setIsEditMode(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={async () => {
                  try {
                    const cleanedData = removeUndefinedValues(lead);
                    await updateAccount(leadId, cleanedData);
                    setIsEditMode(false);
                    setEditingField(null);
                    setEditingAssessmentCard(null);
                    setAssessmentEditData({});
                  } catch (error) {
                    console.error('Error saving lead:', error);
                    setError('Failed to save changes');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold shadow-sm hover:bg-red-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-red-200 dark:border-red-800/60 bg-white dark:bg-gray-800 text-sm font-medium text-red-600 dark:text-red-400 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Lead</span>
              </button>
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setEditingField(null);
                  setEditingAssessmentCard(null);
                  setAssessmentEditData({});
                }}
                className="inline-flex items-center px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content area with tabs */}
      <div className="flex gap-6">
        {/* Main content */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-5 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
            {tabs.map((tab) => {
              const selected = activeTab === tab.toLowerCase();
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab.toLowerCase())}
                  className={`shrink-0 px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap transition-colors ${
                    selected
                      ? 'border-red-600 bg-red-600 text-white shadow-sm'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeTab === 'summary' && (
            <div className="space-y-6">
              {/* Lead Timeline */}
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Received Date */}
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-4 text-center border border-gray-200 dark:border-gray-800">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Received Date</h3>
                    {showReceivedDatePicker ? (
                      <div className="mt-1">
                        <input
                          type="date"
                          value={lead.receivedDate 
                            ? (typeof lead.receivedDate === 'string' 
                              ? lead.receivedDate.split('T')[0] 
                              : lead.receivedDate.toDate 
                              ? lead.receivedDate.toDate().toISOString().split('T')[0]
                              : new Date(lead.receivedDate.seconds * 1000).toISOString().split('T')[0])
                            : lead.createdAt 
                            ? (lead.createdAt.toDate 
                              ? lead.createdAt.toDate().toISOString().split('T')[0]
                              : new Date(lead.createdAt.seconds * 1000).toISOString().split('T')[0])
                            : new Date().toISOString().split('T')[0]}
                          onChange={async (e) => {
                            try {
                              const selectedDate = e.target.value;
                              const dateObj = new Date(selectedDate);
                              dateObj.setHours(0, 0, 0, 0);
                              const timestamp = Timestamp.fromDate(dateObj);
                              const updatedData = { ...lead, receivedDate: timestamp };
                              setLead(updatedData);
                              await updateAccount(leadId, updatedData);
                              setShowReceivedDatePicker(false);
                            } catch (error) {
                              console.error('Error updating received date:', error);
                            }
                          }}
                          onBlur={() => setShowReceivedDatePicker(false)}
                          className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                          autoFocus
                        />
                      </div>
                    ) : (
                      <div 
                        onClick={() => setShowReceivedDatePicker(true)}
                        className={`flex items-center justify-center gap-2 cursor-pointer hover:opacity-75 transition-opacity ${
                          lead.receivedDate || lead.createdAt ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'
                        }`}
                        title="Click to edit date"
                      >
                        <Calendar className="w-4 h-4" />
                        <span className="text-lg font-semibold">
                          {lead.receivedDate 
                            ? (typeof lead.receivedDate === 'string' 
                              ? new Date(lead.receivedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : lead.receivedDate.toDate 
                              ? lead.receivedDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : new Date(lead.receivedDate.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))
                            : lead.createdAt 
                            ? (lead.createdAt.toDate 
                              ? lead.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : new Date(lead.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))
                            : 'Not set'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Discovery Call Date */}
                  <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-4 text-center border border-gray-200 dark:border-gray-800">
                    {/* Discovery Call kicks off the assessment right from its own card */}
                    <button
                      type="button"
                      onClick={() => navigate(`/crm/assessmenttemplate/${leadId}`)}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-2.5 py-0.5 rounded-full transition-colors"
                      title="Start the discovery call assessment"
                    >
                      Start
                    </button>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Discovery Call</h3>
                    {showDiscoveryCallDatePicker ? (
                      <div className="mt-1 space-y-2">
                        <input
                          type="date"
                          value={lead?.discoveryCallDate && lead.discoveryCallDate !== 'TBD'
                            ? (typeof lead.discoveryCallDate === 'string' 
                              ? (lead.discoveryCallDate.includes('T') 
                                ? lead.discoveryCallDate.split('T')[0] 
                                : lead.discoveryCallDate)
                              : new Date(lead.discoveryCallDate).toISOString().split('T')[0])
                            : ''}
                          onChange={async (e) => {
                            try {
                              const selectedDate = e.target.value;
                              if (selectedDate) {
                                const updatedData = { ...lead, discoveryCallDate: selectedDate };
                                setLead(updatedData);
                                await updateAccount(leadId, updatedData);
                              }
                              setShowDiscoveryCallDatePicker(false);
                            } catch (error) {
                              console.error('Error updating discovery call date:', error);
                            }
                          }}
                          onBlur={() => setShowDiscoveryCallDatePicker(false)}
                          className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                          autoFocus
                        />
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={async () => {
                            try {
                              const updatedData = { ...lead, discoveryCallDate: 'TBD' };
                              setLead(updatedData);
                              await updateAccount(leadId, updatedData);
                              setShowDiscoveryCallDatePicker(false);
                            } catch (error) {
                              console.error('Error setting discovery call to TBD:', error);
                            }
                          }}
                          className="w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 transition-colors"
                        >
                          Set to TBD
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => setShowDiscoveryCallDatePicker(true)}
                        className={`flex items-center justify-center gap-2 cursor-pointer hover:opacity-75 transition-opacity ${
                          lead?.discoveryCallDate && lead.discoveryCallDate !== 'TBD' ? 'text-green-600 dark:text-green-400' : 
                          lead?.discoveryCallDate === 'TBD' ? 'text-orange-600 dark:text-orange-400' : 
                          'text-gray-900 dark:text-white'
                        }`}
                        title="Click to edit date"
                      >
                        <Calendar className="w-4 h-4" />
                        <span className="text-lg font-semibold">
                          {lead?.discoveryCallDate 
                            ? (lead.discoveryCallDate === 'TBD' 
                              ? 'TBD'
                              : (() => {
                                  const dateStr = lead.discoveryCallDate;
                                  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                                    const [year, month, day] = dateStr.split('-').map(Number);
                                    const date = new Date(year, month - 1, day);
                                    return formatDate(date.toISOString());
                                  }
                                  return formatDate(dateStr);
                                })())
                            : 'Not set'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Analysis Call Date (stored as onboardingCallDate) */}
                  <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-4 text-center border border-gray-200 dark:border-gray-800">
                    {/* Analysis Call opens the assessment analysis right from its own card */}
                    <button
                      type="button"
                      onClick={() => navigate(`/crm/lead/${leadId}/assessment-analysis`)}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-2.5 py-0.5 rounded-full transition-colors"
                      title="Open the assessment analysis"
                    >
                      Start
                    </button>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Analysis Call</h3>
                    {showOnboardingCallDatePicker ? (
                      <div className="mt-1 space-y-2">
                        <input
                          type="date"
                          value={lead?.onboardingCallDate && lead.onboardingCallDate !== 'TBD'
                            ? (typeof lead.onboardingCallDate === 'string'
                              ? (lead.onboardingCallDate.includes('T')
                                ? lead.onboardingCallDate.split('T')[0]
                                : lead.onboardingCallDate)
                              : new Date(lead.onboardingCallDate).toISOString().split('T')[0])
                            : ''}
                          onChange={async (e) => {
                            try {
                              const selectedDate = e.target.value;
                              if (selectedDate) {
                                const updatedData = { ...lead, onboardingCallDate: selectedDate };
                                setLead(updatedData);
                                await updateAccount(leadId, updatedData);
                              }
                              setShowOnboardingCallDatePicker(false);
                            } catch (error) {
                              console.error('Error updating onboarding call date:', error);
                            }
                          }}
                          onBlur={() => setShowOnboardingCallDatePicker(false)}
                          className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                          autoFocus
                        />
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={async () => {
                            try {
                              const updatedData = { ...lead, onboardingCallDate: 'TBD' };
                              setLead(updatedData);
                              await updateAccount(leadId, updatedData);
                              setShowOnboardingCallDatePicker(false);
                            } catch (error) {
                              console.error('Error setting analysis call to TBD:', error);
                            }
                          }}
                          className="w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 transition-colors"
                        >
                          Set to TBD
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => setShowOnboardingCallDatePicker(true)}
                        className={`flex items-center justify-center gap-2 cursor-pointer hover:opacity-75 transition-opacity ${
                          lead?.onboardingCallDate && lead.onboardingCallDate !== 'TBD' ? 'text-green-600 dark:text-green-400' :
                          lead?.onboardingCallDate === 'TBD' ? 'text-orange-600 dark:text-orange-400' :
                          'text-gray-900 dark:text-white'
                        }`}
                        title="Click to edit date"
                      >
                        <Calendar className="w-4 h-4" />
                        <span className="text-lg font-semibold">
                          {lead?.onboardingCallDate
                            ? (lead.onboardingCallDate === 'TBD'
                              ? 'TBD'
                              : (() => {
                                  const dateStr = lead.onboardingCallDate;
                                  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                                    const [year, month, day] = dateStr.split('-').map(Number);
                                    const date = new Date(year, month - 1, day);
                                    return formatDate(date.toISOString());
                                  }
                                  return formatDate(dateStr);
                                })())
                            : 'Not set'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {leadCalendlySyncing ? (
                  <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">Syncing Calendly…</p>
                ) : null}
              </div>

              {/* Lead Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 text-center flex flex-col justify-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Signing chance</h3>
                  {(isEditMode || isEditingAssessmentModal) ? (
                    <div className="mt-1 flex justify-center items-center gap-3" role="group" aria-label="Signing chance">
                      {SIGNING_CHANCE_VALUES.map((value) => {
                        const active = normalizeLeadSigningChance(lead) === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setLead({ ...lead, signingChance: value })}
                            className={`h-10 w-10 rounded-full transition-transform focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 ${
                              active ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-white scale-110' : 'opacity-70 hover:opacity-100'
                            }`}
                            style={{ backgroundColor: SIGNING_CHANCE_META[value].dotColor }}
                            title={SIGNING_CHANCE_META[value].label}
                            aria-pressed={active}
                            aria-label={SIGNING_CHANCE_META[value].label}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    (() => {
                      const ch = normalizeLeadSigningChance(lead);
                      return (
                        <div className="relative flex justify-center mt-1">
                          <button
                            type="button"
                            onClick={() => setShowSigningChancePopup((prev) => !prev)}
                            className="h-9 w-9 rounded-full ring-2 ring-gray-200 dark:ring-gray-600 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
                            style={{ backgroundColor: SIGNING_CHANCE_META[ch].dotColor }}
                            title={`Signing chance: ${SIGNING_CHANCE_META[ch].label} — click to change`}
                            aria-haspopup="true"
                            aria-expanded={showSigningChancePopup}
                            aria-label={`Signing chance: ${SIGNING_CHANCE_META[ch].label}. Click to change.`}
                          />
                          {showSigningChancePopup && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowSigningChancePopup(false)}
                              />
                              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3">
                                {SIGNING_CHANCE_VALUES.map((value) => {
                                  const active = ch === value;
                                  return (
                                    <button
                                      key={value}
                                      type="button"
                                      onClick={async () => {
                                        const updatedData = { ...lead, signingChance: value };
                                        setLead(updatedData);
                                        setShowSigningChancePopup(false);
                                        try {
                                          await updateAccount(leadId, updatedData);
                                        } catch (error) {
                                          console.error('Error updating signing chance:', error);
                                          setError('Failed to update signing chance');
                                        }
                                      }}
                                      className={`h-9 w-9 rounded-full transition-transform focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 ${
                                        active ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-white scale-110' : 'opacity-70 hover:opacity-100'
                                      }`}
                                      style={{ backgroundColor: SIGNING_CHANCE_META[value].dotColor }}
                                      title={SIGNING_CHANCE_META[value].label}
                                      aria-pressed={active}
                                      aria-label={SIGNING_CHANCE_META[value].label}
                                    />
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>
                {/* Projected Program */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 text-center flex flex-col justify-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Projected Program</h3>
                  {projectedProgramSummary ? (
                    <>
                      <div className="text-2xl font-semibold text-gray-900 dark:text-white truncate" title={projectedProgramSummary.name}>
                        {projectedProgramSummary.name}
                      </div>
                      {projectedProgramSummary.investment && (
                        <div className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">
                          {projectedProgramSummary.investment}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">No program proposed yet</div>
                  )}
                </div>

                {/* Days in Pipeline — with the Needs Contacted tracker folded in */}
                {(() => {
                  const contactProgress = getContactProgress();
                  const disabled = contactProgress?.disabled === true;
                  const isFlagged = !disabled && !!contactProgress?.needsContact;
                  const noContactNotes = contactProgress?.noContactNotes;
                  const daysUntilFlagged = contactProgress?.daysUntilFlagged || 0;
                  const daysElapsed = noContactNotes ? contactProgress?.daysSinceCreation : contactProgress?.daysSinceContact;

                  return (
                    <div
                      className={`rounded-lg shadow-sm p-4 text-center ${
                        isFlagged
                          ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-400'
                          : 'bg-white dark:bg-gray-800'
                      }`}
                    >
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Days in Pipeline</h3>
                      <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {lead.createdAt ? Math.floor((new Date() - new Date(lead.createdAt.seconds * 1000)) / (1000 * 60 * 60 * 24)) : 0}
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Flag className={`w-4 h-4 ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-red-600 dark:text-red-400'}`} />
                          <span
                            className={`text-sm font-semibold ${
                              disabled
                                ? 'text-gray-500 dark:text-gray-400'
                                : isFlagged
                                  ? 'text-red-700 dark:text-red-300'
                                  : 'text-gray-900 dark:text-white'
                            }`}
                          >
                            {isFlagged ? 'NEEDS CONTACTED' : 'Needs Contacted'}
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                const newValue = !lead.needsContactDisabled;
                                const updatedData = { ...lead, needsContactDisabled: newValue };
                                await updateAccount(leadId, updatedData);
                                setLead(updatedData);
                              } catch (error) {
                                console.error('Error toggling Needs Contacted:', error);
                              }
                            }}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                              lead.needsContactDisabled === true ? 'bg-gray-300 dark:bg-gray-600' : 'bg-red-600 dark:bg-red-500'
                            }`}
                            title={lead.needsContactDisabled === true ? 'Click to enable Needs Contacted' : 'Click to disable Needs Contacted'}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                lead.needsContactDisabled === true ? 'translate-x-1' : 'translate-x-[1.125rem]'
                              }`}
                            />
                          </button>
                        </div>

                        {disabled ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Disabled for this lead</p>
                        ) : !contactProgress || daysUntilFlagged === 0 ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            No contact threshold set. Add a contact note to track contact history.
                          </p>
                        ) : (
                          <div
                            className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2"
                            title={
                              isFlagged
                                ? 'Flagged — needs contact'
                                : `${
                                    noContactNotes
                                      ? `No contact yet${daysElapsed !== null ? ` · ${daysElapsed}d since created` : ''}`
                                      : `${daysElapsed}d since last contact`
                                  } · ${Math.max(0, daysUntilFlagged - (daysElapsed || 0))}d until flagged`
                            }
                          >
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                isFlagged
                                  ? 'bg-red-600 dark:bg-red-500'
                                  : contactProgress.progress >= 75
                                    ? 'bg-orange-500 dark:bg-orange-400'
                                    : 'bg-blue-500 dark:bg-blue-400'
                              }`}
                              style={{ width: `${Math.min(contactProgress.progress, 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 text-center flex flex-col justify-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Lead Source</h3>
                  {(isEditMode || isEditingAssessmentModal) ? (
                    <div className="space-y-2">
                      <select
                        value={lead.leadSource === 'Conference' || (lead.leadSource && !['Website', 'Google Ads', 'Meta Ads', 'LinkedIn', 'Facebook', 'Instagram', 'YouTube', 'Tiktok', 'Newsletter', 'Podcast', 'Referral', 'Reddit', 'Jobtread'].includes(lead.leadSource)) ? 'Conference' : (lead.leadSource || 'Website')}
                        onChange={async (e) => {
                          const selectedValue = e.target.value;
                          if (selectedValue === 'Conference') {
                            // Load conferences if not already loaded
                            if (conferences.length === 0) {
                              await loadConferences();
                            }
                            // Set to "Conference" initially, will be updated when specific conference is selected
                            const updatedData = { ...lead, leadSource: 'Conference' };
                            setLead(updatedData);
                          } else {
                            const updatedData = { ...lead, leadSource: selectedValue };
                            setLead(updatedData);
                            // Save immediately
                            await updateAccount(leadId, updatedData);
                          }
                        }}
                        className="mt-1 w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                      >
                        <option value="Website">Website</option>
                        <option value="Google Ads">Google Ads</option>
                        <option value="Meta Ads">Meta Ads</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Tiktok">Tiktok</option>
                        <option value="Newsletter">Newsletter</option>
                        <option value="Podcast">Podcast</option>
                        <option value="Conference">Conference</option>
                        <option value="Referral">Referral</option>
                        <option value="Reddit">Reddit</option>
                        <option value="Jobtread">Jobtread</option>
                      </select>
                      {(lead.leadSource === 'Conference' || (lead.leadSource && !['Website', 'Google Ads', 'Meta Ads', 'LinkedIn', 'Facebook', 'Instagram', 'YouTube', 'Tiktok', 'Newsletter', 'Podcast', 'Referral', 'Reddit', 'Jobtread'].includes(lead.leadSource))) && (
                        <select
                          value={lead.leadSource === 'Conference' ? '' : lead.leadSource}
                          onChange={async (e) => {
                            const selectedConference = e.target.value;
                            if (selectedConference) {
                              const updatedData = { ...lead, leadSource: selectedConference };
                              setLead(updatedData);
                              // Save immediately
                              await updateAccount(leadId, updatedData);
                            }
                          }}
                          className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                          disabled={loadingConferences}
                        >
                          <option value="">{loadingConferences ? 'Loading conferences...' : 'Select Conference...'}</option>
                          {conferences.map((conference) => (
                            <option key={conference.id} value={conference.name}>
                              {conference.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ) : (
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {lead.leadSource || 'Website'}
                    </div>
                  )}
                </div>
              </div>



              {/* Summary and Call Cards - 3/4 and 1/4 layout */}
              <div className="flex gap-6">
                {/* Summary Card - 3/4 width, tabs for summary → intake → discovery → analysis */}
                <div className="w-3/4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 relative">
                  {/* Full-width, evenly spaced tab row; tabs with nothing in them are dimmed */}
                  <div className="grid grid-cols-4 gap-1 p-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-xl" role="tablist">
                    {SUMMARY_TABS.map((tab) => {
                      const isActive = tab.id === activeSummaryTab.id;
                      const hasContent = !!getSummaryValue(tab.id).trim();
                      let tone;
                      if (isActive) {
                        tone = hasContent
                          ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm'
                          : 'bg-white/70 dark:bg-gray-700/60 text-gray-400 dark:text-gray-500 shadow-sm';
                      } else {
                        tone = hasContent
                          ? 'text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-gray-700/60'
                          : 'text-gray-400 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-500 hover:bg-white/40 dark:hover:bg-gray-700/30';
                      }
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          onClick={() => selectSummaryTab(tab.id)}
                          title={hasContent ? undefined : `No ${tab.label.toLowerCase()} yet`}
                          className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tone}`}
                        >
                          {tab.id === 'summary' && <Sparkles className="w-3.5 h-3.5" />}
                          {tab.tabLabel}
                        </button>
                      );
                    })}
                  </div>

                  {activeSummaryTab.id === 'summary' ? (
                    /* AI summary — read-only; it's rebuilt from the other three tabs */
                    <div className={`rounded-lg p-4 min-h-[200px] flex flex-col ${
                      getSummaryValue('summary').trim() || summaryGenerating
                        ? 'bg-gray-50 dark:bg-gray-700'
                        : 'bg-gray-100/70 dark:bg-gray-800/60'
                    }`}>
                      {(getSummaryValue('summary').trim() || summaryGenerating) && (
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                            <Sparkles className="w-3.5 h-3.5 text-red-500" />
                            {summaryGenerating
                              ? 'Writing summary from intake, discovery & analysis…'
                              : leadSummaryIsAi
                                ? `AI summary · ${new Date(lead.leadSummaryGeneratedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${leadSummaryIsStale ? ' · notes changed since' : ''}`
                                : 'Written summary (not AI-generated)'}
                          </span>
                          {hasSummarySources && (
                            <button
                              type="button"
                              onClick={handleGenerateLeadSummary}
                              disabled={summaryGenerating}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-white dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${summaryGenerating ? 'animate-spin' : ''}`} />
                              {leadSummaryIsAi ? 'Regenerate' : 'Generate AI summary'}
                            </button>
                          )}
                        </div>
                      )}

                      {summaryGenError && !summaryGenerating && (
                        <p className="mb-3 text-xs text-red-600 dark:text-red-400">{summaryGenError}</p>
                      )}

                      {lead.leadSummaryAi?.overview && leadSummaryIsAi ? (
                        <div className={`space-y-4 text-sm leading-relaxed text-gray-900 dark:text-white ${summaryGenerating ? 'opacity-60' : ''}`}>
                          <p>{lead.leadSummaryAi.overview}</p>
                          {lead.leadSummaryAi.highlights?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Highlights</h4>
                              <ul className="list-disc pl-5 space-y-1">
                                {lead.leadSummaryAi.highlights.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          {lead.leadSummaryAi.nextSteps?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Next steps</h4>
                              <ul className="list-disc pl-5 space-y-1">
                                {lead.leadSummaryAi.nextSteps.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : getSummaryValue('summary').trim() ? (
                        <p className={`text-gray-900 dark:text-white text-sm leading-relaxed whitespace-pre-wrap ${summaryGenerating ? 'opacity-60' : ''}`}>
                          {getSummaryValue('summary')}
                        </p>
                      ) : summaryGenerating ? (
                        <div className="flex-1 flex items-center justify-center">
                          <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                        </div>
                      ) : (
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center text-gray-400 dark:text-gray-500">
                          <Sparkles className="w-6 h-6" />
                          {hasSummarySources ? (
                            <button
                              type="button"
                              onClick={handleGenerateLeadSummary}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              Generate summary
                            </button>
                          ) : (
                            <p className="text-xs max-w-xs">
                              Nothing to summarize yet. Add an intake message, discovery call, or analysis call and the AI summary will appear here.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : editingSummaryTab === activeSummaryTab.id ? (
                    /* The box itself is the input — click anywhere in it to write, blur to save */
                    <textarea
                      value={summaryDraft}
                      onChange={(e) => setSummaryDraft(e.target.value)}
                      onBlur={() => handleSummarySave(activeSummaryTab.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setEditingSummaryTab(null);
                          setSummaryDraft('');
                        }
                      }}
                      autoFocus
                      className="w-full min-h-[200px] bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm leading-relaxed text-gray-900 dark:text-white border border-transparent focus:border-red-400 focus:outline-none resize-y"
                      placeholder={`Enter the ${activeSummaryTab.label.toLowerCase()} here...`}
                      rows="12"
                    />
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSummaryDraft(getSummaryValue(activeSummaryTab.id));
                        setEditingSummaryTab(activeSummaryTab.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSummaryDraft(getSummaryValue(activeSummaryTab.id));
                          setEditingSummaryTab(activeSummaryTab.id);
                        }
                      }}
                      className={`rounded-lg p-4 min-h-[200px] flex flex-col cursor-text transition-colors ${
                        getSummaryValue(activeSummaryTab.id).trim()
                          ? 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600/60'
                          : 'bg-gray-100/70 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                      title={`Click to edit the ${activeSummaryTab.label.toLowerCase()}`}
                    >
                      {getSummaryValue(activeSummaryTab.id).trim() ? (
                        <p className="text-gray-900 dark:text-white text-sm leading-relaxed whitespace-pre-wrap">
                          {getSummaryValue(activeSummaryTab.id)}
                        </p>
                      ) : (
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400 dark:text-gray-500">
                          <span className="w-10 h-10 bg-gray-300 dark:bg-gray-600 text-white dark:text-gray-300 rounded-full flex items-center justify-center">
                            <Plus className="w-5 h-5" />
                          </span>
                          <p className="text-xs">No {activeSummaryTab.label.toLowerCase()} yet — click to add it</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Side Cards - 1/4 width */}
                <div className="w-1/4 flex flex-col gap-3">
                  {/* Success Manager */}
                  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 text-center">Success Manager</h3>
                    {(() => {
                      const selectedUser = lead.successManagerId
                        ? internalUsers.find((u) => u.id === lead.successManagerId)
                        : null;
                      const displayName = selectedUser ? selectedUser.name : (lead.successManager || null);

                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => setShowSuccessManagerPopup(true)}
                            className="w-full flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-left"
                            title="Click to change success manager"
                          >
                            {selectedUser ? (
                              <>
                                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 overflow-hidden flex-shrink-0 relative">
                                  {selectedUser.profileImage ? (
                                    <img
                                      src={selectedUser.profileImage}
                                      alt={selectedUser.name || 'User'}
                                      className="w-full h-full object-cover absolute inset-0 z-10"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  ) : null}
                                  <span className="w-full h-full flex items-center justify-center">{selectedUser.initials}</span>
                                </div>
                                <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">
                                  {selectedUser.name}
                                </span>
                              </>
                            ) : displayName ? (
                              <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">{displayName}</span>
                            ) : (
                              <span className="flex-1 text-sm text-gray-500 dark:text-gray-400 italic">Not assigned</span>
                            )}
                            <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                          </button>
                          {showSuccessManagerPopup && typeof document !== 'undefined' && ReactDOM.createPortal(
                            <div
                              className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4"
                              onClick={(e) => { if (e.target === e.currentTarget) setShowSuccessManagerPopup(false); }}
                            >
                              <div
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Success Manager</h3>
                                  <button
                                    type="button"
                                    onClick={() => setShowSuccessManagerPopup(false)}
                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                                    aria-label="Close"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>
                                <div className="overflow-y-auto flex-1 p-2">
                                  {internalUsers.length === 0 ? (
                                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                      No internal users found
                                    </div>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          const updatedData = { ...lead, successManagerId: null, successManager: null };
                                          setLead(updatedData);
                                          setShowSuccessManagerPopup(false);
                                          // Clearing is deliberate — don't let the Ian Riddle default put it straight back
                                          successManagerDefaultRef.current = leadId;
                                          try {
                                            await updateAccount(leadId, { successManagerId: null, successManager: null });
                                          } catch (error) {
                                            console.error('Error updating success manager:', error);
                                          }
                                        }}
                                        className="w-full flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-left"
                                      >
                                        <span className="text-sm text-gray-500 dark:text-gray-400">Clear selection</span>
                                      </button>
                                      {internalUsers.map((user) => (
                                        <button
                                          key={user.id}
                                          type="button"
                                          onClick={async () => {
                                            const updatedData = { ...lead, successManagerId: user.id, successManager: user.name };
                                            setLead(updatedData);
                                            setShowSuccessManagerPopup(false);
                                            try {
                                              await updateAccount(leadId, { successManagerId: user.id, successManager: user.name });
                                            } catch (error) {
                                              console.error('Error updating success manager:', error);
                                            }
                                          }}
                                          className={`w-full flex items-center p-3 rounded-lg cursor-pointer text-left ${
                                            selectedUser?.id === user.id
                                              ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                          }`}
                                        >
                                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3 overflow-hidden flex-shrink-0 relative">
                                            {user.profileImage ? (
                                              <img
                                                src={user.profileImage}
                                                alt={user.name || 'User'}
                                                className="w-full h-full object-cover absolute inset-0 z-10"
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                }}
                                              />
                                            ) : null}
                                            <span className="w-full h-full flex items-center justify-center">{user.initials}</span>
                                          </div>
                                          <span className="text-gray-900 dark:text-white font-medium">{user.name}</span>
                                        </button>
                                      ))}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>,
                            document.body
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Recent Activity - five newest entries from the Notes timeline */}
                  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 text-center">Recent Activity</h3>
                    {recentActivityRows.length === 0 ? (
                      <p className="text-center text-xs text-gray-500 dark:text-gray-400 py-6">No activity yet</p>
                    ) : (
                      <ul className="space-y-2">
                        {recentActivityRows.map((row) => {
                          const { Icon, emoji, label } = describeTimelineRow(row);
                          return (
                            <li
                              key={row.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => openTimelineRowInNotes(row)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openTimelineRowInNotes(row);
                                }
                              }}
                              title="Open in Notes"
                              className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-600 px-2 py-1.5 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-gray-50 dark:border-gray-500 dark:bg-gray-900">
                                {emoji ? (
                                  <span className="text-xs leading-none select-none" aria-hidden="true">{emoji}</span>
                                ) : (
                                  <Icon className="h-3.5 w-3.5 text-gray-700 dark:text-gray-200" strokeWidth={1.75} />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-xs font-medium text-gray-900 dark:text-white" title={label}>
                                  {label}
                                </p>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                  {row.at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ·{' '}
                                  {row.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </p>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Portal Completion - full-width progress meter */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Portal Completion</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-4 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className="h-4 rounded-full bg-red-500 transition-all duration-500"
                      style={{ width: `${Math.min(Math.round(portalCompletion), 100)}%` }}
                    />
                  </div>
                  <span className="w-14 shrink-0 text-right text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                    {Math.round(portalCompletion)}%
                  </span>
                </div>
                {portalUserActivityLoading ? (
                  <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">Checking portal…</p>
                ) : portalProgress.hasPortalUser ? (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {portalProgress.steps.map((step) => (
                      <div
                        key={step.id}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                          step.complete
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                            : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60'
                        }`}
                      >
                        <span className="text-base leading-none select-none" aria-hidden="true">
                          {step.emoji}
                        </span>
                        {step.complete ? (
                          <CheckCircle className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                        ) : (
                          <div className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                        )}
                        <span
                          className={`text-xs ${
                            step.complete ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">No portal account yet</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'status' && (
            <div className="space-y-6">
              {/* Current Status & Actions */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white text-center">Current Status & Actions</h2>
                
                {/* Current Status Display */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Lead Stage</h3>
                    <div className="flex items-center justify-center space-x-3">
                      <span className="px-3 py-1 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-sm font-medium">
                        {lead.stage || 'No Stage Set'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Substatus</h3>
                    <div className="flex items-center justify-center space-x-3">
                      <span className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm font-medium">
                        {lead.substatus || 'No Substatus Set'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Qualified</h3>
                    <div className="flex items-center justify-center space-x-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleQualifiedUpdate(true)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            qualified === true
                              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-400'
                              : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => handleQualifiedUpdate(false)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            qualified === false
                              ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-400'
                              : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Portal Signup</h3>
                    <div className="flex items-center justify-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        lead.portalSignupDate 
                          ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                          : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}>
                        {lead.portalSignupDate ? (
                          <div className="flex flex-col items-center">
                            <span className="font-semibold">✓ Signed Up</span>
                            <span className="text-xs opacity-75">
                              {new Date(lead.portalSignupDate).toLocaleDateString()}
                            </span>
                          </div>
                        ) : 'Not Signed Up'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Show on Kanban Toggle and Result */}
                <div className="space-y-6 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Show on Kanban</h3>
                      <div className="flex items-center justify-center space-x-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleShowOnKanbanUpdate(true)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                              showOnKanban
                                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-400'
                                : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => handleShowOnKanbanUpdate(false)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                              !showOnKanban
                                ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-400'
                                : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Result and Reasons row - show side by side when Won/Lost */}
                    {result === 'Won' || result === 'Lost' ? (
                      <>
                        <div className="md:col-span-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Result</h3>
                          <div className="flex items-center justify-center space-x-3">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleResultUpdate(result === 'Won' ? null : 'Won')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                  result === 'Won'
                                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-400'
                                    : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                                }`}
                              >
                                Won
                              </button>
                              <button
                                onClick={() => handleResultUpdate(result === 'Lost' ? null : 'Lost')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                  result === 'Lost'
                                    ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-400'
                                    : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                                }`}
                              >
                                Lost
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 text-center">Reason</h3>
                          <div className="grid grid-cols-2 gap-2">
                            {result === 'Lost' ? (
                              <input
                                type="text"
                                value={resultReason || ''}
                                onChange={(e) => setResultReason(e.target.value)}
                                onBlur={(e) => handleResultReasonUpdate(e.target.value)}
                                placeholder="Enter reason lost..."
                                className="col-span-2 px-3 py-2 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent"
                              />
                            ) : (
                              <>
                                {['Growth', 'Systems', 'Departmental', 'Value'].map((reason) => (
                                  <button
                                    key={reason}
                                    onClick={() => handleResultReasonUpdate(reason)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                      resultReason === reason
                                        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-400'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {reason}
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Result</h3>
                        <div className="flex items-center justify-center space-x-3">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleResultUpdate(result === 'Won' ? null : 'Won')}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                result === 'Won'
                                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-400'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                              }`}
                            >
                              Won
                            </button>
                            <button
                              onClick={() => handleResultUpdate(result === 'Lost' ? null : 'Lost')}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                result === 'Lost'
                                  ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-400'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                              }`}
                            >
                              Lost
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Free Page Selection Card */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 free-page-dropdown-container relative">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 text-center">Free Page</h3>
                      {selectedFreePage ? (
                        // Selected state - show page name large with trash and plus icons
                        <div className="relative">
                          <div className="flex items-center justify-center min-h-[60px]">
                            <div className="text-center">
                              <div className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                {getStartFreePageLabel(selectedFreePage)}
                              </div>
                              {START_PAGES.find(p => p.value === selectedFreePage)?.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {START_PAGES.find(p => p.value === selectedFreePage)?.description}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Trash icon to clear */}
                          <button
                            onClick={() => handleFreePageUpdate(null)}
                            className="absolute top-0 right-0 p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Clear free page"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {/* Plus icon to add another - opens dropdown to change selection */}
                          <button
                            onClick={() => {
                              setShowFreePageDropdown(true);
                              setFreePageSearch('');
                            }}
                            className="absolute top-0 left-0 p-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            title="Change page"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        // Not selected state - show search bar
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search pages..."
                            value={freePageSearch}
                            onChange={(e) => {
                              setFreePageSearch(e.target.value);
                              setShowFreePageDropdown(true);
                            }}
                            onFocus={() => setShowFreePageDropdown(true)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>
                      )}
                      {/* Dropdown - show when searching or when plus is clicked; grouped by department */}
                      {showFreePageDropdown && (() => {
                        const matchingPages = START_PAGES.filter(page =>
                          page.label.toLowerCase().includes(freePageSearch.toLowerCase()) ||
                          (page.description && page.description.toLowerCase().includes(freePageSearch.toLowerCase()))
                        );
                        const departments = [...new Set(matchingPages.map(page => page.department))];
                        return (
                          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                            {departments.map(department => (
                              <div key={department}>
                                <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/60 sticky top-0">
                                  {department}
                                </div>
                                {matchingPages
                                  .filter(page => page.department === department)
                                  .map(page => (
                                    <div
                                      key={page.value}
                                      onClick={() => handleFreePageUpdate(page.value)}
                                      className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                    >
                                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        {page.label}
                                        {page.hasItemSelection && (
                                          <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                            pick items
                                          </span>
                                        )}
                                      </div>
                                      {page.description && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{page.description}</div>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            ))}
                            {matchingPages.length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                                No pages found
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Content Selection Card - shows when a free page is selected */}
                  {selectedFreePage && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                        Select Content for {getStartFreePageLabel(selectedFreePage)}
                      </h3>
                      {selectedFreePage === 'sales/resources' ? (
                        <div>
                          {loadingSalesMaterials ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading sales materials...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific sales materials to unlock for this lead's start portal. You can select multiple materials.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availableSalesMaterials.length > 0 ? (
                                  availableSalesMaterials.map((material) => {
                                    if (!material || !material.id) return null;
                                    const isChecked = selectedSalesMaterials.includes(material.id);
                                    return (
                                      <label
                                        key={material.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleSalesMaterialToggle(material.id, e);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-900 dark:text-white">
                                          {material.emoji ? `${material.emoji} ` : ''}{material.name}
                                        </span>
                                        {material.section && (
                                          <span className="text-xs text-gray-400 dark:text-gray-500">
                                            {material.section}
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                    No sales materials available
                                  </div>
                                )}
                              </div>
                              {selectedSalesMaterials.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                    {selectedSalesMaterials.length} material{selectedSalesMaterials.length !== 1 ? 's' : ''} selected:
                                  </p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedSalesMaterials.map((catalogKey) => {
                                      const material = availableSalesMaterials.find(m => m.id === catalogKey);
                                      return material ? (
                                        <div key={catalogKey} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {material.name}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSaveSalesMaterials}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected Materials
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'operations/sops' ? (
                        <div>
                          {loadingSOPs ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading SOPs...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific SOPs to unlock for this lead's start portal. You can select multiple SOPs.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availableSOPs.length > 0 ? (
                                  availableSOPs.map((sop) => {
                                    // Ensure sop has a valid ID
                                    if (!sop || !sop.id) {
                                      console.warn('SOP missing ID:', sop);
                                      return null;
                                    }
                                    
                                    const isChecked = selectedSOPs.includes(sop.id);
                                    
                                    return (
                                      <label
                                        key={sop.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          // Prevent label click from interfering
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleSOPToggle(sop.id, e);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-900 dark:text-white">
                                          {sop.title || sop.name || 'Untitled SOP'}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                    No SOPs available
                                  </div>
                                )}
                              </div>
                              {selectedSOPs.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                    {selectedSOPs.length} SOP{selectedSOPs.length !== 1 ? 's' : ''} selected:
                                  </p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedSOPs.map((sopId) => {
                                      const sop = availableSOPs.find(s => s.id === sopId);
                                      return sop ? (
                                        <div key={sopId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {sop.title || sop.name || 'Untitled SOP'}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSaveSOPs}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected SOPs
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'operations/kpis' ? (
                        <div>
                          {loadingKPIs ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading KPIs...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific KPIs to unlock for this lead's start portal. You can select multiple KPIs.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availableKPIs.length > 0 ? (
                                  availableKPIs.map((kpi) => {
                                    // Ensure kpi has a valid ID
                                    if (!kpi || !kpi.id) {
                                      console.warn('KPI missing ID:', kpi);
                                      return null;
                                    }
                                    
                                    const isChecked = selectedKPIs.includes(kpi.id);
                                    
                                    return (
                                      <label
                                        key={kpi.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          // Prevent label click from interfering
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleKPIToggle(kpi.id, e);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-900 dark:text-white">
                                          {kpi.title || kpi.name || 'Untitled KPI'}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                    No KPIs available
                                  </div>
                                )}
                              </div>
                              {selectedKPIs.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                    {selectedKPIs.length} KPI{selectedKPIs.length !== 1 ? 's' : ''} selected:
                                  </p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedKPIs.map((kpiId) => {
                                      const kpi = availableKPIs.find(k => k.id === kpiId);
                                      return kpi ? (
                                        <div key={kpiId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {kpi.title || kpi.name || 'Untitled KPI'}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSaveKPIs}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected KPIs
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'operations/job-descriptions' ? (
                        <div>
                          {loadingJobs ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading Jobs...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific job descriptions to unlock for this lead's start portal. You can select multiple jobs.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availableJobs.length > 0 ? (
                                  availableJobs.map((job) => {
                                    // Ensure job has a valid ID
                                    if (!job || !job.id) {
                                      console.warn('Job missing ID:', job);
                                      return null;
                                    }
                                    
                                    const isChecked = selectedJobs.includes(job.id);
                                    
                                    return (
                                      <label
                                        key={job.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          // Prevent label click from interfering
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleJobToggle(job.id, e);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-900 dark:text-white">
                                          {job.title || 'Untitled Job'}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                    No jobs available
                                  </div>
                                )}
                              </div>
                              {selectedJobs.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                    {selectedJobs.length} Job{selectedJobs.length !== 1 ? 's' : ''} selected:
                                  </p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedJobs.map((jobId) => {
                                      const job = availableJobs.find(j => j.id === jobId);
                                      return job ? (
                                        <div key={jobId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {job.title || 'Untitled Job'}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSaveJobs}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected Jobs
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'sales-proposals' ? (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Select which proposal tabs to unlock for this lead in the Start portal. You can select one or more tabs.
                          </p>
                          <div className="max-h-60 overflow-y-auto space-y-2">
                            {PROPOSAL_TABS.map((tab) => {
                              const Icon = tab.icon;
                              const isChecked = selectedProposalTabs.includes(tab.id);
                              return (
                                <label
                                  key={tab.id}
                                  className="flex items-start gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => handleProposalTabToggle(tab.id, e)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 mt-1 shrink-0"
                                  />
                                  <Icon className="w-5 h-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{tab.label}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{tab.desc}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                          {selectedProposalTabs.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                {selectedProposalTabs.length} tab{selectedProposalTabs.length !== 1 ? 's' : ''} selected:
                              </p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {selectedProposalTabs.map((tabId) => {
                                  const tab = PROPOSAL_TABS.find(t => t.id === tabId);
                                  return tab ? (
                                    <div key={tabId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                      {tab.label}
                                    </div>
                                  ) : null;
                                })}
                              </div>
                              <button
                                type="button"
                                onClick={handleSaveProposalTabs}
                                className="mt-3 w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                              >
                                Save Selected Tabs
                              </button>
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'sales-referral-programs' ? (
                        <div>
                          {loadingPrograms ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading Partner Programs...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific partner programs to unlock for this lead's start portal. You can select multiple programs.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availablePrograms.length > 0 ? (
                                  availablePrograms.map((program) => {
                                    // Ensure program has a valid ID
                                    if (!program || !program.id) {
                                      console.warn('Program missing ID:', program);
                                      return null;
                                    }
                                    
                                    const isChecked = selectedPrograms.includes(program.id);
                                    
                                    return (
                                      <label
                                        key={program.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          // Prevent label click from interfering
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleProgramToggle(program.id, e);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-900 dark:text-white">
                                          {program.label || 'Untitled Program'}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                    No partner programs available
                                  </div>
                                )}
                              </div>
                              {selectedPrograms.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                    {selectedPrograms.length} Partner Program{selectedPrograms.length !== 1 ? 's' : ''} selected:
                                  </p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedPrograms.map((programId) => {
                                      const program = availablePrograms.find(p => p.id === programId);
                                      return program ? (
                                        <div key={programId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {program.label || 'Untitled Program'}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSavePrograms}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected Partner Programs
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'technology/estimating-template' ? (
                        <div>
                          {loadingEstimatingCategories ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading Estimating Template Categories...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific estimating template categories to unlock for this lead's start portal. You can select multiple categories.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availableEstimatingCategories.length > 0 ? (
                                  availableEstimatingCategories.map((category) => {
                                    if (!category || !category.id) {
                                      console.warn('Category missing ID:', category);
                                      return null;
                                    }
                                    
                                    const isChecked = selectedEstimatingCategories.includes(category.id);
                                    
                                    return (
                                      <label
                                        key={category.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleEstimatingTemplateToggle(category.id, e);
                                          }}
                                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                          {category.name}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">No categories available</div>
                                )}
                              </div>
                              {selectedEstimatingCategories.length > 0 && (
                                <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Selected Categories ({selectedEstimatingCategories.length}):
                                  </div>
                                  <div className="space-y-1">
                                    {selectedEstimatingCategories.map((categoryId) => {
                                      const category = availableEstimatingCategories.find(c => c.id === categoryId);
                                      return category ? (
                                        <div key={categoryId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {category.name}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSaveEstimatingTemplates}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected Estimating Template Categories
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'technology/chatgpt' ? (
                        <div>
                          {loadingGPTs ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading GPTs...</div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Select specific GPTs to unlock for this lead's start portal. You can select multiple GPTs.
                              </p>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {availableGPTs.length > 0 ? (
                                  availableGPTs.map((gpt) => {
                                    if (!gpt || !gpt.id) {
                                      console.warn('GPT missing ID:', gpt);
                                      return null;
                                    }
                                    
                                    const isChecked = selectedGPTs.includes(gpt.id);
                                    
                                    return (
                                      <label
                                        key={gpt.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleGPTToggle(gpt.id, e);
                                          }}
                                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                          {gpt.title || 'Untitled GPT'}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">No GPTs available</div>
                                )}
                              </div>
                              {selectedGPTs.length > 0 && (
                                <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Selected GPTs ({selectedGPTs.length}):
                                  </div>
                                  <div className="space-y-1">
                                    {selectedGPTs.map((gptId) => {
                                      const gpt = availableGPTs.find(g => g.id === gptId);
                                      return gpt ? (
                                        <div key={gptId} className="text-xs text-gray-600 dark:text-gray-400 flex items-center">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                          {gpt.title || 'Untitled GPT'}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                  <button
                                    onClick={handleSaveGPTs}
                                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    Save Selected GPTs
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : selectedFreePage === 'technology/crmtechstack' ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <p className="mb-2">This page will be unlocked for the lead's start portal.</p>
                          <p className="text-xs">No additional content selection is required for this page.</p>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <p className="mb-2">This page will be unlocked for the lead's start portal.</p>
                          <p className="text-xs">No additional content selection is required for this page.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <button
                    onClick={() => setShowUpdateStatusModal(true)}
                    className="flex items-center justify-center space-x-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <Flag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-blue-900 dark:text-blue-100">Update Status</div>
                      <div className="text-sm text-blue-600 dark:text-blue-300">Move to next stage</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowUpdateSubstatusModal(true)}
                    className="flex items-center justify-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-green-900 dark:text-green-100">Update Substatus</div>
                      <div className="text-sm text-green-600 dark:text-green-300">Change detailed status</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowMakeClientModal(true)}
                    className="flex items-center justify-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-green-900 dark:text-green-100">Make Client</div>
                      <div className="text-sm text-green-600 dark:text-green-300">Convert to client</div>
                    </div>
                  </button>

                  {lead?.portalSignupDate ? (
                    <button
                      onClick={handleDeletePortalAccount}
                      disabled={isDeletingPortalAccount}
                      className="flex items-center justify-center space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-red-900 dark:text-red-100">
                          {isDeletingPortalAccount ? 'Deleting...' : 'Delete Portal Account'}
                        </div>
                        <div className="text-sm text-red-600 dark:text-red-300">
                          {isDeletingPortalAccount ? 'Please wait' : 'Remove portal access'}
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={handleInviteToPortal}
                      disabled={isInvitingToPortal || !lead?.contact?.email}
                      className="flex items-center justify-center space-x-3 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                        <User className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-orange-900 dark:text-orange-100">
                          {isInvitingToPortal ? 'Generating...' : 'Generate Portal Invite'}
                        </div>
                        <div className="text-sm text-orange-600 dark:text-orange-300">
                          {isInvitingToPortal ? 'Please wait' : 'Create shareable signup link'}
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>

              <LeadLongTermFollowUpCard
                lead={lead}
                leadId={leadId}
                currentUser={currentUser}
                onSaved={(value) => setLead(prev => ({ ...prev, longTermFollowUp: value }))}
              />
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 w-full max-w-full overflow-hidden">
              <div className="space-y-6 w-full">
                {/* Activity header + interaction shortcuts */}
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  <div className="flex min-w-0 flex-1 items-center justify-start gap-2">
                      <span className="hidden text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:inline">
                        Client
                      </span>
                    <button
                      type="button"
                      title="Client — Phone"
                      onClick={() => addInteractionNote('client', 'phone')}
                      className="rounded-full bg-green-600 p-2.5 text-white shadow-md ring-2 ring-green-950/30 transition hover:bg-green-700 hover:ring-green-950/50 dark:bg-green-500 dark:text-white dark:ring-green-100/40 dark:hover:bg-green-400"
                    >
                      <span className="text-2xl leading-none select-none" aria-hidden="true">
                        📞
                      </span>
                    </button>
                    <button
                      type="button"
                      title="Client — Text"
                      onClick={() => addInteractionNote('client', 'text')}
                      className="rounded-full bg-green-600 p-2.5 text-white shadow-md ring-2 ring-green-950/30 transition hover:bg-green-700 hover:ring-green-950/50 dark:bg-green-500 dark:text-white dark:ring-green-100/40 dark:hover:bg-green-400"
                    >
                      <span className="text-2xl leading-none select-none" aria-hidden="true">
                        📲
                      </span>
                    </button>
                    <button
                      type="button"
                      title="Client — Email"
                      onClick={() => addInteractionNote('client', 'email')}
                      className="rounded-full bg-green-600 p-2.5 text-white shadow-md ring-2 ring-green-950/30 transition hover:bg-green-700 hover:ring-green-950/50 dark:bg-green-500 dark:text-white dark:ring-green-100/40 dark:hover:bg-green-400"
                    >
                      <span className="text-2xl leading-none select-none" aria-hidden="true">
                        📧
                      </span>
                    </button>
                  </div>
                    <div className="flex shrink-0 items-center justify-center gap-2 px-1">
                      <h3 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Notes</h3>
                      <button
                        type="button"
                        title={
                          quickNoteComposerOpen
                            ? quickNoteDraft.trim()
                              ? 'Save note'
                              : 'Close'
                            : 'Add note'
                        }
                        onClick={() => {
                          if (quickNoteComposerOpen) {
                            if (quickNoteDraft.trim()) {
                              void submitQuickNote();
                            } else {
                              closeQuickNoteComposer();
                            }
                          } else {
                            setQuickNoteComposerOpen(true);
                          }
                        }}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:bg-gray-100 dark:border-gray-500 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                      >
                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                    </div>
                    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                      <span className="hidden text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:inline">BBA</span>
                    <button
                      type="button"
                      title="BBA — Phone"
                      onClick={() => addInteractionNote('bba', 'phone')}
                      className="rounded-full bg-red-600 p-2.5 text-white shadow-md ring-2 ring-red-950/30 transition hover:bg-red-700 hover:ring-red-950/50 dark:bg-red-500 dark:text-white dark:ring-red-100/40 dark:hover:bg-red-400"
                    >
                      <span className="text-2xl leading-none select-none" aria-hidden="true">
                        📞
                      </span>
                    </button>
                    <button
                      type="button"
                      title="BBA — Text"
                      onClick={() => addInteractionNote('bba', 'text')}
                      className="rounded-full bg-red-600 p-2.5 text-white shadow-md ring-2 ring-red-950/30 transition hover:bg-red-700 hover:ring-red-950/50 dark:bg-red-500 dark:text-white dark:ring-red-100/40 dark:hover:bg-red-400"
                    >
                      <span className="text-2xl leading-none select-none" aria-hidden="true">
                        📲
                      </span>
                    </button>
                    <button
                      type="button"
                      title="BBA — Email"
                      onClick={() => addInteractionNote('bba', 'email')}
                      className="rounded-full bg-red-600 p-2.5 text-white shadow-md ring-2 ring-red-950/30 transition hover:bg-red-700 hover:ring-red-950/50 dark:bg-red-500 dark:text-white dark:ring-red-100/40 dark:hover:bg-red-400"
                    >
                      <span className="text-2xl leading-none select-none" aria-hidden="true">
                        📧
                      </span>
                    </button>
                  </div>
                </div>

                {/* Merged timeline: system milestones + notes */}
                <div className="space-y-1.5">
                  {quickNoteComposerOpen && (
                    <div className="flex w-full flex-nowrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-600 dark:bg-gray-800">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-gray-50 dark:border-gray-500 dark:bg-gray-900">
                        <span className="text-lg leading-none select-none" aria-hidden="true">
                          📝
                        </span>
                      </div>
                      <input
                        ref={quickNoteInputRef}
                        type="text"
                        value={quickNoteDraft}
                        onChange={(e) => setQuickNoteDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            closeQuickNoteComposer();
                            return;
                          }
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void submitQuickNote();
                          }
                        }}
                        placeholder="Write your note…"
                        maxLength={4000}
                        aria-label="New note"
                        className="min-w-0 flex-1 border-0 bg-transparent text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-500 md:text-lg"
                      />
                      <button
                        type="button"
                        title="Save note"
                        disabled={!quickNoteDraft.trim()}
                        onClick={() => void submitQuickNote()}
                        className="shrink-0 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-red-500"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        title="Cancel"
                        onClick={closeQuickNoteComposer}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {mergedTimelineRows.length > 0 ? (
                    (() => {
                      const totalPages = Math.ceil(mergedTimelineRows.length / notesPerPage);
                      const startIndex = (currentNotesPage - 1) * notesPerPage;
                      const endIndex = startIndex + notesPerPage;
                      const pageRows = mergedTimelineRows.slice(startIndex, endIndex);

                      const formatDateOnly = (d) =>
                        d.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        });

                      // Timeline rows carry the time of day too — with per-visit Start
                      // activity, "Aug 13" alone can't tell two visits apart.
                      const formatStamp = (d) =>
                        `${formatDateOnly(d)} · ${d.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit'
                        })}`;

                      return (
                        <>
                          {pageRows.map((row) => {
                            // Wrapped so a row opened from Recent Activity can be anchored and ringed
                            const rendered = (() => {
                            if (row.type === 'system') {
                              const SysIcon = getSystemTimelineIcon(row.kind);
                              if (START_PROGRAM_ROW_KINDS.has(row.kind)) {
                                return (
                                  <StartProgramActivityRow
                                    key={row.id}
                                    label={row.label}
                                    detail={row.detail}
                                    stamp={formatStamp(row.at)}
                                    Icon={SysIcon}
                                  />
                                );
                              }
                              // Things the lead did themselves in the portal stand out in blue
                              const isClientAction = CLIENT_PORTAL_ACTION_KINDS.has(row.kind);
                              return (
                                <div
                                  key={row.id}
                                  className={`flex w-full flex-nowrap items-center gap-3 rounded-lg border px-3 py-2 ${
                                    isClientAction
                                      ? 'border-blue-800 bg-blue-600 text-white dark:border-blue-300 dark:bg-blue-700'
                                      : 'border-gray-200 bg-gray-50/90 dark:border-gray-600 dark:bg-gray-800/50'
                                  }`}
                                >
                                  <div
                                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
                                      isClientAction
                                        ? 'border-white/45 bg-blue-900 dark:border-white/50 dark:bg-blue-950'
                                        : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-950'
                                    }`}
                                  >
                                    <SysIcon
                                      className={`h-5 w-5 ${isClientAction ? 'text-white' : 'text-gray-800 dark:text-white'}`}
                                      strokeWidth={1.75}
                                    />
                                  </div>
                                  <span
                                    className={`min-w-0 flex-1 truncate text-sm font-semibold ${
                                      isClientAction ? 'text-white' : 'text-gray-900 dark:text-white'
                                    }`}
                                  >
                                    {row.label}
                                  </span>
                                  <span
                                    className={`shrink-0 text-xs font-semibold tabular-nums ${
                                      isClientAction ? 'text-white/90' : 'text-gray-600 dark:text-gray-300'
                                    }`}
                                  >
                                    {formatStamp(row.at)}
                                  </span>
                                </div>
                              );
                            }

                            const note = row.note;
                            const isPersistedSystem =
                              note.type === 'system' || (note.author?.name === 'System' && !note.interaction);
                            if (isPersistedSystem) {
                              const SysIcon = note.content?.includes('flagged') ? Flag : Activity;
                              const eventTitle = note.content?.includes('flagged')
                                ? 'Contact flag'
                                : 'System update';
                              return (
                                <div
                                  key={row.id}
                                  className="flex w-full flex-nowrap items-center gap-3 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 dark:border-amber-800/60 dark:bg-amber-950/25"
                                >
                                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-amber-300 bg-white dark:border-amber-600 dark:bg-amber-900/40">
                                    <SysIcon className="h-5 w-5 text-amber-800 dark:text-amber-200" strokeWidth={1.75} />
                                  </div>
                                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-white">{eventTitle}</span>
                                  <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">
                                    {formatStamp(row.at)}
                                  </span>
                                </div>
                              );
                            }

                            if (note.interaction) {
                              const channelEmoji = getInteractionChannelEmoji(note.interaction.channel);
                              const eventLabel =
                                note.interaction.channel === 'phone'
                                  ? 'Phone'
                                  : note.interaction.channel === 'text'
                                    ? 'Text'
                                    : 'Email';
                              const dateStr = formatStamp(row.at);
                              const isClient = note.interaction.initiatedBy === 'client';
                              const canEditInteraction = note.author?.uid === currentUser?.uid;
                              const shellClass = isClient
                                ? 'border-green-950 bg-green-600 text-white shadow-md dark:border-green-200 dark:bg-green-700'
                                : 'border-red-950 bg-red-600 text-white shadow-md dark:border-red-200 dark:bg-red-700';
                              const iconRing = isClient
                                ? 'border-white/45 bg-green-900 text-white ring-1 ring-black/10 dark:border-white/50 dark:bg-green-950 dark:text-white'
                                : 'border-white/45 bg-red-900 text-white ring-1 ring-black/10 dark:border-white/50 dark:bg-red-950 dark:text-white';
                              const rowInteractProps = canEditInteraction
                                ? {
                                    role: 'button',
                                    tabIndex: 0,
                                    onClick: () => openInteractionNoteModal(note),
                                    onKeyDown: (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openInteractionNoteModal(note);
                                      }
                                    }
                                  }
                                : {};

                              if (isClient) {
                                return (
                                  <div
                                    key={row.id}
                                    {...rowInteractProps}
                                    className={`relative w-full rounded-lg border px-3 py-2 ${shellClass} ${
                                      canEditInteraction ? 'cursor-pointer transition hover:brightness-110 active:brightness-95' : ''
                                    }`}
                                  >
                                    <div className="flex w-full flex-nowrap items-center gap-3">
                                      <div
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${iconRing}`}
                                      >
                                        <span className="text-lg leading-none select-none" aria-hidden="true">
                                          {channelEmoji}
                                        </span>
                                      </div>
                                      <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-x-2 overflow-hidden">
                                        <span className="truncate text-sm font-bold text-white">{note.author?.name || 'User'}</span>
                                        <span className="shrink-0 text-green-100">·</span>
                                        <span className="shrink-0 text-xs font-semibold text-green-50">{eventLabel}</span>
                                      </div>
                                      <div className="shrink-0 text-xs font-semibold tabular-nums leading-none text-white">{dateStr}</div>
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div
                                  key={row.id}
                                  {...rowInteractProps}
                                  className={`relative w-full rounded-lg border px-3 py-2 ${shellClass} ${
                                    canEditInteraction ? 'cursor-pointer transition hover:brightness-110 active:brightness-95' : ''
                                  }`}
                                >
                                  <div className="flex w-full flex-nowrap items-center gap-3">
                                    <div className="shrink-0 text-xs font-semibold tabular-nums leading-none text-white">{dateStr}</div>
                                    <div className="flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-x-2 overflow-hidden text-right">
                                      <span className="truncate text-sm font-bold text-white">{note.author?.name || 'User'}</span>
                                      <span className="shrink-0 text-red-100">·</span>
                                      <span className="shrink-0 text-xs font-semibold text-red-50">{eventLabel}</span>
                                    </div>
                                    <div
                                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${iconRing}`}
                                    >
                                      <span className="text-lg leading-none select-none" aria-hidden="true">
                                        {channelEmoji}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            let cardClass =
                              'relative w-full rounded-lg border px-3 py-2 ';
                            if (note.contact) {
                              cardClass += 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30';
                            } else {
                              cardClass += 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800';
                            }

                            const dateStrText = formatStamp(row.at);
                            const canEditTextNote =
                              note.author?.uid === currentUser?.uid && note.type !== 'system';
                            const textNoteRowProps = canEditTextNote
                              ? {
                                  role: 'button',
                                  tabIndex: 0,
                                  onClick: () => openTextNoteModal(note),
                                  onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      openTextNoteModal(note);
                                    }
                                  }
                                }
                              : {};

                            return (
                              <div
                                key={row.id}
                                {...textNoteRowProps}
                                className={`${cardClass} ${
                                  canEditTextNote
                                    ? 'cursor-pointer transition hover:brightness-[1.02] active:brightness-95 dark:hover:brightness-110'
                                    : ''
                                }`}
                              >
                                <div className="flex w-full flex-nowrap items-center gap-3">
                                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-gray-50 dark:border-gray-500 dark:bg-gray-900">
                                    <span className="text-lg leading-none select-none" aria-hidden="true">
                                      📝
                                    </span>
                                  </div>
                                  <p className="min-w-0 flex-1 truncate text-sm leading-snug text-gray-900 dark:text-white">
                                    {note.content}
                                  </p>
                                  <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                                    {dateStrText}
                                  </span>
                                </div>
                              </div>
                            );
                            })();

                            return (
                              <div
                                key={row.id}
                                id={`timeline-row-${row.id}`}
                                className={
                                  highlightedTimelineId === row.id
                                    ? 'rounded-lg ring-2 ring-red-500 ring-offset-2 dark:ring-offset-gray-800'
                                    : ''
                                }
                              >
                                {rendered}
                              </div>
                            );
                          })}

                          {totalPages > 1 ? (
                            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-600">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Showing {startIndex + 1} to {Math.min(endIndex, mergedTimelineRows.length)} of{' '}
                                  {mergedTimelineRows.length} entries
                                </span>
                              </div>

                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => setCurrentNotesPage((prev) => Math.max(prev - 1, 1))}
                                  disabled={currentNotesPage === 1}
                                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                                >
                                  Previous
                                </button>

                                <div className="flex items-center space-x-1">
                                  {buildPageWindow(currentNotesPage, totalPages).map((page, i) =>
                                    page === 'gap' ? (
                                      <span
                                        key={`gap-${i}`}
                                        className="px-1 text-sm text-gray-400 dark:text-gray-500"
                                      >
                                        …
                                      </span>
                                    ) : (
                                      <button
                                        key={page}
                                        type="button"
                                        onClick={() => setCurrentNotesPage(page)}
                                        className={`rounded-md px-3 py-2 text-sm font-medium ${
                                          currentNotesPage === page
                                            ? 'bg-red-600 text-white'
                                            : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                                        }`}
                                      >
                                        {page}
                                      </button>
                                    )
                                  )}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => setCurrentNotesPage((prev) => Math.min(prev + 1, totalPages))}
                                  disabled={currentNotesPage === totalPages}
                                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </>
                      );
                    })()
                  ) : (
                    <div className="py-6 text-center text-gray-500 dark:text-gray-400">No activity recorded yet</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <div className="flex items-center justify-end mb-4">
                {/* Filter Dropdown */}
                <div className="relative">
                  <select
                    value={emailsFilter}
                    onChange={(e) => setEmailsFilter(e.target.value)}
                    className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="all">All</option>
                    <option value="from">From Lead</option>
                    <option value="to">To Lead</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Gmail connection warning — without this a disconnected inbox just looks like "no emails" */}
              {!emailsLoading && (
                <GmailConnectionNotice
                  users={gmailDisconnectedUsers}
                  currentUserId={currentUser?.uid}
                  onConnect={() => navigate('/crm/my-profile')}
                />
              )}

              {emailsLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Loading emails...
                </div>
              ) : (
                <EmailThreadList
                  emails={leadEmails}
                  counterpartEmail={lead?.contact?.email || lead?.email || lead?.discovery?.email || ''}
                  counterpartPerson={{
                    email: lead?.contact?.email || lead?.email || lead?.discovery?.email || '',
                    name: lead?.contact?.name || '',
                  }}
                  directory={internalUsers}
                  filter={emailsFilter}
                  labels={{ from: 'From Lead', to: 'To Lead' }}
                  emptyMessage="No emails found with this lead."
                />
              )}
            </div>
          )}

          {activeTab === 'events' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex-1"></div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center flex-1">Events</h2>
                
                {/* Filter Dropdown */}
                <div className="relative flex-1 flex justify-end">
                  <select
                    value={eventsFilter}
                    onChange={(e) => {
                      setEventsFilter(e.target.value);
                      setEventsCurrentPage(1); // Reset to first page when filter changes
                    }}
                    className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                    <option value="all">All</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {eventsLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Loading events...
                </div>
              ) : (() => {
                // Filter events based on selected filter
                const now = new Date();
                const filteredEvents = leadEvents.filter(event => {
                  const eventDate = new Date(event.start || event.startTime);
                  if (eventsFilter === 'upcoming') {
                    return eventDate >= now;
                  } else if (eventsFilter === 'completed') {
                    return eventDate < now;
                  } else {
                    return true; // 'all'
                  }
                });

                if (filteredEvents.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {eventsFilter === 'upcoming' 
                        ? 'No upcoming events found with this lead.'
                        : eventsFilter === 'completed'
                        ? 'No completed events found with this lead.'
                        : 'No events found with this lead.'}
                    </div>
                  );
                }

                // Calculate pagination
                const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
                const startIndex = (eventsCurrentPage - 1) * eventsPerPage;
                const endIndex = startIndex + eventsPerPage;
                const paginatedEvents = filteredEvents.slice(startIndex, endIndex);

                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {paginatedEvents.map((event, index) => {
                    const eventDate = new Date(event.start || event.startTime);
                    const isCompleted = eventDate < now;
                    const formattedDate = eventDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    });
                    const formattedTime = eventDate.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                    });
                    const eventTitle = event.title || event.summary || 'Untitled Event';
                    // Remove time prefix if it exists (from Google Calendar service formatting)
                    const cleanTitle = eventTitle.replace(/^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*/, '');
                    
                    return (
                      <div
                        key={event.id || event.googleEventId || index}
                        className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {formattedDate}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {formattedTime}
                            </div>
                          </div>
                        </div>
                        
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2">
                          {cleanTitle}
                        </h3>
                        
                        {event.host && (
                          <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            {event.host.profileImage ? (
                              <img
                                src={event.host.profileImage}
                                alt={event.host.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center text-white text-xs font-medium">
                                {event.host.initials}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-500 dark:text-gray-400">Host</div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {event.host.name}
                              </div>
                            </div>
                            {event.internalAttendees && event.internalAttendees.length > 0 && (
                              <div className="flex items-center -space-x-2">
                                {event.internalAttendees.slice(0, 3).map((attendee, idx) => (
                                  <div key={attendee.id || idx} className="relative group">
                                    {attendee.profileImage ? (
                                      <img
                                        src={attendee.profileImage}
                                        alt={attendee.name}
                                        title={attendee.name}
                                        className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-gray-800 hover:scale-110 transition-transform cursor-pointer"
                                      />
                                    ) : (
                                      <div 
                                        title={attendee.name}
                                        className="w-8 h-8 rounded-full bg-blue-400 dark:bg-blue-600 flex items-center justify-center text-white text-xs font-medium border-2 border-white dark:border-gray-800 hover:scale-110 transition-transform cursor-pointer"
                                      >
                                        {attendee.initials}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {event.internalAttendees.length > 3 && (
                                  <div 
                                    title={`+${event.internalAttendees.length - 3} more`}
                                    className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 text-xs font-medium border-2 border-white dark:border-gray-800"
                                  >
                                    +{event.internalAttendees.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          {!isCompleted && event.htmlLink && (
                            <a
                              href={event.htmlLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                            >
                              View in Calendar
                              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                          {isCompleted && (
                            <button
                              type="button"
                              onClick={() => handleViewMeetingSummary(event)}
                              className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500"
                            >
                              Meeting Summary
                              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center space-x-2 mt-6">
                        <button
                          onClick={() => setEventsCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={eventsCurrentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            // Show first page, last page, current page, and pages around current
                            if (
                              page === 1 ||
                              page === totalPages ||
                              (page >= eventsCurrentPage - 1 && page <= eventsCurrentPage + 1)
                            ) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => setEventsCurrentPage(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                    eventsCurrentPage === page
                                      ? 'bg-red-600 text-white'
                                      : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            } else if (
                              page === eventsCurrentPage - 2 ||
                              page === eventsCurrentPage + 2
                            ) {
                              return (
                                <span key={page} className="px-2 text-gray-500 dark:text-gray-400">
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                        </div>
                        
                        <button
                          onClick={() => setEventsCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={eventsCurrentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {activeTab === 'assessment' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white text-center">Assessment</h2>
              
              {/* Debug Information */}
              
              <div className="grid grid-cols-3 gap-6">
                {console.log('=== RENDERING ASSESSMENT CARDS ===')}
                {console.log('Lead data for cards:', lead)}
                {console.log('Discovery data check:', lead?.discovery)}
                {console.log('Overall assessment completed:', lead?.businessAssessmentProgress?.overallAssessmentCompleted)}
                {/* General Card - company rating = average of other section ratings */}
                <div 
                  className="bg-purple-100 dark:bg-purple-900 rounded-lg p-6 border border-purple-200 dark:border-purple-700 text-center cursor-pointer hover:shadow-lg transition-all duration-200 relative"
                  onClick={() => handleOpenAssessment('general')}
                >
                  {lead?.businessAssessment?.contractorType &&
                   lead?.businessAssessment?.projectType &&
                   (lead?.businessAssessment?.contractorType === 'General Contractor' 
                     ? lead?.businessAssessment?.projectFocusType 
                     : true) ? (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                  ) : (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                  )}
                  <div className="w-16 h-16 bg-purple-200 dark:bg-purple-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-purple-700 dark:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">General</h3>
                  {(() => {
                    const generalComplete = lead?.businessAssessment?.contractorType &&
                      lead?.businessAssessment?.projectType &&
                      (lead?.businessAssessment?.contractorType === 'General Contractor' ? lead?.businessAssessment?.projectFocusType : true);
                    if (!generalComplete) return null;
                    const ba = lead?.businessAssessment || {};
                    const salesR = lead?.salesData?.salesRating ?? ba.salesRating ?? 0;
                    const opsR = lead?.operationsData?.operationsRating ?? 0;
                    const finR = lead?.financeData?.financeRating ?? lead?.financeData?.rating ?? ba.financeRating ?? 0;
                    const techR = (lead?.techData?.techRating ?? ba.techRating ?? 0);
                    const mktR = (lead?.marketingData?.marketingRating ?? ba.marketingRating ?? 0);
                    const ratings = [salesR, opsR, finR, techR, mktR].filter(r => typeof r === 'number' && r > 0);
                    if (ratings.length === 0) return null;
                    const companyRating = Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 2) / 2;
                    const displayRating = Math.min(5, Math.max(1, Math.round(companyRating)));
                    return (
                      <div className="flex items-center justify-center gap-0.5 mt-2" title={`Company rating ${companyRating}/5 (from ${ratings.length} areas)`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-8 h-8 flex-shrink-0 ${star <= displayRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {/* Sales Card */}
                <div 
                  className="bg-red-100 dark:bg-red-900 rounded-lg p-6 border border-red-200 dark:border-red-700 text-center cursor-pointer hover:shadow-lg transition-all duration-200 relative"
                  onClick={() => handleOpenAssessment('sales')}
                >
                  {(() => {
                    const salesData = lead?.salesData || {};
                    const businessAssessment = lead?.businessAssessment || {};
                    const salesRating = salesData.salesRating ?? businessAssessment.salesRating ?? 0;
                    const closeRatio = salesData.closeRatio ?? businessAssessment.closeRatio ?? 0;
                    const referralBusinessPercentage = salesData.referralBusinessPercentage ?? businessAssessment.referralBusinessPercentage ?? 0;
                    const oneYearAgo = salesData.oneYearAgo ?? salesData.revenueData?.oneYearAgo ?? businessAssessment.revenueData?.oneYearAgo;
                    const currentYearProjection = salesData.currentYearProjection ?? salesData.revenueData?.currentYearProjection ?? businessAssessment.revenueData?.currentYearProjection;
                    const nextYearGoal = salesData.nextYearGoal ?? salesData.revenueData?.nextYearGoal ?? salesData.goalsData?.annualRevenueTarget ?? businessAssessment.revenueData?.nextYearGoal;
                    const isComplete = salesRating > 0 &&
                      closeRatio > 0 &&
                      referralBusinessPercentage > 0 &&
                      oneYearAgo &&
                      currentYearProjection &&
                      nextYearGoal;
                    return isComplete ? (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                    ) : (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                    );
                  })()}
                  <div className="w-16 h-16 bg-red-200 dark:bg-red-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="w-8 h-8 text-red-700 dark:text-red-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Sales</h3>
                  {(() => {
                    const salesData = lead?.salesData || {};
                    const businessAssessment = lead?.businessAssessment || {};
                    const salesRating = salesData.salesRating ?? businessAssessment.salesRating ?? 0;
                    const closeRatio = salesData.closeRatio ?? businessAssessment.closeRatio ?? 0;
                    const refPct = salesData.referralBusinessPercentage ?? businessAssessment.referralBusinessPercentage ?? 0;
                    const oneYearAgo = salesData.oneYearAgo ?? salesData.revenueData?.oneYearAgo ?? businessAssessment.revenueData?.oneYearAgo;
                    const currentProj = salesData.currentYearProjection ?? salesData.revenueData?.currentYearProjection ?? businessAssessment.revenueData?.currentYearProjection;
                    const nextGoal = salesData.nextYearGoal ?? salesData.revenueData?.nextYearGoal ?? salesData.goalsData?.annualRevenueTarget ?? businessAssessment.revenueData?.nextYearGoal;
                    const isComplete = salesRating > 0 && closeRatio > 0 && refPct > 0 && oneYearAgo && currentProj && nextGoal;
                    if (!isComplete || salesRating <= 0) return null;
                    return (
                      <div className="flex items-center justify-center gap-0.5 mt-2" title={`${salesRating}/5`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-8 h-8 flex-shrink-0 ${star <= salesRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {/* Operations Card */}
                <div 
                  className="bg-blue-100 dark:bg-blue-900 rounded-lg p-6 border border-blue-200 dark:border-blue-700 text-center cursor-pointer hover:shadow-lg transition-all duration-200 relative"
                  onClick={() => handleOpenAssessment('operations')}
                >
                  {lead?.operationsData?.operationsRating > 0 &&
                   (lead?.operationsData?.hasSOPs !== undefined && lead?.operationsData?.hasSOPs !== null) &&
                   (lead?.operationsData?.hasJobDescriptions !== undefined && lead?.operationsData?.hasJobDescriptions !== null) &&
                   (lead?.operationsData?.hasKPIs !== undefined && lead?.operationsData?.hasKPIs !== null) &&
                   lead?.operationsData?.teamSize &&
                   lead?.operationsData?.teamGrade ? (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                  ) : (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                  )}
                  <div className="w-16 h-16 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-8 h-8 text-blue-700 dark:text-blue-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Operations</h3>
                  {lead?.operationsData?.operationsRating > 0 &&
                   (lead?.operationsData?.hasSOPs !== undefined && lead?.operationsData?.hasSOPs !== null) &&
                   (lead?.operationsData?.hasJobDescriptions !== undefined && lead?.operationsData?.hasJobDescriptions !== null) &&
                   (lead?.operationsData?.hasKPIs !== undefined && lead?.operationsData?.hasKPIs !== null) &&
                   lead?.operationsData?.teamSize &&
                   lead?.operationsData?.teamGrade && (
                    <div className="flex items-center justify-center gap-0.5 mt-2" title={`${lead.operationsData.operationsRating}/5`}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className={`w-8 h-8 flex-shrink-0 ${star <= lead.operationsData.operationsRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                      ))}
                    </div>
                  )}
                </div>
                {/* Finance Card */}
                <div 
                  className="bg-green-100 dark:bg-green-900 rounded-lg p-6 border border-green-200 dark:border-green-700 text-center cursor-pointer hover:shadow-lg transition-all duration-200 relative"
                  onClick={() => handleOpenAssessment('finance')}
                >
                  {lead?.financeData?.financeRating > 0 &&
                   lead?.financeData?.unencumberedCash &&
                   lead?.financeData?.grossProfitMargin &&
                   lead?.financeData?.netProfitMargin ? (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                  ) : (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                  )}
                  <div className="w-16 h-16 bg-green-200 dark:bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="w-8 h-8 text-green-700 dark:text-green-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Finance</h3>
                  {lead?.financeData?.financeRating > 0 &&
                   lead?.financeData?.unencumberedCash &&
                   lead?.financeData?.grossProfitMargin &&
                   lead?.financeData?.netProfitMargin && (
                    <div className="flex items-center justify-center gap-0.5 mt-2" title={`${lead.financeData.financeRating}/5`}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className={`w-8 h-8 flex-shrink-0 ${star <= (lead.financeData.financeRating ?? lead.financeData.rating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                      ))}
                    </div>
                  )}
                </div>
                {/* Technology Card */}
                <div 
                  className="bg-purple-100 dark:bg-purple-900 rounded-lg p-6 border border-purple-200 dark:border-purple-700 text-center cursor-pointer hover:shadow-lg transition-all duration-200 relative"
                  onClick={() => handleOpenAssessment('technology')}
                >
                  {(() => {
                    const techData = lead?.techData || {};
                    const businessAssessment = lead?.businessAssessment || {};
                    
                    // Check that all required fields have been answered (empty strings count as answered)
                    const techRating = (techData.techRating !== undefined && techData.techRating !== null) 
                      ? techData.techRating 
                      : ((businessAssessment.techRating !== undefined && businessAssessment.techRating !== null) ? businessAssessment.techRating : null);
                    const whatCRM = (techData.whatCRM !== undefined && techData.whatCRM !== null && techData.whatCRM !== '')
                      ? techData.whatCRM
                      : ((businessAssessment.whatCRM !== undefined && businessAssessment.whatCRM !== null && businessAssessment.whatCRM !== '') ? businessAssessment.whatCRM : null);
                    const crmOptimization = (techData.crmOptimization !== undefined && techData.crmOptimization !== null) 
                      ? techData.crmOptimization 
                      : ((businessAssessment.crmOptimization !== undefined && businessAssessment.crmOptimization !== null) ? businessAssessment.crmOptimization : null);
                    const hasAutomations = (techData.hasAutomations !== undefined && techData.hasAutomations !== null)
                      ? techData.hasAutomations
                      : ((businessAssessment.hasAutomations !== undefined && businessAssessment.hasAutomations !== null) ? businessAssessment.hasAutomations : null);
                    const hasAITools = (techData.hasAITools !== undefined && techData.hasAITools !== null)
                      ? techData.hasAITools
                      : ((businessAssessment.hasAITools !== undefined && businessAssessment.hasAITools !== null) ? businessAssessment.hasAITools : null);
                    const hasWebsitePlugins = (techData.hasWebsitePlugins !== undefined && techData.hasWebsitePlugins !== null)
                      ? techData.hasWebsitePlugins
                      : ((techData.hasCustomWebsitePlugins !== undefined && techData.hasCustomWebsitePlugins !== null)
                        ? techData.hasCustomWebsitePlugins
                        : ((businessAssessment.hasWebsitePlugins !== undefined && businessAssessment.hasWebsitePlugins !== null)
                          ? businessAssessment.hasWebsitePlugins
                          : ((businessAssessment.hasCustomWebsitePlugins !== undefined && businessAssessment.hasCustomWebsitePlugins !== null)
                            ? businessAssessment.hasCustomWebsitePlugins
                            : null)));
                    
                    const isComplete = techRating !== null && techRating > 0 &&
                      whatCRM !== null &&
                      crmOptimization !== null &&
                      hasAutomations !== null &&
                      hasAITools !== null &&
                      hasWebsitePlugins !== null;
                    
                    return isComplete ? (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                    ) : (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                    );
                  })()}
                  <div className="w-16 h-16 bg-purple-200 dark:bg-purple-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-8 h-8 text-purple-700 dark:text-purple-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Technology</h3>
                  {(() => {
                    const techData = lead?.techData || {};
                    const businessAssessment = lead?.businessAssessment || {};
                    const techRating = (techData.techRating !== undefined && techData.techRating !== null) ? techData.techRating : (businessAssessment.techRating ?? null);
                    const whatCRM = (techData.whatCRM !== undefined && techData.whatCRM !== null && techData.whatCRM !== '') ? techData.whatCRM : (businessAssessment.whatCRM ?? null);
                    const crmOpt = (techData.crmOptimization !== undefined && techData.crmOptimization !== null) ? techData.crmOptimization : (businessAssessment.crmOptimization ?? null);
                    const hasAuto = (techData.hasAutomations !== undefined && techData.hasAutomations !== null) ? techData.hasAutomations : (businessAssessment.hasAutomations ?? null);
                    const hasAI = (techData.hasAITools !== undefined && techData.hasAITools !== null) ? techData.hasAITools : (businessAssessment.hasAITools ?? null);
                    const hasPlugins = (techData.hasWebsitePlugins !== undefined && techData.hasWebsitePlugins !== null) ? techData.hasWebsitePlugins : ((techData.hasCustomWebsitePlugins !== undefined && techData.hasCustomWebsitePlugins !== null) ? techData.hasCustomWebsitePlugins : (businessAssessment.hasWebsitePlugins ?? businessAssessment.hasCustomWebsitePlugins ?? null));
                    const isComplete = techRating !== null && techRating > 0 && whatCRM !== null && crmOpt !== null && hasAuto !== null && hasAI !== null && hasPlugins !== null;
                    if (!isComplete || !techRating) return null;
                    const rating = techData.techRating ?? businessAssessment.techRating ?? 0;
                    return (
                      <div className="flex items-center justify-center gap-0.5 mt-2" title={`${rating}/5`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-8 h-8 flex-shrink-0 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {/* Marketing Card */}
                <div 
                  className="bg-orange-100 dark:bg-orange-900 rounded-lg p-6 border border-orange-200 dark:border-orange-700 text-center cursor-pointer hover:shadow-lg transition-all duration-200 relative"
                  onClick={() => handleOpenAssessment('marketing')}
                >
                  {(() => {
                    const marketingData = lead?.marketingData || {};
                    const businessAssessment = lead?.businessAssessment || {};
                    
                    // Check that all required fields have been answered
                    const marketingRating = (marketingData.marketingRating !== undefined && marketingData.marketingRating !== null) 
                      ? marketingData.marketingRating 
                      : ((businessAssessment.marketingRating !== undefined && businessAssessment.marketingRating !== null) ? businessAssessment.marketingRating : null);
                    const workingWithAgency = (marketingData.workingWithMarketingAgency !== undefined && marketingData.workingWithMarketingAgency !== null)
                      ? marketingData.workingWithMarketingAgency
                      : ((marketingData.workingWithAgency !== undefined && marketingData.workingWithAgency !== null)
                        ? marketingData.workingWithAgency
                        : ((businessAssessment.workingWithMarketingAgency !== undefined && businessAssessment.workingWithMarketingAgency !== null)
                          ? businessAssessment.workingWithMarketingAgency
                          : null));
                    const websiteManager = marketingData.marketingAgencyType || businessAssessment.marketingAgencyType || marketingData.websiteManager || businessAssessment.websiteManager;
                    
                    const isComplete = marketingRating !== null && marketingRating > 0 &&
                      workingWithAgency !== null &&
                      websiteManager;
                    
                    return isComplete ? (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                    ) : (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                    );
                  })()}
                  <div className="w-16 h-16 bg-orange-200 dark:bg-orange-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-orange-700 dark:text-orange-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Marketing</h3>
                  {(() => {
                    const marketingData = lead?.marketingData || {};
                    const businessAssessment = lead?.businessAssessment || {};
                    const marketingRating = (marketingData.marketingRating !== undefined && marketingData.marketingRating !== null) ? marketingData.marketingRating : (businessAssessment.marketingRating ?? null);
                    const workingWithAgency = (marketingData.workingWithMarketingAgency !== undefined && marketingData.workingWithMarketingAgency !== null) ? marketingData.workingWithMarketingAgency : ((marketingData.workingWithAgency !== undefined && marketingData.workingWithAgency !== null) ? marketingData.workingWithAgency : (businessAssessment.workingWithMarketingAgency ?? null));
                    const websiteManager = marketingData.marketingAgencyType || businessAssessment.marketingAgencyType || marketingData.websiteManager || businessAssessment.websiteManager;
                    const isComplete = marketingRating !== null && marketingRating > 0 && workingWithAgency !== null && websiteManager;
                    if (!isComplete || !marketingRating) return null;
                    const rating = marketingData.marketingRating ?? businessAssessment.marketingRating ?? 0;
                    return (
                      <div className="flex items-center justify-center gap-0.5 mt-2" title={`${rating}/5`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-8 h-8 flex-shrink-0 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* Assessment Analysis Section */}
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => navigate(`/crm/lead/${leadId}/assessment-analysis`)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <span>View Assessment Analysis</span>
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </button>
              </div>
            </div>
          )}

          {activeTab === 'proposal' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              {/* Spacer keeps the heading centered while the edit control sits on the right */}
              <div className="flex items-center justify-between gap-3">
                <div className="w-32 shrink-0" />
                <h2 className="flex-1 text-xl font-semibold text-gray-900 dark:text-white text-center">Proposed Program</h2>
                <div className="w-32 shrink-0 flex justify-end">
                  {buildingProposedProgram ? (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingProposedProgram(false);
                        setCreatingProposedProgram(false);
                        setProposedProgramEditKey((k) => k + 1);
                      }}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  ) : proposedProgram ? (
                    <button
                      type="button"
                      onClick={() => setEditingProposedProgram(true)}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                    >
                      Edit Program
                    </button>
                  ) : null}
                </div>
              </div>
              {buildingProposedProgram && (
                <p className="mt-1 text-sm text-center text-gray-500 dark:text-gray-400">
                  Choose a program and deliverables, then Confirm Program to save.
                </p>
              )}

              <div className="mt-6 space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  {showProposedProgramBuilder ? (
                    <ProgramDetail
                      key={`proposed-program-${proposedProgram ? (editingProposedProgram ? 'edit' : 'view') : 'create'}-${proposedProgramEditKey}`}
                      embedded={true}
                      selectedProgramProp={proposedProgram ? proposedProgramForDetail : defaultProposalProgram.program}
                      selectedTierProp={proposedProgram ? (proposedProgram.selectedTier || '') : defaultProposalProgram.tier}
                      billingCycleProp={proposedProgram?.billingCycle || 'monthly'}
                      proposalMode={true}
                      hideActionButton={!buildingProposedProgram}
                      hideProgramDropdown={!buildingProposedProgram}
                      leadIdProp={leadId}
                      confirmMode={true}
                      proposedProgramData={proposedProgramData}
                      initialSelectedDeliverables={proposedProgramDeliverables}
                      initialDeliverableQuantities={proposedProgramQuantities}
                      onConfirmProgram={saveProposedProgram}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 dark:text-gray-400">
                        No program confirmed yet. Confirm one here or in Assessment Analysis.
                      </p>
                      {defaultProposalProgram ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCreatingProposedProgram(true);
                            setProposedProgramEditKey((k) => k + 1);
                          }}
                          className="mt-4 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                        >
                          Confirm a Program
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bids' && (
            <AccountAlaCarteBidsTab
              accountId={leadId}
              accountType="Leads"
              companyName={lead?.company || ''}
              active={activeTab === 'bids'}
            />
          )}

          {activeTab === 'collection' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <AccountDocumentsTab
                accountId={leadId}
                account={lead}
                onAccountChange={(patch) => setLead((prev) => ({ ...prev, ...patch }))}
                storageFolder="lead-documents"
                filePrefix="lead"
              />
            </div>
          )}


        </div>

        {/* Sidebar */}
        <div className="w-96 space-y-6">
          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4 text-center text-gray-900 dark:text-white">Contact Information</h2>
            <div className="space-y-4">
              {/* Company logo + contact image, side by side */}
              <div className="flex justify-center items-center gap-4 mb-6">
                {/* Company Logo */}
                <div
                  className={`relative group shrink-0 ${isEditMode ? 'cursor-pointer' : ''}`}
                  onDragOver={handleCompanyLogoDragOver}
                  onDragLeave={handleCompanyLogoDragLeave}
                  onDrop={handleCompanyLogoDrop}
                >
                  <div
                    className={`w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden transition-all ${
                      companyLogoDragOver ? 'ring-4 ring-blue-500 ring-offset-2 scale-105' : ''
                    }`}
                    title={lead.company ? `${lead.company} logo` : 'Company logo'}
                  >
                    {uploadingCompanyLogo ? (
                      <div className="flex flex-col items-center justify-center">
                        <BBALoader size={32} />
                        <span className="text-xs text-gray-500 mt-2">Uploading...</span>
                      </div>
                    ) : companyLogoDragOver ? (
                      <div className="flex flex-col items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-xs text-blue-500 mt-2 font-medium">Drop logo here</span>
                      </div>
                    ) : getCrmCompanyLogoDisplayUrl(lead) ? (
                      <img
                        src={getCrmCompanyLogoDisplayUrl(lead)}
                        alt={`${lead.company || 'Company'} logo`}
                        className="w-full h-full object-contain p-4"
                      />
                    ) : (
                      <Building2 className="w-10 h-10 text-gray-400 dark:text-gray-500" aria-hidden />
                    )}
                  </div>
                  {isEditMode && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex space-x-2">
                        <label className="cursor-pointer bg-white dark:bg-gray-700 rounded-full p-2 hover:bg-gray-50 dark:hover:bg-gray-600">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleCompanyLogoUpload}
                            disabled={uploadingCompanyLogo}
                          />
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </label>
                        {lead.crmCompanyLogo && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                setUploadingCompanyLogo(true);
                                try {
                                  await deleteObject(ref(storage, lead.crmCompanyLogo));
                                } catch (err) {
                                  console.log('Error deleting company logo file:', err);
                                }
                                const updatedLead = { ...lead, crmCompanyLogo: '' };
                                await updateAccount(leadId, updatedLead);
                                setLead(updatedLead);
                              } catch (error) {
                                console.error('Error deleting company logo:', error);
                                alert('Failed to delete logo. Please try again.');
                              } finally {
                                setUploadingCompanyLogo(false);
                              }
                            }}
                            disabled={uploadingCompanyLogo}
                            className="bg-white dark:bg-gray-700 rounded-full p-2 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                            title="Remove CRM-only logo (client portal logo is unchanged)"
                            aria-label="Remove CRM-only company logo"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Image */}
                <div
                  className={`relative group shrink-0 ${isEditMode ? 'cursor-pointer' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className={`w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden transition-all ${
                    isDraggingOver ? 'ring-4 ring-blue-500 ring-offset-2 scale-105' : ''
                  }`}>
                    {uploadingImage ? (
                      <div className="flex flex-col items-center justify-center">
                        <BBALoader size={32} />
                        <span className="text-xs text-gray-500 mt-2">Uploading...</span>
                      </div>
                    ) : isDraggingOver ? (
                      <div className="flex flex-col items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-xs text-blue-500 mt-2 font-medium">Drop image here</span>
                      </div>
                    ) : lead.contact?.photoURL ? (
                      <img 
                        src={lead.contact.photoURL} 
                        alt={`${lead.firstName || lead.contact?.firstName} ${lead.lastName || lead.contact?.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-4xl text-gray-400 dark:text-gray-500">
                        {(lead.firstName || lead.contact?.firstName)?.[0]}{(lead.lastName || lead.contact?.lastName)?.[0]}
                      </span>
                    )}
                  </div>
                  {isEditMode && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex space-x-2">
                        <label className="cursor-pointer bg-white dark:bg-gray-700 rounded-full p-2 hover:bg-gray-50 dark:hover:bg-gray-600">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                          />
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </label>
                        {lead.contact?.photoURL && (
                          <button
                            onClick={async () => {
                              try {
                                setUploadingImage(true);
                                
                                // Delete from Firebase Storage
                                const oldImageRef = ref(storage, lead.contact.photoURL);
                                await deleteObject(oldImageRef);
                                
                                // Update the lead
                                const updatedLead = {
                                  ...lead,
                                  contact: {
                                    ...lead.contact,
                                    photoURL: ''
                                  }
                                };
                                
                                await updateAccount(leadId, updatedLead);
                                setLead(updatedLead);
                              } catch (error) {
                                console.error('Error deleting image:', error);
                                alert('Failed to delete image. Please try again.');
                              } finally {
                                setUploadingImage(false);
                              }
                            }}
                            disabled={uploadingImage}
                            className="bg-white dark:bg-gray-700 rounded-full p-2 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Details */}
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <User className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</div>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <input
                          type="text"
                          value={lead.discovery?.firstName || lead.firstName || lead.contact?.firstName || ''}
                          onChange={(e) => {
                            const updatedData = { ...lead };
                            updatedData.firstName = e.target.value;
                            if (updatedData.contact) {
                              updatedData.contact.firstName = e.target.value;
                            }
                            setLead(updatedData);
                          }}
                          className="flex-1 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                          placeholder="First Name"
                        />
                        <input
                          type="text"
                          value={lead.discovery?.lastName || lead.lastName || lead.contact?.lastName || ''}
                          onChange={(e) => {
                            const updatedData = { ...lead };
                            updatedData.lastName = e.target.value;
                            if (updatedData.contact) {
                              updatedData.contact.lastName = e.target.value;
                            }
                            setLead(updatedData);
                          }}
                          className="flex-1 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                          placeholder="Last Name"
                        />
                      </div>
                    ) : (
                      <div 
                        className="text-sm text-gray-900 dark:text-white flex items-center justify-between gap-2 mt-1"
                      >
                        <span className="flex-1 flex items-center gap-2">
                          {(() => {
                            const firstName = lead.discovery?.firstName || lead.firstName || lead.contact?.firstName || '';
                            const lastName = lead.discovery?.lastName || lead.lastName || lead.contact?.lastName || '';
                            const fullName = `${firstName} ${lastName}`.trim();
                            
                            if (!firstName && !lastName) {
                              return <span>Not set</span>;
                            }
                            
                            return (
                              <>
                                {firstName ? (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(firstName);
                                    }}
                                    className="cursor-pointer hover:opacity-70 transition-opacity"
                                    title="Click to copy first name"
                                  >
                                    {firstName}
                                  </span>
                                ) : null}
                                {lastName && <span>{lastName}</span>}
                              </>
                            );
                          })()}
                        </span>
                        {(() => {
                          const firstName = lead.discovery?.firstName || lead.firstName || lead.contact?.firstName || '';
                          const lastName = lead.discovery?.lastName || lead.lastName || lead.contact?.lastName || '';
                          const fullName = `${firstName} ${lastName}`.trim();
                          return fullName && fullName !== 'Not set' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(fullName);
                              }}
                              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0"
                              title="Copy full name"
                            >
                              <Copy className="w-4 h-4 text-red-500" />
                            </button>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Building2 className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Company</div>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <div className="mt-1">
                        <input
                          type="text"
                          value={lead.companyName || lead.company || ''}
                          onChange={(e) => {
                            const updatedData = { ...lead };
                            updatedData.companyName = e.target.value;
                            updatedData.company = e.target.value;
                            setLead(updatedData);
                          }}
                          className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                          placeholder="Company Name"
                        />
                      </div>
                    ) : (
                      <div 
                        className="text-sm text-gray-900 dark:text-white cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-between gap-2"
                        onClick={() => {
                          const company = lead.discovery?.companyName || lead.companyName || lead.company;
                          if (company && company !== 'Not set') {
                            copyToClipboard(company);
                          }
                        }}
                      >
                        <span className="flex-1">{lead.discovery?.companyName || lead.companyName || lead.company || 'Not set'}</span>
                        {(() => {
                          const company = lead.discovery?.companyName || lead.companyName || lead.company;
                          return company && company !== 'Not set' && (
                            <Copy className="w-4 h-4 text-red-500 flex-shrink-0" />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Mail className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</div>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <div className="mt-1">
                        <input
                          type="email"
                          value={lead.contact?.email || ''}
                          onChange={(e) => {
                            const updatedData = { ...lead };
                            updatedData.contact = {
                              ...updatedData.contact,
                              email: e.target.value
                            };
                            setLead(updatedData);
                          }}
                          className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                          placeholder="Email Address"
                        />
                      </div>
                    ) : (
                      <div 
                        className="text-sm text-gray-900 dark:text-white cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-between gap-2"
                        onClick={() => {
                          if (lead.contact?.email && lead.contact.email !== 'Not set') {
                            copyToClipboard(lead.contact.email);
                          }
                        }}
                      >
                        <span className="flex-1">{lead.contact?.email || 'Not set'}</span>
                        {lead.contact?.email && lead.contact.email !== 'Not set' && (
                          <Copy className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <ContactEmailAliases
                  account={lead}
                  accountId={leadId}
                  contactKey={PRIMARY_CONTACT_KEY}
                  contactName={`${lead.discovery?.firstName || lead.firstName || lead.contact?.firstName || ''} ${lead.discovery?.lastName || lead.lastName || lead.contact?.lastName || ''}`.trim()}
                  primaryEmail={lead.contact?.email || lead.email}
                  liveContactKeys={[PRIMARY_CONTACT_KEY]}
                  onSaved={(fields) => setLead((prev) => (prev ? { ...prev, ...fields } : prev))}
                  onCopy={copyToClipboard}
                  rowClassName="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                />
                <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Phone className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</div>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <div className="mt-1">
                        <input
                          type="tel"
                          value={lead.discovery?.phoneNumber || lead.phoneNumber || lead.contact?.phone || ''}
                          onChange={(e) => {
                            const updatedData = { ...lead };
                            updatedData.phoneNumber = e.target.value;
                            if (updatedData.contact) {
                              updatedData.contact.phone = e.target.value;
                            }
                            setLead(updatedData);
                          }}
                          className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                          placeholder="Phone Number"
                        />
                      </div>
                    ) : (
                      <div 
                        className="text-sm text-gray-900 dark:text-white cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-between gap-2"
                        onClick={() => {
                          const phoneNumber = lead.discovery?.phoneNumber || lead.phoneNumber || lead.contact?.phone;
                          if (phoneNumber && phoneNumber !== 'Not set') {
                            copyToClipboard(phoneNumber);
                          }
                        }}
                      >
                        <span className="flex-1">
                          {(() => {
                            const phoneNumber = lead.discovery?.phoneNumber || lead.phoneNumber || lead.contact?.phone;
                            console.log('Phone display - discovery:', lead.discovery?.phoneNumber);
                            console.log('Phone display - lead:', lead.phoneNumber);
                            console.log('Phone display - contact:', lead.contact?.phone);
                            console.log('Phone display - final:', phoneNumber);
                            return phoneNumber || 'Not set';
                          })()}
                        </span>
                        {(() => {
                          const phoneNumber = lead.discovery?.phoneNumber || lead.phoneNumber || lead.contact?.phone;
                          return phoneNumber && phoneNumber !== 'Not set' && (
                            <Copy className="w-4 h-4 text-red-500 flex-shrink-0" />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</div>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <div className="space-y-2 mt-1">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={lead.discovery?.streetAddress || lead.streetAddress || lead.location || ''}
                            onChange={(e) => {
                              const updatedData = { ...lead };
                              updatedData.streetAddress = e.target.value;
                              updatedData.location = e.target.value;
                              setLead(updatedData);
                            }}
                            className="flex-1 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                            placeholder="Address"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={lead.discovery?.city || lead.city || ''}
                            onChange={(e) => {
                              const updatedData = { ...lead, city: e.target.value };
                              setLead(updatedData);
                            }}
                            className="flex-1 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                            placeholder="City"
                          />
                          <input
                            type="text"
                            value={lead.discovery?.state || lead.state || ''}
                            onChange={(e) => {
                              const updatedData = { ...lead, state: e.target.value };
                              setLead(updatedData);
                            }}
                            className="w-20 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                            placeholder="State"
                          />
                          <input
                            type="text"
                            value={lead.discovery?.zipCode || lead.zipCode || ''}
                            onChange={(e) => {
                              const updatedData = { ...lead, zipCode: e.target.value };
                              setLead(updatedData);
                            }}
                            className="w-24 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                            placeholder="ZIP"
                          />
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="text-sm text-gray-900 dark:text-white cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-between gap-2"
                        onClick={() => {
                          const parts = [];
                          const streetAddress = lead.discovery?.streetAddress || lead.streetAddress;
                          const city = lead.discovery?.city || lead.city;
                          const state = lead.discovery?.state || lead.state;
                          const zipCode = lead.discovery?.zipCode || lead.zipCode;
                          const country = lead.discovery?.country || lead.country;
                          
                          if (streetAddress) parts.push(streetAddress);
                          if (city) parts.push(city);
                          if (state) parts.push(state);
                          if (zipCode) parts.push(zipCode);
                          if (country && country !== 'US') parts.push(country);
                          const address = parts.length > 0 ? parts.join(', ') : 'Not set';
                          if (address && address !== 'Not set') {
                            copyToClipboard(address);
                          }
                        }}
                      >
                        <span className="flex-1">
                          {(() => {
                            const parts = [];
                            const streetAddress = lead.discovery?.streetAddress || lead.streetAddress;
                            const city = lead.discovery?.city || lead.city;
                            const state = lead.discovery?.state || lead.state;
                            const zipCode = lead.discovery?.zipCode || lead.zipCode;
                            const country = lead.discovery?.country || lead.country;
                            
                            if (streetAddress) parts.push(streetAddress);
                            if (city) parts.push(city);
                            if (state) parts.push(state);
                            if (zipCode) parts.push(zipCode);
                            if (country && country !== 'US') parts.push(country);
                            return parts.length > 0 ? parts.join(', ') : 'Not set';
                          })()}
                        </span>
                        {(() => {
                          const parts = [];
                          const streetAddress = lead.discovery?.streetAddress || lead.streetAddress;
                          const city = lead.discovery?.city || lead.city;
                          const state = lead.discovery?.state || lead.state;
                          const zipCode = lead.discovery?.zipCode || lead.zipCode;
                          const country = lead.discovery?.country || lead.country;
                          
                          if (streetAddress) parts.push(streetAddress);
                          if (city) parts.push(city);
                          if (state) parts.push(state);
                          if (zipCode) parts.push(zipCode);
                          if (country && country !== 'US') parts.push(country);
                          const address = parts.length > 0 ? parts.join(', ') : 'Not set';
                          return address && address !== 'Not set' && (
                            <Copy className="w-4 h-4 text-red-500 flex-shrink-0" />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Building2 className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Website</div>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <div className="mt-1">
                        <input
                          type="url"
                          value={lead.discovery?.website || lead.website || ''}
                          onChange={(e) => {
                            const updatedData = { ...lead, website: e.target.value };
                            setLead(updatedData);
                          }}
                          className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                          placeholder="https://example.com"
                        />
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900 dark:text-white">
                        {(lead.discovery?.website || lead.website) ? (
                          <a
                            href={(lead.discovery?.website || lead.website).startsWith('http') ? (lead.discovery?.website || lead.website) : `https://${lead.discovery?.website || lead.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer flex items-center justify-between gap-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Also copy to clipboard on click
                              copyToClipboard(lead.discovery?.website || lead.website);
                            }}
                            title="Click to open website in new tab (also copies to clipboard)"
                          >
                            <span className="flex-1">{lead.discovery?.website || lead.website}</span>
                            <Copy className="w-4 h-4 text-red-500 flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="cursor-pointer hover:opacity-70 transition-opacity">Not set</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      </div>


      {/* Edit contact log (interaction) */}
      {interactionNoteModal?.note && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600/60 dark:bg-black/70"
          onClick={closeInteractionNoteModal}
          role="presentation"
        >
          <div
            className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="interaction-log-modal-title"
          >
            <h3 id="interaction-log-modal-title" className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Contact log
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="interaction-log-date" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Date
                </label>
                <input
                  id="interaction-log-date"
                  type="date"
                  value={interactionModalDate}
                  onChange={(e) => setInteractionModalDate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="interaction-log-who" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Initiated by
                </label>
                <select
                  id="interaction-log-who"
                  value={interactionModalInitiatedBy}
                  onChange={(e) => setInteractionModalInitiatedBy(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                >
                  <option value="client">Client</option>
                  <option value="bba">BBA</option>
                </select>
              </div>
              <div>
                <label htmlFor="interaction-log-channel" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Channel
                </label>
                <select
                  id="interaction-log-channel"
                  value={interactionModalChannel}
                  onChange={(e) => setInteractionModalChannel(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                >
                  <option value="phone">📞 Phone</option>
                  <option value="text">📲 Text</option>
                  <option value="email">📧 Email</option>
                </select>
              </div>
              <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-300">
                {buildInteractionNoteContent(interactionModalInitiatedBy, interactionModalChannel)}
              </p>
            </div>
            <div className="mt-6 flex items-end justify-between gap-3">
              <button
                type="button"
                onClick={() => deleteInteractionNoteFromModal()}
                className="rounded-md border border-red-400 bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-red-700 dark:border-red-500 dark:bg-red-700 dark:hover:bg-red-600"
              >
                Delete
              </button>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={closeInteractionNoteModal}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveInteractionNoteModal()}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-red-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit text note */}
      {textNoteModal?.note && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600/60 dark:bg-black/70"
          onClick={closeTextNoteModal}
          role="presentation"
        >
          <div
            className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="text-note-modal-title"
          >
            <h3 id="text-note-modal-title" className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Note
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="text-note-date" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Date
                </label>
                <input
                  id="text-note-date"
                  type="date"
                  value={textNoteModalDate}
                  onChange={(e) => setTextNoteModalDate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="text-note-body" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Note
                </label>
                <textarea
                  id="text-note-body"
                  value={textNoteModalBody}
                  onChange={(e) => setTextNoteModalBody(e.target.value)}
                  rows={5}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  placeholder="Note text"
                />
              </div>
            </div>
            <div className="mt-6 flex items-end justify-between gap-3">
              <button
                type="button"
                onClick={() => deleteTextNoteFromModal()}
                className="rounded-md border border-red-400 bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-red-700 dark:border-red-500 dark:bg-red-700 dark:hover:bg-red-600"
              >
                Delete
              </button>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={closeTextNoteModal}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!textNoteModalBody.trim()}
                  onClick={() => saveTextNoteModal()}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {showAddDocumentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Add Document</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Document Title
                </label>
                <input
                  type="text"
                  value={newDocument.title || ''}
                  onChange={(e) => setNewDocument({...newDocument, title: e.target.value})}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter document title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload File
                </label>
                <input
                  type="file"
                  onChange={(e) => setNewDocument({...newDocument, file: e.target.files[0]})}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                />
                {newDocument.file && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Selected: {newDocument.file.name}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddDocumentModal(false);
                  setNewDocument({ title: '', file: null });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDocument}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white text-center">Delete Lead</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete this lead? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteLead}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2 w-32">
                {(isEditMode || isEditingAssessmentModal) ? (
                  <>
                    <button
                      onClick={async () => {
                        await handleSaveAssessmentCard(currentAssessmentType);
                        setShowAssessmentModal(false);
                        setIsEditingAssessmentModal(false);
                        setAssessmentEditData({});
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    {!isEditMode && (
                      <button
                        onClick={() => {
                          setIsEditingAssessmentModal(false);
                          setAssessmentEditData({});
                        }}
                        className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        Cancel
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => {
                      initializeAssessmentEditData(currentAssessmentType);
                      setIsEditingAssessmentModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white flex-1 text-center">
                {currentAssessmentType === 'general' && 'General Assessment'}
                {currentAssessmentType === 'business' && 'Business Assessment'}
                {currentAssessmentType === 'background' && 'Background Assessment'}
                {currentAssessmentType === 'sales' && 'Sales Assessment'}
                {currentAssessmentType === 'operations' && 'Operations Assessment'}
                {currentAssessmentType === 'finance' && 'Finance Assessment'}
                {currentAssessmentType === 'technology' && 'Technology Assessment'}
                {currentAssessmentType === 'marketing' && 'Marketing Assessment'}
                {currentAssessmentType === 'hr' && 'HR Assessment'}
              </h3>
              <div className="w-32 flex justify-end">
                <button
                  onClick={() => {
                    setShowAssessmentModal(false);
                    setIsEditingAssessmentModal(false);
                    setAssessmentEditData({});
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {(isEditMode || isEditingAssessmentModal) && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-sm text-blue-800 dark:text-blue-200 text-center">
                  <strong>Edit Mode:</strong> Fields below are editable. Click "Save" in the header when done.
                </p>
              </div>
            )}

            {/* General Assessment Content */}
            {currentAssessmentType === 'general' && (
              <div className="space-y-6">
                {console.log('=== RENDERING GENERAL ASSESSMENT ===')}
                {console.log('Business assessment:', lead?.businessAssessment)}
                
                {/* Contractor Type, Project Type, Project Focus */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Contractor Type */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Contractor Type</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="text"
                        value={assessmentEditData.general?.contractorType || ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          general: { ...assessmentEditData.general, contractorType: e.target.value }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="e.g., General Contractor"
                      />
                    ) : lead?.businessAssessment?.contractorType ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead.businessAssessment.contractorType}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>

                  {/* Project Type */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Project Type</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="text"
                        value={assessmentEditData.general?.projectType || ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          general: { ...assessmentEditData.general, projectType: e.target.value }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="e.g., Residential"
                      />
                    ) : lead?.businessAssessment?.projectType ? (
                      <p className="text-gray-600 dark:text-gray-300 capitalize">
                        {lead.businessAssessment.projectType === 'both' ? 'Residential & Commercial' : lead.businessAssessment.projectType}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>

                  {/* Project Focus */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Project Focus</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <textarea
                        value={(() => {
                          const contractorType = assessmentEditData.general?.contractorType || lead?.businessAssessment?.contractorType;
                          let focusData = null;
                          
                          if (contractorType === 'General Contractor') {
                            focusData = assessmentEditData.general?.projectFocusType || lead?.businessAssessment?.projectFocusType;
                            if (focusData && typeof focusData === 'string') {
                              return focusData;
                            }
                            if (focusData && Array.isArray(focusData)) {
                              return focusData.join(', ');
                            }
                          } else if (contractorType === 'Subcontractor') {
                            focusData = assessmentEditData.general?.subcontractorFocus || lead?.businessAssessment?.subcontractorFocus;
                            if (focusData && Array.isArray(focusData)) {
                              return focusData.join(', ');
                            }
                          } else if (contractorType === 'Home Improvement') {
                            focusData = assessmentEditData.general?.homeImprovementFocus || lead?.businessAssessment?.homeImprovementFocus;
                            if (focusData && Array.isArray(focusData)) {
                              return focusData.join(', ');
                            }
                          }
                          return '';
                        })()}
                        onChange={(e) => {
                          const contractorType = assessmentEditData.general?.contractorType || lead?.businessAssessment?.contractorType;
                          const value = e.target.value;
                          const valueArray = value.split(',').map(v => v.trim()).filter(v => v);
                          
                          let updateData = { ...assessmentEditData.general };
                          
                          if (contractorType === 'General Contractor') {
                            updateData.projectFocusType = valueArray.length === 1 ? valueArray[0] : valueArray;
                          } else if (contractorType === 'Subcontractor') {
                            updateData.subcontractorFocus = valueArray;
                          } else if (contractorType === 'Home Improvement') {
                            updateData.homeImprovementFocus = valueArray;
                          }
                          
                          setAssessmentEditData({
                            ...assessmentEditData,
                            general: updateData
                          });
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="Enter values separated by commas (e.g., New Construction, Remodels)"
                        rows={3}
                      />
                    ) : (() => {
                      const contractorType = lead?.businessAssessment?.contractorType;
                      let focusData = null;
                      
                      if (contractorType === 'General Contractor') {
                        focusData = lead?.businessAssessment?.projectFocusType;
                        // Handle backward compatibility: convert string to array if needed
                        if (focusData && typeof focusData === 'string') {
                          focusData = [focusData];
                        }
                        if (focusData && Array.isArray(focusData) && focusData.length > 0) {
                          return (
                            <div className="space-y-1">
                              {focusData.map((focus, index) => (
                                <p key={index} className="text-gray-600 dark:text-gray-300 capitalize">
                                  {focus === 'newConstruction' ? 'New Construction' : focus}
                                </p>
                              ))}
                            </div>
                          );
                        }
                      } else if (contractorType === 'Subcontractor') {
                        focusData = lead?.businessAssessment?.subcontractorFocus;
                        if (focusData && Array.isArray(focusData) && focusData.length > 0) {
                          return (
                            <div className="space-y-1">
                              {focusData.map((focus, index) => (
                                <p key={index} className="text-gray-600 dark:text-gray-300">
                                  {focus}
                                </p>
                              ))}
                            </div>
                          );
                        }
                      } else if (contractorType === 'Home Improvement') {
                        focusData = lead?.businessAssessment?.homeImprovementFocus;
                        if (focusData && Array.isArray(focusData) && focusData.length > 0) {
                          return (
                            <div className="space-y-1">
                              {focusData.map((focus, index) => (
                                <p key={index} className="text-gray-600 dark:text-gray-300">
                                  {focus}
                                </p>
                              ))}
                            </div>
                          );
                        }
                      }
                      
                      return <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>;
                    })()}
                  </div>
                </div>
              </div>
            )}


            {/* Business Assessment Content */}
            {currentAssessmentType === 'business' && (
              <div className="space-y-6">
                {console.log('=== RENDERING BUSINESS ASSESSMENT ===')}
                {console.log('Business assessment data:', lead?.businessAssessment)}
                {console.log('Business assessment progress:', lead?.businessAssessmentProgress)}
                
                {!lead?.businessAssessment ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No business assessment data available for this lead.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Contractor Type */}
                    {lead?.businessAssessment?.contractorType && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Contractor Type</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.contractorType}
                        </p>
                      </div>
                    )}

                    {/* Project Type */}
                    {lead?.businessAssessment?.projectType && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Project Type</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.projectType}
                        </p>
                      </div>
                    )}

                    {/* Design Services */}
                    {lead?.businessAssessment?.designServices && lead?.businessAssessment?.designServices !== '' && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Design Services</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.designServices}
                        </p>
                      </div>
                    )}

                    {/* Design Fee Percentage - only show if design services is not "no-design" */}
                    {lead?.businessAssessment?.designFeePercentage && 
                     lead?.businessAssessment?.designFeePercentage !== '' && 
                     lead?.businessAssessment?.designServices !== 'no-design' && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Design Fee Percentage</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.designFeePercentage}%
                        </p>
                      </div>
                    )}

                    {/* Preferred Residential */}
                    {lead?.businessAssessment?.preferredResidential && lead?.businessAssessment?.preferredResidential !== '' && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Preferred Residential</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.preferredResidential}
                        </p>
                      </div>
                    )}

                    {/* Most Common Residential */}
                    {lead?.businessAssessment?.mostCommonResidential && lead?.businessAssessment?.mostCommonResidential !== '' && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Most Common Residential</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.mostCommonResidential}
                        </p>
                      </div>
                    )}

                    {/* Residential Focus */}
                    {lead?.businessAssessment?.residentialFocus && lead?.businessAssessment?.residentialFocus.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Residential Focus</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.residentialFocus.join(', ')}
                        </p>
                      </div>
                    )}

                    {/* Homeowners Percentage - only show if not exactly 50 */}
                    {lead?.businessAssessment?.homeownersPercentage !== undefined && lead?.businessAssessment?.homeownersPercentage !== 50 && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Homeowners Percentage</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.homeownersPercentage}%
                        </p>
                      </div>
                    )}

                    {/* Residential Percentage - only show if not exactly 50 */}
                    {lead?.businessAssessment?.residentialPercentage !== undefined && lead?.businessAssessment?.residentialPercentage !== 50 && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Residential Percentage</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {lead?.businessAssessment?.residentialPercentage}%
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}


            {/* Background Assessment Content */}
            {currentAssessmentType === 'background' && (assessmentData?.discovery || lead?.discovery) && (
              <div className="space-y-6">
                {(assessmentData?.discovery || lead?.discovery)?.background && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Background & Experience</h4>
                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                      {(assessmentData?.discovery || lead?.discovery)?.background}
                    </p>
                  </div>
                )}
                {(assessmentData?.discovery || lead?.discovery)?.whyStarted && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Why They Started Their Company</h4>
                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                      {(assessmentData?.discovery || lead?.discovery)?.whyStarted}
                    </p>
                  </div>
                )}
                {(assessmentData?.discovery || lead?.discovery)?.marketInfo && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Market Information</h4>
                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                      {(assessmentData?.discovery || lead?.discovery)?.marketInfo}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Show message when no background assessment data available */}
            {currentAssessmentType === 'background' && !(assessmentData?.discovery || lead?.discovery) && (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                No background assessment data available for this lead.
              </div>
            )}


            {/* Sales Assessment Content */}
            {currentAssessmentType === 'sales' && (
              <div className="space-y-6">
                {console.log('=== RENDERING SALES ASSESSMENT ===')}
                {console.log('Lead sales data:', lead?.salesData)}
                
                {/* Sales Rating */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Sales Rating</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={assessmentEditData.sales?.salesRating || ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          sales: { ...assessmentEditData.sales, salesRating: parseInt(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="1-5"
                      />
                    ) : (lead?.salesData?.salesRating || lead?.businessAssessment?.salesRating) > 0 ? (
                      <div className="flex items-center justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-2xl ${
                            star <= (lead?.salesData?.salesRating || lead?.businessAssessment?.salesRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-gray-600 dark:text-gray-300">
                          {(lead?.salesData?.salesRating || lead?.businessAssessment?.salesRating)}/5
                        </span>
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Revenue: 1 Year Ago, Current Year Projection, Next Year Goal */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{new Date().getFullYear() - 1} Revenue</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        value={assessmentEditData.sales?.oneYearAgo ?? assessmentEditData.sales?.revenueData?.oneYearAgo ?? ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setAssessmentEditData({
                            ...assessmentEditData,
                            sales: {
                              ...assessmentEditData.sales,
                              oneYearAgo: value,
                              revenueData: {
                                ...assessmentEditData.sales?.revenueData,
                                oneYearAgo: value
                              }
                            }
                          });
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.salesData?.oneYearAgo || lead?.salesData?.revenueData?.oneYearAgo || lead?.businessAssessment?.revenueData?.oneYearAgo) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        ${parseInt(lead?.salesData?.oneYearAgo || lead?.salesData?.revenueData?.oneYearAgo || lead?.businessAssessment?.revenueData?.oneYearAgo || 0).toLocaleString()}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{new Date().getFullYear()} Revenue Projection</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        value={assessmentEditData.sales?.currentYearProjection ?? assessmentEditData.sales?.revenueData?.currentYearProjection ?? ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setAssessmentEditData({
                            ...assessmentEditData,
                            sales: {
                              ...assessmentEditData.sales,
                              currentYearProjection: value,
                              revenueData: {
                                ...assessmentEditData.sales?.revenueData,
                                currentYearProjection: value
                              }
                            }
                          });
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.salesData?.currentYearProjection || lead?.salesData?.revenueData?.currentYearProjection || lead?.businessAssessment?.revenueData?.currentYearProjection) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        ${parseInt(lead?.salesData?.currentYearProjection || lead?.salesData?.revenueData?.currentYearProjection || lead?.businessAssessment?.revenueData?.currentYearProjection || 0).toLocaleString()}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{new Date().getFullYear() + 1} Revenue Goal</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        value={assessmentEditData.sales?.nextYearGoal ?? assessmentEditData.sales?.revenueData?.nextYearGoal ?? assessmentEditData.sales?.goalsData?.annualRevenueTarget ?? ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setAssessmentEditData({
                            ...assessmentEditData,
                            sales: {
                              ...assessmentEditData.sales,
                              nextYearGoal: value,
                              revenueData: {
                                ...assessmentEditData.sales?.revenueData,
                                nextYearGoal: value
                              },
                              goalsData: {
                                ...assessmentEditData.sales?.goalsData,
                                annualRevenueTarget: value
                              }
                            }
                          });
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.salesData?.nextYearGoal || lead?.salesData?.revenueData?.nextYearGoal || lead?.salesData?.goalsData?.annualRevenueTarget || lead?.businessAssessment?.revenueData?.nextYearGoal) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        ${parseInt(lead?.salesData?.nextYearGoal || lead?.salesData?.revenueData?.nextYearGoal || lead?.salesData?.goalsData?.annualRevenueTarget || lead?.businessAssessment?.revenueData?.nextYearGoal || 0).toLocaleString()}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Close Ratio and Referral Business % Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Close Ratio Section */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-center mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Close Ratio</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">What percentage of qualified leads convert to sales?</p>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-4">
                      {/* Circular Percentage Ring */}
                      <div className="relative w-48 h-48 flex items-center justify-center">
                        {(() => {
                          const percentage = isEditMode 
                            ? (assessmentEditData.sales?.closeRatio ?? 0)
                            : (lead?.salesData?.closeRatio ?? lead?.businessAssessment?.closeRatio ?? 0);
                          let ringColor = '#ef4444'; // Red default (< 50%)
                          let textColor = 'text-red-600 dark:text-red-400';
                          
                          if (percentage >= 75) {
                            ringColor = '#22c55e'; // Green (75%+)
                            textColor = 'text-green-600 dark:text-green-400';
                          } else if (percentage >= 50) {
                            ringColor = '#eab308'; // Yellow (50-75%)
                            textColor = 'text-yellow-600 dark:text-yellow-400';
                          }
                          
                          return (
                            <>
                              <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 100 100">
                                {/* Background Circle */}
                                <circle
                                  cx="50"
                                  cy="50"
                                  r="45"
                                  fill="none"
                                  stroke="currentColor"
                                  className="text-gray-300 dark:text-gray-600"
                                  strokeWidth="8"
                                />
                                {/* Progress Circle */}
                                <circle
                                  cx="50"
                                  cy="50"
                                  r="45"
                                  fill="none"
                                  stroke={ringColor}
                                  strokeWidth="8"
                                  strokeLinecap="round"
                                  strokeDasharray={`${2 * Math.PI * 45}`}
                                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - percentage / 100)}`}
                                  className="transition-all duration-300"
                                />
                              </svg>
                              {/* Percentage Text in Center */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  {(isEditMode || isEditingAssessmentModal) ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={assessmentEditData.sales?.closeRatio ?? 0}
                                      onChange={(e) => {
                                        const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                        setAssessmentEditData({
                                          ...assessmentEditData,
                                          sales: { ...assessmentEditData.sales, closeRatio: value }
                                        });
                                      }}
                                      className={`text-4xl font-bold w-20 text-center bg-transparent border-2 border-transparent focus:border-red-500 focus:outline-none rounded ${textColor}`}
                                      style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                                    />
                                  ) : (
                                    <div className={`text-4xl font-bold ${textColor}`}>
                                      {percentage}
                                    </div>
                                  )}
                                  <div className="text-lg font-medium text-gray-500 dark:text-gray-400">
                                    %
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      
                      {/* Slider Input with Labels */}
                      {isEditMode && (
                        <div className="w-full max-w-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">0%</span>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">100%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={assessmentEditData.sales?.closeRatio ?? 0}
                            onChange={(e) => {
                              setAssessmentEditData({
                                ...assessmentEditData,
                                sales: { ...assessmentEditData.sales, closeRatio: parseInt(e.target.value) }
                              });
                            }}
                            className="w-full h-2 appearance-none cursor-pointer bg-gray-300 dark:bg-gray-600 rounded-lg"
                            style={{
                              background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${(assessmentEditData.sales?.closeRatio ?? 0)}%, #d1d5db ${(assessmentEditData.sales?.closeRatio ?? 0)}%, #d1d5db 100%)`
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Referral Business % Section */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-center mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Referral based business %</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">What percentage of your business comes from referrals?</p>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-4">
                      {/* Circular Percentage Ring */}
                      <div className="relative w-48 h-48 flex items-center justify-center">
                        {(() => {
                          const percentage = isEditMode 
                            ? (assessmentEditData.sales?.referralBusinessPercentage ?? 0)
                            : (lead?.salesData?.referralBusinessPercentage ?? lead?.businessAssessment?.referralBusinessPercentage ?? 0);
                          let ringColor = '#ef4444'; // Red default (< 50%)
                          let textColor = 'text-red-600 dark:text-red-400';
                          
                          if (percentage >= 75) {
                            ringColor = '#22c55e'; // Green (75%+)
                            textColor = 'text-green-600 dark:text-green-400';
                          } else if (percentage >= 50) {
                            ringColor = '#eab308'; // Yellow (50-75%)
                            textColor = 'text-yellow-600 dark:text-yellow-400';
                          }
                          
                          return (
                            <>
                              <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 100 100">
                                {/* Background Circle */}
                                <circle
                                  cx="50"
                                  cy="50"
                                  r="45"
                                  fill="none"
                                  stroke="currentColor"
                                  className="text-gray-300 dark:text-gray-600"
                                  strokeWidth="8"
                                />
                                {/* Progress Circle */}
                                <circle
                                  cx="50"
                                  cy="50"
                                  r="45"
                                  fill="none"
                                  stroke={ringColor}
                                  strokeWidth="8"
                                  strokeLinecap="round"
                                  strokeDasharray={`${2 * Math.PI * 45}`}
                                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - percentage / 100)}`}
                                  className="transition-all duration-300"
                                />
                              </svg>
                              {/* Percentage Text in Center */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  {(isEditMode || isEditingAssessmentModal) ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={assessmentEditData.sales?.referralBusinessPercentage ?? 0}
                                      onChange={(e) => {
                                        const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                        setAssessmentEditData({
                                          ...assessmentEditData,
                                          sales: { ...assessmentEditData.sales, referralBusinessPercentage: value }
                                        });
                                      }}
                                      className={`text-4xl font-bold w-20 text-center bg-transparent border-2 border-transparent focus:border-red-500 focus:outline-none rounded ${textColor}`}
                                      style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                                    />
                                  ) : (
                                    <div className={`text-4xl font-bold ${textColor}`}>
                                      {percentage}
                                    </div>
                                  )}
                                  <div className="text-lg font-medium text-gray-500 dark:text-gray-400">
                                    %
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      
                      {/* Slider Input with Labels */}
                      {isEditMode && (
                        <div className="w-full max-w-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">0%</span>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">100%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={assessmentEditData.sales?.referralBusinessPercentage ?? 0}
                            onChange={(e) => {
                              setAssessmentEditData({
                                ...assessmentEditData,
                                sales: { ...assessmentEditData.sales, referralBusinessPercentage: parseInt(e.target.value) }
                              });
                            }}
                            className="w-full h-2 appearance-none cursor-pointer bg-gray-300 dark:bg-gray-600 rounded-lg"
                            style={{
                              background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${(assessmentEditData.sales?.referralBusinessPercentage ?? 0)}%, #d1d5db ${(assessmentEditData.sales?.referralBusinessPercentage ?? 0)}%, #d1d5db 100%)`
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Operations Assessment Content */}
            {currentAssessmentType === 'operations' && (
              <div className="space-y-6">
                {console.log('=== RENDERING OPERATIONS ASSESSMENT ===')}
                {console.log('Lead operations data:', lead?.operationsData)}
                {console.log('Lead business assessment:', lead?.businessAssessment)}
                
                {/* Operations Rating */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Operations Rating</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={assessmentEditData.operations?.operationsRating ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, operationsRating: parseInt(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="1-5"
                      />
                    ) : (lead?.operationsData?.operationsRating || lead?.businessAssessment?.operationsRating) > 0 ? (
                      <div className="flex items-center justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-2xl ${
                            star <= (lead?.operationsData?.operationsRating || lead?.businessAssessment?.operationsRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-gray-600 dark:text-gray-300">
                          {(lead?.operationsData?.operationsRating || lead?.businessAssessment?.operationsRating)}/5
                        </span>
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Has the following: SOP Library, Job Descriptions, KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">SOP Library</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <select
                        value={assessmentEditData.operations?.hasSOPs === true || assessmentEditData.operations?.hasSOPs === 'yes' ? 'yes' : assessmentEditData.operations?.hasSOPs === false || assessmentEditData.operations?.hasSOPs === 'no' ? 'no' : ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, hasSOPs: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Not Set</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300">
                        {((lead?.operationsData?.hasSOPs || lead?.businessAssessment?.hasSOPs) === 'yes' || (lead?.operationsData?.hasSOPs || lead?.businessAssessment?.hasSOPs) === true) ? 'Yes' : 'No'}
                      </p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Job Descriptions</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <select
                        value={assessmentEditData.operations?.hasJobDescriptions === true || assessmentEditData.operations?.hasJobDescriptions === 'yes' ? 'yes' : assessmentEditData.operations?.hasJobDescriptions === false || assessmentEditData.operations?.hasJobDescriptions === 'no' ? 'no' : ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, hasJobDescriptions: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Not Set</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300">
                        {((lead?.operationsData?.hasJobDescriptions || lead?.businessAssessment?.hasJobDescriptions) === 'yes' || (lead?.operationsData?.hasJobDescriptions || lead?.businessAssessment?.hasJobDescriptions) === true) ? 'Yes' : 'No'}
                      </p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">KPIs</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <select
                        value={assessmentEditData.operations?.hasKPIs === true || assessmentEditData.operations?.hasKPIs === 'yes' ? 'yes' : assessmentEditData.operations?.hasKPIs === false || assessmentEditData.operations?.hasKPIs === 'no' ? 'no' : ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, hasKPIs: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Not Set</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300">
                        {((lead?.operationsData?.hasKPIs || lead?.businessAssessment?.hasKPIs) === 'yes' || (lead?.operationsData?.hasKPIs || lead?.businessAssessment?.hasKPIs) === true) ? 'Yes' : 'No'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Team Size, Team Grade, and Culture Grade */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Team Size</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        value={assessmentEditData.operations?.teamSize ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, teamSize: parseInt(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.operationsData?.teamSize || lead?.businessAssessment?.teamSize) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.operationsData?.teamSize || lead?.businessAssessment?.teamSize}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Team Grade</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="text"
                        value={assessmentEditData.operations?.teamGrade ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, teamGrade: e.target.value }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="e.g., A, B, C"
                      />
                    ) : (lead?.operationsData?.teamGrade || lead?.businessAssessment?.teamGrade) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.operationsData?.teamGrade || lead?.businessAssessment?.teamGrade}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Culture Grade</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="text"
                        value={assessmentEditData.operations?.cultureGrade ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          operations: { ...assessmentEditData.operations, cultureGrade: e.target.value }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="e.g., A, B, C"
                      />
                    ) : (lead?.operationsData?.cultureGrade || lead?.businessAssessment?.cultureGrade) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.operationsData?.cultureGrade || lead?.businessAssessment?.cultureGrade}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

              </div>
            )}


            {/* Finance Assessment Content */}
            {currentAssessmentType === 'finance' && (
              <div className="space-y-6">
                {console.log('=== RENDERING FINANCE ASSESSMENT ===')}
                {console.log('Lead finance data:', lead?.financeData)}
                {console.log('Lead business assessment:', lead?.businessAssessment)}
                
                {/* Finance Rating */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Finance Rating</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={assessmentEditData.finance?.financeRating ?? assessmentEditData.finance?.rating ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          finance: {
                            ...assessmentEditData.finance,
                            financeRating: parseInt(e.target.value) || 0,
                            rating: parseInt(e.target.value) || 0
                          }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="1-5"
                      />
                    ) : (lead?.financeData?.financeRating || lead?.financeData?.rating || lead?.businessAssessment?.financeRating) > 0 ? (
                      <div className="flex items-center justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-2xl ${
                            star <= (lead?.financeData?.financeRating || lead?.financeData?.rating || lead?.businessAssessment?.financeRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-gray-600 dark:text-gray-300">
                          {(lead?.financeData?.financeRating || lead?.financeData?.rating || lead?.businessAssessment?.financeRating)}/5
                        </span>
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Unencumbered Cash, Gross Profit Margin, Net Profit Margin */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Unencumbered Cash</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        value={assessmentEditData.finance?.unencumberedCash ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          finance: { ...assessmentEditData.finance, unencumberedCash: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.financeData?.unencumberedCash || lead?.businessAssessment?.unencumberedCash) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        ${parseInt(lead?.financeData?.unencumberedCash || lead?.businessAssessment?.unencumberedCash || 0).toLocaleString()}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Gross Profit Margin</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        step="0.01"
                        value={assessmentEditData.finance?.grossProfitMargin ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          finance: { ...assessmentEditData.finance, grossProfitMargin: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.financeData?.grossProfitMargin || lead?.businessAssessment?.grossProfitMargin) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.financeData?.grossProfitMargin || lead?.businessAssessment?.grossProfitMargin}%
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Net Profit Margin</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        step="0.01"
                        value={assessmentEditData.finance?.netProfitMargin ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          finance: { ...assessmentEditData.finance, netProfitMargin: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0"
                      />
                    ) : (lead?.financeData?.netProfitMargin || lead?.businessAssessment?.netProfitMargin) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.financeData?.netProfitMargin || lead?.businessAssessment?.netProfitMargin}%
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Do you know the following? */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Do you know the following?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Overhead</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.finance?.knowsOverhead === true || assessmentEditData.finance?.knowsOverhead === 'yes' ? 'yes' : assessmentEditData.finance?.knowsOverhead === false || assessmentEditData.finance?.knowsOverhead === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            finance: { ...assessmentEditData.finance, knowsOverhead: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const value = lead?.financeData?.knowsOverhead ?? lead?.businessAssessment?.knowsOverhead;
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Labor Burden</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.finance?.knowsLaborBurden === true || assessmentEditData.finance?.knowsLaborBurden === 'yes' ? 'yes' : assessmentEditData.finance?.knowsLaborBurden === false || assessmentEditData.finance?.knowsLaborBurden === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            finance: { ...assessmentEditData.finance, knowsLaborBurden: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const value = lead?.financeData?.knowsLaborBurden ?? lead?.businessAssessment?.knowsLaborBurden;
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Project Runway</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.finance?.knowsProjectRunway === true || assessmentEditData.finance?.knowsProjectRunway === 'yes' ? 'yes' : assessmentEditData.finance?.knowsProjectRunway === false || assessmentEditData.finance?.knowsProjectRunway === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            finance: { ...assessmentEditData.finance, knowsProjectRunway: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const value = lead?.financeData?.knowsProjectRunway ?? lead?.businessAssessment?.knowsProjectRunway;
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Financial Statements */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Financial Statements</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Balance Sheet */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Balance Sheet</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <div className="space-y-2">
                          <input
                            type="file"
                            accept=".pdf,.xlsx,.xls"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setAssessmentEditData({
                                  ...assessmentEditData,
                                  finance: {
                                    ...assessmentEditData.finance,
                                    balanceSheetFileName: file.name
                                  }
                                });
                              }
                            }}
                            className="w-full text-xs"
                          />
                          {assessmentEditData.finance?.balanceSheetFileName && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {assessmentEditData.finance.balanceSheetFileName}
                            </p>
                          )}
                        </div>
                      ) : (lead?.financeData?.balanceSheetFileName || lead?.businessAssessment?.balanceSheetFileName) ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {lead?.financeData?.balanceSheetFileName || lead?.businessAssessment?.balanceSheetFileName}
                          </p>
                          {(lead?.financeData?.balanceSheetUrl || lead?.businessAssessment?.balanceSheetUrl) && (
                            <a
                              href={lead?.financeData?.balanceSheetUrl || lead?.businessAssessment?.balanceSheetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              View File
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400 dark:text-gray-500 italic text-sm">Not uploaded</p>
                      )}
                    </div>

                    {/* Profit & Loss (Income Statement) */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Profit & Loss</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <div className="space-y-2">
                          <input
                            type="file"
                            accept=".pdf,.xlsx,.xls"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setAssessmentEditData({
                                  ...assessmentEditData,
                                  finance: {
                                    ...assessmentEditData.finance,
                                    incomeStatementFileName: file.name
                                  }
                                });
                              }
                            }}
                            className="w-full text-xs"
                          />
                          {assessmentEditData.finance?.incomeStatementFileName && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {assessmentEditData.finance.incomeStatementFileName}
                            </p>
                          )}
                        </div>
                      ) : (lead?.financeData?.incomeStatementFileName || lead?.businessAssessment?.incomeStatementFileName) ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {lead?.financeData?.incomeStatementFileName || lead?.businessAssessment?.incomeStatementFileName}
                          </p>
                          {(lead?.financeData?.incomeStatementUrl || lead?.businessAssessment?.incomeStatementUrl) && (
                            <a
                              href={lead?.financeData?.incomeStatementUrl || lead?.businessAssessment?.incomeStatementUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              View File
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400 dark:text-gray-500 italic text-sm">Not uploaded</p>
                      )}
                    </div>

                    {/* Cash Flow Statement */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Cash Flow</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <div className="space-y-2">
                          <input
                            type="file"
                            accept=".pdf,.xlsx,.xls"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setAssessmentEditData({
                                  ...assessmentEditData,
                                  finance: {
                                    ...assessmentEditData.finance,
                                    cashFlowStatementFileName: file.name
                                  }
                                });
                              }
                            }}
                            className="w-full text-xs"
                          />
                          {assessmentEditData.finance?.cashFlowStatementFileName && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {assessmentEditData.finance.cashFlowStatementFileName}
                            </p>
                          )}
                        </div>
                      ) : (lead?.financeData?.cashFlowStatementFileName || lead?.businessAssessment?.cashFlowStatementFileName) ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {lead?.financeData?.cashFlowStatementFileName || lead?.businessAssessment?.cashFlowStatementFileName}
                          </p>
                          {(lead?.financeData?.cashFlowStatementUrl || lead?.businessAssessment?.cashFlowStatementUrl) && (
                            <a
                              href={lead?.financeData?.cashFlowStatementUrl || lead?.businessAssessment?.cashFlowStatementUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              View File
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400 dark:text-gray-500 italic text-sm">Not uploaded</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}


            {/* Technology Assessment Content */}
            {currentAssessmentType === 'technology' && (
              <div className="space-y-6">
                {console.log('=== RENDERING TECHNOLOGY ASSESSMENT ===')}
                {console.log('Lead tech data:', lead?.techData)}
                {console.log('Lead business assessment:', lead?.businessAssessment)}
                
                {/* Technology Rating */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Technology Rating</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={assessmentEditData.technology?.techRating ?? assessmentEditData.technology?.rating ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          technology: {
                            ...assessmentEditData.technology,
                            techRating: parseInt(e.target.value) || 0,
                            rating: parseInt(e.target.value) || 0
                          }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="1-5"
                      />
                    ) : (lead?.techData?.techRating || lead?.techData?.rating || lead?.businessAssessment?.techRating) > 0 ? (
                      <div className="flex items-center justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-2xl ${
                            star <= (lead?.techData?.techRating || lead?.techData?.rating || lead?.businessAssessment?.techRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-gray-600 dark:text-gray-300">
                          {(lead?.techData?.techRating || lead?.techData?.rating || lead?.businessAssessment?.techRating)}/5
                        </span>
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* CRM and CRM Optimization */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">CRM</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="text"
                        value={assessmentEditData.technology?.whatCRM ?? assessmentEditData.technology?.techStackData?.crm ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          technology: {
                            ...assessmentEditData.technology,
                            whatCRM: e.target.value,
                            techStackData: {
                              ...assessmentEditData.technology?.techStackData,
                              crm: e.target.value
                            }
                          }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="e.g., Salesforce"
                      />
                    ) : (lead?.techData?.whatCRM || lead?.techData?.techStackData?.crm || lead?.businessAssessment?.whatCRM) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.techData?.whatCRM || lead?.techData?.techStackData?.crm || lead?.businessAssessment?.whatCRM}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">CRM Optimization</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={assessmentEditData.technology?.crmOptimization ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          technology: { ...assessmentEditData.technology, crmOptimization: parseInt(e.target.value) || 0 }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="0-100"
                      />
                    ) : (lead?.techData?.crmOptimization !== undefined && lead?.techData?.crmOptimization !== null) || 
                     (lead?.businessAssessment?.crmOptimization !== undefined && lead?.businessAssessment?.crmOptimization !== null) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {(lead?.techData?.crmOptimization ?? lead?.businessAssessment?.crmOptimization ?? 0)}%
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Has the following: Automations, AI Tools, Website Plugins */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Has the following:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Automations</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.technology?.hasAutomations === true || assessmentEditData.technology?.hasAutomations === 'yes' ? 'yes' : assessmentEditData.technology?.hasAutomations === false || assessmentEditData.technology?.hasAutomations === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            technology: { ...assessmentEditData.technology, hasAutomations: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const techValue = lead?.techData?.hasAutomations;
                        const businessValue = lead?.businessAssessment?.hasAutomations;
                        const value = techValue !== undefined && techValue !== null ? techValue : businessValue;
                        if (value === undefined || value === null) {
                          return <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>;
                        }
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">AI Tools</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.technology?.hasAITools === true || assessmentEditData.technology?.hasAITools === 'yes' ? 'yes' : assessmentEditData.technology?.hasAITools === false || assessmentEditData.technology?.hasAITools === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            technology: { ...assessmentEditData.technology, hasAITools: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const techValue = lead?.techData?.hasAITools;
                        const businessValue = lead?.businessAssessment?.hasAITools;
                        const value = techValue !== undefined && techValue !== null ? techValue : businessValue;
                        if (value === undefined || value === null) {
                          return <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>;
                        }
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Website Plugins</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.technology?.hasWebsitePlugins === true || assessmentEditData.technology?.hasWebsitePlugins === 'yes' || assessmentEditData.technology?.hasCustomWebsitePlugins === true || assessmentEditData.technology?.hasCustomWebsitePlugins === 'yes' ? 'yes' : assessmentEditData.technology?.hasWebsitePlugins === false || assessmentEditData.technology?.hasWebsitePlugins === 'no' || assessmentEditData.technology?.hasCustomWebsitePlugins === false || assessmentEditData.technology?.hasCustomWebsitePlugins === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            technology: {
                              ...assessmentEditData.technology,
                              hasWebsitePlugins: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null,
                              hasCustomWebsitePlugins: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null
                            }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const techValue = lead?.techData?.hasWebsitePlugins ?? lead?.techData?.hasCustomWebsitePlugins;
                        const businessValue = lead?.businessAssessment?.hasWebsitePlugins ?? lead?.businessAssessment?.hasCustomWebsitePlugins;
                        const value = techValue !== undefined && techValue !== null ? techValue : businessValue;
                        if (value === undefined || value === null) {
                          return <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>;
                        }
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Marketing Assessment Content */}
            {currentAssessmentType === 'marketing' && (
              <div className="space-y-6">
                {console.log('=== RENDERING MARKETING ASSESSMENT ===')}
                {console.log('Lead marketing data:', lead?.marketingData)}
                {console.log('Lead business assessment:', lead?.businessAssessment)}
                
                {/* Marketing Rating */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Marketing Rating</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={assessmentEditData.marketing?.marketingRating ?? assessmentEditData.marketing?.rating ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          marketing: {
                            ...assessmentEditData.marketing,
                            marketingRating: parseInt(e.target.value) || 0,
                            rating: parseInt(e.target.value) || 0
                          }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        placeholder="1-5"
                      />
                    ) : (lead?.marketingData?.marketingRating || lead?.marketingData?.rating || lead?.businessAssessment?.marketingRating) > 0 ? (
                      <div className="flex items-center justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-2xl ${
                            star <= (lead?.marketingData?.marketingRating || lead?.marketingData?.rating || lead?.businessAssessment?.marketingRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-gray-600 dark:text-gray-300">
                          {(lead?.marketingData?.marketingRating || lead?.marketingData?.rating || lead?.businessAssessment?.marketingRating)}/5
                        </span>
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Working with Marketing Agency and Website Management */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Marketing Agency</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <select
                        value={assessmentEditData.marketing?.workingWithMarketingAgency === true || assessmentEditData.marketing?.workingWithMarketingAgency === 'yes' || assessmentEditData.marketing?.workingWithAgency === true || assessmentEditData.marketing?.workingWithAgency === 'yes' ? 'yes' : assessmentEditData.marketing?.workingWithMarketingAgency === false || assessmentEditData.marketing?.workingWithMarketingAgency === 'no' || assessmentEditData.marketing?.workingWithAgency === false || assessmentEditData.marketing?.workingWithAgency === 'no' ? 'no' : ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          marketing: {
                            ...assessmentEditData.marketing,
                            workingWithMarketingAgency: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null,
                            workingWithAgency: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null
                          }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Not Set</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (() => {
                      const workingWithAgency = lead?.marketingData?.workingWithMarketingAgency || lead?.marketingData?.workingWithAgency || lead?.businessAssessment?.workingWithMarketingAgency;
                      if (workingWithAgency === undefined || workingWithAgency === null) {
                        return <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>;
                      }
                      return (
                        <p className="text-gray-600 dark:text-gray-300">
                          {workingWithAgency === 'yes' || workingWithAgency === true ? 'Yes' : 'No'}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Who manages your website?</h4>
                    {(isEditMode || isEditingAssessmentModal) ? (
                      <select
                        value={assessmentEditData.marketing?.marketingAgencyType ?? assessmentEditData.marketing?.websiteManager ?? ''}
                        onChange={(e) => setAssessmentEditData({
                          ...assessmentEditData,
                          marketing: { 
                            ...assessmentEditData.marketing, 
                            marketingAgencyType: e.target.value,
                            websiteManager: e.target.value
                          }
                        })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Not Set</option>
                        <option value="In House">In House</option>
                        <option value="External">External</option>
                      </select>
                    ) : (lead?.marketingData?.marketingAgencyType || lead?.businessAssessment?.marketingAgencyType || lead?.marketingData?.websiteManager || lead?.businessAssessment?.websiteManager) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {lead?.marketingData?.marketingAgencyType || lead?.businessAssessment?.marketingAgencyType || lead?.marketingData?.websiteManager || lead?.businessAssessment?.websiteManager}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>

                {/* Do you engage in the following? */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Do you engage in the following?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Social Media</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.marketing?.engagesSocialMedia === true || assessmentEditData.marketing?.engagesSocialMedia === 'yes' ? 'yes' : assessmentEditData.marketing?.engagesSocialMedia === false || assessmentEditData.marketing?.engagesSocialMedia === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            marketing: { ...assessmentEditData.marketing, engagesSocialMedia: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const value = lead?.marketingData?.engagesSocialMedia ?? lead?.businessAssessment?.engagesSocialMedia;
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Paid Ads</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.marketing?.engagesPaidAds === true || assessmentEditData.marketing?.engagesPaidAds === 'yes' ? 'yes' : assessmentEditData.marketing?.engagesPaidAds === false || assessmentEditData.marketing?.engagesPaidAds === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            marketing: { ...assessmentEditData.marketing, engagesPaidAds: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const value = lead?.marketingData?.engagesPaidAds ?? lead?.businessAssessment?.engagesPaidAds;
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 text-center">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Newsletter</h5>
                      {(isEditMode || isEditingAssessmentModal) ? (
                        <select
                          value={assessmentEditData.marketing?.engagesNewsletter === true || assessmentEditData.marketing?.engagesNewsletter === 'yes' ? 'yes' : assessmentEditData.marketing?.engagesNewsletter === false || assessmentEditData.marketing?.engagesNewsletter === 'no' ? 'no' : ''}
                          onChange={(e) => setAssessmentEditData({
                            ...assessmentEditData,
                            marketing: { ...assessmentEditData.marketing, engagesNewsletter: e.target.value === 'yes' ? 'yes' : e.target.value === 'no' ? 'no' : null }
                          })}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Not Set</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (() => {
                        const value = lead?.marketingData?.engagesNewsletter ?? lead?.businessAssessment?.engagesNewsletter;
                        return (
                          <p className="text-gray-600 dark:text-gray-300">
                            {value === 'yes' || value === true ? 'Yes' : 'No'}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* HR Assessment Content */}
            {currentAssessmentType === 'hr' && (
              <div className="space-y-6">
                {console.log('=== RENDERING HR ASSESSMENT ===')}
                {console.log('Lead HR data:', lead?.hrData)}
                {console.log('Assessment data HR data:', assessmentData?.hrData)}
                
                {!(lead?.hrData || assessmentData?.hrData) ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No HR assessment data available for this lead.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">HR Rating</h4>
                    {(lead?.hrData?.hrRating || assessmentData?.hrData?.hrRating) > 0 ? (
                      <div className="flex items-center justify-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-2xl ${
                            star <= (lead?.hrData?.hrRating || assessmentData?.hrData?.hrRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-gray-600 dark:text-gray-300">
                          {(lead?.hrData?.hrRating || assessmentData?.hrData?.hrRating)}/5
                        </span>
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Hiring Coordinator</h4>
                    {(lead?.hrData?.talentScoutingManager || assessmentData?.hrData?.talentScoutingManager) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {(lead?.hrData?.talentScoutingManager || assessmentData?.hrData?.talentScoutingManager)}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Application Form Library</h4>
                    {(lead?.hrData?.hasApplicationForms || assessmentData?.hrData?.hasApplicationForms) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {(lead?.hrData?.hasApplicationForms || assessmentData?.hrData?.hasApplicationForms) ? 'Yes' : 'No'}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Job Listings Library</h4>
                    {(lead?.hrData?.hasJobListings || assessmentData?.hrData?.hasJobListings) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {(lead?.hrData?.hasJobListings || assessmentData?.hrData?.hasJobListings) ? 'Yes' : 'No'}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Employee Handbook</h4>
                    {(lead?.hrData?.hasEmployeeHandbook || assessmentData?.hrData?.hasEmployeeHandbook) ? (
                      <p className="text-gray-600 dark:text-gray-300">
                        {(lead?.hrData?.hasEmployeeHandbook || assessmentData?.hrData?.hasEmployeeHandbook) ? 'Yes' : 'No'}
                      </p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Mission</h4>
                  {(lead?.hrData?.mission || assessmentData?.hrData?.mission) ? (
                    <p className="text-gray-600 dark:text-gray-300">
                      {(lead?.hrData?.mission || assessmentData?.hrData?.mission)}
                    </p>
                  ) : (
                    <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                  )}
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Vision</h4>
                  {(lead?.hrData?.vision || assessmentData?.hrData?.vision) ? (
                    <p className="text-gray-600 dark:text-gray-300">
                      {(lead?.hrData?.vision || assessmentData?.hrData?.vision)}
                    </p>
                  ) : (
                    <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                  )}
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Values</h4>
                  {((lead?.hrData?.values && lead.hrData.values.length > 0) || (assessmentData?.hrData?.values && assessmentData.hrData.values.length > 0)) ? (
                    <p className="text-gray-600 dark:text-gray-300">
                      {(lead?.hrData?.values || assessmentData?.hrData?.values || []).map(item => item.text || item).join(', ')}
                    </p>
                  ) : (
                    <p className="text-gray-400 dark:text-gray-500 italic">Not completed yet</p>
                  )}
                </div>
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showUpdateStatusModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Update Lead Status</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Select the new status for this lead. This will update the lead's stage in the pipeline.
            </p>
            <div className="space-y-3 mb-6">
              {['Prospects', 'Lead Received', 'Discovery Call', 'Assessment', 'Proposal', 'Onboarding Call', 'Won', 'Lost', 'Archived'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleUpdateStatus(status)}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    status === lead.stage
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300'
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {status}
                  {status === lead.stage && <span className="ml-2 text-sm">(Current)</span>}
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowUpdateStatusModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Substatus Modal */}
      {showUpdateSubstatusModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Update Substatus</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Select a substatus for the current stage: <strong>{lead.stage}</strong>
            </p>
            <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
              {getCurrentSubstatuses().map((substatus, index) => (
                <button
                  key={index}
                  onClick={() => handleSubstatusUpdate(substatus.name)}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    substatus.name === lead.substatus
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300'
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="font-medium">{substatus.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{substatus.description}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowUpdateSubstatusModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Substatus Date Modal */}
      {showSubstatusDateModal && selectedSubstatus && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Update Substatus Date
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              <strong>{selectedSubstatus.name}</strong> ({selectedSubstatus.stageName})
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Completion Date
                </label>
                <input
                  type="date"
                  value={substatusDate}
                  onChange={(e) => setSubstatusDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {substatusDate ? 'Substatus will be marked as complete' : 'Leave empty to mark as incomplete'}
                </p>
              </div>
              
              {selectedSubstatus.currentDate && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Current date:</strong> {formatDate(getDateField(selectedSubstatus.currentDate))}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSubstatusDateModal(false);
                  setSelectedSubstatus(null);
                  setSubstatusDate('');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (substatusDate) {
                    handleSubstatusDateUpdate(selectedSubstatus.name, selectedSubstatus.stageName, substatusDate);
                  } else {
                    // If no date, remove the date (mark as incomplete)
                    handleSubstatusDateUpdate(selectedSubstatus.name, selectedSubstatus.stageName, null);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                {substatusDate ? 'Save Date' : 'Mark as Incomplete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Make Client Modal */}
      {showMakeClientModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Convert to Client</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Please provide the following information to convert this lead to a client:
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Program *
                </label>
                <select
                  name="program"
                  value={clientFormData.program}
                  onChange={handleClientFormChange}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select a program</option>
                  <option value="Custom">Custom</option>
                  <option value="Lite">Lite</option>
                  <option value="Plus">Plus</option>
                  <option value="Pro">Pro</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={clientFormData.startDate}
                  onChange={handleClientFormChange}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Minimum Term *
                </label>
                <select
                  name="minimumTerm"
                  value={clientFormData.minimumTerm}
                  onChange={handleClientFormChange}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select a minimum term</option>
                  {MINIMUM_TERM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowMakeClientModal(false);
                  setClientFormData({
                    program: '',
                    startDate: '',
                    minimumTerm: ''
                  });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleMakeClient}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Convert to Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Summary Modal */}
      <MeetingSummaryModal
        open={showMeetingSummaryModal}
        event={selectedEventForSummary}
        summary={meetingSummary}
        loading={meetingSummaryLoading}
        onClose={() => {
          setShowMeetingSummaryModal(false);
          setSelectedEventForSummary(null);
          setMeetingSummary(null);
        }}
      />

      {/* Invite Link Modal */}
      {showInviteLinkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Portal Invite Link Generated</h3>
              <button
                onClick={() => setShowInviteLinkModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                A secure signup link has been generated for <strong>{lead?.firstName || lead?.contact?.firstName} {lead?.lastName || lead?.contact?.lastName}</strong> ({lead?.contact?.email}).
                Share this link with your client so they can create their portal account.
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invite Link:
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={generatedInviteLink}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono"
                  />
                  <button
                    onClick={copyInviteLink}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Important Notes:
                    </h3>
                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      <ul className="list-disc list-inside space-y-1">
                        <li>This link is valid for 10 days</li>
                        <li>Only the invited email address can use this link</li>
                        <li>The link will automatically pre-fill the client's email</li>
                        <li>Once used, the link cannot be used again</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowInviteLinkModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NDA View Modal */}
      {showNDAModal && signedNDA && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center flex-1">Signed Non-Disclosure Agreement</h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowNDAModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-6">
                {/* Document Status */}
                {signedNDA.isFullyExecuted ? (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                      <h4 className="text-md font-semibold text-green-900 dark:text-green-100">Fully Executed NDA</h4>
                    </div>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      This NDA has been signed by both parties and is fully executed as of {new Date(signedNDA.finalizedAt).toLocaleString()}.
                    </p>
                  </div>
                ) : (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2" />
                      <h4 className="text-md font-semibold text-orange-900 dark:text-orange-100">Awaiting BBA Signature</h4>
                    </div>
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                      This NDA has been signed by the client but requires BBA signature to be fully executed.
                    </p>
                  </div>
                )}

                {/* Document Content */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Document Content</h4>
                  <div className="prose dark:prose-invert max-w-none prose-ul:list-disc prose-ul:ml-4 prose-li:my-1">
                    {signedNDA.finalizedDocumentContent ? (
                      <div 
                        className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2 [&_li]:my-1 [&_ul_ul]:ml-4 [&_ul_ul]:mt-1"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(signedNDA.finalizedDocumentContent) }}
                      />
                    ) : ndaTemplate && ndaTemplate.ndaElements && renderedNDAContent ? (
                      <div 
                        className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2 [&_li]:my-1 [&_ul_ul]:ml-4 [&_ul_ul]:mt-1"
                      >
                        {renderedNDAContent}
                      </div>
                    ) : (
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700 p-4 rounded border">
                        {signedNDA.documentContent || 'Document content not available'}
                      </pre>
                    )}
                  </div>
                </div>

                {/* Client Signature */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Client Electronic Signature</h4>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                    {signedNDA.signature}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Signed by: {signedNDA.signerName} ({signedNDA.signerTitle}) on {new Date(signedNDA.signedAt).toLocaleString()}
                  </div>
                </div>

                {/* BBA Signature (if fully executed) */}
                {signedNDA.isFullyExecuted && signedNDA.bbaSignature && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-blue-900 dark:text-blue-100 mb-3">BBA Electronic Signature</h4>
                    {(() => {
                      // The BBA signature data is stored directly in signedNDA.bbaSignature
                      const bbaFormData = signedNDA.bbaSignature;
                      
                      console.log('BBA Form Data for signature display:', bbaFormData);
                      console.log('All BBA Form Data keys:', Object.keys(bbaFormData));
                      console.log('All BBA Form Data values:', Object.values(bbaFormData));
                      
                      // Look for signature-type fields specifically (case insensitive)
                      const signatureKeys = Object.keys(bbaFormData).filter(key => 
                        key.toLowerCase().includes('signature') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                      );
                      
                      console.log('Signature keys found:', signatureKeys);
                      
                      // If we found signature fields, use the first one
                      if (signatureKeys.length > 0) {
                        const signatureValue = bbaFormData[signatureKeys[0]];
                        console.log('Using signature from key:', signatureKeys[0], 'value:', signatureValue);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {signatureValue}
                          </div>
                        );
                      }
                      
                      // Look for BBA signature fields with the specific pattern we know exists
                      const bbaSignatureKeys = Object.keys(bbaFormData).filter(key => 
                        key.includes('-bba-Signature-') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                      );
                      
                      console.log('BBA Signature keys found:', bbaSignatureKeys);
                      
                      if (bbaSignatureKeys.length > 0) {
                        const signatureValue = bbaFormData[bbaSignatureKeys[0]];
                        console.log('Using BBA signature from key:', bbaSignatureKeys[0], 'value:', signatureValue);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {signatureValue}
                          </div>
                        );
                      }
                      
                      // Look for any field that might contain signature text (longer text values)
                      // Also look for fields that contain "Signature" in the key (case insensitive)
                      const textFields = Object.entries(bbaFormData).filter(([key, value]) => 
                        value && typeof value === 'string' && value.trim().length > 3 && 
                        !key.toLowerCase().includes('date') && 
                        !key.toLowerCase().includes('title') &&
                        !key.toLowerCase().includes('name')
                      );
                      
                      // Also look for fields with "Signature" in the key name
                      const signatureFields = Object.entries(bbaFormData).filter(([key, value]) => 
                        key.toLowerCase().includes('signature') && value && typeof value === 'string' && value.trim().length > 0
                      );
                      
                      console.log('Text fields found:', textFields);
                      console.log('Signature fields found:', signatureFields);
                      
                      // If we have signature fields, use the first one
                      if (signatureFields.length > 0) {
                        const signatureValue = signatureFields[0][1];
                        console.log('Using signature field:', signatureFields[0][0], 'value:', signatureValue);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {signatureValue}
                          </div>
                        );
                      }
                      
                      // If we have text fields, use the longest one (most likely the signature)
                      if (textFields.length > 0) {
                        const longestField = textFields.reduce((longest, current) => 
                          current[1].length > longest[1].length ? current : longest
                        );
                        console.log('Using longest text field as signature:', longestField);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {longestField[1]}
                          </div>
                        );
                      }
                      
                      // Fallback: look for any non-empty values that might be signatures
                      const nonEmptyValues = Object.values(bbaFormData).filter(value => 
                        value && typeof value === 'string' && value.trim().length > 0
                      );
                      
                      console.log('Non-empty values found:', nonEmptyValues);
                      
                      // If we have values, use the first one (most likely the signature)
                      if (nonEmptyValues.length > 0) {
                        console.log('Using first non-empty value as signature:', nonEmptyValues[0]);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {nonEmptyValues[0]}
                          </div>
                        );
                      }
                      
                      // Final fallback - show the signer name in regular font (not cursive)
                      console.log('No signature found, using signer name as fallback');
                      return (
                        <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          BBA Signature
                        </div>
                      );
                    })()}
                    <div className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      {(() => {
                        // Extract signer name and date from the BBA signature data
                        // First check top-level properties (from bbaSignatureData spread)
                        let signerName = signedNDA.bbaSignerName || signedNDA.bbaName || 'Unknown';
                        let signDate = signedNDA.bbaSignedAt || signedNDA.bbaSignatureDate || signedNDA.bbaDate || 'Unknown Date';
                        
                        // If not found at top level, check inside bbaSignature form data
                        if (signerName === 'Unknown' && signedNDA.bbaSignature) {
                          const bbaFormData = signedNDA.bbaSignature;
                          
                          // Try to find name in form data
                          const nameKeys = Object.keys(bbaFormData).filter(key => 
                            (key.includes('-bba-Name-') || key.toLowerCase().includes('name')) && 
                            bbaFormData[key] && 
                            typeof bbaFormData[key] === 'string' &&
                            bbaFormData[key].trim().length > 0
                          );
                          if (nameKeys.length > 0) {
                            signerName = bbaFormData[nameKeys[0]];
                          } else {
                            // Try common name field variations
                            signerName = bbaFormData.bbaName || bbaFormData.name || bbaFormData.bbaSignerName || 'Unknown';
                          }
                        }
                        
                        // If date not found at top level, check inside bbaSignature form data
                        if (signDate === 'Unknown Date' && signedNDA.bbaSignature) {
                          const bbaFormData = signedNDA.bbaSignature;
                          
                          // Try to find date in form data
                          const dateKeys = Object.keys(bbaFormData).filter(key => 
                            (key.includes('-bba-Date-') || key.toLowerCase().includes('date')) && 
                            bbaFormData[key] && 
                            typeof bbaFormData[key] === 'string' &&
                            bbaFormData[key].trim().length > 0
                          );
                          if (dateKeys.length > 0) {
                            signDate = bbaFormData[dateKeys[0]];
                          } else {
                            // Try common date field variations
                            signDate = bbaFormData.bbaSignatureDate || bbaFormData.bbaDate || bbaFormData.date || 'Unknown Date';
                          }
                        }
                        
                        // Format the date if it's an ISO string
                        if (signDate !== 'Unknown Date' && signDate.includes('T')) {
                          try {
                            signDate = new Date(signDate).toLocaleString();
                          } catch (e) {
                            // Keep original if parsing fails
                          }
                        }
                        
                        return `Signed by: ${signerName} on ${signDate}`;
                      })()}
                    </div>
                  </div>
                )}

                {/* E-SIGN Act Compliance */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-900 dark:text-blue-100 mb-3">E-SIGN Act Compliance</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Electronic signature consent: {signedNDA.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Electronic records consent: {signedNDA.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Signed at: {new Date(signedNDA.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        IP Address: {signedNDA.eSignCompliance?.auditTrail?.ipAddress}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Browser: {signedNDA.eSignCompliance?.auditTrail?.browser} on {signedNDA.eSignCompliance?.auditTrail?.device}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              {signedNDA.isFullyExecuted ? (
                <>
                  <button
                    onClick={() => setShowAuditTrailModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Audit Trail
                  </button>
                  <button
                    onClick={handleDownloadNDA}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                </>
              ) : (
                <button
                  onClick={handleOpenBBASigning}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm flex items-center"
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  BBA Sign Required
                </button>
              )}
              <button
                onClick={() => setShowNDAModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACH Information Modal */}
      {showACHModal && achInfo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1"></div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white text-center flex-1">
                ACH Bank Information
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowSensitiveData(!showSensitiveData)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  title={showSensitiveData ? 'Hide sensitive data' : 'Show sensitive data'}
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showSensitiveData ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {achInfo.accountType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
                  <div className="relative p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                    <span>{achInfo.accountType}</span>
                    <button
                      onClick={() => copyToClipboard(achInfo.accountType)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Copy to clipboard"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name</label>
                <div className="relative p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                  <span>{achInfo.bankName || 'Not provided'}</span>
                  {achInfo.bankName && (
                    <button
                      onClick={() => copyToClipboard(achInfo.bankName)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Copy to clipboard"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Address</label>
                <div className="relative p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                  <span>{achInfo.bankAddress || 'Not provided'}</span>
                  {achInfo.bankAddress && (
                    <button
                      onClick={() => copyToClipboard(achInfo.bankAddress)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Copy to clipboard"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Routing Number</label>
                <div className="relative p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                  <span className="font-mono">{maskSensitiveData(achInfo.routingNumber, 4)}</span>
                  {achInfo.routingNumber && (
                    <button
                      onClick={() => copyToClipboard(achInfo.routingNumber)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Copy to clipboard"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Number</label>
                <div className="relative p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                  <span className="font-mono">{maskSensitiveData(achInfo.accountNumber, 4)}</span>
                  {achInfo.accountNumber && (
                    <button
                      onClick={() => copyToClipboard(achInfo.accountNumber)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Copy to clipboard"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowACHModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract View Modal */}
      {showContractModal && contractData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center flex-1">
                {contractData.isUploadedContractPreview ? 'Contract document' : 'Signed Service Agreement'}
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowContractModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {contractData.isUploadedContractPreview ? (
                <div className="space-y-4 p-1">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                    <p className="text-sm text-green-800 dark:text-green-200">
                      Uploaded contract file (preview below).
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-50 dark:bg-gray-900 min-h-[50vh] flex items-center justify-center">
                    {(() => {
                      const url = contractData.previewUrl || '';
                      const fromType = contractData.uploadFileType && String(contractData.uploadFileType).includes('image');
                      const fromName = /\.(jpe?g|png|gif|webp)(\?|$)/i.test((url.split('?')[0] || '').toLowerCase());
                      const showImage = fromType || fromName;
                      return showImage ? (
                        <img src={url} alt="Uploaded contract" className="max-w-full max-h-[70vh] object-contain" />
                      ) : (
                        <iframe title="Uploaded contract" src={url} className="w-full min-h-[65vh] border-0" />
                      );
                    })()}
                  </div>
                  <div className="flex justify-center pb-2">
                    <a
                      href={contractData.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 dark:text-green-400 hover:underline text-sm font-medium"
                    >
                      Open file in new tab
                    </a>
                  </div>
                </div>
              ) : (
              <div className="space-y-6">
                {/* Document Status */}
                {contractData.isFullyExecuted ? (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                      <h4 className="text-md font-semibold text-green-900 dark:text-green-100">Fully Executed Contract</h4>
                    </div>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      This contract has been signed by both parties and is fully executed as of {new Date(contractData.finalizedAt).toLocaleString()}.
                    </p>
                  </div>
                ) : (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2" />
                      <h4 className="text-md font-semibold text-orange-900 dark:text-orange-100">Awaiting BBA Signature</h4>
                    </div>
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                      This contract has been signed by the client but requires BBA signature to be fully executed.
                    </p>
                  </div>
                )}

                {/* Document Content */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Document Content</h4>
                  <div className="prose dark:prose-invert max-w-none prose-ul:list-disc prose-ul:ml-4 prose-li:my-1">
                    {contractData.templateElements && contractData.templateElements.length > 0 ? (
                      <div 
                        className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2 [&_li]:my-1 [&_ul_ul]:ml-4 [&_ul_ul]:mt-1 prose prose-sm dark:prose-invert"
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtml(renderContractElements(contractData.templateElements))
                        }}
                      />
                    ) : contractData.finalizedDocumentContent ? (
                      <div 
                        className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2 [&_li]:my-1 [&_ul_ul]:ml-4 [&_ul_ul]:mt-1 prose prose-sm dark:prose-invert"
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtml(renderContractContentWithVariables(
                            contractData.finalizedDocumentContent || contractData.documentContent || 'Document content not available'
                          ))
                        }}
                      />
                    ) : (
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto">
                        {renderContractContentWithVariables(
                          contractData.finalizedDocumentContent || contractData.documentContent || 'Document content not available'
                        )}
                      </pre>
                    )}
                  </div>
                </div>

                {/* Client Signature */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Client Electronic Signature</h4>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                    {contractData.signature}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {(() => {
                      console.log('Contract data for client signature:', {
                        signerName: contractData.signerName,
                        signerTitle: contractData.signerTitle,
                        companyName: contractData.companyName,
                        signedAt: contractData.signedAt
                      });
                      
                      console.log('Lead data for client signature:', {
                        firstName: lead?.firstName,
                        lastName: lead?.lastName,
                        contactName: lead?.contact?.name,
                        contactFirstName: lead?.contact?.firstName,
                        contactLastName: lead?.contact?.lastName,
                        discoverySignerName: lead?.discovery?.signerName,
                        discoveryData: lead?.discovery
                      });
                      
                      // Try to get the actual signer's name from lead data first
                      let displayName = 'Unknown';
                      
                      // Priority 1: Use discovery data first (most reliable for contract signer)
                      if (lead?.discovery) {
                        // First try to combine first and last name from discovery
                        if (lead.discovery.firstName && lead.discovery.lastName) {
                          displayName = `${lead.discovery.firstName} ${lead.discovery.lastName}`;
                        }
                        // Then look for any single name field in discovery data
                        else {
                          const discoveryName = lead.discovery.signerName || 
                                              lead.discovery.firstName || 
                                              lead.discovery.lastName ||
                                              lead.discovery.name ||
                                              lead.discovery.clientName ||
                                              lead.discovery.contactName;
                          if (discoveryName) {
                            displayName = discoveryName;
                          }
                        }
                      }
                      // Priority 2: Use lead's actual name
                      else if (lead?.firstName && lead?.lastName) {
                        displayName = `${lead.firstName} ${lead.lastName}`;
                      }
                      // Priority 3: Use lead contact name
                      else if (lead?.contact?.name) {
                        displayName = lead.contact.name;
                      }
                      // Priority 4: Use contact first/last name
                      else if (lead?.contact?.firstName && lead?.contact?.lastName) {
                        displayName = `${lead.contact.firstName} ${lead.contact.lastName}`;
                      }
                      // Priority 5: Try to extract from document content
                      else if (contractData.documentContent) {
                        // Look for common patterns in document content
                        const docContent = contractData.documentContent;
                        
                        // Pattern 1: Look for "Signed by: [Name]" or "Name: [Name]"
                        const signedByMatch = docContent.match(/(?:Signed by|Name):\s*([A-Za-z\s]+?)(?:\s*\(|,|\n|$)/i);
                        if (signedByMatch && signedByMatch[1] && !signedByMatch[1].includes('Chase') && !signedByMatch[1].includes('Bank')) {
                          displayName = signedByMatch[1].trim();
                        }
                        // Pattern 2: Look for "Client Name: [Name]"
                        else {
                          const clientNameMatch = docContent.match(/Client Name:\s*([A-Za-z\s]+?)(?:\s*\(|,|\n|$)/i);
                          if (clientNameMatch && clientNameMatch[1] && !clientNameMatch[1].includes('Chase') && !clientNameMatch[1].includes('Bank')) {
                            displayName = clientNameMatch[1].trim();
                          }
                        }
                      }
                      // Priority 6: Fall back to contract signerName (but avoid bank name)
                      else if (contractData.signerName && !contractData.signerName.includes('Chase') && !contractData.signerName.includes('Bank')) {
                        displayName = contractData.signerName;
                      }
                      // Last resort: use company name
                      else if (contractData.companyName) {
                        displayName = contractData.companyName;
                      }
                      
                      const displayTitle = contractData.signerTitle || lead?.discovery?.signerTitle || 'N/A';
                      const displayDate = contractData.signedAt ? new Date(contractData.signedAt).toLocaleString() : 'Unknown Date';
                      
                      return `Signed by: ${displayName} (${displayTitle}) on ${displayDate}`;
                    })()}
                  </div>
                </div>

                {/* BBA Signature (if fully executed) */}
                {contractData.isFullyExecuted && contractData.bbaSignature && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-blue-900 dark:text-blue-100 mb-3">BBA Electronic Signature</h4>
                    {(() => {
                      // The BBA signature data is stored directly in contractData.bbaSignature
                      const bbaFormData = contractData.bbaSignature;
                      
                      console.log('BBA Form Data for signature display:', bbaFormData);
                      console.log('All BBA Form Data keys:', Object.keys(bbaFormData));
                      console.log('All BBA Form Data values:', Object.values(bbaFormData));
                      
                      // Look for signature-type fields specifically (case insensitive)
                      const signatureKeys = Object.keys(bbaFormData).filter(key => 
                        key.toLowerCase().includes('signature') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                      );
                      
                      console.log('Signature keys found:', signatureKeys);
                      
                      // If we found signature fields, use the first one
                      if (signatureKeys.length > 0) {
                        const signatureValue = bbaFormData[signatureKeys[0]];
                        console.log('Using signature from key:', signatureKeys[0], 'value:', signatureValue);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {signatureValue}
                          </div>
                        );
                      }
                      
                      // Look for BBA signature fields with the specific pattern we know exists
                      const bbaSignatureKeys = Object.keys(bbaFormData).filter(key => 
                        key.includes('-bba-Signature-') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                      );
                      
                      console.log('BBA Signature keys found:', bbaSignatureKeys);
                      
                      if (bbaSignatureKeys.length > 0) {
                        const signatureValue = bbaFormData[bbaSignatureKeys[0]];
                        console.log('Using BBA signature from key:', bbaSignatureKeys[0], 'value:', signatureValue);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {signatureValue}
                          </div>
                        );
                      }
                      
                      // Look for any field that might contain signature text (longer text values)
                      // Filter out date, time, title, and name fields more strictly
                      const textFields = Object.entries(bbaFormData).filter(([key, value]) => {
                        if (!value || typeof value !== 'string' || value.trim().length < 3) return false;
                        
                        const keyLower = key.toLowerCase();
                        const valueStr = value.toString();
                        
                        // Skip fields that are clearly not signatures
                        if (keyLower.includes('date') || keyLower.includes('time') || 
                            keyLower.includes('title') || keyLower.includes('name')) return false;
                        
                        // Skip values that look like dates or times
                        if (valueStr.match(/^\d{4}-\d{2}-\d{2}$/) || // YYYY-MM-DD
                            valueStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/) || // MM/DD/YYYY
                            valueStr.match(/^\d{4}-\d{2}-\d{2}T/) || // ISO datetime
                            valueStr.match(/^\d{1,2}:\d{2}/) || // HH:MM
                            valueStr.match(/^\d+$/)) { // Pure numbers
                          return false;
                        }
                        
                        return true;
                      });
                      
                      // Also look for fields with "Signature" in the key name
                      const signatureFields = Object.entries(bbaFormData).filter(([key, value]) => 
                        key.toLowerCase().includes('signature') && value && typeof value === 'string' && value.trim().length > 0
                      );
                      
                      console.log('Text fields found:', textFields);
                      console.log('Signature fields found:', signatureFields);
                      
                      // If we have signature fields, use the first one
                      if (signatureFields.length > 0) {
                        const signatureValue = signatureFields[0][1];
                        console.log('Using signature field:', signatureFields[0][0], 'value:', signatureValue);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {signatureValue}
                          </div>
                        );
                      }
                      
                      // If we have text fields, use the longest one (most likely the signature)
                      if (textFields.length > 0) {
                        const longestField = textFields.reduce((longest, current) => 
                          current[1].length > longest[1].length ? current : longest
                        );
                        console.log('Using longest text field as signature:', longestField);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {longestField[1]}
                          </div>
                        );
                      }
                      
                      // Fallback: look for any non-empty values that might be signatures (excluding dates)
                      const nonEmptyValues = Object.values(bbaFormData).filter(value => {
                        if (!value || typeof value !== 'string' || value.trim().length === 0) return false;
                        const valueStr = value.toString();
                        // Skip date-like values
                        return !valueStr.match(/^\d{4}-\d{2}-\d{2}$/) && 
                               !valueStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/) && 
                               !valueStr.match(/^\d{4}-\d{2}-\d{2}T/);
                      });
                      
                      console.log('Non-empty values found:', nonEmptyValues);
                      
                      // If we have values, use the first one (most likely the signature)
                      if (nonEmptyValues.length > 0) {
                        console.log('Using first non-empty value as signature:', nonEmptyValues[0]);
                        return (
                          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                            {nonEmptyValues[0]}
                          </div>
                        );
                      }
                      
                      // Final fallback - show the signer name in regular font (not cursive)
                      console.log('No signature found, using signer name as fallback');
                      return (
                        <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          BBA Signature
                        </div>
                      );
                    })()}
                    <div className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      {(() => {
                        // Extract signer name, title, and date from the BBA signature data
                        const bbaFormData = contractData.bbaSignature;
                        
                        // Find the name field - try both direct field names and NDA pattern
                        let signerName = bbaFormData.bbaName || bbaFormData.name || 'Unknown';
                        if (signerName === 'Unknown') {
                          const nameKeys = Object.keys(bbaFormData).filter(key => 
                            key.includes('-bba-Name-') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                          );
                          if (nameKeys.length > 0) {
                            signerName = bbaFormData[nameKeys[0]];
                          }
                        }
                        
                        // Find the title field
                        let signerTitle = bbaFormData.bbaTitle || bbaFormData.title || '';
                        if (!signerTitle) {
                          const titleKeys = Object.keys(bbaFormData).filter(key => 
                            key.includes('-bba-Title-') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                          );
                          if (titleKeys.length > 0) {
                            signerTitle = bbaFormData[titleKeys[0]];
                          }
                        }
                        
                        // Find the date field - try both direct field names and NDA pattern
                        let signDate = bbaFormData.bbaSignatureDate || bbaFormData.date || 'Unknown Date';
                        if (signDate === 'Unknown Date') {
                          const dateKeys = Object.keys(bbaFormData).filter(key => 
                            key.includes('-bba-Date-') && bbaFormData[key] && bbaFormData[key].trim().length > 0
                          );
                          if (dateKeys.length > 0) {
                            signDate = bbaFormData[dateKeys[0]];
                          }
                        }
                        
                        // Format the display string
                        const titleText = signerTitle ? ` (${signerTitle})` : '';
                        return `Signed by: ${signerName}${titleText} on ${signDate}`;
                      })()}
                    </div>
                  </div>
                )}

                {/* E-SIGN Act Compliance */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-900 dark:text-blue-100 mb-3">E-SIGN Act Compliance</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Electronic signature consent: {contractData.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Electronic records consent: {contractData.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Signed at: {new Date(contractData.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        IP Address: {contractData.eSignCompliance?.auditTrail?.ipAddress}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-blue-800 dark:text-blue-200">
                        Browser: {contractData.eSignCompliance?.auditTrail?.browser} on {contractData.eSignCompliance?.auditTrail?.device}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              {contractData.isUploadedContractPreview ? (
                <>
                  <a
                    href={contractData.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    Open in new tab
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowContractModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                  >
                    Close
                  </button>
                </>
              ) : contractData.isFullyExecuted ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowContractAuditTrailModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Audit Trail
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadContract}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowContractModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleOpenContractBBASigning}
                    className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm flex items-center"
                  >
                    <PenTool className="h-4 w-4 mr-2" />
                    BBA Sign Required
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowContractModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contract BBA Signing Modal */}
      {showContractBBASigningModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="relative flex justify-center items-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                BBA Contract Signature
              </h3>
              <button
                onClick={() => setShowContractBBASigningModal(false)}
                className="absolute right-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Contract Document */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Contract Document</h4>
                <div className="prose dark:prose-invert max-w-none prose-ul:list-disc prose-ul:ml-4 prose-li:my-1">
                  {lead.signedContract?.finalizedDocumentContent ? (
                    <div 
                      className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2 [&_li]:my-1 [&_ul_ul]:ml-4 [&_ul_ul]:mt-1 prose prose-sm dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(lead.signedContract.finalizedDocumentContent) }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto">
                      {lead.signedContract?.documentContent || 'Document content not available'}
                    </pre>
                  )}
                </div>
              </div>

              {/* BBA Signature Form */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">BBA Signature Information</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    BBA Representative Name *
                  </label>
                  <input
                    type="text"
                    value={contractBBAData.bbaName || ''}
                    onChange={(e) => setContractBBAData({...contractBBAData, bbaName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter BBA representative name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    BBA Representative Title *
                  </label>
                  <input
                    type="text"
                    value={contractBBAData.bbaTitle || ''}
                    onChange={(e) => setContractBBAData({...contractBBAData, bbaTitle: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter BBA representative title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    BBA Signature *
                  </label>
                  <input
                    type="text"
                    value={contractBBAData.bbaSignature || ''}
                    onChange={(e) => setContractBBAData({...contractBBAData, bbaSignature: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter BBA signature"
                    style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Signature Date *
                  </label>
                  <input
                    type="date"
                    value={contractBBAData.bbaSignatureDate || ''}
                    onChange={(e) => setContractBBAData({...contractBBAData, bbaSignatureDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
            
            {contractSigningError && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">{contractSigningError}</p>
              </div>
            )}
            
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => {
                  setShowContractBBASigningModal(false);
                  setContractSigningError(null);
                }}
                disabled={contractSigningLoading}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleContractBBASigning}
                disabled={!contractBBAData.bbaName || !contractBBAData.bbaTitle || !contractBBAData.bbaSignature || !contractBBAData.bbaSignatureDate || contractSigningLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {contractSigningLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Sign & Finalize Contract
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Modal */}
      {showAuditTrailModal && signedNDA && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                NDA Audit Trail
              </h3>
              <button
                onClick={() => setShowAuditTrailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Document Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Document Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Document ID:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{signedNDA.documentId || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Document Type:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">Non-Disclosure Agreement</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Company:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{signedNDA.companyName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signer:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{signedNDA.signerName || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Signature Details */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Signature Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signed At:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {new Date(signedNDA.signedAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signature Method:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">Electronic Signature</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signature:</span>
                    <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-white" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                      {signedNDA.signature || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* E-SIGN Act Compliance */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">E-SIGN Act Compliance</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-blue-800 dark:text-blue-200">Electronic Signature Consent:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      signedNDA.eSignCompliance?.consentToElectronicSignatures 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {signedNDA.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-blue-800 dark:text-blue-200">Electronic Records Consent:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      signedNDA.eSignCompliance?.consentToElectronicRecords 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {signedNDA.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Technical Audit Trail */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Technical Audit Trail</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Timestamp:</span>
                    <span className="text-gray-900 dark:text-white">
                      {new Date(signedNDA.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">IP Address:</span>
                    <span className="text-gray-900 dark:text-white font-mono">
                      {signedNDA.eSignCompliance?.auditTrail?.ipAddress || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Browser:</span>
                    <span className="text-gray-900 dark:text-white">
                      {signedNDA.eSignCompliance?.auditTrail?.browser || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Device:</span>
                    <span className="text-gray-900 dark:text-white">
                      {signedNDA.eSignCompliance?.auditTrail?.device || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">User Agent:</span>
                    <span className="text-gray-900 dark:text-white text-xs break-all">
                      {signedNDA.eSignCompliance?.auditTrail?.userAgent || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Document Hash/Integrity */}
              {signedNDA.documentHash && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Document Integrity</h4>
                  <div className="text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Document Hash:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs break-all">
                      {signedNDA.documentHash}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => setShowAuditTrailModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Audit Trail Modal */}
      {showContractAuditTrailModal && contractData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Service Agreement Audit Trail
              </h3>
              <button
                onClick={() => setShowContractAuditTrailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Document Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Document Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Document ID:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {(() => {
                        // Try to get a unique contract ID
                        if (contractData.contractId) {
                          return contractData.contractId;
                        }
                        // If no contractId, generate one based on lead ID and contract data
                        if (contractData.documentId && contractData.documentId.includes('nda-')) {
                          // If it's an NDA ID, generate a contract-specific ID using lead ID
                          const contractId = `contract-${leadId}-${contractData.signedAt ? new Date(contractData.signedAt).getTime() : Date.now()}`;
                          return contractId;
                        }
                        // Use the existing documentId if it's not an NDA ID
                        return contractData.documentId || 'N/A';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Document Type:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">Service Agreement</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Company:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{contractData.companyName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signer:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {(() => {
                        // Use the same logic as the contract modal to get the correct signer name
                        let displayName = 'Unknown';
                        
                        // Priority 1: Use discovery data first (most reliable for contract signer)
                        if (lead?.discovery) {
                          // First try to combine first and last name from discovery
                          if (lead.discovery.firstName && lead.discovery.lastName) {
                            displayName = `${lead.discovery.firstName} ${lead.discovery.lastName}`;
                          }
                          // Then look for any single name field in discovery data
                          else {
                            const discoveryName = lead.discovery.signerName || 
                                                lead.discovery.firstName || 
                                                lead.discovery.lastName ||
                                                lead.discovery.name ||
                                                lead.discovery.clientName ||
                                                lead.discovery.contactName;
                            if (discoveryName) {
                              displayName = discoveryName;
                            }
                          }
                        }
                        // Priority 2: Use lead's actual name
                        else if (lead?.firstName && lead?.lastName) {
                          displayName = `${lead.firstName} ${lead.lastName}`;
                        }
                        // Priority 3: Use lead's contact name
                        else if (lead?.contact?.name) {
                          displayName = lead.contact.name;
                        }
                        // Priority 4: Use lead's contact first and last name
                        else if (lead?.contact?.firstName && lead?.contact?.lastName) {
                          displayName = `${lead.contact.firstName} ${lead.contact.lastName}`;
                        }
                        // Priority 5: Try to extract from document content (avoid bank names)
                        else if (contractData.finalizedDocumentContent) {
                          const docContent = contractData.finalizedDocumentContent;
                          // Pattern 1: Look for "Signed by: [Name]" or "Name: [Name]"
                          const signedByMatch = docContent.match(/(?:Signed by|Name):\s*([A-Za-z\s]+?)(?:\s*\(|,|\n|$)/i);
                          if (signedByMatch && signedByMatch[1] && !signedByMatch[1].includes('Chase') && !signedByMatch[1].includes('Bank')) {
                            displayName = signedByMatch[1].trim();
                          }
                          // Pattern 2: Look for "Client Name: [Name]"
                          else {
                            const clientNameMatch = docContent.match(/Client Name:\s*([A-Za-z\s]+?)(?:\s*\(|,|\n|$)/i);
                            if (clientNameMatch && clientNameMatch[1] && !clientNameMatch[1].includes('Chase') && !clientNameMatch[1].includes('Bank')) {
                              displayName = clientNameMatch[1].trim();
                            }
                          }
                        }
                        // Priority 6: Fall back to contract signerName (but avoid bank name)
                        else if (contractData.signerName && !contractData.signerName.includes('Chase') && !contractData.signerName.includes('Bank')) {
                          displayName = contractData.signerName;
                        }
                        // Last resort: use company name
                        else if (contractData.companyName) {
                          displayName = contractData.companyName;
                        }
                        
                        return displayName;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Signature Details */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Signature Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signed At:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {new Date(contractData.signedAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signature Method:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">Electronic Signature</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Signature:</span>
                    <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-white" style={{fontFamily: 'Dancing Script, Brush Script MT, cursive'}}>
                      {contractData.signature || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* E-SIGN Act Compliance */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">E-SIGN Act Compliance</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-blue-800 dark:text-blue-200">Electronic Signature Consent:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      contractData.eSignCompliance?.consentToElectronicSignatures 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {contractData.eSignCompliance?.consentToElectronicSignatures ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-blue-800 dark:text-blue-200">Electronic Records Consent:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      contractData.eSignCompliance?.consentToElectronicRecords 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {contractData.eSignCompliance?.consentToElectronicRecords ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Technical Audit Trail */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Technical Audit Trail</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Timestamp:</span>
                    <span className="text-gray-900 dark:text-white">
                      {new Date(contractData.eSignCompliance?.auditTrail?.signedDateTime).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">IP Address:</span>
                    <span className="text-gray-900 dark:text-white font-mono">
                      {contractData.eSignCompliance?.auditTrail?.ipAddress || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Browser:</span>
                    <span className="text-gray-900 dark:text-white">
                      {contractData.eSignCompliance?.auditTrail?.browser || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Device:</span>
                    <span className="text-gray-900 dark:text-white">
                      {contractData.eSignCompliance?.auditTrail?.device || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">User Agent:</span>
                    <span className="text-gray-900 dark:text-white text-xs break-all">
                      {contractData.eSignCompliance?.auditTrail?.userAgent || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Document Hash/Integrity */}
              {contractData.documentHash && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Document Integrity</h4>
                  <div className="text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Document Hash:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs break-all">
                      {contractData.documentHash}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => setShowContractAuditTrailModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BBA Signing Modal */}
      {showBBASigningModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center flex-1">BBA Signature Required</h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowBBASigningModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {templateLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading NDA template...</p>
                </div>
              ) : templateError ? (
                <div className="text-center py-12">
                  <div className="text-red-600 dark:text-red-400 mb-4">
                    <X className="h-8 w-8 mx-auto" />
                  </div>
                  <p className="text-red-600 dark:text-red-400 text-center mb-4">{templateError}</p>
                  <button
                    onClick={() => loadNDATemplate()}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* NDA Document Content */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">NDA Document</h4>
                    <div className="prose dark:prose-invert max-w-none prose-ul:list-disc prose-ul:ml-4 prose-li:my-1">
                      <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded border max-h-96 overflow-y-auto [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2 [&_li]:my-1 [&_ul_ul]:ml-4 [&_ul_ul]:mt-1">
                        {renderedNDAContent || <p>No NDA template content available</p>}
                      </div>
                    </div>
                  </div>

                  {/* BBA Signature Form */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">BBA Signature Required</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Please complete the BBA-assigned fields and sign the document to finalize the NDA.
                    </p>
                    
                    <div className="space-y-4">
                      {getBBAVariables().length > 0 ? (
                        getBBAVariables().map((variable) => (
                          <div key={variable.id} className="w-full">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {variable.variableTitle}*
                            </label>
                            
                            {variable.type === 'checkbox' ? (
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={bbaFormData[variable.id] === 'true' || bbaFormData[variable.id] === true}
                                  onChange={(e) => handleBBAFormChange(variable.id, e.target.checked ? 'true' : 'false')}
                                  className="mr-2"
                                />
                              </div>
                            ) : variable.type === 'date' ? (
                              <input
                                type="date"
                                value={bbaFormData[variable.id] || ''}
                                onChange={(e) => handleBBAFormChange(variable.id, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                              />
                            ) : (
                              <input
                                type="text"
                                value={bbaFormData[variable.id] || ''}
                                onChange={(e) => handleBBAFormChange(variable.id, e.target.value)}
                                placeholder={variable.content}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                              />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-500 dark:text-gray-400 mb-4">
                            <Shield className="h-12 w-12 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                              No BBA Variables Found
                            </h3>
                            <p className="text-sm">
                              This NDA template doesn't have any BBA-assigned variables. 
                              The document can be signed without additional BBA input.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => setShowBBASigningModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleBBASigning}
                disabled={(() => {
                  const bbaVars = getBBAVariables();
                  const hasEmptyFields = bbaVars.length > 0 && bbaVars.some(variable => !bbaFormData[variable.id]);
                  console.log('Button disabled check:', {
                    templateLoading,
                    bbaVarsCount: bbaVars.length,
                    bbaVars: bbaVars.map(v => ({ id: v.id, title: v.variableTitle })),
                    formData: bbaFormData,
                    hasEmptyFields,
                    disabled: templateLoading || hasEmptyFields
                  });
                  return templateLoading || hasEmptyFields;
                })()}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Shield className="h-4 w-4 mr-2" />
                Sign & Finalize NDA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACH Upload Modal */}
      {showACHUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1"></div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white text-center flex-1">
                ACH Bank Information
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowACHUploadModal(false);
                    setAchFormData({
                      accountType: '',
                      bankName: '',
                      routingNumber: '',
                      accountNumber: '',
                      nameOnAccount: ''
                    });
                    setAchFormErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-purple-50 dark:bg-purple-900 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h5 className="text-sm font-medium text-purple-800 dark:text-purple-200">Enter ACH Information for {lead?.company || lead?.contact?.company || 'Lead'}</h5>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                      Please provide the bank account information for this lead. This information will be stored securely.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleACHFormSubmit(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Account Type *
                  </label>
                  <select
                    value={achFormData.accountType}
                    onChange={(e) => setAchFormData({ ...achFormData, accountType: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      achFormErrors.accountType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Account Type</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                  {achFormErrors.accountType && <p className="text-red-500 text-sm mt-1">{achFormErrors.accountType}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    value={achFormData.bankName}
                    onChange={(e) => setAchFormData({ ...achFormData, bankName: e.target.value })}
                    placeholder="Enter bank name"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      achFormErrors.bankName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {achFormErrors.bankName && <p className="text-red-500 text-sm mt-1">{achFormErrors.bankName}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Routing Number *
                  </label>
                  <input
                    type="text"
                    value={achFormData.routingNumber}
                    onChange={(e) => {
                      const formatted = formatRoutingNumber(e.target.value);
                      setAchFormData({ ...achFormData, routingNumber: formatted });
                    }}
                    placeholder="Enter routing number"
                    maxLength="9"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      achFormErrors.routingNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {achFormErrors.routingNumber && <p className="text-red-500 text-sm mt-1">{achFormErrors.routingNumber}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    value={achFormData.accountNumber}
                    onChange={(e) => {
                      const formatted = formatAccountNumber(e.target.value);
                      setAchFormData({ ...achFormData, accountNumber: formatted });
                    }}
                    placeholder="Enter account number"
                    maxLength="17"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      achFormErrors.accountNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {achFormErrors.accountNumber && <p className="text-red-500 text-sm mt-1">{achFormErrors.accountNumber}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name on Account *
                  </label>
                  <input
                    type="text"
                    value={achFormData.nameOnAccount}
                    onChange={(e) => setAchFormData({ ...achFormData, nameOnAccount: e.target.value })}
                    placeholder="Enter name on account"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      achFormErrors.nameOnAccount ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {achFormErrors.nameOnAccount && <p className="text-red-500 text-sm mt-1">{achFormErrors.nameOnAccount}</p>}
                </div>
              </form>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowACHUploadModal(false);
                  setAchFormData({
                    accountType: '',
                    bankName: '',
                    routingNumber: '',
                    accountNumber: '',
                    nameOnAccount: ''
                  });
                  setAchFormErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleACHFormSubmit}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Save ACH Information
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Development Debuggers */}
    </div>
  );
};

export default LeadOverview; 
